import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import * as XLSX from "xlsx-js-style";
import JuridictionView from "./JuridictionView";
// Export Excel DCF géré par le backend Python (FastAPI)


// ==========================================================================
// DCF MODEL EXCEL EXPORT — audit-ready, fully chained formulas
// ==========================================================================


// ==========================================================================
// CALCULATION ENGINES (local JS — identical to Python API)
// ==========================================================================
function calcDCF(p){const proj=[];let rev=p.base_revenue;for(let y=1;y<=p.forecast_years;y++){const t=Math.min(y/p.growth_convergence_year,1),gr=p.revenue_growth_initial+(p.revenue_growth_terminal-p.revenue_growth_initial)*t;rev*=(1+gr);const tm=Math.min(y/p.margin_convergence_year,1),mg=p.base_operating_margin+(p.target_operating_margin-p.base_operating_margin)*tm,ebit=rev*mg,nopat=ebit*(1-p.tax_rate),reinv=p.sales_to_capital_ratio?((rev-(y===1?p.base_revenue:proj[y-2].revenue))/p.sales_to_capital_ratio):0,fcff=nopat-reinv,tw=Math.min(y/p.wacc_convergence_year,1),wacc=p.cost_of_capital_initial+(p.cost_of_capital_terminal-p.cost_of_capital_initial)*tw,ic=y===1?(p.sales_to_capital_ratio?p.base_revenue/p.sales_to_capital_ratio:p.base_revenue):proj[y-2]._ic+reinv,roic=nopat/ic;proj.push({year:y,revenue:rev,revenue_growth_rate:gr,operating_margin:mg,ebit,nopat,reinvestment:reinv,fcff,wacc,roic,_ic:ic});}let cum=1,sumPV=0;for(const x of proj){cum*=(1+x.wacc);x.discount_factor=1/cum;x.pv_fcff=x.fcff*x.discount_factor;sumPV+=x.pv_fcff;}const lw=proj[proj.length-1].wacc,tv=(proj[proj.length-1].fcff*(1+p.terminal_growth_rate))/(lw-p.terminal_growth_rate),pvTV=tv*proj[proj.length-1].discount_factor,ev=sumPV+pvTV,eq=ev-p.net_debt-p.minority_interests;return{company_name:p.company_name,equity_value:eq,enterprise_value:ev,value_per_share:p.shares_outstanding>0?eq/p.shares_outstanding:null,terminal_value:pvTV,sum_pv_fcff:sumPV,implied_exit_multiple:proj[proj.length-1].nopat>0?tv/proj[proj.length-1].nopat:null,terminal_value_pct:(ev!==0&&isFinite(ev))?pvTV/ev:null,projections:proj.map(x=>({year:x.year,revenue:x.revenue,revenue_growth_rate:x.revenue_growth_rate,operating_margin:x.operating_margin,nopat:x.nopat,fcff:x.fcff,roic:x.roic,wacc:x.wacc,pv_fcff:x.pv_fcff}))};}

function calcExcess(p){const proj=[];let rev=p.base_revenue,ic=p.base_invested_capital;for(let y=1;y<=p.forecast_years;y++){const tg=Math.min(y/p.growth_convergence_year,1),gr=p.revenue_growth_initial+(p.revenue_growth_terminal-p.revenue_growth_initial)*tg;rev*=(1+gr);const tr=Math.min(y/p.roic_convergence_year,1),roic=p.roic_initial+(p.roic_terminal-p.roic_initial)*tr,nopat=ic*roic,tw=Math.min(y/p.wacc_convergence_year,1),wacc=p.cost_of_capital+(p.cost_of_capital_terminal-p.cost_of_capital)*tw,capCharge=ic*wacc,excess=nopat-capCharge,tri=Math.min(y/p.reinvestment_convergence_year,1),rr=p.reinvestment_rate_initial+(p.reinvestment_rate_terminal-p.reinvestment_rate_initial)*tri,reinv=nopat*rr;proj.push({year:y,revenue:rev,nopat,capital_charge:capCharge,excess_return:excess,roic,wacc,reinvestment:reinv,invested_capital:ic});ic+=reinv;}let cm=1,sPV=0,tVC=0;for(const x of proj){cm*=(1+x.wacc);x.pv_excess_return=x.excess_return/cm;sPV+=x.pv_excess_return;tVC+=x.excess_return;}const ev=p.base_invested_capital+sPV,eq=ev-p.net_debt-p.minority_interests,sp=proj.map(x=>x.roic-x.wacc);return{equity_value:eq,enterprise_value:ev,value_per_share:p.shares_outstanding>0?eq/p.shares_outstanding:null,base_invested_capital:p.base_invested_capital,total_value_created:tVC,avg_roic_wacc_spread:sp.reduce((a,b)=>a+b,0)/sp.length,projections:proj};}

function calcMultiples(p){const types=[{key:"ev_revenue",metric:p.revenue,label:"EV/Revenue"},{key:"ev_ebitda",metric:p.ebitda,label:"EV/EBITDA"},{key:"ev_ebit",metric:p.ebit,label:"EV/EBIT"},{key:"pe_ratio",metric:p.net_income,label:"P/E",isEq:true},{key:"pb_ratio",metric:p.book_value_equity,label:"P/B",isEq:true}];const res=[];for(const t of types){const vals=p.comparables.filter(c=>c[t.key]!=null).map(c=>({v:c[t.key],w:c.weight||1}));if(!vals.length||!t.metric)continue;const wS=vals.reduce((a,b)=>a+b.v*b.w,0),wT=vals.reduce((a,b)=>a+b.w,0),wA=wS/wT,so=vals.map(v=>v.v).sort((a,b)=>a-b),med=so.length%2===0?(so[so.length/2-1]+so[so.length/2])/2:so[Math.floor(so.length/2)],rEV=t.isEq?null:wA*t.metric,rEq=t.isEq?wA*t.metric:(rEV?rEV-p.net_debt-p.minority_interests:null),adj=(1-p.illiquidity_discount)*(1+p.control_premium)*(1-p.size_discount);res.push({multiple_type:t.label,weighted_avg_multiple:wA,median_multiple:med,enterprise_value:t.isEq?(rEq||0)+p.net_debt:rEV,equity_value:rEq?rEq*adj:null});}const evs=res.filter(r=>r.enterprise_value).map(r=>r.enterprise_value),eqs=res.filter(r=>r.equity_value).map(r=>r.equity_value);return{enterprise_value_low:evs.length?Math.min(...evs):null,enterprise_value_mid:evs.length?evs.reduce((a,b)=>a+b,0)/evs.length:null,enterprise_value_high:evs.length?Math.max(...evs):null,equity_value_low:eqs.length?Math.min(...eqs):null,equity_value_mid:eqs.length?eqs.reduce((a,b)=>a+b,0)/eqs.length:null,equity_value_high:eqs.length?Math.max(...eqs):null,multiple_results:res};}

function calcRfR(p){const proj=[];let rev=p.base_revenue;for(let y=1;y<=p.forecast_years;y++){const t=Math.min(y/p.growth_convergence_year,1),gr=p.revenue_growth_initial+(p.revenue_growth_terminal-p.revenue_growth_initial)*t;rev*=(1+gr);const roy=rev*p.royalty_rate,obs=Math.pow(1-p.obsolescence_factor,y),adj=roy*(1-p.tax_rate)*obs;proj.push({year:y,revenue:rev,royalty_saving:roy,adjusted_royalty:adj,obsolescence:obs});}let cm=1,sPV=0;for(const x of proj){cm*=(1+p.discount_rate);x.pv=x.adjusted_royalty/cm;sPV+=x.pv;}const tab=sPV*0.134,fv=sPV+tab;const sens=[.03,.04,.05,.06,.07].map(rate=>{let r2=p.base_revenue,s=0,c=1;for(let y=1;y<=p.forecast_years;y++){const t=Math.min(y/p.growth_convergence_year,1);r2*=(1+p.revenue_growth_initial+(p.revenue_growth_terminal-p.revenue_growth_initial)*t);c*=(1+p.discount_rate);s+=r2*rate*(1-p.tax_rate)/c;}return{label:`${(rate*100).toFixed(0)}%`,fair_value:s*1.134};});return{fair_value:fv,sum_pv_royalties:sPV,tax_amortization_benefit:tab,avg_royalty_saving:proj.reduce((a,x)=>a+x.adjusted_royalty,0)/proj.length,implied_royalty_multiple:(p.base_revenue&&p.royalty_rate)?fv/(p.base_revenue*p.royalty_rate):null,projections:proj,sensitivity:sens};}

function calcMPEEM(p){const proj=[];let rev=p.base_revenue;for(let y=1;y<=p.forecast_years;y++){const t=Math.min(y/p.growth_convergence_year,1),gr=p.revenue_growth_initial+(p.revenue_growth_terminal-p.revenue_growth_initial)*t;rev*=(1+gr);const nopat=rev*p.operating_margin*(1-p.tax_rate),tc=(p.contributory_assets||[]).reduce((a,ca)=>a+ca.value*ca.charge_rate,0),excess=nopat-tc,att=Math.pow(1-p.annual_attrition_rate,y),adjE=excess*att;proj.push({year:y,revenue:rev,nopat,contributory_charge:tc,excess_earnings:excess,attrition_factor:att,adjusted_excess:adjE});}let cm=1,sPV=0;for(const x of proj){cm*=(1+p.discount_rate);x.pv=x.adjusted_excess/cm;sPV+=x.pv;}const tab=sPV*.134,fv=sPV+tab;let pb=null,cumCF=0;for(const x of proj){cumCF+=x.adjusted_excess;if(cumCF>=fv&&!pb)pb=x.year;}return{fair_value:fv,sum_pv_excess:sPV,tax_amortization_benefit:tab,year_1_excess_earnings:proj[0]?.adjusted_excess,implied_multiple:proj[0]?.adjusted_excess>0?fv/proj[0].adjusted_excess:null,payback_years:pb,projections:proj};}

function calcAsset(p){const tBA=p.assets.reduce((a,x)=>a+(x.book_value||0),0),tFA=p.assets.reduce((a,x)=>a+(x.fair_value||0),0),tBL=p.liabilities.reduce((a,x)=>a+(x.book_value||0),0),tFL=p.liabilities.reduce((a,x)=>a+(x.fair_value||x.book_value||0),0),gains=tFA-tBA,dT=p.apply_deferred_tax?gains*(p.deferred_tax_rate||0):0,bNav=tBA-tBL,aNav=tFA-tFL-(p.contingent_liabilities||0)-dT,ad=p.assets.map(a=>({name:a.name,category:a.category,book_value:a.book_value||0,fair_value:a.fair_value||0,adjustment:(a.fair_value||0)-(a.book_value||0),liquidation_value:(a.fair_value||0)*(1-(a.liquidation_discount||0))})),tLiq=ad.reduce((a,x)=>a+x.liquidation_value,0),lNav=tLiq-tFL-(p.contingent_liabilities||0)-dT;return{adjusted_nav:aNav,liquidation_nav:lNav,book_nav:bNav,premium_to_book:bNav>0?(aNav-bNav)/bNav:null,deferred_tax_on_gains:dT,total_assets_fair:tFA,value_per_share_anav:p.shares_outstanding>0?aNav/p.shares_outstanding:null,assets_detail:ad};}


// ==========================================================================
// FRENCH FISCAL ADMINISTRATION ENGINES — Guide DGI nov. 2006
// ==========================================================================

// --- VP: Valeur de Productivité ---
// Capitalise le bénéfice net courant moyen pondéré
// VP = Bénéfice_moyen / Taux_capitalisation
// Taux = Taux_sans_risque_déflaté + β × Prime_risque_historique
function calcVP(p) {
  // Bénéfice moyen pondéré (coeff 1,2,3 sur 3 ans)
  const total_w = (p.benefice_n2 * 1 + p.benefice_n1 * 2 + p.benefice_n * 3);
  const bef_moy = total_w / 6;
  // Ajustements : salaire dirigeant (sociétés familiales), éléments exceptionnels non récurrents
  const bef_recurrent = bef_moy + (p.reintegration_dirigeant || 0) - (p.elements_non_recurrents || 0);
  // Taux: rf déflaté + β × 5%
  const rf_deflate = p.taux_rf - p.taux_inflation;
  const prime_risque = p.beta_risque * 0.05;
  const taux_cap = rf_deflate + prime_risque;
  if (taux_cap <= 0) throw new Error("Taux de capitalisation nul ou négatif");
  const vp = bef_recurrent / taux_cap;
  const vp_unitaire = p.nombre_titres > 0 ? vp / p.nombre_titres : null;
  return {
    bef_moyen_pondere: bef_moy,
    bef_recurrent,
    rf_deflate,
    prime_risque,
    taux_capitalisation: taux_cap,
    valeur_productivite: vp,
    valeur_unitaire: vp_unitaire,
    // pour combinaison
    equity_value: vp,
  };
}

// --- VM: Valeur Mathématique (Actif Net Réévalué) ---
// VM = Capitaux Propres + Plus-values latentes - Impôt latent (si applicable) - Passif réel
function calcVM(p) {
  const total_pv = (p.revaluations||[]).reduce((s, r) => s + r.plus_value, 0);
  const impot_latent = p.apply_impot_latent ? total_pv * p.taux_is : 0;
  const vm = p.capitaux_propres + total_pv - impot_latent - (p.passif_hors_bilan || 0);
  // Survaleur (goodwill) calculée ici si demandée
  let survaleur = 0, superprofit = 0, taux_survaleur = 0;
  if (p.include_survaleur && p.bef_recurrent != null) {
    taux_survaleur = p.taux_rf - p.taux_inflation + p.beta_risque * 0.05;
    const anc_hors_fonds = vm - (p.valeur_fonds_commerce || 0);
    const remuneration_cap = anc_hors_fonds * taux_survaleur;
    superprofit = p.bef_recurrent - remuneration_cap;
    // Coefficient = valeur actualisée rente 1€ sur n ans au taux i
    const n = p.duree_survaleur || 8;
    const i = taux_survaleur;
    const coeff = i > 0 ? (1 - Math.pow(1 + i, -n)) / i : n;
    survaleur = superprofit * coeff;
  }
  const vm_avec_survaleur = vm + Math.max(survaleur, 0);
  const vm_unitaire = p.nombre_titres > 0 ? vm_avec_survaleur / p.nombre_titres : null;
  return {
    capitaux_propres: p.capitaux_propres,
    total_plus_values: total_pv,
    impot_latent,
    passif_hors_bilan: p.passif_hors_bilan || 0,
    valeur_mathematique_brute: vm,
    taux_survaleur,
    superprofit,
    survaleur,
    valeur_mathematique: vm_avec_survaleur,
    valeur_unitaire: vm_unitaire,
    equity_value: vm_avec_survaleur,
    revaluations: p.revaluations,
  };
}

// --- VR: Valeur de Rendement ---
// VR = Dividende_moyen / Taux_rendement × (1 - décote non-liquidité)
function calcVR(p) {
  // Dividende moyen pondéré ou simple
  const total_w = (p.dividende_n2 * 1 + p.dividende_n1 * 2 + p.dividende_n * 3);
  const div_moy = total_w / 6;
  // Taux: taux rendement actions cotées (historique ~3%) + décote non-liquidité 30%
  const taux_base = p.taux_rendement_actions_cotees || 0.03;
  const valeur_brute = div_moy / taux_base;
  const decote_liquidite = p.decote_non_liquidite || 0.30;
  const vr = valeur_brute * (1 - decote_liquidite);
  const vr_unitaire = p.nombre_titres > 0 ? vr / p.nombre_titres : null;
  return {
    dividende_moyen: div_moy,
    taux_base,
    valeur_brute,
    decote_liquidite,
    valeur_rendement: vr,
    valeur_unitaire: vr_unitaire,
    equity_value: vr,
  };
}

// --- MBA: Valeur par la Marge Brute d'Autofinancement ---
// MBA = Bénéfice net + Dotations amort + Variations provisions à caractère réserves
// Valeur = MBA × Coefficient - Endettement financier net
function calcMBA(p) {
  const mba = p.benefice_net + p.dotations_amortissements + (p.dotations_provisions_reserves || 0) - (p.reprises_provisions || 0);
  const valeur_ev = mba * p.coefficient_mba;
  const valeur_equity = valeur_ev - p.endettement_financier_net;
  const ve_unitaire = p.nombre_titres > 0 ? valeur_equity / p.nombre_titres : null;
  return {
    mba,
    coefficient: p.coefficient_mba,
    valeur_enterprise: valeur_ev,
    endettement_net: p.endettement_financier_net,
    valeur_capitaux_propres: valeur_equity,
    valeur_unitaire: ve_unitaire,
    equity_value: valeur_equity,
  };
}

// --- VE_EBE: Valeur par l'EBE (EBITDA multiple) ---
// VE = EBE × Multiple ; Equity = VE - Endettement net
function calcVE_EBE(p) {
  // EBE = VA + Subv. exploitation - Impôts/taxes - Charges personnel
  // ou EBE = RE + Dotations amort/prov + Autres charges - Reprises
  const ebe = p.ebe; // user inputs directly
  const valeur_ev = ebe * p.multiple_ebe;
  const valeur_equity = valeur_ev - p.endettement_financier_net;
  const ve_unitaire = p.nombre_titres > 0 ? valeur_equity / p.nombre_titres : null;
  return {
    ebe,
    multiple: p.multiple_ebe,
    valeur_enterprise: valeur_ev,
    endettement_net: p.endettement_financier_net,
    valeur_capitaux_propres: valeur_equity,
    valeur_unitaire: ve_unitaire,
    equity_value: valeur_equity,
  };
}

// --- VE_RE: Valeur par le Résultat d'Exploitation (EBIT multiple) ---
function calcVE_RE(p) {
  const valeur_ev = p.resultat_exploitation * p.multiple_re;
  const valeur_equity = valeur_ev - p.endettement_financier_net;
  const ve_unitaire = p.nombre_titres > 0 ? valeur_equity / p.nombre_titres : null;
  return {
    re: p.resultat_exploitation,
    multiple: p.multiple_re,
    valeur_enterprise: valeur_ev,
    endettement_net: p.endettement_financier_net,
    valeur_capitaux_propres: valeur_equity,
    valeur_unitaire: ve_unitaire,
    equity_value: valeur_equity,
  };
}

// --- COMBINAISON DGI: Pondération réglementaire ---
// Combine VM + V.Rent selon la taille/nature et le paquet de titres
// V.Rent = moyenne pondérée de VP, VR, VE_EBE, VE_RE, MBA
function calcCombinaison(p) {
  const results = {};
  // Calculate all available methods
  const methods_to_run = [
    {key:'vm', fn:calcVM, params: {
      capitaux_propres: p.capitaux_propres,
      revaluations: p.revaluations,
      apply_impot_latent: p.apply_impot_latent,
      taux_is: p.taux_is,
      passif_hors_bilan: p.passif_hors_bilan,
      include_survaleur: p.include_survaleur,
      bef_recurrent: p.bef_recurrent,
      taux_rf: p.taux_rf,
      taux_inflation: p.taux_inflation,
      beta_risque: p.beta_risque,
      duree_survaleur: p.duree_survaleur,
      valeur_fonds_commerce: p.valeur_fonds_commerce,
      nombre_titres: p.nombre_titres,
    }},
    {key:'vp', fn:calcVP, params: {
      benefice_n2: p.benefice_n2,
      benefice_n1: p.benefice_n1,
      benefice_n: p.benefice_n,
      reintegration_dirigeant: p.reintegration_dirigeant,
      elements_non_recurrents: p.elements_non_recurrents,
      taux_rf: p.taux_rf,
      taux_inflation: p.taux_inflation,
      beta_risque: p.beta_risque,
      nombre_titres: p.nombre_titres,
    }},
  ];
  if(p.use_vr) methods_to_run.push({key:'vr', fn:calcVR, params:{dividende_n2:p.dividende_n2,dividende_n1:p.dividende_n1,dividende_n:p.dividende_n,taux_rendement_actions_cotees:p.taux_rendement_actions_cotees,decote_non_liquidite:p.decote_non_liquidite,nombre_titres:p.nombre_titres}});
  if(p.use_ebe) methods_to_run.push({key:'ebe', fn:calcVE_EBE, params:{ebe:p.ebe,multiple_ebe:p.multiple_ebe,endettement_financier_net:p.endettement_financier_net,nombre_titres:p.nombre_titres}});
  if(p.use_re) methods_to_run.push({key:'re', fn:calcVE_RE, params:{resultat_exploitation:p.resultat_exploitation,multiple_re:p.multiple_re,endettement_financier_net:p.endettement_financier_net,nombre_titres:p.nombre_titres}});
  if(p.use_mba) methods_to_run.push({key:'mba', fn:calcMBA, params:{benefice_net:p.benefice_net,dotations_amortissements:p.dotations_amortissements,dotations_provisions_reserves:p.dotations_provisions_reserves,reprises_provisions:p.reprises_provisions,coefficient_mba:p.coefficient_mba,endettement_financier_net:p.endettement_financier_net,nombre_titres:p.nombre_titres}});

  for(const m of methods_to_run) {
    try { results[m.key] = m.fn(m.params); } catch(e) { results[m.key] = null; }
  }

  const vm_val = results.vm?.equity_value || 0;
  // V.Rent = weighted avg of available rentability methods
  const vrent_sources = [];
  if(results.vp) vrent_sources.push({v: results.vp.equity_value, w: p.poids_vp || 1});
  if(results.vr) vrent_sources.push({v: results.vr.equity_value, w: p.poids_vr || 1});
  if(results.ebe) vrent_sources.push({v: results.ebe.equity_value, w: p.poids_ebe || 1});
  if(results.re) vrent_sources.push({v: results.re.equity_value, w: p.poids_re || 1});
  if(results.mba) vrent_sources.push({v: results.mba.equity_value, w: p.poids_mba || 1});

  const vrent_num = vrent_sources.reduce((s, x) => s + x.v * x.w, 0);
  const vrent_den = vrent_sources.reduce((s, x) => s + x.w, 0);
  const vrent_val = vrent_den > 0 ? vrent_num / vrent_den : 0;

  // Pondération finale selon taille/nature et pouvoir de décision
  // poids_vm + poids_vrent doit faire la somme totale
  const pw_vm = p.poids_vm_final || 1;
  const pw_vrent = p.poids_vrent_final || 2;
  const valeur_finale = (vm_val * pw_vm + vrent_val * pw_vrent) / (pw_vm + pw_vrent);

  // Décotes et corrections finales
  const decote_minorite = p.decote_minorite || 0;
  const decote_agrément = p.decote_clause_agrement || 0;
  const valeur_apres_decotes = valeur_finale * (1 - decote_minorite) * (1 - decote_agrément);
  const valeur_unitaire = p.nombre_titres > 0 ? valeur_apres_decotes / p.nombre_titres : null;

  return {
    vm: vm_val,
    vrent: vrent_val,
    valeur_avant_decotes: valeur_finale,
    decote_minorite,
    decote_agrement: decote_agrément,
    valeur_venale: valeur_apres_decotes,
    valeur_unitaire,
    equity_value: valeur_apres_decotes,
    detail: results,
    ponderation: { vm: pw_vm, vrent: pw_vrent },
    vrent_sources: vrent_sources.map((x, i) => ({...x, label: ['VP','VR','EBE','RE','MBA'][i] || `M${i}`})),
  };
}


// ==========================================================================
// JURISDICTION CALCULATION ENGINES
// ==========================================================================

// --- US: IRS Rev. Ruling 59-60 — Earnings Capitalization ---
// Normalizes 3-year weighted earnings, capitalizes at CAPM-built rate.
// Primary method for closely-held business valuation under §4 of Rev. Ruling 59-60.
function calcUSEarnings(p) {
  // Weighted average net income: N×3 + N-1×2 + N-2×1
  const ni_avg = (p.ni_n * 3 + p.ni_n1 * 2 + p.ni_n2 * 1) / 6;
  // Normalization: add back non-arm's-length owner comp, strip non-recurring
  const normalized = ni_avg + (p.addback_owner_comp || 0) - (p.nonrecurring_items || 0);
  // CAPM build-up (US): Ke = Rf + β×ERP + size + specific
  const ke = p.rf_rate + p.beta * p.erp + (p.size_premium || 0) + (p.specific_premium || 0);
  // Capitalization rate = ke − sustainable long-term growth
  const cap_rate = ke - (p.sustainable_growth || 0.02);
  if (cap_rate <= 0) throw new Error("Capitalization rate must be positive (reduce growth or increase discount rate)");
  if (normalized <= 0) throw new Error("Normalized earnings must be positive");
  const earnings_value = normalized / cap_rate;
  const equity_value = earnings_value - (p.net_debt || 0);
  const value_per_share = (p.shares_outstanding || 0) > 0 ? equity_value / p.shares_outstanding : null;
  return {
    ni_avg,
    normalized_earnings: normalized,
    ke,
    cap_rate,
    earnings_value,
    equity_value,
    value_per_share,
    // breakdown for results panel
    weights: [
      { year: "N−2", ni: p.ni_n2, weight: 1, weighted: p.ni_n2 * 1 },
      { year: "N−1", ni: p.ni_n1, weight: 2, weighted: p.ni_n1 * 2 },
      { year: "N",   ni: p.ni_n,  weight: 3, weighted: p.ni_n * 3 },
    ],
    _params: p,
  };
}

// --- DE: IDW S1 (2008) — Ertragswertverfahren (after-personal-tax CAPM) ---
// Distributable earnings discounted at after-tax Ke (Basiszinssatz + β×MRP, after Abgeltungssteuer).
// IDW S1 §§100–167 / objectivized value standard.
function calcIDW(p) {
  // Hamada: β_levered = β_unlevered × [1 + (1−IS) × D/E]
  const beta_levered = p.beta_unlevered * (1 + (1 - p.tax_rate) * (p.debt_to_equity || 0));
  // After-personal-tax discount rate (IDW S1 post-2008 Abgeltungssteuer)
  const s = p.abgeltungssteuer || 0.26375;
  const ke_after = p.basiszinssatz * (1 - s) + beta_levered * p.mrp_after_tax;
  const g = p.terminal_growth || 0;
  if (ke_after <= g) throw new Error("Ke (after-tax) must exceed terminal growth rate");
  // Project earnings with convergence (same pattern as DCF)
  const proj = [];
  let earnings = p.base_earnings;
  let cum = 1;
  let sumPV = 0;
  for (let y = 1; y <= p.forecast_years; y++) {
    const t = Math.min(y / (p.growth_convergence_year || 5), 1);
    const gr = p.earnings_growth_initial + (p.earnings_growth_terminal - p.earnings_growth_initial) * t;
    earnings *= (1 + gr);
    cum *= (1 + ke_after);
    const pv = earnings / cum;
    sumPV += pv;
    proj.push({ year: y, earnings, growth: gr, pv });
  }
  // Terminal value: Gordon Growth
  const tv = proj[proj.length - 1].earnings * (1 + g) / (ke_after - g);
  const pvTV = tv / cum;
  const ertragswert = sumPV + pvTV;
  const equity_value = ertragswert - (p.net_debt || 0);
  const value_per_share = (p.shares_outstanding || 0) > 0 ? equity_value / p.shares_outstanding : null;
  return {
    beta_levered,
    ke_after,
    basiszinssatz: p.basiszinssatz,
    abgeltungssteuer: s,
    sum_pv_earnings: sumPV,
    terminal_value: pvTV,
    terminal_value_pct: ertragswert > 0 ? pvTV / ertragswert : null,
    ertragswert,
    equity_value,
    value_per_share,
    projections: proj,
    _params: p,
  };
}

// --- CH: CSI Circulaire n°28 — Formule praticien ---
// Valeur fiscale = (2×VR + VI) / 3
// VR = bénéfice moyen / taux cantonal ; VI = capitaux propres + réserves latentes nettes
function calcCSI(p) {
  // Valeur de rendement: double-weighted average of last 2 years
  const benefice_moyen = (2 * p.profit_n + 1 * (p.profit_n1 || 0)) / 3;
  const taux_cap = p.taux_capitalisation || 0.07;
  if (taux_cap <= 0) throw new Error("Le taux de capitalisation doit être > 0");
  const valeur_rendement = benefice_moyen / taux_cap;
  // Valeur intrinsèque: book equity + hidden reserves net of tax
  const reserves_nettes = (p.hidden_reserves || 0) * (1 - (p.hidden_reserves_tax_rate || 0.215));
  const valeur_intrinseque = (p.equity_book || 0) + reserves_nettes;
  // Practitioner formula
  const valeur_brute = (2 * valeur_rendement + valeur_intrinseque) / 3;
  const decote = p.minority_discount || 0;
  const valeur_fiscale = valeur_brute * (1 - decote);
  const value_per_share = (p.shares_outstanding || 0) > 0 ? valeur_fiscale / p.shares_outstanding : null;
  return {
    benefice_moyen,
    taux_capitalisation: taux_cap,
    valeur_rendement,
    valeur_intrinseque,
    reserves_nettes,
    valeur_brute,
    minority_discount: decote,
    valeur_fiscale,
    equity_value: valeur_fiscale,
    value_per_share,
    _params: p,
  };
}

// --- UK: HMRC SAV — P/E Earnings Basis (SVM107070) ---
// OMV = Maintainable Earnings × Sector P/E, adjusted for illiquidity/control/size.
// Standard for unlisted shares under TCGA 1992 §272.
function calcUKPE(p) {
  if ((p.sector_pe || 0) <= 0) throw new Error("Sector P/E must be > 0");
  if ((p.maintainable_earnings || 0) <= 0) throw new Error("Maintainable earnings must be > 0");
  const gross_omv = p.maintainable_earnings * p.sector_pe;
  // Apply stake-dependent adjustments
  const stake = p.stake_pct || 1;
  const control_adj = stake >= 0.5 ? (p.control_premium || 0) : 0;
  const illiq = p.illiquidity_discount || 0.20;
  const size  = p.size_discount || 0.10;
  const adjusted_omv = gross_omv * (1 + control_adj) * (1 - illiq) * (1 - size);
  const total_shares = p.total_shares || p.shares_outstanding || 1;
  const value_per_share = adjusted_omv / total_shares;
  const stake_value = value_per_share * (total_shares * stake);
  return {
    gross_omv,
    control_adj,
    illiquidity_discount: illiq,
    size_discount: size,
    adjusted_omv,
    equity_value: adjusted_omv,
    value_per_share,
    stake_value,
    effective_pe: adjusted_omv / p.maintainable_earnings,
    _params: p,
  };
}

function calcFR(p) { return p; } // FR_COMP manages sub-calcs via individual DGI engines
const CALCS={dcf:calcDCF,exc:calcExcess,mul:calcMultiples,rfr:calcRfR,mpe:calcMPEEM,ast:calcAsset,vp:calcVP,vm:calcVM,vr:calcVR,mba:calcMBA,ebe:calcVE_EBE,re:calcVE_RE,comb:calcCombinaison,us:calcUSEarnings,de:calcIDW,ch:calcCSI,uk:calcUKPE,fr:calcFR};
const useCalc=(key)=>{const[r,sR]=useState(null);const[l,sL]=useState(false);const[e,sE]=useState(null);const ref=useRef(null);const cbRef=useRef(null);const run=async(body,cb)=>{sL(true);sE(null);cbRef.current=cb;try{await new Promise(r=>setTimeout(r,250));const res=CALCS[key](body);sR(res);if(cbRef.current)cbRef.current(res);setTimeout(()=>ref.current?.scrollIntoView({behavior:"smooth"}),100);}catch(x){sE(x.message);}finally{sL(false);}};return{r,l,e,run,ref,sR,sE};};

// ==========================================================================
// JURISDICTION EXPORT — STYLED XLSX HELPERS
// ==========================================================================
const JUR_XLS = {
  // Palette
  DARK: "0D1117", DARK2: "161B22", DARK3: "1C2333",
  BORDER: "2D333B", TEXT: "E6EDF3", DIM: "8B949E", WHITE: "FFFFFF",
  // Reusable style builders
  _bdr(color) { const c = {rgb:color||this.BORDER}; return {top:{style:"thin",color:c},bottom:{style:"thin",color:c},left:{style:"thin",color:c},right:{style:"thin",color:c}}; },
  _font(sz,bold,color) { return {name:"Calibri",sz:sz||11,bold:!!bold,color:{rgb:color||this.TEXT}}; },
  // Preset styles
  title(accent) { return {font:this._font(14,true,this.WHITE),fill:{fgColor:{rgb:accent}},alignment:{horizontal:"left",vertical:"center"},border:this._bdr(accent)}; },
  subtitle() { return {font:this._font(10,false,this.DIM),fill:{fgColor:{rgb:this.DARK}},alignment:{horizontal:"left"},border:this._bdr()}; },
  sectionHdr(accent) { return {font:this._font(11,true,accent),fill:{fgColor:{rgb:this.DARK2}},border:this._bdr(),alignment:{horizontal:"left"}}; },
  colHdr(accent) { return {font:this._font(10,true,this.WHITE),fill:{fgColor:{rgb:accent||"30363D"}},border:this._bdr(),alignment:{horizontal:"center"}}; },
  colHdrL(accent) { return {...this.colHdr(accent),alignment:{horizontal:"left"}}; },
  inputLabel() { return {font:this._font(10,false,this.DIM),fill:{fgColor:{rgb:this.DARK}},border:this._bdr(),alignment:{horizontal:"left"}}; },
  inputVal() { return {font:this._font(11,false,this.TEXT),fill:{fgColor:{rgb:this.DARK3}},border:this._bdr(),alignment:{horizontal:"right"}}; },
  inputValPct() { return {...this.inputVal(), numFmt:"0.00%"}; },
  inputValNum() { return {...this.inputVal(), numFmt:"#,##0"}; },
  inputValDec() { return {...this.inputVal(), numFmt:"0.00"}; },
  resultLabel(accent) { return {font:this._font(10,true,accent),fill:{fgColor:{rgb:this.DARK2}},border:this._bdr(),alignment:{horizontal:"left"}}; },
  resultVal(accent) { return {font:this._font(12,true,accent),fill:{fgColor:{rgb:this.DARK2}},border:this._bdr(),alignment:{horizontal:"right"},numFmt:"#,##0"}; },
  resultValPct(accent) { return {...this.resultVal(accent), numFmt:"0.00%"}; },
  resultValDec(accent) { return {...this.resultVal(accent), numFmt:"0.00"}; },
  formulaLabel() { return {font:this._font(10,false,this.TEXT),fill:{fgColor:{rgb:this.DARK}},border:this._bdr(),alignment:{horizontal:"left"}}; },
  formulaVal() { return {font:this._font(11,false,this.TEXT),fill:{fgColor:{rgb:this.DARK}},border:this._bdr(),alignment:{horizontal:"right"},numFmt:"#,##0"}; },
  formulaValPct() { return {...this.formulaVal(), numFmt:"0.00%"}; },
  spacer() { return {fill:{fgColor:{rgb:this.DARK}}}; },
  refTitle() { return {font:this._font(13,true,this.TEXT),fill:{fgColor:{rgb:this.DARK2}},border:this._bdr()}; },
  refBody() { return {font:this._font(10,false,this.DIM),fill:{fgColor:{rgb:this.DARK}},alignment:{wrapText:true}}; },
  refFooter() { return {font:this._font(9,false,"6E7681"),fill:{fgColor:{rgb:this.DARK}},alignment:{wrapText:true}}; },
  // Apply style to a cell range in a worksheet
  applyStyle(ws, ref, style) {
    const cell = ws[ref];
    if (cell) cell.s = style;
  },
  // Apply style to entire row
  styleRow(ws, row, cols, style) {
    for (let c = 0; c < cols; c++) {
      const ref = XLSX.utils.encode_cell({r:row, c});
      if (!ws[ref]) ws[ref] = {v:"",t:"s"};
      ws[ref].s = style;
    }
  },
  // Set all empty cells in range to have background
  fillBg(ws, r1, c1, r2, c2, style) {
    for (let r = r1; r <= r2; r++)
      for (let c = c1; c <= c2; c++) {
        const ref = XLSX.utils.encode_cell({r, c});
        if (!ws[ref]) ws[ref] = {v:"",t:"s"};
        if (!ws[ref].s) ws[ref].s = style;
      }
  }
};

// ==========================================================================
// DIAGNOSTIC COMPUTATION ENGINES
// ==========================================================================
const computeStratProfile = (data) => {
  if (!data) return { opType: null, methodFamily: ["dcf","mul","ast"], riskScore: "medium", riskFlags: [], porterIntensity: 3, filledModules: 0 };
  const d = data;
  const filled = d._filled || 0;

  // --- Operation type detection ---
  const nature = (d.analyse?.nature || "").toLowerCase();
  let opType = null;
  let methodFamily = ["dcf","mul","ast"];
  if (/cession|vente|c[eé]dant|sell/i.test(nature)) { opType = "cession"; methodFamily = ["dcf","mul","exc"]; }
  else if (/restructur|retournement|turnaround/i.test(nature)) { opType = "restructuration"; methodFamily = ["ast","dcf"]; }
  else if (/apport/i.test(nature)) { opType = "apport"; methodFamily = ["mul","ast","dcf"]; }
  else if (/fusion|absorption|merge/i.test(nature)) { opType = "fusion"; methodFamily = ["dcf","mul","exc"]; }
  else if (/transfer|interco|TP|prix de transfert/i.test(nature)) { opType = "tp_interco"; methodFamily = ["mul","rfr","mpe"]; }

  // --- Risk score from SWOT + Porter ---
  const porter = d.porter || {};
  const porterVals = [porter.pouvoir_acheteurs||3, porter.pouvoir_fournisseurs||3, porter.substituts||3, porter.nouveaux_entrants||3, porter.rivalite||3];
  const porterIntensity = porterVals.reduce((a,b)=>a+b,0) / porterVals.length;

  const swot = d.swot || {};
  const positive = (swot.forces||"").length + (swot.opportunites||"").length;
  const negative = (swot.faiblesses||"").length + (swot.menaces||"").length;
  const swotNegative = negative > positive * 1.3;

  let riskScore = "low";
  if (porterIntensity > 3.5 && swotNegative) riskScore = "high";
  else if (porterIntensity > 3 || swotNegative) riskScore = "medium";

  // --- Risk flags from Risques clés ---
  const riskFlags = [];
  (d.risques || []).forEach(r => {
    const crit = (r.probabilite || 2) * (r.impact || 2);
    const lab = (r.label || "").toLowerCase();
    const userLabel = r.label || "Risque non nommé";
    if (crit >= 4 && /client|concentr/i.test(lab))
      riskFlags.push({ label: userLabel, category: "concentration_clientele", source: "Risques clés — Diagnostic Stratégique", impact: "Haircut sur la croissance projetée", param: "revenue_growth", adjustment: "-2% à -5%", criticite: crit });
    if (crit >= 4 && /homme.cl[eé]|key.?man|dirigeant/i.test(lab))
      riskFlags.push({ label: userLabel, category: "homme_cle", source: "Risques clés — Diagnostic Stratégique", impact: "Prime de risque spécifique intégrée au taux d'actualisation", param: "cost_of_capital", adjustment: "+1% à +3%", criticite: crit });
    if (crit >= 6 && /r[eé]glement|regulat|l[eé]gisl/i.test(lab))
      riskFlags.push({ label: userLabel, category: "reglementaire", source: "Risques clés — Diagnostic Stratégique", impact: "Prime réglementaire intégrée au taux d'actualisation", param: "cost_of_capital", adjustment: "+1% à +2%", criticite: crit });
    if (crit >= 4 && /techno|obsol|disrupt/i.test(lab))
      riskFlags.push({ label: userLabel, category: "obsolescence", source: "Risques clés — Diagnostic Stratégique", impact: "Durée de projection réduite", param: "forecast_years", adjustment: "-2 à -3 ans", criticite: crit });
  });

  return { opType, methodFamily, riskScore, riskFlags, porterIntensity, filledModules: filled };
};

const computeFinProfile = (fin) => {
  if (!fin) return null;
  const rev = fin.revenue || [];
  const ebitda = fin.ebitda || [];
  const years = fin.years || [];

  // --- CAGR ---
  const cagr = (arr) => {
    const vals = arr.filter(v => v != null && v > 0);
    if (vals.length < 2) return null;
    const first = vals[0], last = vals[vals.length - 1];
    const n = vals.length - 1;
    return Math.pow(last / first, 1 / n) - 1;
  };
  const revenueCagr = cagr(rev);
  const ebitdaCagr = cagr(ebitda);

  // --- Margin stability ---
  const margins = rev.map((r, i) => r && r > 0 && ebitda[i] != null ? ebitda[i] / r : null).filter(v => v != null);
  let ebitdaMarginStdDev = null;
  let marginStability = "moderate";
  if (margins.length >= 2) {
    const mean = margins.reduce((a, b) => a + b, 0) / margins.length;
    ebitdaMarginStdDev = Math.sqrt(margins.reduce((s, m) => s + (m - mean) ** 2, 0) / margins.length);
    if (ebitdaMarginStdDev < 0.02) marginStability = "stable";
    else if (ebitdaMarginStdDev < 0.05) marginStability = "moderate";
    else marginStability = "volatile";
  }

  // --- Debt metrics ---
  const lastEbitda = ebitda[ebitda.length - 1];
  const netDebtToEbitda = lastEbitda && lastEbitda > 0 ? (fin.net_debt || 0) / lastEbitda : null;
  const gearing = fin.capitaux_propres && fin.capitaux_propres > 0 ? (fin.net_debt || 0) / fin.capitaux_propres : null;

  // --- Suggestions ---
  const suggestedGrowth = revenueCagr != null ? Math.max(-0.10, Math.min(0.30, revenueCagr * 0.8)) : null;
  const suggestedMargin = margins.length >= 2 ? margins.slice().sort((a, b) => a - b)[Math.floor(margins.length / 2)] : null;
  const lastMargin = margins.length > 0 ? margins[margins.length - 1] : null;
  const assetMethodRelevant = (lastEbitda != null && lastEbitda < 0) || (lastMargin != null && lastMargin < 0.05);
  const assetMethodReasonKey = assetMethodRelevant ? (lastEbitda < 0 ? "syn_ebitda_neg" : "syn_margins_low") : null;

  return {
    revenueCagr, ebitdaCagr, ebitdaMarginStdDev, marginStability,
    netDebtToEbitda, gearing, suggestedGrowth, suggestedMargin,
    assetMethodRelevant, assetMethodReasonKey, lastMargin,
  };
};

const OP_LABELS = { cession: "Cession", restructuration: "Restructuration", apport: "Apport partiel", fusion: "Fusion / Absorption", tp_interco: "Prix de transfert / Interco" };
const METHOD_NAMES = { dcf:"DCF", exc:"Excess Return", mul:"Multiples", rfr:"Relief from Royalty", mpe:"MPEEM", ast:"Actif Net Réévalué", vp:"Productivité", vm:"Mathématique", vr:"Rendement", mba:"MBA", ebe_re:"EBE/RE", comb:"Combinaison DGI", us:"US Earnings Cap.", de:"Ertragswert IDW", ch:"CSI Praticien", uk:"HMRC P/E", fr:"DGI France" };

const computeSynthesisLocal = (stratProfile, finProfile, L) => {
  // --- Method ranking ---
  const seen = new Set();
  const methods = [];
  const addMethod = (key, rationale, rank) => { if (!seen.has(key)) { seen.add(key); methods.push({ key, name: L[`ds_meth_${key}`] || METHOD_NAMES[key] || key, rationale, rank }); } };

  if (stratProfile?.methodFamily) {
    stratProfile.methodFamily.forEach((k, i) => {
      const opLabel = L[`ds_op_${stratProfile.opType}`] || OP_LABELS[stratProfile.opType] || L.syn_context;
      addMethod(k, (L.syn_method_rationale||"").replace("{0}", opLabel.toLowerCase()), i + 1);
    });
  }
  if (finProfile?.assetMethodRelevant && !seen.has("ast")) addMethod("ast", `${L[finProfile.assetMethodReasonKey]||""} — ${L.syn_asset_approach}`, methods.length + 1);
  if (!seen.has("dcf")) addMethod("dcf", L.syn_dcf_rationale, methods.length + 1);
  if (!seen.has("mul")) addMethod("mul", L.syn_mul_rationale, methods.length + 1);
  const ranked = methods.slice(0, 5).map((m, i) => ({ ...m, rank: i + 1 }));

  // --- Hypotheses ---
  const hyp = {
    revenueGrowth: finProfile?.suggestedGrowth != null ? { value: finProfile.suggestedGrowth, source: L.syn_src_rev_tcam } : null,
    ebitdaMargin: finProfile?.suggestedMargin != null ? { value: finProfile.suggestedMargin, source: L.syn_src_ebitda_med } : null,
    discountRateRange: { low: 0.08, high: 0.12, source: L.syn_src_disc_std },
    terminalGrowth: { value: 0.02, source: L.syn_src_terminal },
  };
  if (stratProfile?.riskScore === "high") { hyp.discountRateRange = { low: 0.10, high: 0.14, source: L.syn_src_disc_high }; }
  else if (stratProfile?.riskScore === "medium") { hyp.discountRateRange = { low: 0.09, high: 0.13, source: L.syn_src_disc_med }; }

  // --- Alerts ---
  const alerts = [];
  if ((stratProfile?.filledModules || 0) < 3) alerts.push({ type: "warning", message: L.syn_alert_incomplete });
  if (!finProfile) alerts.push({ type: "warning", message: L.syn_alert_no_fin });
  if (stratProfile?.riskScore === "high" && finProfile?.marginStability === "stable") alerts.push({ type: "info", message: L.syn_alert_risk_margins });
  if (finProfile?.assetMethodRelevant) alerts.push({ type: "error", message: `${L[finProfile.assetMethodReasonKey]||""} — ${L.syn_alert_anr_required}` });
  if (stratProfile?.riskFlags?.length > 0) {
    stratProfile.riskFlags.forEach(f => alerts.push({ type: "warning", message: `« ${f.label} » (${L.syn_alert_risk_in} ${L.ds_risques}, ${L.syn_alert_criticite} ${f.criticite}/25) → ${L[`ds_risk_impact_${f.category}`]||f.impact} (${L[`ds_risk_adj_${f.category}`]||f.adjustment}).` }));
  }

  return { recommendedMethods: ranked, suggestedHypotheses: hyp, alerts };
};

// ==========================================================================
// HELPERS & FORMATTING
// ==========================================================================
const fmt=(n,d=0)=>n==null?"—":new Intl.NumberFormat("en-US",{minimumFractionDigits:d,maximumFractionDigits:d}).format(n);
const fP=n=>n==null?"—":(n*100).toFixed(1)+"%";
const fC=n=>{if(n==null)return"—";const a=Math.abs(n);if(a>=1e9)return(n/1e9).toFixed(2)+"B";if(a>=1e6)return(n/1e6).toFixed(2)+"M";if(a>=1e3)return(n/1e3).toFixed(0)+"K";return fmt(n);};
// Smart monetary input: parse "1.5M", "500K", "2.3B" etc.
const parseMoney = (raw) => {
  if (typeof raw === "number") return raw;
  if (!raw || typeof raw !== "string") return "";
  const s = raw.trim().toUpperCase();
  const match = s.match(/^([\d.,]+)\s*([KMBT]?)$/);
  if (!match) return raw;
  const num = parseFloat(match[1].replace(",", "."));
  if (isNaN(num)) return raw;
  const mult = { K: 1e3, M: 1e6, B: 1e9, T: 1e12 }[match[2]] || 1;
  return num * mult;
};

// Format for display in monetary inputs
const fMon = (n) => {
  if (n === "" || n == null) return "";
  const v = Number(n);
  if (isNaN(v)) return String(n);
  if (Math.abs(v) >= 1e9) return (v/1e9).toFixed(2).replace(/\.?0+$/, "") + "B";
  if (Math.abs(v) >= 1e6) return (v/1e6).toFixed(2).replace(/\.?0+$/, "") + "M";
  if (Math.abs(v) >= 1e4) return (v/1e3).toFixed(1).replace(/\.?0+$/, "") + "K";
  return String(v);
};

// ==========================================================================
// AI API — Calls to backend
// ==========================================================================
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const callAI = async (endpoint, payload) => {
  const res = await fetch(`${API_BASE}/api/ai/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errBody = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${errBody.slice(0, 200)}`);
  }
  return res.json();
};


// ==========================================================================
// TRANSLATIONS
// ==========================================================================
const TR={en:{brand:"Hard to Value",tag:"Financial & Tax Valuation Platform",dash:"Dashboard",sel:"Select a valuation model",calc:"Calculate",calcing:"Calculating...",back:"← Back",clr:"Clear",proj:"Projections",eqV:"Equity Value",evV:"Enterprise Value",vps:"Value per Share",tv:"Terminal Value",spv:"Sum PV FCFF",em:"Exit Multiple",tp:"Terminal %",fv:"Fair Value",anav:"Adjusted NAV",liq:"Liquidation",bnav:"Book NAV",ptb:"Premium/Book",summary:"Valuation Summary",warn:"Warnings",calcdone:"Calculated",
  company:"Company",shares:"Shares",companyName:"Company name",sharesOutstanding:"Shares outstanding",
  alert_run_first:"Please run the calculation before exporting.",
  alert_export_err:"Export error: ",
  err_server:"Server error",
  err_export:"Export error: ",
  sec_company:"Company",sec_base:"Base Metrics",sec_growth:"Growth",sec_margins:"Margins",
  sec_capital:"Capital",sec_forecast:"Forecast",sec_adjustments:"Adjustments",
  sec_financials:"Financials",sec_comparables:"Comparables",sec_assets:"Assets",
  sec_rates:"Rates",sec_revenue:"Revenue",
  dashboard_methods:"Financial Methods",
  dashboard_hint:"Calculate at least 2 models to generate the report.",
  btn_report:"Generate synthesis report",
  lbl_methods:"methods",synth_title:"Method Selection & Hypotheses",synth_recommended:"Recommended Methods",synth_hypotheses:"Suggested Hypotheses",synth_alerts:"Alerts & Flags",synth_apply:"Apply to models",synth_ai_enrich:"AI Enrichment",synth_op_profile:"Operation Profile",synth_risk_level:"Risk Level",synth_risk_flags:"Risk Flags",synth_method_family:"Method Family",synth_fin_metrics:"Computed Metrics",synth_cagr:"Revenue CAGR",synth_margin_stability:"Margin Stability",synth_leverage:"Leverage",synth_revenue_growth:"Revenue Growth",synth_ebitda_margin:"EBITDA Margin",synth_discount_rate:"Discount Rate",synth_terminal_growth:"Terminal Growth",errors:"Errors",fix_errors:"Fix errors to calculate",
  // ── Diagnostic Stratégique ──
  ds_title:"Strategic Diagnostic",ds_subtitle:"5 modules · locally persisted data · AI on demand",ds_modules:"/5 modules",
  ds_analyse:"Operation Analysis",ds_nature:"Nature of the operation",ds_nature_ph:"Sale, LBO, gift, partial contribution…",ds_parties:"Stakeholders",ds_parties_ph:"Seller, buyer, minority shareholders…",ds_enjeux:"Financial stakes",ds_enjeux_ph:"Target price, earn-out, asset/liability guarantees…",ds_contexte:"Context & motivations",ds_contexte_ph:"Succession, development, restructuring…",
  ds_swot:"SWOT Analysis",ds_forces:"Strengths",ds_forces_ph:"Competitive advantages, brands, patents…",ds_faiblesses:"Weaknesses",ds_faiblesses_ph:"Dependencies, cost structure, debt…",ds_opportunites:"Opportunities",ds_opportunites_ph:"Emerging markets, sector growth…",ds_menaces:"Threats",ds_menaces_ph:"Disruption, regulation, competitors…",
  ds_porter:"Porter — 5 Forces",ds_pouvoir_acheteurs:"Buyer power",ds_pouvoir_fournisseurs:"Supplier power",ds_substituts:"Substitute threat",ds_nouveaux_entrants:"New entrants",ds_rivalite:"Competitive rivalry",
  ds_tip_acheteurs:"Buyer power — Customers' ability to negotiate lower prices or better terms. High score = strong buyer leverage, compresses margins and pricing power.",ds_tip_fournisseurs:"Supplier power — Suppliers' ability to impose conditions, raise prices or restrict supply. High score = limited alternatives, raises input costs.",ds_tip_substituts:"Substitute threat — Risk that customers switch to alternative products or services. High score = easy substitution, caps pricing and growth.",ds_tip_entrants:"New entrants — Ease with which new competitors can enter the market. High score = low barriers, increases competitive pressure.",ds_tip_rivalite:"Competitive rivalry — Intensity of competition among existing players. High score = fierce rivalry, erodes margins and market share.",
  ds_op_cession:"Sale",ds_op_restructuration:"Restructuring",ds_op_apport:"Partial contribution",ds_op_fusion:"Merger / Acquisition",ds_op_tp_interco:"Transfer pricing / Interco",
  ds_meth_dcf:"DCF",ds_meth_exc:"Excess Return",ds_meth_mul:"Multiples",ds_meth_rfr:"Relief from Royalty",ds_meth_mpe:"MPEEM",ds_meth_ast:"Net Asset Value",ds_meth_vp:"Productivity",ds_meth_vm:"Mathematical",ds_meth_vr:"Yield",ds_meth_mba:"MBA",ds_meth_ebe_re:"EBE/RE",ds_meth_comb:"DGI Combination",ds_meth_us:"US Earnings Cap.",ds_meth_de:"Ertragswert IDW",ds_meth_ch:"CSI Praticien",ds_meth_uk:"HMRC P/E",ds_meth_fr:"DGI France",
  ds_positionnement:"Competitive Positioning",ds_marche_cible:"Target market",ds_marche_cible_ph:"Segment, geography, TAM…",ds_avantage:"Competitive advantage",ds_avantage_ph:"Cost, differentiation, focus…",ds_part_marche:"Market share",ds_part_marche_ph:"Leader, challenger, follower, niche…",ds_facteurs:"Differentiating factors",ds_facteurs_ph:"Technology, brand, network, regulation…",
  ds_risques:"Key Risks",ds_risque_col:"Risk",ds_prob_col:"Probability (1-3)",ds_impact_col:"Impact (1-3)",ds_crit_col:"Criticality",ds_nouveau_risque:"New risk",ds_ajouter_risque:"+ Add risk",
  ds_critique:"CRITICAL",ds_eleve:"HIGH",ds_moyen:"MEDIUM",ds_faible:"LOW",
  ds_profil:"Operation Profile",ds_type_op:"OPERATION TYPE",ds_non_detecte:"Not detected",ds_niveau_risque:"RISK LEVEL",ds_risk_high:"High",ds_risk_medium:"Moderate",ds_risk_low:"Low",ds_porter_avg:"Porter avg:",ds_completude:"COMPLETENESS",ds_famille_methodes:"METHOD FAMILY",ds_signaux_risque:"RISK SIGNALS",
  ds_risk_impact_concentration_clientele:"Projected growth haircut",ds_risk_adj_concentration_clientele:"-2% to -5%",ds_risk_impact_homme_cle:"Specific risk premium in discount rate",ds_risk_adj_homme_cle:"+1% to +3%",ds_risk_impact_reglementaire:"Regulatory premium in discount rate",ds_risk_adj_reglementaire:"+1% to +2%",ds_risk_impact_obsolescence:"Reduced projection period",ds_risk_adj_obsolescence:"-2 to -3 years",
  // ── Diagnostic Financier ──
  df_title:"Financial Diagnostic",df_import_msg:"Import the Excel file to generate the automatic analysis",df_societe:"Company",df_exercice:"fiscal year",
  df_marge_ebitda:"EBITDA Margin (N)",df_marge_ebit:"EBIT Margin (N)",df_marge_nette:"Net Margin (N)",df_gearing:"Gearing (D/E)",df_roe:"ROE",df_roce:"ROCE",
  df_ca_ebitda:"Revenue & EBITDA",df_ca:"Revenue",df_ebitda:"EBITDA",df_evol_marges:"Margin Evolution",df_ebitda_pct:"EBITDA%",df_ebit_pct:"EBIT%",df_net_pct:"Net%",
  df_metriques:"Computed Metrics",df_tcam_ca:"Revenue CAGR",df_tcam_ebitda:"EBITDA CAGR",df_stabilite:"Margin Stability",df_stable:"Stable",df_volatile:"Volatile",df_moderee:"Moderate",df_endettement:"Leverage",df_dn_ebitda:"ND/EBITDA",
  df_croissance_sugg:"Suggested growth (CAGR×0.8)",df_marge_sugg:"Suggested EBITDA margin (median)",df_anr_warning:"asset-based approach (NAV) recommended",
  df_commentaire_ia:"AI Commentary",df_backend_err:"Backend unavailable:",
  // ── BlocSynthese ──
  syn_subtitle:"Aggregation of strategic and financial signals",syn_analyse_loading:"Analyzing…",syn_commentaire_ia:"AI Commentary — Synthesis",
  syn_method_rationale:"Method suited to {0} context",syn_context:"context",syn_asset_approach:"asset-based approach (NAV) recommended",syn_dcf_rationale:"Reference method — discounted future cash flows",syn_mul_rationale:"Market multiples cross-check",
  syn_src_rev_tcam:"Historical revenue CAGR × 0.8",syn_src_ebitda_med:"Median historical EBITDA margins",syn_src_disc_std:"Standard SME range",syn_src_terminal:"Long-term eurozone inflation",syn_src_disc_high:"Adjusted for high strategic risk",syn_src_disc_med:"Adjusted for moderate strategic risk",
  syn_alert_incomplete:"Incomplete strategic diagnostic — fill at least 3/5 modules to refine recommendations.",syn_alert_no_fin:"No financial data imported — hypotheses based on default values.",syn_alert_risk_margins:"High strategic risk despite stable historical margins — verify margin sustainability.",syn_alert_anr_required:"asset-based methods (NAV) should be prioritized.",syn_alert_risk_in:"identified in",syn_alert_criticite:"criticality",
  syn_ebitda_neg:"Negative EBITDA",syn_margins_low:"EBITDA margins < 5%",
  // ── ExcelImporter ──
  xl_format_err:"Unrecognized format. Drop the file HardToValue_Template.xlsx",xl_sheets_missing:"Missing sheets:",xl_societe_importee:"Imported company",xl_exercice:"fiscal year",xl_donnees_importees:"Data imported ·",xl_comparables:"Comparables",xl_actifs_incorp:"Intangible assets",xl_effacer:"Clear",xl_lecture:"Reading file…",xl_erreur_import:"Import error",xl_deposez:"Drop the file",xl_fichier:"HardToValue_Template.xlsx",xl_auto_fill:"Models auto-fill · Missing values entered directly in the platform",xl_parcourir:"Browse",xl_download_tpl:"⬇ Download template",
  ds_xl_banner:"File imported — financial data available in Financial Diagnostic",
  // ── Dash ──
  dash_calculés:"calculated",
  // ── CH Praticien ──
  ch_nom_societe:"Company name",ch_nb_titres:"Shares outstanding",ch_vr_section:"Earnings Value",ch_profit_n:"Profit N (double-weighted)",ch_profit_n_tip:"Net income for the most recent year — weight 2",ch_profit_n1:"Profit N−1",ch_profit_n1_tip:"Net income for the previous year — weight 1",ch_taux_cap:"Cantonal capitalization rate",ch_taux_cap_tip:"Rate set by cantonal tax authority — usually 7–10%",ch_vi_section:"Intrinsic Value (Substanzwert)",ch_vi_formula:"IV = Equity + Hidden reserves × (1 − CIT)",ch_equity_book:"Equity (book)",ch_equity_book_tip:"Shareholders' equity per balance sheet",ch_reserves:"Hidden reserves",ch_reserves_tip:"Unrealized gains on unrecorded assets (real estate, investments, inventory)",ch_taux_is:"CIT rate on hidden reserves",ch_taux_is_tip:"Standard federal rate ~21.5% — may vary by canton",ch_decote_min:"Minority discount",ch_decote_min_tip:"Applicable if the valued interest is minority (< 50%) — usually 20–30%",ch_result_title:"CSI Circular n°28 — Formule Praticien",ch_valeur_fiscale:"Tax value",ch_valeur_titre:"Value per share:",ch_decote_de:"Minority discount of",ch_lbl_vf:"Tax Value",ch_lbl_vt:"Value / Share",ch_lbl_vr:"Earnings Value",ch_lbl_vi:"Intrinsic Value",ch_benefice_moyen:"Average profit",ch_reserves_nettes:"Net hidden reserves",
  // ── Export ──
  exp_methode:"Method",exp_categorie:"Category",exp_ev_basse:"EV Low",exp_ev_centrale:"EV Central",exp_ev_haute:"EV High",exp_eq_basse:"Equity Low",exp_eq_centrale:"Equity Central",exp_eq_haute:"Equity High",exp_fourchette:"Range (%)",exp_v_action:"V/Share (€)",exp_poids:"Suggested Weight",
  // ── Jurisdiction Export ──
  jur_exp_btn_us:"Export IRS 59-60 (.xlsx)",jur_exp_btn_de:"Export IDW S1 (.xlsx)",jur_exp_btn_ch:"Export CSI n°28 (.xlsx)",jur_exp_btn_uk:"Export HMRC SAV (.xlsx)",jur_exp_generating:"Generating…",
  jur_exp_sheet_summary:"Summary",jur_exp_sheet_detail:"Calculation Detail",jur_exp_sheet_ref:"Regulatory Reference",
  jur_exp_company:"Company",jur_exp_date:"Date",jur_exp_method:"Valuation Method",jur_exp_parameter:"Parameter",jur_exp_value:"Value",jur_exp_step:"Step",jur_exp_amount:"Amount",jur_exp_result:"Result",
  jur_exp_equity_value:"Equity Value",jur_exp_vps:"Value per Share",jur_exp_shares:"Shares Outstanding",jur_exp_net_debt:"Net Debt",jur_exp_earnings_value:"Earnings Value",
  jur_exp_norm_earnings:"Normalized Earnings",jur_exp_ke:"Ke (Cost of Equity)",jur_exp_cap_rate:"Capitalization Rate",jur_exp_growth:"Sustainable Growth",jur_exp_weighted_avg:"Weighted Average",
  jur_exp_beta_levered:"Beta Levered",jur_exp_ke_after_tax:"Ke After-Tax",jur_exp_sum_pv:"Sum PV Earnings",jur_exp_terminal_value:"Terminal Value",jur_exp_terminal_pct:"Terminal %",jur_exp_ertragswert:"Ertragswert",jur_exp_year:"Year",jur_exp_earnings:"Earnings",jur_exp_pv:"PV",
  jur_exp_avg_profit:"Average Profit",jur_exp_vr:"Earnings Value (VR)",jur_exp_vi:"Intrinsic Value (VI)",jur_exp_gross_value:"Gross Value",jur_exp_tax_value:"Tax Value",jur_exp_minority_discount:"Minority Discount",jur_exp_reserves_net:"Net Hidden Reserves",jur_exp_cap_rate_cantonal:"Cantonal Cap. Rate",
  jur_exp_gross_omv:"Gross OMV",jur_exp_adj_omv:"Adjusted OMV",jur_exp_control_prem:"Control Premium",jur_exp_illiquidity:"Illiquidity Discount",jur_exp_size_disc:"Size Discount",jur_exp_eff_pe:"Effective P/E",jur_exp_stake:"Stake %",jur_exp_sector_pe:"Sector P/E",jur_exp_maint_earnings:"Maintainable Earnings",
  jur_exp_us_ref_title:"IRS Revenue Ruling 59-60 — Fair Market Value of Closely Held Stock",jur_exp_us_ref_text:"Section 4 prescribes 8 factors including earnings capacity, dividend-paying capacity, goodwill, book value, and comparable transactions. The CAPM build-up method is the standard discount rate approach for closely held businesses.",
  jur_exp_de_ref_title:"IDW S1 (2008) — Objectivierter Unternehmenswert",jur_exp_de_ref_text:"The IDW Standard S1 (revised 2008) requires discounting distributable earnings at an after-personal-tax CAPM rate. The Abgeltungssteuer (26.375%) is applied to the risk-free component. Beta is relevered using the Hamada formula.",
  jur_exp_ch_ref_title:"CSI Circular n°28 — Formule Praticien",jur_exp_ch_ref_text:"The practitioner formula (2×VR + VI) / 3 is the canonical Swiss tax valuation method, binding for all cantonal administrations. VR = earnings value, VI = intrinsic value (adjusted book equity).",
  jur_exp_uk_ref_title:"HMRC SAV Manual SVM107070 — Open Market Value",jur_exp_uk_ref_text:"Open Market Value per TCGA 1992 §272: the price obtainable on an open market between a willing vendor and a willing purchaser. Adjustments for illiquidity, control, and size are standard for unlisted shares.",
  // ── AI shared ──
  ai_suggestions:"✦ AI Suggestions",ai_analyse_loading:"Analyzing…",ai_backend_err:"Backend unavailable",
  // ── Revue de Valorisation ──
  rv_title:"Valuation Review",rv_subtitle:"AI-powered critical review of your DCF model",rv_drop_label:"Drop your client DCF model (.xlsx / .xls)",rv_drop_hint:"The parser will attempt to identify sheets and extract key assumptions",rv_browse:"Browse",rv_reading:"Reading file…",rv_parsed:"File parsed",rv_sheets:"sheets identified",rv_assumptions:"assumptions extracted",rv_projections:"projection rows",rv_clear:"Clear",rv_parse_err:"Could not extract DCF assumptions from this file",
  rv_ctx_title:"Context",rv_ctx_company:"Company name",rv_ctx_company_ph:"Name of the valued company",rv_ctx_sector:"Sector",rv_ctx_sector_ph:"Industry / sector",rv_ctx_purpose:"Purpose of valuation",rv_ctx_purpose_ph:"Transaction, litigation, tax filing…",rv_ctx_date:"Valuation date",rv_ctx_notes:"Additional notes",rv_ctx_notes_ph:"Any context the reviewer should know…",
  rv_run:"Run AI Review",rv_running:"Analyzing model…",rv_err:"Review error",
  rv_overall:"Overall Assessment",rv_axes:["Discount Rate & WACC","Growth & Terminal Value","Cash Flow Projections","Comparables & Cross-checks","Presentation & Compliance"],
  rv_severity_critical:"Critical",rv_severity_major:"Major",rv_severity_minor:"Minor",rv_severity_ok:"Adequate",
  rv_no_findings:"No findings for this axis.",rv_export_pdf:"Export Review (PDF)",rv_exporting:"Exporting…",rv_projection_rows:"projection rows",
  // ── Login ──
  login_title:"Hard to Value",login_subtitle:"Financial & Tax Valuation Platform",login_enter_pin:"Enter your 6-digit access PIN",login_unlock:"Unlock",login_session_expires:"Session expires when browser closes",login_incorrect:"Incorrect PIN",login_attempts_remaining:"attempts remaining",login_attempt_remaining:"attempt remaining",login_too_many:"Too many attempts",login_locked:"Locked — try again in",
  // ── Reset & Nav ──
  reset_btn:"New session",reset_confirm_msg:"Are you sure? All session data will be erased.",reset_cancel:"Cancel",reset_clear:"Clear all",
  nav_jurisdiction:"By jurisdiction",nav_logout:"Log out",nav_models:"models",nav_jurisdictions:"jurisdictions",
  // ── US IRS 59-60 ──
  us_company:"Company",us_company_name:"Company Name",us_company_name_ph:"Acme Corp",us_shares:"Shares Outstanding",us_shares_tip:"Total number of shares issued",us_net_debt:"Net Debt",us_net_debt_tip:"Total debt minus cash — bridge to equity value",
  us_ni_section:"Net Income — 3-Year History",us_ni_formula:"Normalized Earnings = (N×3 + N−1×2 + N−2×1) / 6",us_ni_n2:"Net Income N−2",us_ni_n2_tip:"Earliest year — weight 1",us_ni_n1:"Net Income N−1",us_ni_n1_tip:"Prior year — weight 2",us_ni_n:"Net Income N",us_ni_n_tip:"Most recent year — weight 3 (highest)",
  us_norm_section:"Normalization Adjustments",us_addback:"Owner Comp Add-back",us_addback_tip:"Excess owner compensation above arm's-length market rate — add back to normalize",us_nonrecurring:"Non-Recurring Items",us_nonrecurring_tip:"One-time gains or losses to strip from earnings (e.g. asset sale, litigation)",
  us_capm_section:"Discount Rate — CAPM Build-Up",us_capm_formula:"Ke = Rf + β×ERP + Size + Specific  |  Cap Rate = Ke − g",us_rf:"Rf — US Treasury 20Y",us_rf_tip:"20-year US Treasury yield (spot rate)",us_erp:"ERP (Damodaran US)",us_erp_tip:"Equity Risk Premium — Damodaran US estimate",us_beta:"Beta",us_beta_tip:"Relevered beta from comparable public companies",us_size_prem:"Size Premium",us_size_prem_tip:"Kroll/Duff & Phelps small-cap premium by decile",us_spec_prem:"Specific Premium",us_spec_prem_tip:"Company-specific risk: concentration, key-man, etc.",us_growth:"Sustainable Growth Rate",us_growth_tip:"Long-run earnings growth — cap rate = Ke minus this",
  us_ke:"Ke",us_cap_rate:"Cap Rate",us_result_title:"IRS Rev. Ruling 59-60 §4 — Fair Market Value",us_earnings_cap:"Earnings Capitalization",us_norm_of:"Normalized earnings of",us_cap_at:"capitalized at",us_vps:"Value per share:",
  us_equity_value:"Equity Value",us_earnings_value:"Earnings Value",us_value_per_share:"Value per Share",us_norm_earnings:"Normalized Earnings",us_ke_buildup:"Ke (Build-Up)",
  us_weighted_title:"Weighted Earnings — Rev. Ruling 59-60 §4",us_tbl_year:"Year",us_tbl_ni:"Net Income",us_tbl_weight:"Weight",us_tbl_weighted:"Weighted",us_tbl_wavg:"Weighted Average (÷6)",
  us_capm_title:"CAPM Build-Up — Discount Rate",us_note_title:"Rev. Ruling 59-60 §5 reminder:",us_note_text:"The IRS requires consideration of market prices of similar stocks and prior arm's-length transactions. Market multiples should be used as a sanity check — run the Multiples model for cross-validation.",
  // ── DE IDW S1 ──
  de_company:"Unternehmen / Company",de_company_name:"Company Name",de_company_name_ph:"Musterfirma GmbH",de_shares:"Shares Outstanding",de_net_debt:"Net Debt (Nettoverschuldung)",
  de_earnings_section:"Earnings Plan (Ertragsplan)",de_earnings_formula:"Distributable earnings (ausschüttungsfähige Erträge) — IDW S1 §29",de_base_earnings:"Base Earnings (Year 0)",de_base_earnings_tip:"Current normalized distributable earnings before projection",de_growth_initial:"Initial Growth",de_growth_initial_tip:"Expected earnings growth in early years",de_growth_terminal:"Terminal Growth (g)",de_growth_terminal_tip:"Long-run sustainable growth — typically 0–1.5%",de_conv_year:"Conv. Year",de_conv_year_tip:"Year when growth reaches terminal rate",de_terminal_gordon:"Terminal Growth (Gordon)",de_terminal_gordon_tip:"Perpetuity growth for terminal value — IDW S1 typically 0–1%",de_forecast_years:"Forecast Years",de_forecast_years_tip:"Explicit planning horizon — IDW S1: typically 5–10 years",
  de_capm_section:"IDW S1 — After-Tax CAPM (Abgeltungssteuer)",de_capm_formula:"ke = Basiszinssatz×(1−s) + β_lev×MRP  |  s = Abgeltungssteuer",de_basiszinssatz:"Basiszinssatz (IDW/Bundesbank)",de_basiszinssatz_tip:"Quarterly base rate per IDW — derived from Svensson method on German government bonds",de_mrp:"MRP after-tax (IDW guidance)",de_mrp_tip:"Market risk premium after personal tax — IDW range 5.0–7.5%",de_beta:"β unlevered",de_beta_tip:"Sector beta unlevered — Damodaran Germany data",de_de_ratio:"D/E Ratio",de_de_ratio_tip:"Debt-to-equity at market value (Hamada formula)",de_tax:"IS Tax Rate",de_tax_tip:"Corporate tax rate — used in Hamada beta relevering",de_abgelt:"Abgeltungssteuer (personal div. tax)",de_abgelt_tip:"German capital gains / dividend tax — 25% + 5.5% Solidaritätszuschlag = 26.375%",
  de_beta_lev:"β levered",de_ke_after:"Ke after-tax",de_result_title:"IDW S1 (2008) — Objectivierter Unternehmenswert",de_ertragswert:"Ertragswertverfahren",de_terminal_value:"Terminal value",de_of_ertragswert:"of Ertragswert",
  de_equity_value:"Equity Value",de_ertragswert_lbl:"Ertragswert",de_value_per_share:"Value per Share",de_sum_pv:"Sum PV Earnings",de_terminal_lbl:"Terminal Value",de_terminal_pct:"Terminal %",
  de_proj_title:"Earnings Projections (Ertragsplan)",de_tbl_yr:"Yr",de_tbl_earnings:"Earnings",de_tbl_growth:"Growth",de_tbl_pv:"PV Earnings",
  de_note_title:"IDW S1 — Regulatory Note",de_note_ke:"Ke formula (IDW S1 post-2008):",de_note_ke_formula:"Basiszinssatz×(1−s) + β_levered × MRP_after-tax",de_note_beta:"Beta:",de_note_beta_text:"Re-levered per Hamada using current capital structure → β_lev =",de_note_abgelt:"Abgeltungssteuer:",de_note_abgelt_text:"(25% + 5.5% Solidaritätszuschlag) applied to Basiszinssatz component per IDW RS HFA 10.",de_note_ref:"Ref.: IDW S1 i.d.F. 2008 §§100–167 · IDW RS HFA 10 · Bundesbank Svensson base rate",
  // ── UK HMRC ──
  uk_company:"Company",uk_company_name:"Company Name",uk_company_name_ph:"Acme Ltd",uk_total_shares:"Total Shares (all issued)",uk_total_shares_tip:"Total number of shares in issue — used to compute per-share value",uk_stake:"Stake % being valued",uk_stake_tip:"Percentage stake being valued — drives minority vs. control treatment",
  uk_earnings_section:"Earnings Basis",uk_earnings_formula:"Gross OMV = Maintainable Earnings × Sector P/E",uk_maintainable:"Maintainable Earnings (post-tax)",uk_maintainable_tip:"Normalized sustainable post-tax earnings — strip non-recurring items, adjust owner remuneration",uk_pe:"Sector P/E (quoted comps)",uk_pe_tip:"Price-to-earnings ratio from comparable listed companies — HMRC expects sector-appropriate multiple",
  uk_gross_omv:"Gross OMV",uk_adj_omv:"Adjusted OMV",
  uk_adj_section:"HMRC Adjustments",uk_control_note_pre:"Control premium only applies if stake ≥ 50% (current stake:",uk_majority:"majority",uk_minority:"minority",uk_control:"Control Premium",uk_control_tip:"Premium for controlling stake — only applied if stake ≥ 50%",uk_illiquidity:"Illiquidity Discount",uk_illiquidity_tip:"HMRC SAV: typically 15–30% for unlisted minority shares (SVM107070)",uk_size:"Size Discount",uk_size_tip:"Additional discount for small company risk — typically 5–15%",
  uk_result_title:"HMRC SAV — Open Market Value (SVM107070)",uk_pe_basis:"P/E Earnings Basis",uk_adj_to:"adjusted to",uk_eff_pe:"Effective P/E:",uk_vps:"Value per share:",
  uk_adj_omv_lbl:"Adjusted OMV",uk_vps_lbl:"Value / Share",uk_gross_omv_lbl:"Gross OMV",uk_eff_pe_lbl:"Effective P/E",
  uk_waterfall_title:"Adjustment Waterfall — HMRC SAV",uk_wf_me_pe:"Maintainable Earnings × P/E",uk_wf_gross:"Gross OMV",uk_wf_control:"Control Premium",uk_wf_na_minority:"N/A (minority)",uk_wf_majority_only:"§ Majority only",uk_wf_illiquidity:"Illiquidity Discount",uk_wf_illiquidity_ref:"SVM107070",uk_wf_size:"Size Discount",uk_wf_size_ref:"Small company",uk_wf_omv:"Open Market Value (TCGA 1992 §272)",
  uk_note_title:"HMRC SAV — Regulatory Note",uk_note_standard:"Standard:",uk_note_standard_text:"Open Market Value per TCGA 1992 §272 — the price obtainable on an open market between a willing vendor and a willing purchaser.",uk_note_goodwill:"Goodwill:",uk_note_goodwill_text:"If the company carries unrecognized goodwill, HMRC SAV applies CG68300 protocol — run a separate analysis.",uk_note_crosscheck:"Cross-check:",uk_note_crosscheck_text:"HMRC expects a dividend yield or asset basis cross-check for minority holdings. Consider running the DCF model as supporting evidence.",uk_note_ref:"Ref.: HMRC SAV Manual SVM107070 · TCGA 1992 §272 · CG68300 (Goodwill) · ITEPA 2003 (employment-related securities)",
  // ── CH extras ──
  ch_canton_label:"Canton",ch_vr_preview:"VR (preview)",ch_vi_preview:"VI (preview)",ch_calc_title:"Formule Praticien CSI — Calculation Detail",ch_gross_value:"Gross value =",ch_after_discount:"After minority discount",ch_note_ref:"Ref.: CSI Circular n°28 §3.1 (2023) · FTA Circular n°37 · Applicable in all Swiss cantons for wealth tax and transfer duties.",
  // ── SynthesisReport ──
  sr_no_model:"No model calculated",sr_back_dash:"← Back to dashboard",sr_back:"← Back",
  sr_subtitle_report:"Synthesis report",sr_method:"method",sr_methods:"methods",sr_calculated:"calculated",sr_calculated_pl:"calculated",
  sr_kpi_median:"Median Equity",sr_kpi_range:"Range Q1–Q3",sr_kpi_nfd:"Net Financial Debt",sr_kpi_vps:"Value/share (median)",sr_kpi_percentile:"25th–75th percentile",sr_kpi_ev_bridge:"EV → Equity bridge",sr_kpi_shares:"shares",sr_kpi_shares_na:"Shares not provided",
  sr_tab_football:"🏈 Football Field",sr_tab_table:"📋 Detailed Table",sr_tab_bridge:"🔗 Bridge EV → Equity",sr_tab_overview:"📊 Overview",
  sr_cat_financial:"Financial Methods",sr_cat_intangible:"Intangible Assets",sr_cat_asset:"Asset-Based Methods",sr_cat_jur_fr:"Jurisdiction 🇫🇷",sr_cat_jur_us:"Jurisdiction 🇺🇸",sr_cat_jur_de:"Jurisdiction 🇩🇪",sr_cat_jur_ch:"Jurisdiction 🇨🇭",sr_cat_jur_uk:"Jurisdiction 🇬🇧",
  sr_nm_dcf:"DCF",sr_nm_exc:"Excess Return",sr_nm_mul:"Multiples",sr_nm_rfr:"Relief/Royalty",sr_nm_mpe:"MPEEM",sr_nm_ast:"Asset-Based",sr_nm_vp:"V. Productivité",sr_nm_vm:"V. Mathématique",sr_nm_vr:"V. Rendement",sr_nm_mba:"Val. MBA",sr_nm_ebe_re:"Val. EBE/RE",sr_nm_comb:"Comb. DGI",sr_nm_us:"IRS Rev. Ruling 59-60",sr_nm_de:"IDW S1",sr_nm_ch:"CSI n°28",sr_nm_uk:"HMRC SAV",
  sr_ff_legend_zone:"Convergence zone Q1–Q3",sr_ff_legend_range:"Low → Central → High",sr_ff_legend_mid:"Central point (model median)",sr_ff_eq_header:"← Equity Value (low | central | high) →",sr_ff_section_intl:"Financial Methods",sr_ff_section_jur:"Methods by Jurisdiction",sr_ff_median:"Median",
  sr_tbl_method:"Method",sr_tbl_category:"Category",sr_tbl_ev_low:"EV low",sr_tbl_ev_mid:"EV central",sr_tbl_ev_high:"EV high",sr_tbl_eq_low:"Equity low",sr_tbl_eq_mid:"Equity central",sr_tbl_eq_high:"Equity high",sr_tbl_range:"Range",sr_tbl_vps:"V/share",sr_tbl_weight:"Suggested weight",sr_tbl_median:"▶ MEDIAN",
  sr_br_nfd:"Net financial debt (NFD)",sr_br_shares:"Number of shares",sr_br_eq_median:"Equity median",sr_br_vps_median:"V/share median",sr_br_ev_median:"EV median",sr_br_eq_median_short:"Equity median",sr_nfd_short:"NFD",sr_vpa_short:"V/SHARE",
  sr_ov_eq_by_method:"Equity value by method",sr_ov_convergence:"Convergence analysis",sr_ov_conv_level:"Convergence level",sr_ov_strong:"Strong",sr_ov_moderate:"Moderate",sr_ov_weak:"Weak",sr_ov_dispersion:"Dispersion",sr_ov_of_median:"of the median",sr_ov_cat_title:"Methods by category",sr_ov_cat_median:"cat. median",
  sr_pdf_title:"PDF Report",sr_pdf_content:"Report contents",sr_pdf_pages:"pages",sr_pdf_cover:"Cover page — company, date, firm",sr_pdf_exec:"Executive summary — range, KPIs, narrative analysis",sr_pdf_ff:"Football field chart — all methods",sr_pdf_table:"Synthesis table — EV/Equity low/mid/high",sr_pdf_detail:"Detail:",sr_pdf_annex:"Methodological appendix",sr_pdf_firm:"Firm name",sr_pdf_analyst:"Analyst name (optional)",sr_pdf_analyst_ph:"e.g. John Smith, CFA",sr_pdf_logo:"Firm logo (PNG/JPG, optional)",sr_pdf_logo_loaded:"Logo loaded ✓",sr_pdf_logo_choose:"Choose an image...",sr_pdf_logo_remove:"Remove",sr_pdf_logo_preview:"Preview — will be placed at the top of the cover page",sr_pdf_cancel:"Cancel",sr_pdf_generate:"Generate PDF report",
  sr_exp_valuation_report:"VALUATION REPORT",sr_exp_synthesis_multimethods:"Synthesis report and multi-method analysis",sr_exp_equity_median_label:"EQUITY VALUE (MEDIAN)",sr_exp_ie_per_share:"i.e. {0} EUR per share",sr_exp_range_retained:"Selected range (Q1-Q3) : {0} - {1}",sr_exp_methods_valued:"valued",sr_exp_report_date:"report date",sr_exp_cabinet:"firm",sr_exp_prepared_by:"Prepared by : {0}",sr_exp_methodologies_used:"Methodologies used :",sr_exp_confidential:"CONFIDENTIAL",sr_exp_footer_conf:"Confidential report - Prepared by Hard to Value",sr_exp_footer_disclaimer:"The valuations presented are based on the data provided and do not constitute financial advice.",
  sr_exp_exec_summary:"Executive summary",sr_exp_exec_title:"EXECUTIVE SUMMARY",sr_exp_valuation_of:"Valuation of {0} | {1}",sr_exp_kpi_med_eq:"Median Equity",sr_exp_kpi_range_q:"Range Q1-Q3",sr_exp_kpi_nfd_label:"Net debt (NFD)",sr_exp_kpi_vpa_med:"V/share median",sr_exp_kpi_nb_methods:"No. methods",sr_exp_kpi_dispersion:"Dispersion",sr_exp_not_provided:"Not provided",sr_exp_not_calc:"Not calc.",sr_exp_low:"Low",sr_exp_moderate:"Moderate",sr_exp_high:"High",sr_exp_narrative_title:"NARRATIVE ANALYSIS",sr_exp_recommended_range:"RECOMMENDED RANGE",sr_exp_median_label:"Median : {0}",sr_exp_vpa_label:"V/share : {0}",
  sr_exp_narr_base:"Based on {0} valuation method{1} applied to {2}, the equity value has a median of {3}. {4}",sr_exp_narr_low_disp:"The different methods converge towards a narrow range, reinforcing the robustness of the valuation and indicating coherence between asset-based, yield and flow approaches.",sr_exp_narr_mod_disp:"Moderate dispersion is observed between methods, reflecting differences in assumptions on growth, risk and capital structure. This amplitude is typical for an unlisted SME.",sr_exp_narr_high_disp:"The strong dispersion between methods highlights significant sensitivity to the assumptions used. It is recommended to favor the methods best suited to the sector and to discard the identified outliers.",
  sr_exp_narr_range:"The selected range (Q1-Q3) runs from {0} EUR to {1} EUR, representing the interquartile consensus zone of all implemented approaches.",sr_exp_narr_aligned:" The international financial methods (median {0} EUR) and DGI tax methods (median {1} EUR) are well aligned, validating the evaluation coherence.",sr_exp_narr_gap:" A gap of {0}% is noted between the financial methods median ({1} EUR) and the DGI methods median ({2} EUR), reflecting distinct perspectives on asset value and normative profitability.",
  sr_exp_narr_nfd_pos:"The net financial debt of {0} EUR was deducted from the enterprise value (EV) to obtain equity value.",sr_exp_narr_nfd_zero:"No net financial debt was provided; the enterprise value equals the equity value.",sr_exp_narr_outlier_high:"Warning: {0} show{1} a value more than 50% above the median and should be interpreted with caution.",sr_exp_narr_outlier_low:" {0} come{1} in significantly below the median.",sr_exp_narr_closest:" The {0} method ({1} EUR) is the closest to the overall median.",
  sr_exp_ff_subtitle:"Football Field -- Graphical synthesis",sr_exp_ff_methods_intl:"FINANCIAL METHODS",sr_exp_ff_methods_jur:"METHODS BY JURISDICTION",sr_exp_tbl_subtitle:"Synthesis table",sr_exp_tbl_title:"VALUATION SYNTHESIS",sr_exp_tbl_info:"All values in EUR | NFD : {0} | Shares : {1}",sr_exp_tbl_method:"Method",sr_exp_tbl_category:"Category",sr_exp_tbl_ev_low:"EV Low",sr_exp_tbl_ev_mid:"EV Central",sr_exp_tbl_ev_high:"EV High",sr_exp_tbl_eq_low:"Eq. Low",sr_exp_tbl_eq_mid:"Eq. Central",sr_exp_tbl_eq_high:"Eq. High",sr_exp_tbl_range:"Range",sr_exp_tbl_vpa:"V/Share",sr_exp_tbl_median_q:"MEDIAN / Q1-Q3",
  sr_exp_bridge_eq:"Equity Value = Enterprise Value - Net Financial Debt ({0})",sr_exp_bridge_formula:"Median EV : {0}   -   NFD : {1}   =   Median Equity : {2}",sr_exp_bridge_vpa:"Value / share (median) : {0} EUR  (on {1} shares)",
  sr_exp_detail_method:"Detail method : {0}",sr_exp_eq_low:"Equity Low",sr_exp_eq_mid:"Equity Central",sr_exp_eq_high:"Equity High",sr_exp_range_label:"Range",sr_exp_vpa_central:"Value per share (central) : {0}",sr_exp_relative_pos:"RELATIVE POSITION (vs all methods)",sr_exp_median_all:"Median all methods",sr_exp_calc_params:"CALCULATION PARAMETERS",sr_exp_methodology:"METHODOLOGY",
  sr_exp_annex_subtitle:"Methodological appendix",sr_exp_annex_title:"METHODOLOGICAL APPENDIX",sr_exp_annex_desc:"Summary description of the valuation methods implemented",sr_exp_annex_contd:"Methodological appendix (continued)",sr_exp_warning:"WARNING",sr_exp_disclaimer:"This report was prepared on the basis of the information and assumptions provided by the client. The valuations presented reflect the opinion of the evaluators at the report date and do not constitute a guarantee of value or investment advice. Any valuation is by nature subjective and depends on assumptions that may evolve. This document is strictly confidential.",
  sr_png_methods_header:"FINANCIAL METHODS",sr_png_jur_header:"METHODS BY JURISDICTION",sr_png_median:"Median:",sr_png_per_share:"/share",
  sr_xls_title:"SYNTHESIS REPORT — VALUATION",sr_xls_company:"Company:",sr_xls_date:"Date:",sr_xls_methods:"Methods:",sr_xls_nfd:"NFD:",sr_xls_shares:"Shares:",sr_xls_median:"MEDIAN",sr_xls_sheet1:"Synthesis",sr_xls_sheet2:"Bridge EV-Equity",sr_xls_sheet3:"Chart data",sr_xls_br_title:"BRIDGE EV → EQUITY VALUE",sr_xls_br_ev:"EV Central (k€)",sr_xls_br_nfd:"NFD (k€)",sr_xls_br_eq:"Equity Value (k€)",
grp:{intl:"International Financial Methods",dgi:"French Tax Administration Methods — DGI Guide 2006",jur:"Jurisdiction Calculators — Official Fiscal Standards",diagStrat:"Strategic Diagnostic",diagFin:"Financial Diagnostic",models:"Valuation Models",diagSynth:"Synthesis",revue:"Valuation Review"},m:{dcf:{n:"Discounted Cash Flow",d:"Intrinsic Value — Free Cash Flow",x:"Projects future free cash flows and discounts them at WACC. Best for companies with predictable cash generation and a multi-year business plan."},exc:{n:"Excess Return / Economic Profit",d:"Intrinsic Value — Value Creation",x:"Values the firm as invested capital plus the present value of future economic profits (ROIC − WACC). Highlights whether the business creates or destroys value."},mul:{n:"Market Multiples",d:"Relative Value — Comparable Transactions",x:"Derives value by applying EV/EBITDA, EV/EBIT or P/E multiples from a peer group. Provides a market-anchored reference range."},rfr:{n:"Relief from Royalty",d:"Intangible Assets — Brands & Patents",x:"Values an intangible asset as the royalty stream avoided by owning it. Standard approach for trademark and patent valuation under IFRS 3 / PPA."},mpe:{n:"Multi-Period Excess Earnings (MPEEM)",d:"Intangible Assets — Customer Relationships",x:"Isolates earnings attributable to a specific intangible (customers, contracts) after charging contributory assets. Primary method under IFRS 3 for customer-related intangibles."},ast:{n:"Asset-Based / Net Asset Value",d:"Balance Sheet — Adjusted Book Value",x:"Restates each balance-sheet item at fair value and deducts liabilities. Appropriate for holding companies, real-estate portfolios and liquidation scenarios."},vp:{n:"Valeur de Productivité",d:"Capitalisation of Recurring Earnings",x:"Capitalises the weighted average net profit at a risk-adjusted rate (deflated risk-free rate + β × 5%). Core method in the DGI guide for operating companies."},vm:{n:"Valeur Mathématique",d:"Revalued Net Assets + Goodwill",x:"Restates equity at fair market value, then optionally adds a goodwill (survaleur) calculated on the super-profit capitalised over 8 years. Reference method for asset-rich entities."},vr:{n:"Valeur de Rendement",d:"Capitalisation of Dividends",x:"Capitalises the weighted average dividend yield at a market rate with a 30% illiquidity discount. Complementary method for minority shareholdings with regular distributions."},mba:{n:"Valeur par la Marge Brute d'Autofinancement",d:"Multiple of Operating Cash Flow",x:"Values the company as a multiple of net income plus depreciation (self-financing margin). Preferred for capital-intensive industrial businesses."},ebe_re:{n:"Valeur par l'EBE ou le Résultat d'Exploitation",d:"EBITDA / EBIT Multiple",x:"Applies a sector multiple to EBITDA (EBE) or EBIT (RE) then deducts net debt. Standard cross-check for mid-market transactions."},comb:{n:"Combinaison DGI",d:"Statutory Weighted Average — DGI Guide Nov. 2006",x:"Blends Valeur Mathématique and Valeur de Rentabilité according to the weighting prescribed by the French tax authority for gift and inheritance tax filings."},us:{n:"🇺🇸 Earnings Capitalization",d:"IRS Rev. Ruling 59-60 — Fair Market Value",x:"Normalizes 3-year weighted average earnings and capitalizes at a CAPM build-up rate. Primary method for closely-held business valuation per IRS Rev. Ruling 59-60 §4."},de:{n:"🇩🇪 Ertragswertverfahren",d:"IDW S1 (2008) — Objectivized Value",x:"Discounts distributable earnings at an after-personal-tax CAPM rate (Basiszinssatz + β×MRP). Dominant method for German business valuation per IDW S1 §§100–167."},ch:{n:"🇨🇭 Formule Praticien CSI",d:"Circulaire CSI n°28 — Valeur Fiscale",x:"(2×Valeur de Rendement + Valeur Intrinsèque) ÷ 3. Canonical Swiss tax formula binding for all cantonal administrations under CSI Circular n°28."},uk:{n:"🇬🇧 P/E Earnings Basis",d:"HMRC SAV — Open Market Value",x:"Maintainable earnings × sector P/E, adjusted for illiquidity, control premium and size. Standard HMRC approach for unlisted shares under TCGA 1992 §272 (SVM107070)."},fr:{n:"🇫🇷 DGI Methods (France)",d:"Guide DGI Nov. 2006 — Tax Administration",x:"6 official French tax valuation methods (VP, VM, VR, MBA, EBE/RE, Combination). Prescribed by French tax authority for gift, inheritance and transfer pricing filings."}}},fr:{brand:"Hard to Value",tag:"Plateforme de Valorisation Financière & Fiscale",dash:"Tableau de bord",sel:"Sélectionnez un modèle de valorisation",calc:"Calculer",calcing:"Calcul...",back:"← Retour",clr:"Effacer",proj:"Projections",eqV:"Capitaux Propres",evV:"Valeur d'Entreprise",vps:"Valeur/Action",tv:"Valeur Terminale",spv:"Somme PV FCFF",em:"Multiple Sortie",tp:"% Terminale",fv:"Juste Valeur",anav:"Actif Net Réévalué",liq:"Liquidation",bnav:"Actif Net Comptable",ptb:"Prime/Book",summary:"Synthèse de Valorisation",warn:"Alertes",calcdone:"Calculé",
  company:"Société",shares:"Titres",companyName:"Nom de la société",sharesOutstanding:"Nombre de titres",
  alert_run_first:"Lancez d'abord le calcul avant d'exporter.",
  alert_export_err:"Erreur export : ",
  err_server:"Erreur serveur",
  err_export:"Erreur export : ",
  sec_company:"Société",sec_base:"Données de base",sec_growth:"Croissance",sec_margins:"Marges",
  sec_capital:"Capital",sec_forecast:"Projection",sec_adjustments:"Ajustements",
  sec_financials:"Données financières",sec_comparables:"Comparables",sec_assets:"Actifs",
  sec_rates:"Taux",sec_revenue:"Revenus",
  dashboard_methods:"Méthodes Financières",
  dashboard_hint:"Calculez au moins 2 modèles pour générer le rapport.",
  btn_report:"Générer le rapport de synthèse",
  lbl_methods:"méthodes",synth_title:"Sélection de méthodes & Hypothèses",synth_recommended:"Méthodes recommandées",synth_hypotheses:"Hypothèses suggérées",synth_alerts:"Alertes & Signaux",synth_apply:"Appliquer aux modèles",synth_ai_enrich:"Enrichissement IA",synth_op_profile:"Profil de l'opération",synth_risk_level:"Niveau de risque",synth_risk_flags:"Signaux de risque",synth_method_family:"Famille de méthodes",synth_fin_metrics:"Métriques calculées",synth_cagr:"TCAM du CA",synth_margin_stability:"Stabilité des marges",synth_leverage:"Endettement",synth_revenue_growth:"Croissance CA",synth_ebitda_margin:"Marge EBITDA",synth_discount_rate:"Taux d'actualisation",synth_terminal_growth:"Croissance terminale",errors:"Erreurs",fix_errors:"Corriger les erreurs pour calculer",
  // ── Diagnostic Stratégique ──
  ds_title:"Diagnostic Stratégique",ds_subtitle:"5 modules · données persistées localement · IA sur demande",ds_modules:"/5 modules",
  ds_analyse:"Analyse de l'opération",ds_nature:"Nature de l'opération",ds_nature_ph:"Cession, LBO, donation, apport partiel…",ds_parties:"Parties prenantes",ds_parties_ph:"Cédant, acquéreur, actionnaires minoritaires…",ds_enjeux:"Enjeux financiers",ds_enjeux_ph:"Prix cible, earn-out, garanties actif/passif…",ds_contexte:"Contexte & motivations",ds_contexte_ph:"Succession, développement, restructuration…",
  ds_swot:"Analyse SWOT",ds_forces:"Forces",ds_forces_ph:"Avantages concurrentiels, marques, brevets…",ds_faiblesses:"Faiblesses",ds_faiblesses_ph:"Dépendances, structure coûts, dette…",ds_opportunites:"Opportunités",ds_opportunites_ph:"Marchés émergents, croissance sectorielle…",ds_menaces:"Menaces",ds_menaces_ph:"Disruption, réglementation, concurrents…",
  ds_porter:"Porter — 5 Forces",ds_pouvoir_acheteurs:"Pouvoir acheteurs",ds_pouvoir_fournisseurs:"Pouvoir fournisseurs",ds_substituts:"Menace substituts",ds_nouveaux_entrants:"Nouveaux entrants",ds_rivalite:"Rivalité concurrentielle",
  ds_tip_acheteurs:"Pouvoir acheteurs — Capacité des clients à négocier des prix plus bas ou de meilleures conditions. Score élevé = fort levier des acheteurs, comprime les marges et le pouvoir de fixation des prix.",ds_tip_fournisseurs:"Pouvoir fournisseurs — Capacité des fournisseurs à imposer leurs conditions, augmenter les prix ou restreindre l'offre. Score élevé = alternatives limitées, hausse des coûts d'entrée.",ds_tip_substituts:"Menace de substitution — Risque que les clients se tournent vers des produits ou services alternatifs. Score élevé = substitution facile, plafonne les prix et la croissance.",ds_tip_entrants:"Nouveaux entrants — Facilité avec laquelle de nouveaux concurrents peuvent entrer sur le marché. Score élevé = faibles barrières, pression concurrentielle accrue.",ds_tip_rivalite:"Rivalité concurrentielle — Intensité de la concurrence entre les acteurs existants. Score élevé = rivalité féroce, érosion des marges et des parts de marché.",
  ds_op_cession:"Cession",ds_op_restructuration:"Restructuration",ds_op_apport:"Apport partiel",ds_op_fusion:"Fusion / Absorption",ds_op_tp_interco:"Prix de transfert / Interco",
  ds_meth_dcf:"DCF",ds_meth_exc:"Excess Return",ds_meth_mul:"Multiples",ds_meth_rfr:"Relief from Royalty",ds_meth_mpe:"MPEEM",ds_meth_ast:"Actif Net Réévalué",ds_meth_vp:"Productivité",ds_meth_vm:"Mathématique",ds_meth_vr:"Rendement",ds_meth_mba:"MBA",ds_meth_ebe_re:"EBE/RE",ds_meth_comb:"Combinaison DGI",ds_meth_us:"US Earnings Cap.",ds_meth_de:"Ertragswert IDW",ds_meth_ch:"CSI Praticien",ds_meth_uk:"HMRC P/E",ds_meth_fr:"DGI France",
  ds_positionnement:"Positionnement concurrentiel",ds_marche_cible:"Marché cible",ds_marche_cible_ph:"Segment, géographie, TAM…",ds_avantage:"Avantage concurrentiel",ds_avantage_ph:"Coût, différenciation, focalisation…",ds_part_marche:"Part de marché",ds_part_marche_ph:"Leader, challenger, suiveur, niche…",ds_facteurs:"Facteurs de différenciation",ds_facteurs_ph:"Technologie, marque, réseau, réglementation…",
  ds_risques:"Risques clés",ds_risque_col:"Risque",ds_prob_col:"Probabilité (1-3)",ds_impact_col:"Impact (1-3)",ds_crit_col:"Criticité",ds_nouveau_risque:"Nouveau risque",ds_ajouter_risque:"+ Ajouter un risque",
  ds_critique:"CRITIQUE",ds_eleve:"ÉLEVÉ",ds_moyen:"MOYEN",ds_faible:"FAIBLE",
  ds_profil:"Profil de l'opération",ds_type_op:"TYPE D'OPÉRATION",ds_non_detecte:"Non détecté",ds_niveau_risque:"NIVEAU DE RISQUE",ds_risk_high:"Élevé",ds_risk_medium:"Modéré",ds_risk_low:"Faible",ds_porter_avg:"Porter avg:",ds_completude:"COMPLÉTUDE",ds_famille_methodes:"FAMILLE DE MÉTHODES",ds_signaux_risque:"SIGNAUX DE RISQUE",
  ds_risk_impact_concentration_clientele:"Haircut sur la croissance projetée",ds_risk_adj_concentration_clientele:"-2% à -5%",ds_risk_impact_homme_cle:"Prime de risque spécifique intégrée au taux d'actualisation",ds_risk_adj_homme_cle:"+1% à +3%",ds_risk_impact_reglementaire:"Prime réglementaire intégrée au taux d'actualisation",ds_risk_adj_reglementaire:"+1% à +2%",ds_risk_impact_obsolescence:"Durée de projection réduite",ds_risk_adj_obsolescence:"-2 à -3 ans",
  // ── Diagnostic Financier ──
  df_title:"Diagnostic Financier",df_import_msg:"Importez le fichier Excel pour générer l'analyse automatique",df_societe:"Société",df_exercice:"exercice",
  df_marge_ebitda:"Marge EBITDA (N)",df_marge_ebit:"Marge EBIT (N)",df_marge_nette:"Marge Nette (N)",df_gearing:"Gearing (DN/CP)",df_roe:"ROE",df_roce:"ROCE",
  df_ca_ebitda:"CA & EBITDA",df_ca:"CA",df_ebitda:"EBITDA",df_evol_marges:"Évolution des marges",df_ebitda_pct:"EBITDA%",df_ebit_pct:"EBIT%",df_net_pct:"Net%",
  df_metriques:"Métriques calculées",df_tcam_ca:"TCAM du CA",df_tcam_ebitda:"TCAM EBITDA",df_stabilite:"Stabilité des marges",df_stable:"Stable",df_volatile:"Volatile",df_moderee:"Modérée",df_endettement:"Endettement",df_dn_ebitda:"DN/EBITDA",
  df_croissance_sugg:"Croissance suggérée (TCAM×0.8)",df_marge_sugg:"Marge EBITDA suggérée (médiane)",df_anr_warning:"approche patrimoniale (ANR) à privilégier",
  df_commentaire_ia:"Commentaire IA",df_backend_err:"Backend non disponible :",
  // ── BlocSynthese ──
  syn_subtitle:"Agrégation des signaux stratégiques et financiers",syn_analyse_loading:"Analyse…",syn_commentaire_ia:"Commentaire IA — Synthèse",
  syn_method_rationale:"Méthode adaptée au contexte {0}",syn_context:"contexte",syn_asset_approach:"approche patrimoniale (ANR) recommandée",syn_dcf_rationale:"Méthode de référence — actualisation des flux futurs",syn_mul_rationale:"Cross-check par les multiples de marché",
  syn_src_rev_tcam:"TCAM CA historique × 0.8",syn_src_ebitda_med:"Médiane marges EBITDA historiques",syn_src_disc_std:"Fourchette standard PME",syn_src_terminal:"Inflation long-terme zone euro",syn_src_disc_high:"Ajusté risque stratégique élevé",syn_src_disc_med:"Ajusté risque stratégique modéré",
  syn_alert_incomplete:"Diagnostic stratégique incomplet — complétez au moins 3/5 modules pour affiner les recommandations.",syn_alert_no_fin:"Aucune donnée financière importée — les hypothèses sont basées sur des valeurs par défaut.",syn_alert_risk_margins:"Risque stratégique élevé malgré des marges historiques stables — vérifier la soutenabilité des marges.",syn_alert_anr_required:"les méthodes patrimoniales (ANR) doivent être privilégiées.",syn_alert_risk_in:"identifié dans",syn_alert_criticite:"criticité",
  syn_ebitda_neg:"EBITDA négatif",syn_margins_low:"Marges EBITDA < 5%",
  // ── ExcelImporter ──
  xl_format_err:"Format non reconnu. Déposez le fichier HardToValue_Template.xlsx",xl_sheets_missing:"Onglets manquants :",xl_societe_importee:"Société importée",xl_exercice:"exercice",xl_donnees_importees:"Données importées ·",xl_comparables:"Comparables",xl_actifs_incorp:"Actifs incorporels",xl_effacer:"Effacer",xl_lecture:"Lecture du fichier…",xl_erreur_import:"Erreur d'import",xl_deposez:"Déposez le fichier",xl_fichier:"HardToValue_Template.xlsx",xl_auto_fill:"Les modèles se pré-remplissent automatiquement · Valeurs manquantes saisies directement dans la plateforme",xl_parcourir:"Parcourir",xl_download_tpl:"⬇ Télécharger le template",
  ds_xl_banner:"Fichier importé — données financières disponibles dans le Diagnostic Financier",
  // ── Dash ──
  dash_calculés:"calculés",
  // ── CH Praticien ──
  ch_nom_societe:"Nom de la société",ch_nb_titres:"Nombre de titres",ch_vr_section:"Valeur de Rendement",ch_profit_n:"Bénéfice N (double pondéré)",ch_profit_n_tip:"Résultat net de l'exercice le plus récent — pondération 2",ch_profit_n1:"Bénéfice N−1",ch_profit_n1_tip:"Résultat net de l'année précédente — pondération 1",ch_taux_cap:"Taux de capitalisation cantonal",ch_taux_cap_tip:"Taux fixé par l'administration fiscale cantonale — généralement 7 à 10%",ch_vi_section:"Valeur Intrinsèque (Substanzwert)",ch_vi_formula:"VI = Capitaux propres + Réserves latentes × (1 − IS)",ch_equity_book:"Capitaux propres (book)",ch_equity_book_tip:"Fonds propres selon le bilan comptable",ch_reserves:"Réserves latentes",ch_reserves_tip:"Plus-values latentes sur actifs non comptabilisées (biens immobiliers, participations, stocks)",ch_taux_is:"Taux IS sur réserves latentes",ch_taux_is_tip:"Taux standard fédéral ~21.5% — peut varier selon canton",ch_decote_min:"Décote minoritaire",ch_decote_min_tip:"Décote applicable si la participation valorisée est minoritaire (< 50%) — généralement 20–30%",ch_result_title:"CSI Circulaire n°28 — Formule Praticien",ch_valeur_fiscale:"Valeur fiscale",ch_valeur_titre:"Valeur par titre :",ch_decote_de:"Décote minoritaire de",ch_lbl_vf:"Valeur Fiscale",ch_lbl_vt:"Valeur / Titre",ch_lbl_vr:"Valeur de Rendement",ch_lbl_vi:"Valeur Intrinsèque",ch_benefice_moyen:"Bénéfice moyen",ch_reserves_nettes:"Réserves latentes nettes",
  // ── Export ──
  exp_methode:"Méthode",exp_categorie:"Catégorie",exp_ev_basse:"EV Basse",exp_ev_centrale:"EV Centrale",exp_ev_haute:"EV Haute",exp_eq_basse:"Equity Basse",exp_eq_centrale:"Equity Centrale",exp_eq_haute:"Equity Haute",exp_fourchette:"Fourchette (%)",exp_v_action:"V/Action (€)",exp_poids:"Poids suggéré",
  // ── Jurisdiction Export ──
  jur_exp_btn_us:"Exporter IRS 59-60 (.xlsx)",jur_exp_btn_de:"Exporter IDW S1 (.xlsx)",jur_exp_btn_ch:"Exporter CSI n°28 (.xlsx)",jur_exp_btn_uk:"Exporter HMRC SAV (.xlsx)",jur_exp_generating:"Génération…",
  jur_exp_sheet_summary:"Synthèse",jur_exp_sheet_detail:"Détail du calcul",jur_exp_sheet_ref:"Référence réglementaire",
  jur_exp_company:"Société",jur_exp_date:"Date",jur_exp_method:"Méthode de valorisation",jur_exp_parameter:"Paramètre",jur_exp_value:"Valeur",jur_exp_step:"Étape",jur_exp_amount:"Montant",jur_exp_result:"Résultat",
  jur_exp_equity_value:"Capitaux Propres",jur_exp_vps:"Valeur par Action",jur_exp_shares:"Nombre de titres",jur_exp_net_debt:"Endettement net",jur_exp_earnings_value:"Valeur de rendement",
  jur_exp_norm_earnings:"Bénéfice normalisé",jur_exp_ke:"Ke (Coût des fonds propres)",jur_exp_cap_rate:"Taux de capitalisation",jur_exp_growth:"Croissance durable",jur_exp_weighted_avg:"Moyenne pondérée",
  jur_exp_beta_levered:"Bêta relevé",jur_exp_ke_after_tax:"Ke après impôt",jur_exp_sum_pv:"Somme VA bénéfices",jur_exp_terminal_value:"Valeur terminale",jur_exp_terminal_pct:"% terminale",jur_exp_ertragswert:"Ertragswert",jur_exp_year:"Année",jur_exp_earnings:"Bénéfices",jur_exp_pv:"VA",
  jur_exp_avg_profit:"Bénéfice moyen",jur_exp_vr:"Valeur de Rendement (VR)",jur_exp_vi:"Valeur Intrinsèque (VI)",jur_exp_gross_value:"Valeur brute",jur_exp_tax_value:"Valeur fiscale",jur_exp_minority_discount:"Décote minoritaire",jur_exp_reserves_net:"Réserves latentes nettes",jur_exp_cap_rate_cantonal:"Taux cap. cantonal",
  jur_exp_gross_omv:"OMV brute",jur_exp_adj_omv:"OMV ajustée",jur_exp_control_prem:"Prime de contrôle",jur_exp_illiquidity:"Décote d'illiquidité",jur_exp_size_disc:"Décote de taille",jur_exp_eff_pe:"P/E effectif",jur_exp_stake:"Part %",jur_exp_sector_pe:"P/E sectoriel",jur_exp_maint_earnings:"Bénéfice récurrent",
  jur_exp_us_ref_title:"IRS Revenue Ruling 59-60 — Juste Valeur Marchande",jur_exp_us_ref_text:"La section 4 prescrit 8 facteurs incluant la capacité bénéficiaire, la capacité de distribution de dividendes, le goodwill, la valeur comptable et les transactions comparables. La méthode CAPM build-up est l'approche standard pour les sociétés non cotées.",
  jur_exp_de_ref_title:"IDW S1 (2008) — Objectivierter Unternehmenswert",jur_exp_de_ref_text:"La norme IDW S1 (révisée 2008) exige l'actualisation des bénéfices distribuables à un taux CAPM après impôt personnel. L'Abgeltungssteuer (26,375%) est appliquée à la composante sans risque. Le bêta est relevé selon la formule de Hamada.",
  jur_exp_ch_ref_title:"Circulaire CSI n°28 — Formule Praticien",jur_exp_ch_ref_text:"La formule praticien (2×VR + VI) / 3 est la méthode canonique d'évaluation fiscale suisse, contraignante pour toutes les administrations cantonales. VR = valeur de rendement, VI = valeur intrinsèque (capitaux propres réévalués).",
  jur_exp_uk_ref_title:"HMRC SAV Manual SVM107070 — Open Market Value",jur_exp_uk_ref_text:"Open Market Value selon TCGA 1992 §272 : le prix réalisable sur un marché ouvert entre un vendeur consentant et un acheteur consentant. Les ajustements pour illiquidité, contrôle et taille sont standards pour les titres non cotés.",
  // ── AI shared ──
  ai_suggestions:"✦ Suggestions IA",ai_analyse_loading:"Analyse…",ai_backend_err:"Backend non disponible",
  // ── Revue de Valorisation ──
  rv_title:"Revue de Valorisation",rv_subtitle:"Revue critique IA de votre modèle DCF client",rv_drop_label:"Déposez votre modèle DCF client (.xlsx / .xls)",rv_drop_hint:"Le parseur tentera d'identifier les onglets et d'extraire les hypothèses clés",rv_browse:"Parcourir",rv_reading:"Lecture du fichier…",rv_parsed:"Fichier analysé",rv_sheets:"onglets identifiés",rv_assumptions:"hypothèses extraites",rv_projections:"lignes de projection",rv_clear:"Effacer",rv_parse_err:"Impossible d'extraire les hypothèses DCF de ce fichier",
  rv_ctx_title:"Contexte",rv_ctx_company:"Nom de la société",rv_ctx_company_ph:"Nom de la société valorisée",rv_ctx_sector:"Secteur",rv_ctx_sector_ph:"Industrie / secteur d'activité",rv_ctx_purpose:"Objet de la valorisation",rv_ctx_purpose_ph:"Transaction, contentieux, déclaration fiscale…",rv_ctx_date:"Date de valorisation",rv_ctx_notes:"Notes complémentaires",rv_ctx_notes_ph:"Tout contexte utile pour le réviseur…",
  rv_run:"Lancer la Revue IA",rv_running:"Analyse du modèle…",rv_err:"Erreur de revue",
  rv_overall:"Appréciation Globale",rv_axes:["Taux d'actualisation & WACC","Croissance & Valeur Terminale","Projections de Flux","Comparables & Recoupements","Présentation & Conformité"],
  rv_severity_critical:"Critique",rv_severity_major:"Majeur",rv_severity_minor:"Mineur",rv_severity_ok:"Adéquat",
  rv_no_findings:"Aucune observation sur cet axe.",rv_export_pdf:"Exporter la Revue (PDF)",rv_exporting:"Export…",rv_projection_rows:"lignes de projection",
  // ── Login ──
  login_title:"Hard to Value",login_subtitle:"Plateforme de Valorisation Financière & Fiscale",login_enter_pin:"Entrez votre code PIN à 6 chiffres",login_unlock:"Déverrouiller",login_session_expires:"Session expire à la fermeture du navigateur",login_incorrect:"Code PIN incorrect",login_attempts_remaining:"tentatives restantes",login_attempt_remaining:"tentative restante",login_too_many:"Trop de tentatives",login_locked:"Verrouillé — réessayez dans",
  // ── Reset & Nav ──
  reset_btn:"Nouvelle session",reset_confirm_msg:"Êtes-vous sûr ? Toutes les données de la session seront effacées.",reset_cancel:"Annuler",reset_clear:"Effacer tout",
  nav_jurisdiction:"Par juridiction",nav_logout:"Déconnexion",nav_models:"modèles",nav_jurisdictions:"juridictions",
  // ── US IRS 59-60 ──
  us_company:"Société",us_company_name:"Nom de la société",us_company_name_ph:"Acme Corp",us_shares:"Nombre de titres",us_shares_tip:"Nombre total de titres émis",us_net_debt:"Dette nette",us_net_debt_tip:"Total dette moins trésorerie — pont vers la valeur des capitaux propres",
  us_ni_section:"Résultat Net — Historique 3 ans",us_ni_formula:"Bénéfice normalisé = (N×3 + N−1×2 + N−2×1) / 6",us_ni_n2:"Résultat Net N−2",us_ni_n2_tip:"Année la plus ancienne — pondération 1",us_ni_n1:"Résultat Net N−1",us_ni_n1_tip:"Année précédente — pondération 2",us_ni_n:"Résultat Net N",us_ni_n_tip:"Année la plus récente — pondération 3 (maximale)",
  us_norm_section:"Ajustements de Normalisation",us_addback:"Réintégration rémunération dirigeant",us_addback_tip:"Rémunération dirigeant excédentaire au-delà du taux de marché — réintégrée pour normaliser",us_nonrecurring:"Éléments non récurrents",us_nonrecurring_tip:"Plus/moins-values exceptionnelles à exclure du résultat",
  us_capm_section:"Taux d'Actualisation — CAPM Build-Up",us_capm_formula:"Ke = Rf + β×PRM + Taille + Spécifique  |  Taux de cap. = Ke − g",us_rf:"Rf — Trésor US 20 ans",us_rf_tip:"Rendement du Trésor US 20 ans (taux spot)",us_erp:"PRM (Damodaran US)",us_erp_tip:"Prime de risque marché — estimation Damodaran US",us_beta:"Bêta",us_beta_tip:"Bêta relevé à partir de sociétés cotées comparables",us_size_prem:"Prime de taille",us_size_prem_tip:"Prime small-cap par décile (Kroll/Duff & Phelps)",us_spec_prem:"Prime spécifique",us_spec_prem_tip:"Risque spécifique : concentration, homme-clé, etc.",us_growth:"Taux de croissance soutenable",us_growth_tip:"Croissance long-terme du résultat — taux de cap. = Ke moins ce taux",
  us_ke:"Ke",us_cap_rate:"Taux de cap.",us_result_title:"IRS Rev. Ruling 59-60 §4 — Juste Valeur Marchande",us_earnings_cap:"Capitalisation du Bénéfice",us_norm_of:"Bénéfice normalisé de",us_cap_at:"capitalisé à",us_vps:"Valeur par titre :",
  us_equity_value:"Capitaux Propres",us_earnings_value:"Valeur par les Bénéfices",us_value_per_share:"Valeur par Titre",us_norm_earnings:"Bénéfice Normalisé",us_ke_buildup:"Ke (Build-Up)",
  us_weighted_title:"Bénéfice Pondéré — Rev. Ruling 59-60 §4",us_tbl_year:"Année",us_tbl_ni:"Résultat Net",us_tbl_weight:"Pondération",us_tbl_weighted:"Pondéré",us_tbl_wavg:"Moyenne Pondérée (÷6)",
  us_capm_title:"CAPM Build-Up — Taux d'Actualisation",us_note_title:"Rappel Rev. Ruling 59-60 §5 :",us_note_text:"L'IRS exige la prise en compte des prix de marché de titres similaires et des transactions antérieures entre parties indépendantes. Les multiples de marché doivent servir de recoupement — lancez le modèle Multiples pour cross-validation.",
  // ── DE IDW S1 ──
  de_company:"Unternehmen / Société",de_company_name:"Nom de la société",de_company_name_ph:"Musterfirma GmbH",de_shares:"Nombre de titres",de_net_debt:"Dette nette (Nettoverschuldung)",
  de_earnings_section:"Plan de Résultats (Ertragsplan)",de_earnings_formula:"Résultats distribuables (ausschüttungsfähige Erträge) — IDW S1 §29",de_base_earnings:"Résultat de base (Année 0)",de_base_earnings_tip:"Résultat distribuable normalisé courant avant projection",de_growth_initial:"Croissance initiale",de_growth_initial_tip:"Croissance attendue du résultat les premières années",de_growth_terminal:"Croissance terminale (g)",de_growth_terminal_tip:"Croissance soutenable long-terme — typiquement 0–1,5%",de_conv_year:"Année conv.",de_conv_year_tip:"Année à laquelle la croissance atteint le taux terminal",de_terminal_gordon:"Croissance terminale (Gordon)",de_terminal_gordon_tip:"Croissance perpétuelle pour la valeur terminale — IDW S1 typiquement 0–1%",de_forecast_years:"Années de prévision",de_forecast_years_tip:"Horizon de projection explicite — IDW S1 : typiquement 5–10 ans",
  de_capm_section:"IDW S1 — CAPM après impôt (Abgeltungssteuer)",de_capm_formula:"ke = Basiszinssatz×(1−s) + β_lev×PRM  |  s = Abgeltungssteuer",de_basiszinssatz:"Basiszinssatz (IDW/Bundesbank)",de_basiszinssatz_tip:"Taux de base trimestriel IDW — méthode Svensson sur obligations d'État allemandes",de_mrp:"PRM après impôt (guide IDW)",de_mrp_tip:"Prime de risque marché après impôt personnel — fourchette IDW 5,0–7,5%",de_beta:"β non endetté",de_beta_tip:"Bêta sectoriel non endetté — données Damodaran Allemagne",de_de_ratio:"Ratio D/E",de_de_ratio_tip:"Ratio dette/capitaux propres en valeur de marché (formule de Hamada)",de_tax:"Taux IS",de_tax_tip:"Taux d'imposition des sociétés — utilisé pour le relevering du bêta",de_abgelt:"Abgeltungssteuer (impôt forfaitaire)",de_abgelt_tip:"Impôt allemand sur les plus-values — 25% + 5,5% Solidaritätszuschlag = 26,375%",
  de_beta_lev:"β endetté",de_ke_after:"Ke après impôt",de_result_title:"IDW S1 (2008) — Valeur Objectivée de l'Entreprise",de_ertragswert:"Ertragswertverfahren",de_terminal_value:"Valeur terminale",de_of_ertragswert:"de l'Ertragswert",
  de_equity_value:"Capitaux Propres",de_ertragswert_lbl:"Ertragswert",de_value_per_share:"Valeur par Titre",de_sum_pv:"Somme VA Résultats",de_terminal_lbl:"Valeur Terminale",de_terminal_pct:"% Terminale",
  de_proj_title:"Projections de Résultats (Ertragsplan)",de_tbl_yr:"An",de_tbl_earnings:"Résultat",de_tbl_growth:"Croissance",de_tbl_pv:"VA Résultat",
  de_note_title:"IDW S1 — Note Réglementaire",de_note_ke:"Formule Ke (IDW S1 post-2008) :",de_note_ke_formula:"Basiszinssatz×(1−s) + β_endetté × PRM_après-impôt",de_note_beta:"Bêta :",de_note_beta_text:"Relevé par Hamada selon la structure financière courante → β_lev =",de_note_abgelt:"Abgeltungssteuer :",de_note_abgelt_text:"(25% + 5,5% Solidaritätszuschlag) appliqué au composant Basiszinssatz conformément à IDW RS HFA 10.",de_note_ref:"Réf. : IDW S1 i.d.F. 2008 §§100–167 · IDW RS HFA 10 · Taux de base Svensson Bundesbank",
  // ── UK HMRC ──
  uk_company:"Société",uk_company_name:"Nom de la société",uk_company_name_ph:"Acme Ltd",uk_total_shares:"Total titres (émis)",uk_total_shares_tip:"Nombre total de titres émis — utilisé pour le calcul par titre",uk_stake:"Quote-part valorisée (%)",uk_stake_tip:"Pourcentage de la participation valorisée — détermine le traitement minoritaire/majoritaire",
  uk_earnings_section:"Base de Résultat",uk_earnings_formula:"OMV brute = Bénéfice maintenable × P/E sectoriel",uk_maintainable:"Bénéfice maintenable (après impôt)",uk_maintainable_tip:"Résultat après impôt normalisé et récurrent — exclure les éléments exceptionnels",uk_pe:"P/E sectoriel (comps cotées)",uk_pe_tip:"Ratio cours/bénéfice de sociétés cotées comparables — HMRC attend un multiple sectoriel approprié",
  uk_gross_omv:"OMV brute",uk_adj_omv:"OMV ajustée",
  uk_adj_section:"Ajustements HMRC",uk_control_note_pre:"La prime de contrôle ne s'applique que si quote-part ≥ 50% (quote-part actuelle :",uk_majority:"majoritaire",uk_minority:"minoritaire",uk_control:"Prime de contrôle",uk_control_tip:"Prime pour participation de contrôle — ne s'applique que si quote-part ≥ 50%",uk_illiquidity:"Décote d'illiquidité",uk_illiquidity_tip:"HMRC SAV : typiquement 15–30% pour les titres non cotés minoritaires (SVM107070)",uk_size:"Décote de taille",uk_size_tip:"Décote supplémentaire pour risque lié à la taille — typiquement 5–15%",
  uk_result_title:"HMRC SAV — Open Market Value (SVM107070)",uk_pe_basis:"P/E Earnings Basis",uk_adj_to:"ajustée à",uk_eff_pe:"P/E effectif :",uk_vps:"Valeur par titre :",
  uk_adj_omv_lbl:"OMV Ajustée",uk_vps_lbl:"Valeur / Titre",uk_gross_omv_lbl:"OMV Brute",uk_eff_pe_lbl:"P/E Effectif",
  uk_waterfall_title:"Cascade d'Ajustements — HMRC SAV",uk_wf_me_pe:"Bénéfice maintenable × P/E",uk_wf_gross:"OMV brute",uk_wf_control:"Prime de contrôle",uk_wf_na_minority:"N/A (minoritaire)",uk_wf_majority_only:"§ Majoritaire uniquement",uk_wf_illiquidity:"Décote d'illiquidité",uk_wf_illiquidity_ref:"SVM107070",uk_wf_size:"Décote de taille",uk_wf_size_ref:"Petite entreprise",uk_wf_omv:"Open Market Value (TCGA 1992 §272)",
  uk_note_title:"HMRC SAV — Note Réglementaire",uk_note_standard:"Standard :",uk_note_standard_text:"Open Market Value selon TCGA 1992 §272 — le prix obtenu sur un marché ouvert entre un vendeur et un acheteur consentants.",uk_note_goodwill:"Goodwill :",uk_note_goodwill_text:"Si la société détient un goodwill non comptabilisé, HMRC SAV applique le protocole CG68300 — effectuer une analyse distincte.",uk_note_crosscheck:"Recoupement :",uk_note_crosscheck_text:"HMRC attend un recoupement par rendement du dividende ou approche patrimoniale pour les participations minoritaires. Considérer le modèle DCF comme preuve complémentaire.",uk_note_ref:"Réf. : HMRC SAV Manual SVM107070 · TCGA 1992 §272 · CG68300 (Goodwill) · ITEPA 2003 (valeurs mobilières liées à l'emploi)",
  // ── CH extras ──
  ch_canton_label:"Canton",ch_vr_preview:"VR (aperçu)",ch_vi_preview:"VI (aperçu)",ch_calc_title:"Formule Praticien CSI — Détail du Calcul",ch_gross_value:"Valeur brute =",ch_after_discount:"Après décote minoritaire",ch_note_ref:"Réf. : Circulaire CSI n°28 §3.1 (2023) · AFC Circulaire n°37 · Applicable dans tous les cantons suisses pour l'impôt sur la fortune et les droits de mutation.",
  // ── SynthesisReport ──
  sr_no_model:"Aucun modèle calculé",sr_back_dash:"← Retour au dashboard",sr_back:"← Retour",
  sr_subtitle_report:"Rapport de synthèse",sr_method:"méthode",sr_methods:"méthodes",sr_calculated:"calculée",sr_calculated_pl:"calculées",
  sr_kpi_median:"Médiane Equity",sr_kpi_range:"Fourchette Q1–Q3",sr_kpi_nfd:"DFN retenue",sr_kpi_vps:"Valeur/action (médiane)",sr_kpi_percentile:"25e–75e percentile",sr_kpi_ev_bridge:"EV → Equity bridge",sr_kpi_shares:"titres",sr_kpi_shares_na:"Titres non renseignés",
  sr_tab_football:"🏈 Football Field",sr_tab_table:"📋 Tableau détaillé",sr_tab_bridge:"🔗 Bridge EV → Equity",sr_tab_overview:"📊 Vue d'ensemble",
  sr_cat_financial:"Méthodes Financières",sr_cat_intangible:"Actifs Incorporels",sr_cat_asset:"Méthodes Patrimoniales",sr_cat_jur_fr:"Juridiction 🇫🇷",sr_cat_jur_us:"Juridiction 🇺🇸",sr_cat_jur_de:"Juridiction 🇩🇪",sr_cat_jur_ch:"Juridiction 🇨🇭",sr_cat_jur_uk:"Juridiction 🇬🇧",
  sr_nm_dcf:"DCF",sr_nm_exc:"Excess Return",sr_nm_mul:"Multiples",sr_nm_rfr:"Relief/Royalty",sr_nm_mpe:"MPEEM",sr_nm_ast:"Asset-Based",sr_nm_vp:"V. Productivité",sr_nm_vm:"V. Mathématique",sr_nm_vr:"V. Rendement",sr_nm_mba:"Val. MBA",sr_nm_ebe_re:"Val. EBE/RE",sr_nm_comb:"Comb. DGI",sr_nm_us:"IRS Rev. Ruling 59-60",sr_nm_de:"IDW S1",sr_nm_ch:"CSI n°28",sr_nm_uk:"HMRC SAV",
  sr_ff_legend_zone:"Zone convergence Q1–Q3",sr_ff_legend_range:"Basse → Centrale → Haute",sr_ff_legend_mid:"Point central (médiane modèle)",sr_ff_eq_header:"← Equity Value (fourchette basse | centrale | haute) →",sr_ff_section_intl:"Méthodes Financières",sr_ff_section_jur:"Méthodes par Juridiction",sr_ff_median:"Médiane",
  sr_tbl_method:"Méthode",sr_tbl_category:"Catégorie",sr_tbl_ev_low:"EV basse",sr_tbl_ev_mid:"EV centrale",sr_tbl_ev_high:"EV haute",sr_tbl_eq_low:"Equity basse",sr_tbl_eq_mid:"Equity centrale",sr_tbl_eq_high:"Equity haute",sr_tbl_range:"Fourchette",sr_tbl_vps:"V/action",sr_tbl_weight:"Poids suggéré",sr_tbl_median:"▶ MÉDIANE",
  sr_br_nfd:"Dette fin. nette (DFN)",sr_br_shares:"Nombre de titres",sr_br_eq_median:"Equity médiane",sr_br_vps_median:"V/action médiane",sr_br_ev_median:"EV médiane",sr_br_eq_median_short:"Equity médiane",sr_nfd_short:"DFN",sr_vpa_short:"V/ACTION",
  sr_ov_eq_by_method:"Equity value par méthode",sr_ov_convergence:"Analyse de convergence",sr_ov_conv_level:"Niveau de convergence",sr_ov_strong:"Forte",sr_ov_moderate:"Modérée",sr_ov_weak:"Faible",sr_ov_dispersion:"Dispersion",sr_ov_of_median:"de la médiane",sr_ov_cat_title:"Méthodes par catégorie",sr_ov_cat_median:"médiane cat.",
  sr_pdf_title:"Rapport PDF",sr_pdf_content:"Contenu du rapport",sr_pdf_pages:"pages",sr_pdf_cover:"Page de garde — société, date, cabinet",sr_pdf_exec:"Résumé exécutif — fourchette, KPIs, analyse narrative",sr_pdf_ff:"Football field chart — toutes méthodes",sr_pdf_table:"Tableau synthèse — EV/Equity low/mid/high",sr_pdf_detail:"Détail :",sr_pdf_annex:"Annexe méthodologique",sr_pdf_firm:"Nom du cabinet",sr_pdf_analyst:"Nom de l'analyste (optionnel)",sr_pdf_analyst_ph:"ex. Jean Dupont, CFA",sr_pdf_logo:"Logo cabinet (PNG/JPG, optionnel)",sr_pdf_logo_loaded:"Logo chargé ✓",sr_pdf_logo_choose:"Choisir une image...",sr_pdf_logo_remove:"Retirer",sr_pdf_logo_preview:"Aperçu — sera placé en haut de la page de garde",sr_pdf_cancel:"Annuler",sr_pdf_generate:"Générer le rapport PDF",
  sr_exp_valuation_report:"RAPPORT DE VALORISATION",sr_exp_synthesis_multimethods:"Rapport de synthese et analyse multi-methodes",sr_exp_equity_median_label:"VALEUR DES CAPITAUX PROPRES (MEDIANE)",sr_exp_ie_per_share:"soit {0} EUR par action",sr_exp_range_retained:"Fourchette retenue (Q1-Q3) : {0} - {1}",sr_exp_methods_valued:"valorisees",sr_exp_report_date:"date du rapport",sr_exp_cabinet:"cabinet",sr_exp_prepared_by:"Prepare par : {0}",sr_exp_methodologies_used:"Methodologies utilisees :",sr_exp_confidential:"CONFIDENTIEL",sr_exp_footer_conf:"Rapport confidentiel - Prepare par Hard to Value",sr_exp_footer_disclaimer:"Les valorisations presentees sont etablies sur la base des donnees transmises et ne constituent pas un conseil financier.",
  sr_exp_exec_summary:"Resume executif",sr_exp_exec_title:"RESUME EXECUTIF",sr_exp_valuation_of:"Valorisation de {0} | {1}",sr_exp_kpi_med_eq:"Mediane Equity",sr_exp_kpi_range_q:"Fourchette Q1-Q3",sr_exp_kpi_nfd_label:"Dette nette (DFN)",sr_exp_kpi_vpa_med:"V/action mediane",sr_exp_kpi_nb_methods:"Nb methodes",sr_exp_kpi_dispersion:"Dispersion",sr_exp_not_provided:"Non renseignee",sr_exp_not_calc:"Non calc.",sr_exp_low:"Faible",sr_exp_moderate:"Moderee",sr_exp_high:"Elevee",sr_exp_narrative_title:"ANALYSE NARRATIVE",sr_exp_recommended_range:"FOURCHETTE RECOMMANDEE",sr_exp_median_label:"Mediane : {0}",sr_exp_vpa_label:"V/action : {0}",
  sr_exp_narr_base:"Sur la base de {0} methode{1} de valorisation appliquees a {2}, la valeur des capitaux propres ressort a une mediane de {3}. {4}",sr_exp_narr_low_disp:"Les differentes methodes convergent vers une fourchette etroite, ce qui renforce la robustesse de la valorisation et indique une coherence entre les approches patrimoniales, rendement et flux.",sr_exp_narr_mod_disp:"Une dispersion moderee est observee entre les methodes, refletant des differences d'hypotheses sur la croissance, le risque et la structure du capital. Cette amplitude est habituelle pour une PME non cotee.",sr_exp_narr_high_disp:"La forte dispersion entre methodes met en evidence une sensibilite significative aux hypotheses retenues. Il est recommande de privilegier les methodes les plus adaptees au secteur et d'ecarter les outliers identifies.",
  sr_exp_narr_range:"La fourchette retenue (Q1-Q3) s'etablit de {0} EUR a {1} EUR, representant la zone de consensus interquartile de l'ensemble des approches implementees.",sr_exp_narr_aligned:" Les methodes financieres internationales (mediane {0} EUR) et les methodes fiscales DGI (mediane {1} EUR) sont bien alignees, ce qui valide la coherence de l'evaluation.",sr_exp_narr_gap:" On note un ecart de {0}% entre la mediane des methodes financieres ({1} EUR) et celle des methodes DGI ({2} EUR), refletant des perspectives distinctes sur la valeur patrimoniale et la rentabilite normative.",
  sr_exp_narr_nfd_pos:"La dette financiere nette de {0} EUR a ete deduite de la valeur d'entreprise (EV) pour obtenir la valeur des capitaux propres (Equity Value).",sr_exp_narr_nfd_zero:"Aucune dette financiere nette n'a ete renseignee ; la valeur d'entreprise est egale a la valeur des capitaux propres.",sr_exp_narr_outlier_high:"Attention : {0} affiche{1} une valeur superieure de plus de 50% a la mediane et doit{2} etre interprete{3} avec precaution.",sr_exp_narr_outlier_low:" {0} ressort{1} significativement en-dessous de la mediane.",sr_exp_narr_closest:" La methode {0} ({1} EUR) est la plus proche de la mediane toutes methodes confondues.",
  sr_exp_ff_subtitle:"Football Field -- Synthese graphique",sr_exp_ff_methods_intl:"METHODES FINANCIERES",sr_exp_ff_methods_jur:"METHODES PAR JURIDICTION",sr_exp_tbl_subtitle:"Tableau de synthese",sr_exp_tbl_title:"SYNTHESE DES VALORISATIONS",sr_exp_tbl_info:"Toutes valeurs en EUR | DFN : {0} | Titres : {1}",sr_exp_tbl_method:"Methode",sr_exp_tbl_category:"Categorie",sr_exp_tbl_ev_low:"EV Basse",sr_exp_tbl_ev_mid:"EV Centrale",sr_exp_tbl_ev_high:"EV Haute",sr_exp_tbl_eq_low:"Eq. Basse",sr_exp_tbl_eq_mid:"Eq. Centrale",sr_exp_tbl_eq_high:"Eq. Haute",sr_exp_tbl_range:"Fourchette",sr_exp_tbl_vpa:"V/Action",sr_exp_tbl_median_q:"MEDIANE / Q1-Q3",
  sr_exp_bridge_eq:"Equity Value = Enterprise Value - Dette Financiere Nette ({0})",sr_exp_bridge_formula:"Mediane EV : {0}   -   DFN : {1}   =   Mediane Equity : {2}",sr_exp_bridge_vpa:"Valeur / action (mediane) : {0} EUR  (sur {1} titres)",
  sr_exp_detail_method:"Detail methode : {0}",sr_exp_eq_low:"Equity Basse",sr_exp_eq_mid:"Equity Centrale",sr_exp_eq_high:"Equity Haute",sr_exp_range_label:"Fourchette",sr_exp_vpa_central:"Valeur par action (centrale) : {0}",sr_exp_relative_pos:"POSITION RELATIVE (vs toutes methodes)",sr_exp_median_all:"Mediane toutes methodes",sr_exp_calc_params:"PARAMETRES DE CALCUL",sr_exp_methodology:"METHODOLOGIE",
  sr_exp_annex_subtitle:"Annexe methodologique",sr_exp_annex_title:"ANNEXE METHODOLOGIQUE",sr_exp_annex_desc:"Description synthetique des methodes de valorisation implementees",sr_exp_annex_contd:"Annexe methodologique (suite)",sr_exp_warning:"AVERTISSEMENT",sr_exp_disclaimer:"Ce rapport a ete etabli sur la base des informations et hypotheses fournies par le client. Les valorisations presentees refletent l'opinion des evaluateurs a la date du rapport et ne constituent pas une garantie de valeur ou un conseil en investissement. Toute valorisation est par nature subjective et depend d'hypotheses qui peuvent evoluer. Ce document est strictement confidentiel.",
  sr_png_methods_header:"MÉTHODES FINANCIÈRES",sr_png_jur_header:"MÉTHODES PAR JURIDICTION",sr_png_median:"Médiane :",sr_png_per_share:"/action",
  sr_xls_title:"RAPPORT DE SYNTHÈSE — VALORISATION",sr_xls_company:"Société :",sr_xls_date:"Date :",sr_xls_methods:"Méthodes :",sr_xls_nfd:"DFN :",sr_xls_shares:"Titres :",sr_xls_median:"MÉDIANE",sr_xls_sheet1:"Synthèse",sr_xls_sheet2:"Bridge EV-Equity",sr_xls_sheet3:"Données graphique",sr_xls_br_title:"BRIDGE EV → EQUITY VALUE",sr_xls_br_ev:"EV Central (k€)",sr_xls_br_nfd:"DFN (k€)",sr_xls_br_eq:"Equity Value (k€)",
grp:{intl:"Méthodes Financières Internationales",dgi:"Méthodes de l'Administration Fiscale — Guide DGI nov. 2006",jur:"Calculateurs par Juridiction — Référentiels Fiscaux Officiels",diagStrat:"Diagnostic Stratégique",diagFin:"Diagnostic Financier",models:"Modèles de valorisation",diagSynth:"Synthèse",revue:"Revue de Valorisation"},m:{dcf:{n:"Discounted Cash Flow",d:"Valeur Intrinsèque — Flux de Trésorerie",x:"Projette les flux de trésorerie disponibles puis les actualise au WACC. Méthode de référence pour les sociétés à génération de cash prévisible, disposant d'un business plan sur 5 à 10 ans."},exc:{n:"Excess Return / Profit Économique",d:"Valeur Intrinsèque — Création de Valeur",x:"Valorise l'entreprise comme la somme des capitaux investis et de la valeur actuelle des profits économiques futurs (ROIC − WACC). Met en évidence si l'activité crée ou détruit de la valeur."},mul:{n:"Multiples de Marché",d:"Valeur Relative — Transactions Comparables",x:"Dérive la valeur en appliquant les multiples EV/EBITDA, EV/EBIT ou P/E d'un panel de sociétés comparables. Fournit une fourchette ancrée aux conditions de marché."},rfr:{n:"Relief from Royalty",d:"Actifs Incorporels — Marques & Brevets",x:"Valorise un actif incorporel comme la somme des redevances évitées grâce à sa détention. Approche normative pour les marques et brevets dans le cadre d'un PPA (IFRS 3)."},mpe:{n:"Multi-Period Excess Earnings (MPEEM)",d:"Actifs Incorporels — Relations Clients",x:"Isole les flux attribuables à un actif spécifique (clientèle, contrats) après rémunération des actifs contributeurs. Méthode principale sous IFRS 3 pour les actifs liés à la clientèle."},ast:{n:"Asset-Based / Actif Net Réévalué",d:"Bilan — Valeur Patrimoniale Ajustée",x:"Réévalue chaque poste du bilan à la valeur de marché et déduit les passifs. Adapté aux holdings, patrimoines immobiliers et scénarios de liquidation."},vp:{n:"Valeur de Productivité",d:"Capitalisation du Bénéfice Récurrent",x:"Capitalise le bénéfice net moyen pondéré (coefficients 1-2-3) à un taux composé du taux sans risque déflaté et d'une prime de risque (β × 5%). Méthode centrale du guide DGI pour les sociétés opérationnelles."},vm:{n:"Valeur Mathématique",d:"Actif Net Réévalué + Survaleur (Goodwill)",x:"Réévalue les capitaux propres aux valeurs vénales réelles, puis intègre optionnellement une survaleur calculée sur le superprofit actualisé sur 8 ans. Méthode de référence pour les entités à forte composante patrimoniale."},vr:{n:"Valeur de Rendement",d:"Capitalisation du Dividende Distribué",x:"Capitalise le dividende moyen pondéré au taux de rendement des actions cotées, avec une décote de 30% pour non-liquidité. Méthode complémentaire pour les participations minoritaires à distribution régulière."},mba:{n:"Valeur par la Marge Brute d'Autofinancement",d:"Multiple de la Capacité d'Autofinancement",x:"Valorise l'entreprise comme un multiple de la MBA (résultat net + dotations aux amortissements). Méthode privilégiée pour les sociétés industrielles à forts amortissements."},ebe_re:{n:"Valeur par l'EBE ou le Résultat d'Exploitation",d:"Multiple EBITDA / EBIT sectoriel",x:"Applique un multiple sectoriel à l'Excédent Brut d'Exploitation (EBE) ou au Résultat d'Exploitation (RE), puis déduit l'endettement net. Référence de recoupement pour les transactions mid-market."},comb:{n:"Combinaison DGI",d:"Pondération Réglementaire — Guide Fiscal nov. 2006",x:"Combine la Valeur Mathématique et la Valeur de Rentabilité selon les pondérations prescrites par l'administration fiscale française pour les dossiers de donation et succession."},us:{n:"🇺🇸 Earnings Capitalization",d:"IRS Rev. Ruling 59-60 — Fair Market Value",x:"Capitalise le bénéfice net moyen pondéré (3 ans) à un taux CAPM build-up. Méthode principale pour les sociétés non cotées selon l'IRS Rev. Ruling 59-60 §4."},de:{n:"🇩🇪 Ertragswertverfahren",d:"IDW S1 (2008) — Valeur Objectivée",x:"Actualise les bénéfices distribuables au taux CAPM après imposition personnelle (Basiszinssatz + β×MRP). Méthode dominante pour l'évaluation en droit allemand (IDW S1 §§100–167)."},ch:{n:"🇨🇭 Formule Praticien CSI",d:"Circulaire CSI n°28 — Valeur Fiscale",x:"(2×Valeur de Rendement + Valeur Intrinsèque) ÷ 3. Formule canonique suisse applicable dans toutes les administrations cantonales selon la Circulaire CSI n°28."},uk:{n:"🇬🇧 P/E Earnings Basis",d:"HMRC SAV — Open Market Value",x:"Bénéfice maintenable × P/E sectoriel, ajusté pour illiquidité, prime de contrôle et taille. Approche standard HMRC pour les titres non cotés (TCGA 1992 §272 — SVM107070)."},fr:{n:"🇫🇷 Méthodes DGI (France)",d:"Guide DGI nov. 2006 — Administration Fiscale",x:"6 méthodes officielles de valorisation fiscale française (VP, VM, VR, MBA, EBE/RE, Combinaison). Prescrites par l'administration fiscale pour les donations, successions et prix de transfert."}}}}

// ==========================================================================
// TOOLTIPS DATABASE
// ==========================================================================
const TIPS={
  company_name:"Name of the company being valued",
  base_revenue:"Current annual revenue (last 12 months)",
  base_ebit:"Earnings Before Interest & Taxes",
  base_operating_margin:"EBIT / Revenue ratio (e.g. 12%)",
  revenue_growth_initial:"Expected revenue growth in Year 1",
  revenue_growth_terminal:"Long-term sustainable growth rate",
  growth_convergence_year:"Year when growth reaches terminal rate",
  target_operating_margin:"Long-term target operating margin",
  margin_convergence_year:"Year when margin reaches target",
  tax_rate:"Effective corporate tax rate",
  sales_to_capital_ratio:"Revenue / Invested capital efficiency",
  cost_of_capital_initial:"Current WACC (Weighted Average Cost of Capital)",
  cost_of_capital_terminal:"Long-term stabilized WACC",
  wacc_convergence_year:"Year when WACC reaches terminal rate",
  forecast_years:"Number of explicit forecast years (typically 5-15)",
  terminal_growth_rate:"Perpetual growth rate (should be ≤ GDP growth)",
  net_debt:"Total debt minus cash & equivalents",
  minority_interests:"Value of non-controlling interests",
  shares_outstanding:"Total number of shares issued",
  base_invested_capital:"Total capital invested in the business",
  roic_initial:"Current Return on Invested Capital",
  roic_terminal:"Long-term sustainable ROIC",
  roic_convergence_year:"Year when ROIC reaches terminal level",
  cost_of_capital:"Current WACC",
  cost_of_capital_term:"Long-term WACC",
  reinvestment_rate_initial:"% of NOPAT reinvested currently",
  reinvestment_rate_terminal:"Long-term reinvestment rate",
  reinvestment_convergence_year:"Year when reinvestment stabilizes",
  royalty_rate:"Arm's length royalty rate for the asset",
  discount_rate:"Risk-adjusted discount rate for the asset",
  obsolescence_factor:"Annual technological/economic obsolescence",
  tax_amortization_years:"Tax amortization period",
  operating_margin:"Operating profit / Revenue",
  annual_attrition_rate:"Annual customer/contract loss rate",
  illiquidity_discount:"Discount for lack of marketability",
  control_premium:"Premium for controlling interest",
  size_discount:"Discount for small company size",
  contingent_liabilities:"Potential liabilities not yet realized",
  deferred_tax_rate:"Rate applied to unrealized gains",
};
// ==========================================================================
// SECTOR PRESETS
// ==========================================================================
const SECTORS = {
  tech: { label: "🖥️ Tech/SaaS", base_revenue: 5e6, base_ebit: 5e5, base_operating_margin: .10, revenue_growth_initial: .25, revenue_growth_terminal: .04, target_operating_margin: .25, tax_rate: .25, sales_to_capital_ratio: 2.5, cost_of_capital_initial: .11, cost_of_capital_terminal: .09, terminal_growth_rate: .025, base_invested_capital: 2e6, roic_initial: .15, roic_terminal: .20, ebitda: 75e4, ebit: 5e5, net_income: 35e4, book_value_equity: 2e6, royalty_rate: .06, discount_rate: .13, operating_margin: .22 },
  retail: { label: "🛒 Retail", base_revenue: 10e6, base_ebit: 5e5, base_operating_margin: .05, revenue_growth_initial: .06, revenue_growth_terminal: .02, target_operating_margin: .07, tax_rate: .25, sales_to_capital_ratio: 3.5, cost_of_capital_initial: .09, cost_of_capital_terminal: .08, terminal_growth_rate: .02, base_invested_capital: 2.86e6, roic_initial: .10, roic_terminal: .12, ebitda: 8e5, ebit: 5e5, net_income: 3e5, book_value_equity: 3e6, royalty_rate: .03, discount_rate: .11, operating_margin: .07 },
  industry: { label: "🏭 Industrial", base_revenue: 15e6, base_ebit: 1.5e6, base_operating_margin: .10, revenue_growth_initial: .05, revenue_growth_terminal: .02, target_operating_margin: .12, tax_rate: .25, sales_to_capital_ratio: 1.2, cost_of_capital_initial: .09, cost_of_capital_terminal: .085, terminal_growth_rate: .02, base_invested_capital: 12.5e6, roic_initial: .09, roic_terminal: .11, ebitda: 2.5e6, ebit: 1.5e6, net_income: 1e6, book_value_equity: 8e6, royalty_rate: .04, discount_rate: .11, operating_margin: .12 },
  health: { label: "🏥 Healthcare", base_revenue: 8e6, base_ebit: 1.2e6, base_operating_margin: .15, revenue_growth_initial: .10, revenue_growth_terminal: .03, target_operating_margin: .18, tax_rate: .25, sales_to_capital_ratio: 1.8, cost_of_capital_initial: .10, cost_of_capital_terminal: .085, terminal_growth_rate: .025, base_invested_capital: 4.4e6, roic_initial: .14, roic_terminal: .16, ebitda: 1.6e6, ebit: 1.2e6, net_income: 8e5, book_value_equity: 5e6, royalty_rate: .05, discount_rate: .12, operating_margin: .18 },
  finance: { label: "🏦 Financial Services", base_revenue: 12e6, base_ebit: 3e6, base_operating_margin: .25, revenue_growth_initial: .08, revenue_growth_terminal: .03, target_operating_margin: .28, tax_rate: .25, sales_to_capital_ratio: 0.8, cost_of_capital_initial: .10, cost_of_capital_terminal: .09, terminal_growth_rate: .025, base_invested_capital: 15e6, roic_initial: .12, roic_terminal: .14, ebitda: 4e6, ebit: 3e6, net_income: 2e6, book_value_equity: 10e6, royalty_rate: .02, discount_rate: .11, operating_margin: .28 },
  energy: { label: "⚡ Energy", base_revenue: 20e6, base_ebit: 3e6, base_operating_margin: .15, revenue_growth_initial: .04, revenue_growth_terminal: .02, target_operating_margin: .16, tax_rate: .30, sales_to_capital_ratio: 0.6, cost_of_capital_initial: .10, cost_of_capital_terminal: .09, terminal_growth_rate: .02, base_invested_capital: 33e6, roic_initial: .07, roic_terminal: .09, ebitda: 5e6, ebit: 3e6, net_income: 1.8e6, book_value_equity: 15e6, royalty_rate: .02, discount_rate: .10, operating_margin: .16 },
};

// Sector picker component
const SectorPicker = ({onSelect, current}) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{position:"relative",marginBottom:16}}>
      <button onClick={()=>setOpen(!open)} style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:9,padding:"10px 14px",color:"#94a3b8",fontSize:13,cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.2s"}}
        onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(59,130,246,0.4)"}
        onMouseLeave={e=>{if(!open)e.currentTarget.style.borderColor="rgba(255,255,255,0.1)";}}>
        <span>{current ? SECTORS[current]?.label : "Load sector preset..."}</span>
        <span style={{fontSize:10}}>{open?"▲":"▼"}</span>
      </button>
      {open&&<div style={{position:"absolute",top:"100%",left:0,right:0,marginTop:4,background:"#1e293b",border:"1px solid rgba(255,255,255,0.12)",borderRadius:9,overflow:"hidden",zIndex:50,boxShadow:"0 12px 32px rgba(0,0,0,0.5)"}}>
        {Object.entries(SECTORS).map(([k,s])=>(
          <div key={k} onClick={()=>{onSelect(k);setOpen(false);}} style={{padding:"10px 14px",cursor:"pointer",fontSize:13,color:"#e2e8f0",borderBottom:"1px solid rgba(201,168,76,0.12)",transition:"background 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(59,130,246,0.1)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            {s.label}
          </div>
        ))}
        {current&&<div onClick={()=>{onSelect(null);setOpen(false);}} style={{padding:"10px 14px",cursor:"pointer",fontSize:12,color:"#64748b",fontStyle:"italic"}}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          ✕ Clear preset
        </div>}
      </div>}
    </div>
  );
};

// ==========================================================================
// WACC BUILDER — Interactive build-up
// ==========================================================================
// ==========================================================================
// WACC BUILDER — Étape 1 : Transparence totale Beta unlevered/levered + waterfall
// ==========================================================================
const WACCBuilder = ({onWaccChange, onWaccTerminalChange, initialWacc, L}) => {
  const [open, setOpen]   = useState(false);
  const [tab, setTab]     = useState("ke"); // "ke" | "kd" | "struct" | "result"
  const [w, setW] = useState({
    // Ke components
    risk_free_rate:       0.035,
    equity_risk_premium:  0.055,
    beta_unlevered:       1.0,
    debt_to_equity:       0.30,   // D/E ratio (market values)
    tax_rate:             0.25,
    size_premium:         0.02,
    specific_premium:     0.01,
    // Kd components
    cost_of_debt_pretax:  0.045,
    credit_spread:        0.010,  // informational
    // Structure (current)
    equity_weight:        0.77,
    // Terminal structure (target — for WACC terminal)
    target_debt_to_equity: 0.20,  // D/E cible à maturité (moins de dette)
    target_equity_weight:  0.83,  // E/V cible
    // Sources (text)
    src_rf:   "OAT 10 ans — Banque de France",
    src_erp:  "Damodaran (France) — mise à jour annuelle",
    src_beta: "Damodaran — Industry betas",
    src_size: "Kroll / Duff & Phelps — décile taille",
  });
  const uw = k => v => setW(p => ({...p, [k]: v}));

  // ── Calculs intermédiaires ────────────────────────────────────────────────
  // ── WACC Initial (structure actuelle) ────────────────────────────────────
  const beta_levered  = w.beta_unlevered * (1 + (1 - w.tax_rate) * w.debt_to_equity);
  const ke            = w.risk_free_rate + beta_levered * w.equity_risk_premium + w.size_premium + w.specific_premium;
  const kd_net        = w.cost_of_debt_pretax * (1 - w.tax_rate);
  const debt_weight   = Math.max(0, Math.min(1, 1 - w.equity_weight));
  const wacc          = w.equity_weight * ke + debt_weight * kd_net;

  // ── WACC Terminal (structure cible à maturité) ────────────────────────────
  // Beta re-leveré sur la structure cible — société mature, moins endettée
  const beta_levered_terminal = w.beta_unlevered * (1 + (1 - w.tax_rate) * w.target_debt_to_equity);
  // À maturité : primes taille/spécifique réduites de moitié (société stabilisée)
  const ke_terminal    = w.risk_free_rate + beta_levered_terminal * w.equity_risk_premium
                         + w.size_premium * 0.5 + w.specific_premium * 0.5;
  const debt_weight_t  = Math.max(0, Math.min(1, 1 - w.target_equity_weight));
  const wacc_terminal  = w.target_equity_weight * ke_terminal + debt_weight_t * kd_net;

  const apply = () => {
    if (onWaccChange)         onWaccChange(wacc);
    if (onWaccTerminalChange) onWaccTerminalChange(wacc_terminal);
  };

  // ── Reusable row ─────────────────────────────────────────────────────────
  const CalcRow = ({label, formula, value, color, highlight, source}) => (
    <div style={{
      padding:"8px 10px",marginBottom:3,borderRadius:7,
      background: highlight ? "rgba(59,130,246,0.08)" : "transparent",
      border: highlight ? "1px solid rgba(59,130,246,0.15)" : "1px solid transparent",
    }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div style={{flex:1}}>
          <span style={{fontSize:11,color: highlight ? "#e2e8f0" : "#94a3b8",fontWeight: highlight ? 700 : 400}}>{label}</span>
          {formula && <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",marginTop:2}}>{formula}</div>}
          {source  && <div style={{fontSize:10,color:"#334155",fontStyle:"italic",marginTop:1}}>↗ {source}</div>}
        </div>
        <span style={{fontSize: highlight ? 15 : 13, fontWeight:700, color: color||"#64748b", fontFamily:"monospace", marginLeft:12, whiteSpace:"nowrap"}}>
          {fP(value)}
        </span>
      </div>
    </div>
  );

  // ── Waterfall chart (SVG-based, pixel-perfect) ───────────────────────────
  const WaterfallWACC = () => {
    const bars = [
      {lb:"Rf",        v: w.risk_free_rate,                       c:"#475569", group:"ke"},
      {lb:"β×ERP",     v: beta_levered * w.equity_risk_premium,   c:"#10b981", group:"ke"},
      {lb:"Size",      v: w.size_premium,                         c:"#06b6d4", group:"ke"},
      {lb:"Spec.",     v: w.specific_premium,                     c:"#8b5cf6", group:"ke"},
      {lb:"──",        v: 0,                                       c:"transparent", group:"sep"},
      {lb:"Ke×E/V",    v: ke * w.equity_weight,                   c:"#10b981", group:"wacc"},
      {lb:"Kd×D/V",    v: kd_net * debt_weight,                   c:"#f59e0b", group:"wacc"},
    ].filter(b => b.group !== "sep" || true);

    const maxV = Math.max(...bars.filter(b=>b.v>0).map(b=>b.v), wacc, ke) * 1.15;
    const H = 72;
    const barW = 32;
    const gap  = 8;
    const totalW = bars.length * (barW + gap);

    return (
      <div style={{overflowX:"auto",paddingBottom:4}}>
        <svg width={totalW + 60} height={H + 44} style={{display:"block",margin:"0 auto"}}>
          {/* Grid lines */}
          {[0.02,0.04,0.06,0.08,0.10,0.12].map(v => v <= maxV && (
            <g key={v}>
              <line x1={30} x2={totalW+50} y1={H - (v/maxV)*H} y2={H - (v/maxV)*H}
                stroke="rgba(255,255,255,0.04)" strokeWidth={1}/>
              <text x={26} y={H - (v/maxV)*H + 3} fontSize={7} fill="#334155" textAnchor="end">{(v*100).toFixed(0)}%</text>
            </g>
          ))}

          {bars.map((b,i) => {
            if (b.group === "sep") {
              return <line key={i} x1={30 + i*(barW+gap) + barW/2} x2={30 + i*(barW+gap) + barW/2}
                y1={4} y2={H+2} stroke="rgba(255,255,255,0.06)" strokeWidth={1} strokeDasharray="3 3"/>;
            }
            const bH = Math.max(b.v / maxV * H, 3);
            const x  = 30 + i * (barW + gap);
            const y  = H - bH;
            return (
              <g key={i}>
                <rect x={x} y={y} width={barW} height={bH} rx={3}
                  fill={b.c} opacity={b.group==="wacc"?0.9:0.65}/>
                <text x={x+barW/2} y={y-4} fontSize={8} fill={b.c} textAnchor="middle" fontWeight="700">
                  {(b.v*100).toFixed(1)}%
                </text>
                <text x={x+barW/2} y={H+12} fontSize={8} fill="#475569" textAnchor="middle">{b.lb}</text>
              </g>
            );
          })}

          {/* WACC final marker */}
          <line x1={30 + (bars.length-2)*(barW+gap)} x2={30+(bars.length)*(barW+gap)+10}
            y1={H - (wacc/maxV)*H} y2={H - (wacc/maxV)*H}
            stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.6}/>
          <text x={30+(bars.length)*(barW+gap)+12} y={H-(wacc/maxV)*H+4}
            fontSize={9} fill="#3b82f6" fontWeight="700">WACC</text>

          {/* Ke marker on ke bars */}
          <line x1={30} x2={30 + 4*(barW+gap) - gap}
            y1={H - (ke/maxV)*H} y2={H - (ke/maxV)*H}
            stroke="#10b981" strokeWidth={1} strokeDasharray="3 2" opacity={0.5}/>
          <text x={26} y={H-(ke/maxV)*H+3} fontSize={7} fill="#10b981" textAnchor="end">Ke</text>

          {/* Group labels */}
          <text x={30 + 2*(barW+gap)} y={H+26} fontSize={8} fill="#334155" textAnchor="middle">Composantes Ke</text>
          <text x={30 + 5.5*(barW+gap)} y={H+26} fontSize={8} fill="#334155" textAnchor="middle">Contrib. WACC</text>

          {/* Axis */}
          <line x1={30} x2={30} y1={0} y2={H+2} stroke="rgba(255,255,255,0.08)" strokeWidth={1}/>
          <line x1={30} x2={totalW+50} y1={H} y2={H} stroke="rgba(255,255,255,0.08)" strokeWidth={1}/>
        </svg>
      </div>
    );
  };

  // ── Closed state ─────────────────────────────────────────────────────────
  if (!open) return (
    <div onClick={()=>setOpen(true)}
      style={{background:"rgba(59,130,246,0.04)",border:"1px solid rgba(59,130,246,0.15)",
        borderRadius:9,padding:"10px 14px",cursor:"pointer",marginBottom:12,transition:"all 0.2s"}}
      onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(59,130,246,0.35)"}
      onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(59,130,246,0.15)"}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:"#3b82f6",fontWeight:700}}>WACC Build-up</span>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:10,color:"#475569"}}>Initial</span>
            <span style={{fontFamily:"monospace",fontSize:13,fontWeight:800,color:"#3b82f6"}}>{fP(wacc)}</span>
          </div>
          <span style={{color:"#334155",fontSize:10}}>→</span>
          <div style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:10,color:"#475569"}}>Terminal</span>
            <span style={{fontFamily:"monospace",fontSize:13,fontWeight:800,color:"#06b6d4"}}>{fP(wacc_terminal)}</span>
          </div>
          <span style={{fontSize:10,color:"#475569"}}>β={beta_levered.toFixed(2)}→{beta_levered_terminal.toFixed(2)}</span>
        </div>
        <span style={{fontSize:10,color:"#475569"}}>Développer ▼</span>
      </div>
    </div>
  );

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const tabs = [
    {id:"ke",       label:"Ke — Fonds propres",   color:"#10b981"},
    {id:"kd",       label:"Kd — Dette",            color:"#f59e0b"},
    {id:"struct",   label:"Structure",              color:"#8b5cf6"},
    {id:"terminal", label:"WACC Terminal",          color:"#06b6d4"},
    {id:"result",   label:"Résultat & Waterfall",   color:"#3b82f6"},
  ];

  return (
    <div style={{background:"rgba(59,130,246,0.03)",border:"1px solid rgba(59,130,246,0.2)",
      borderRadius:12,padding:16,marginBottom:16}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:12,fontWeight:700,color:"#3b82f6"}}>WACC Build-up</span>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <div style={{background:"rgba(59,130,246,0.12)",borderRadius:6,padding:"2px 10px",
              fontFamily:"monospace",fontSize:14,fontWeight:800,color:"#3b82f6"}}>
              {fP(wacc)}
            </div>
            <span style={{fontSize:10,color:"#334155"}}>→</span>
            <div style={{background:"rgba(6,182,212,0.1)",borderRadius:6,padding:"2px 10px",
              fontFamily:"monospace",fontSize:14,fontWeight:800,color:"#06b6d4"}}>
              {fP(wacc_terminal)}
            </div>
            <span style={{fontSize:10,color:"#475569"}}>terminal</span>
          </div>
        </div>
        <button onClick={()=>setOpen(false)}
          style={{background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:11}}>
          ▲ Fermer
        </button>
      </div>

      {/* Tab bar */}
      <div style={{display:"flex",gap:4,marginBottom:14,
        borderBottom:"1px solid rgba(255,255,255,0.06)",paddingBottom:0}}>
        {tabs.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{background:"none",border:"none",cursor:"pointer",padding:"6px 12px",
              fontSize:10,fontWeight:600,
              color: tab===t.id ? t.color : "#475569",
              borderBottom: tab===t.id ? `2px solid ${t.color}` : "2px solid transparent",
              marginBottom:"-1px",transition:"all 0.15s"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Ke ─────────────────────────────────────────────────────── */}
      {tab === "ke" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(255,255,255,0.02)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            Ke = Rf + β_levered × ERP + Prime taille + Prime spécifique
          </div>

          {/* Beta section — le cœur de l'étape 1 */}
          <div style={{background:"rgba(16,185,129,0.04)",border:"1px solid rgba(16,185,129,0.12)",
            borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#10b981",marginBottom:8,
              textTransform:"uppercase",letterSpacing:"0.05em"}}>
              β — Unlevered → Levered (Hamada)
            </div>
            <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",marginBottom:10,
              background:"rgba(0,0,0,0.15)",borderRadius:5,padding:"5px 8px"}}>
              β_levered = β_unlevered × [1 + (1 − IS) × D/E]
            </div>
            <div style={g3}>
              <I lb="β unlevered (sectoriel)" v={w.beta_unlevered} oc={uw("beta_unlevered")} s="0.05"
                tip="Unlevered beta of the economic asset — Damodaran by sector. Strips out financial leverage effect."/>
              <I lb="Ratio D/E (marché)" v={w.debt_to_equity} oc={uw("debt_to_equity")} s="0.05"
                tip="Financial debt / equity at market value. Use market values, not book values."/>
              <I lb="Taux IS" v={w.tax_rate} oc={uw("tax_rate")} pct
                tip="Marginal tax rate for the debt tax shield."/>
            </div>
            {/* Beta result highlight */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:10,
              background:"rgba(16,185,129,0.08)",borderRadius:7,padding:"8px 12px"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:"#64748b"}}>β unlevered</div>
                <div style={{fontFamily:"monospace",fontSize:14,fontWeight:800,color:"#94a3b8"}}>
                  {w.beta_unlevered.toFixed(2)}
                </div>
              </div>
              <div style={{fontSize:16,color:"#334155"}}>×</div>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:"#64748b"}}>[1+(1−IS)×D/E]</div>
                <div style={{fontFamily:"monospace",fontSize:14,fontWeight:800,color:"#94a3b8"}}>
                  {(1+(1-w.tax_rate)*w.debt_to_equity).toFixed(3)}
                </div>
              </div>
              <div style={{fontSize:16,color:"#334155"}}>=</div>
              <div style={{flex:1.2,background:"rgba(16,185,129,0.12)",borderRadius:6,padding:"4px 8px"}}>
                <div style={{fontSize:10,color:"#10b981"}}>β levered (equity β)</div>
                <div style={{fontFamily:"monospace",fontSize:16,fontWeight:800,color:"#10b981"}}>
                  {beta_levered.toFixed(3)}
                </div>
              </div>
            </div>
            <div style={{fontSize:10,color:"#334155",marginTop:6,fontStyle:"italic"}}>
              ↗ Source β unlevered : {w.src_beta}
            </div>
          </div>

          {/* Rf + ERP */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:8,textTransform:"uppercase"}}>
              Taux sans risque & Prime de risque marché
            </div>
            <div style={g2}>
              <div>
                <I lb="Rf — Taux sans risque" v={w.risk_free_rate} oc={uw("risk_free_rate")} pct
                  tip="10-year government bond yield. Use current spot rate, not historical average."/>
                <div style={{fontSize:10,color:"#334155",marginTop:3,fontStyle:"italic"}}>↗ {w.src_rf}</div>
              </div>
              <div>
                <I lb="ERP — Prime de risque marché" v={w.equity_risk_premium} oc={uw("equity_risk_premium")} pct
                  tip="Expected equity market excess return over Rf. Damodaran publishes annually by country."/>
                <div style={{fontSize:10,color:"#334155",marginTop:3,fontStyle:"italic"}}>↗ {w.src_erp}</div>
              </div>
            </div>
          </div>

          {/* Primes */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:8,textTransform:"uppercase"}}>
              Primes additionnelles (Build-up)
            </div>
            <div style={g2}>
              <div>
                <I lb="Prime de taille" v={w.size_premium} oc={uw("size_premium")} pct
                  tip="Historical small-cap premium (Kroll/Duff & Phelps — CRSP deciles)."/>
                <div style={{fontSize:10,color:"#334155",marginTop:3,fontStyle:"italic"}}>↗ {w.src_size}</div>
              </div>
              <I lb="Prime spécifique société" v={w.specific_premium} oc={uw("specific_premium")} pct
                tip="Company-specific risks: customer concentration, key-man dependency, liquidity, etc. Must be justified."/>
            </div>
          </div>

          <CalcRow label="Coût des fonds propres Ke"
            formula={`Rf ${fP(w.risk_free_rate)} + β ${beta_levered.toFixed(2)} × ERP ${fP(w.equity_risk_premium)} + ${fP(w.size_premium)} + ${fP(w.specific_premium)}`}
            value={ke} color="#10b981" highlight/>
        </div>
      )}

      {/* ── TAB: Kd ─────────────────────────────────────────────────────── */}
      {tab === "kd" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(255,255,255,0.02)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            Kd_net = Kd_brut × (1 − IS)  ← bouclier fiscal sur les intérêts
          </div>
          <div style={{background:"rgba(245,158,11,0.04)",border:"1px solid rgba(245,158,11,0.12)",
            borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#f59e0b",marginBottom:8,textTransform:"uppercase"}}>
              Taux d'emprunt
            </div>
            <div style={g2}>
              <I lb="Taux d'emprunt brut (Kd)" v={w.cost_of_debt_pretax} oc={uw("cost_of_debt_pretax")} pct
                tip="Weighted average rate on outstanding debt. Include all credit facilities."/>
              <I lb="Spread de crédit (informatif)" v={w.credit_spread} oc={uw("credit_spread")} pct
                tip="Spread over risk-free rate. Indicative only — gross Kd is the contractual value."/>
            </div>
            <div style={{fontSize:10,color:"#475569",marginTop:8,fontStyle:"italic"}}>
              💡 Kd = taux contractuel moyen, pas le coût marginal. Si dette variable, utiliser taux actuel swap + spread.
            </div>
          </div>
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:8,textTransform:"uppercase"}}>
              Bouclier fiscal
            </div>
            <I lb="Taux IS marginal" v={w.tax_rate} oc={uw("tax_rate")} pct
              tip="Tax rate applicable to deductible interest. France: 25% standard rate, reduced for small companies."/>
            <div style={{fontSize:10,color:"#475569",marginTop:8,fontStyle:"italic"}}>
              ⚠️ Utiliser le taux légal (25%) et non le taux effectif — le bouclier porte sur les intérêts futurs déductibles.
            </div>
          </div>
          <CalcRow label="Coût de la dette après IS (Kd net)"
            formula={`${fP(w.cost_of_debt_pretax)} × (1 − ${fP(w.tax_rate)})`}
            value={kd_net} color="#f59e0b" highlight/>
        </div>
      )}

      {/* ── TAB: Structure ──────────────────────────────────────────────── */}
      {tab === "struct" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(255,255,255,0.02)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            WACC = E/V × Ke + D/V × Kd × (1−IS)  ← pondérations en valeur de marché
          </div>
          <div style={{background:"rgba(139,92,246,0.04)",border:"1px solid rgba(139,92,246,0.12)",
            borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#8b5cf6",marginBottom:8,textTransform:"uppercase"}}>
              Structure financière cible (valeurs de marché)
            </div>
            <div style={{fontSize:10,color:"#475569",marginBottom:10,fontStyle:"italic"}}>
              ⚠️ Utiliser les valeurs de marché des CP et de la dette — pas les valeurs comptables.
              Pour les sociétés non cotées : approche itérative ou structure sectorielle comparable.
            </div>
            <I lb="Poids des fonds propres E/V" v={w.equity_weight}
              oc={v => setW(p => ({...p, equity_weight: Math.min(1, Math.max(0, v))}))} pct
              tip="Market value equity / (equity + net debt). Long-term target capital structure."/>
            {/* Visual split */}
            <div style={{marginTop:12,borderRadius:8,overflow:"hidden",height:20,display:"flex"}}>
              <div style={{width:`${w.equity_weight*100}%`,background:"rgba(139,92,246,0.5)",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:10,fontWeight:700,color:"#c4b5fd",transition:"width 0.3s"}}>
                E {(w.equity_weight*100).toFixed(0)}%
              </div>
              <div style={{flex:1,background:"rgba(245,158,11,0.4)",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:10,fontWeight:700,color:"#fcd34d"}}>
                D {(debt_weight*100).toFixed(0)}%
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginTop:8}}>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:10,color:"#64748b"}}>Ke contribution</div>
                <div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#8b5cf6"}}>
                  {fP(ke * w.equity_weight)}
                </div>
              </div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:10,color:"#64748b"}}>Kd contribution</div>
                <div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#f59e0b"}}>
                  {fP(kd_net * debt_weight)}
                </div>
              </div>
              <div style={{textAlign:"center",background:"rgba(59,130,246,0.1)",
                borderRadius:7,padding:"4px 12px"}}>
                <div style={{fontSize:10,color:"#64748b"}}>WACC</div>
                <div style={{fontFamily:"monospace",fontSize:15,fontWeight:800,color:"#3b82f6"}}>
                  {fP(wacc)}
                </div>
              </div>
            </div>
            <div style={{marginTop:10,fontSize:10,color:"#475569",fontStyle:"italic"}}>
              💡 D/E implicite : {w.debt_to_equity.toFixed(2)} — cohérent avec le re-levering du Beta ?{" "}
              {Math.abs(debt_weight / (w.equity_weight||1) - w.debt_to_equity) > 0.15 ?
                <span style={{color:"#f59e0b"}}>⚠️ Écart D/E Beta vs structure — vérifier cohérence</span> :
                <span style={{color:"#10b981"}}>✓ Cohérent</span>
              }
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: WACC Terminal ─────────────────────────────────────────── */}
      {tab === "terminal" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(255,255,255,0.02)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            WACC_terminal = f(β_unlevered, D/E_cible, structure_cible) — société arrivée à maturité
          </div>

          {/* Explication pédagogique */}
          <div style={{background:"rgba(6,182,212,0.04)",border:"1px solid rgba(6,182,212,0.12)",
            borderRadius:9,padding:12,marginBottom:12,fontSize:10,color:"#475569",lineHeight:1.7}}>
            <div style={{fontWeight:700,color:"#06b6d4",fontSize:10,marginBottom:6}}>
              💡 Pourquoi un WACC terminal différent ?
            </div>
            À l'horizon de la projection, la société est supposée avoir atteint un état stationnaire :<br/>
            • Elle s'est partiellement désendettée → D/E plus faible → beta re-leveré plus bas<br/>
            • Son profil de risque se normalise → les primes taille et spécifique diminuent de moitié<br/>
            • Sa structure financière converge vers celle d'un acteur mature du secteur<br/>
            <span style={{color:"#0891b2"}}>→ Le WACC terminal est typiquement 50-150 bps en dessous du WACC initial.</span>
          </div>

          {/* Structure cible */}
          <div style={{background:"rgba(6,182,212,0.04)",border:"1px solid rgba(6,182,212,0.12)",
            borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#06b6d4",marginBottom:8,
              textTransform:"uppercase",letterSpacing:"0.05em"}}>
              Structure financière cible (à maturité)
            </div>
            <div style={g2}>
              <I lb="D/E cible à maturité" v={w.target_debt_to_equity}
                oc={uw("target_debt_to_equity")} s="0.05"
                tip="Target D/E ratio at horizon. Usually lower than current D/E — deleveraging company."/>
              <I lb="Poids CP cible E/V" v={w.target_equity_weight}
                oc={v => setW(p => ({...p, target_equity_weight: Math.min(1, Math.max(0, v))}))} pct
                tip="Target E/(E+D). Must be consistent with target D/E: E/V = 1/(1+D/E)."/>
            </div>
            {/* Cohérence D/E vs E/V */}
            {(() => {
              const implied_ew = 1 / (1 + w.target_debt_to_equity);
              const diff = Math.abs(implied_ew - w.target_equity_weight);
              return (
                <div style={{marginTop:8,fontSize:10,
                  color: diff < 0.02 ? "#10b981" : "#f59e0b",fontStyle:"italic"}}>
                  {diff < 0.02
                    ? `✓ Cohérent — E/V implicite par D/E : ${(implied_ew*100).toFixed(1)}%`
                    : `⚠️ Écart D/E → E/V : ${(implied_ew*100).toFixed(1)}% vs ${(w.target_equity_weight*100).toFixed(1)}% saisi — vérifier`
                  }
                </div>
              );
            })()}
          </div>

          {/* Beta terminal recalculé */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:8,textTransform:"uppercase"}}>
              Beta re-leveré sur structure cible
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,
              background:"rgba(6,182,212,0.06)",borderRadius:7,padding:"10px 12px"}}>
              <div style={{flex:1,textAlign:"center"}}>
                <div style={{fontSize:10,color:"#64748b"}}>β unlevered (inchangé)</div>
                <div style={{fontFamily:"monospace",fontSize:14,fontWeight:800,color:"#94a3b8"}}>
                  {w.beta_unlevered.toFixed(2)}
                </div>
              </div>
              <div style={{fontSize:12,color:"#334155"}}>×</div>
              <div style={{flex:1.3,textAlign:"center"}}>
                <div style={{fontSize:10,color:"#64748b"}}>[1+(1−IS)×D/E_cible]</div>
                <div style={{fontFamily:"monospace",fontSize:14,fontWeight:800,color:"#94a3b8"}}>
                  {(1+(1-w.tax_rate)*w.target_debt_to_equity).toFixed(3)}
                </div>
              </div>
              <div style={{fontSize:12,color:"#334155"}}>=</div>
              <div style={{flex:1.2,background:"rgba(6,182,212,0.12)",borderRadius:6,padding:"4px 10px",textAlign:"center"}}>
                <div style={{fontSize:10,color:"#06b6d4"}}>β levered terminal</div>
                <div style={{fontFamily:"monospace",fontSize:16,fontWeight:800,color:"#06b6d4"}}>
                  {beta_levered_terminal.toFixed(3)}
                </div>
              </div>
            </div>
            <div style={{marginTop:6,fontSize:10,color:"#334155",fontStyle:"italic"}}>
              Primes taille et spécifique réduites de 50% à maturité
              ({fP(w.size_premium*0.5)} + {fP(w.specific_premium*0.5)} vs {fP(w.size_premium)} + {fP(w.specific_premium)} aujourd'hui)
            </div>
          </div>

          {/* Comparison initial vs terminal */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12,marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:10,textTransform:"uppercase"}}>
              Comparaison Initial → Terminal
            </div>
            {[
              {lb:"D/E",     init: w.debt_to_equity.toFixed(2)+"x",          term: w.target_debt_to_equity.toFixed(2)+"x", c:"#8b5cf6"},
              {lb:"β levered", init: beta_levered.toFixed(3),                 term: beta_levered_terminal.toFixed(3),       c:"#10b981"},
              {lb:"Ke",       init: fP(ke),                                   term: fP(ke_terminal),                        c:"#10b981"},
              {lb:"WACC",     init: fP(wacc),                                 term: fP(wacc_terminal),                      c:"#3b82f6"},
            ].map((row,i) => (
              <div key={i} style={{display:"flex",alignItems:"center",
                padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                <span style={{width:80,fontSize:10,color:"#64748b"}}>{row.lb}</span>
                <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,
                  color: i===3 ? "#3b82f6" : "#94a3b8",width:70,textAlign:"right"}}>{row.init}</span>
                <span style={{flex:1,textAlign:"center",fontSize:14,color:"#334155"}}>→</span>
                <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,
                  color:row.c,width:70,textAlign:"right"}}>{row.term}</span>
                <span style={{fontFamily:"monospace",fontSize:10,
                  color: row.term < row.init ? "#10b981" : "#f59e0b",
                  width:55,textAlign:"right",marginLeft:8}}>
                  {i===3 ? `${((wacc_terminal-wacc)*10000).toFixed(0)} bps` :
                   i===2 ? `${((ke_terminal-ke)*10000).toFixed(0)} bps` : ""}
                </span>
              </div>
            ))}
          </div>

          <CalcRow label="WACC Terminal"
            formula={`${fP(w.target_equity_weight)} × ${fP(ke_terminal)} + ${(debt_weight_t*100).toFixed(0)}% × ${fP(kd_net)}`}
            value={wacc_terminal} color="#06b6d4" highlight/>
        </div>
      )}

      {/* ── TAB: Résultat & Waterfall ────────────────────────────────────── */}
      {tab === "result" && (
        <div>
          {/* Summary grid */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            {[
              {lb:"β levered", v:beta_levered.toFixed(3), c:"#10b981", sub:`unlevered ${w.beta_unlevered.toFixed(2)}`},
              {lb:"Ke",        v:fP(ke),                  c:"#10b981", sub:`${fP(w.risk_free_rate)}+${beta_levered.toFixed(2)}×${fP(w.equity_risk_premium)}…`},
              {lb:"Kd net",    v:fP(kd_net),              c:"#f59e0b", sub:`${fP(w.cost_of_debt_pretax)}×(1−${fP(w.tax_rate)})`},
              {lb:"E/V",       v:`${(w.equity_weight*100).toFixed(0)}%`, c:"#8b5cf6", sub:"poids CP"},
              {lb:"D/V",       v:`${(debt_weight*100).toFixed(0)}%`,     c:"#f59e0b", sub:"poids dette"},
              {lb:"WACC",      v:fP(wacc), c:"#3b82f6", sub:`${fP(ke*w.equity_weight)}+${fP(kd_net*debt_weight)}`},
            ].map((item,i) => (
              <div key={i} style={{background: i===5 ? "rgba(59,130,246,0.1)" : "rgba(255,255,255,0.02)",
                border: i===5 ? "1px solid rgba(59,130,246,0.25)" : "1px solid rgba(255,255,255,0.05)",
                borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontSize:10,color:"#475569",textTransform:"uppercase",letterSpacing:"0.05em"}}>{item.lb}</div>
                <div style={{fontFamily:"monospace",fontSize: i===5 ? 18 : 15, fontWeight:800,color:item.c,margin:"3px 0"}}>{item.v}</div>
                <div style={{fontSize:10,color:"#334155",fontFamily:"monospace"}}>{item.sub}</div>
              </div>
            ))}
          </div>

          {/* Waterfall */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12,marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"#475569",marginBottom:10,textTransform:"uppercase"}}>
              Décomposition waterfall
            </div>
            <WaterfallWACC/>
          </div>

          {/* Sensitivity mini-table: WACC vs D/E */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"#475569",marginBottom:8,textTransform:"uppercase"}}>
              Sensibilité WACC — β unlevered × D/E
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"monospace"}}>
                <thead>
                  <tr>
                    <th style={{padding:"4px 6px",color:"#475569",textAlign:"left",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>β_u \ D/E</th>
                    {[0,0.1,0.2,0.3,0.5,0.7,1.0].map(de => (
                      <th key={de} style={{padding:"4px 6px",color: Math.abs(de-w.debt_to_equity)<0.05 ? "#f59e0b":"#475569",
                        textAlign:"right",borderBottom:"1px solid rgba(255,255,255,0.06)",
                        fontWeight: Math.abs(de-w.debt_to_equity)<0.05 ? 800 : 400}}>
                        {de.toFixed(1)}x
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[0.6,0.8,1.0,1.2,1.5].map(bu => (
                    <tr key={bu} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                      <td style={{padding:"4px 6px",color: Math.abs(bu-w.beta_unlevered)<0.05 ? "#10b981":"#475569",
                        fontWeight: Math.abs(bu-w.beta_unlevered)<0.05 ? 800 : 400}}>β {bu.toFixed(1)}</td>
                      {[0,0.1,0.2,0.3,0.5,0.7,1.0].map(de => {
                        const bl = bu * (1 + (1-w.tax_rate)*de);
                        const ke2 = w.risk_free_rate + bl*w.equity_risk_premium + w.size_premium + w.specific_premium;
                        const w2  = w.equity_weight*ke2 + debt_weight*kd_net;
                        const isCurrent = Math.abs(bu-w.beta_unlevered)<0.05 && Math.abs(de-w.debt_to_equity)<0.05;
                        return (
                          <td key={de} style={{padding:"4px 6px",textAlign:"right",
                            color: isCurrent ? "#3b82f6" : w2>0.12?"#ef4444":w2<0.08?"#10b981":"#94a3b8",
                            fontWeight: isCurrent ? 800 : 400,
                            background: isCurrent ? "rgba(59,130,246,0.12)" : "transparent"}}>
                            {(w2*100).toFixed(1)}%
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{fontSize:10,color:"#334155",marginTop:6,fontStyle:"italic"}}>
              Valeur courante surlignée en bleu. Rouge = WACC &gt; 12% · Vert = WACC &lt; 8%
            </div>
          </div>
        </div>
      )}

      {/* Apply button — always visible at bottom */}
      <button onClick={apply}
        style={{width:"100%",marginTop:14,
          background:"linear-gradient(135deg,#1e3a8a,#2563eb,#0e7490)",
          border:"none",borderRadius:9,padding:"10px",
          color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",
          display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
        <span>Appliquer</span>
        <div style={{display:"flex",alignItems:"center",gap:4,
          background:"rgba(0,0,0,0.15)",borderRadius:6,padding:"2px 8px"}}>
          <span style={{fontSize:10,color:"#93c5fd"}}>Initial</span>
          <span style={{fontFamily:"monospace",fontSize:14,color:"#93c5fd"}}>{fP(wacc)}</span>
        </div>
        <span style={{fontSize:10,color:"#60a5fa"}}>+</span>
        <div style={{display:"flex",alignItems:"center",gap:4,
          background:"rgba(0,0,0,0.15)",borderRadius:6,padding:"2px 8px"}}>
          <span style={{fontSize:10,color:"#67e8f9"}}>Terminal</span>
          <span style={{fontFamily:"monospace",fontSize:14,color:"#67e8f9"}}>{fP(wacc_terminal)}</span>
        </div>
        <span style={{fontSize:10,color:"#60a5fa"}}>→ DCF</span>
      </button>
    </div>
  );
};

// ==========================================================================
// TERMINAL VALUE — Dual Method Panel
// ==========================================================================
// ==========================================================================
// TERMINAL VALUE PANEL — Étape 2 : Décomposition complète
// ==========================================================================
const TVPanel = ({r, f, L}) => {
  const [exitMultiple, setExitMultiple] = useState(12);
  const [tvTab, setTvTab] = useState("gordon"); // gordon | exit | roic | sensitivity

  if (!r || !r.projections) return null;
  const last = r.projections[r.projections.length - 1];
  if (!last) return null;

  const df_last = 1 / Math.pow(1 + last.wacc, f.forecast_years);

  // ── Gordon Growth Model ───────────────────────────────────────────────────
  const gg_fcff1      = last.fcff * (1 + f.terminal_growth_rate);
  const gg_cap_rate   = last.wacc - f.terminal_growth_rate;
  const gg_tv         = gg_cap_rate > 0 ? gg_fcff1 / gg_cap_rate : null;
  const gg_pv         = gg_tv ? gg_tv * df_last : null;

  // ── Exit Multiple ─────────────────────────────────────────────────────────
  // EBITDA terminal correctement calculé depuis NOPAT + D&A estimée
  // NOPAT = EBIT×(1-T) → EBIT = NOPAT/(1-T) → EBITDA ≈ EBIT / operating_margin × (operating_margin + da_margin)
  // Approche plus robuste : utiliser le ratio EBITDA/NOPAT implicite de la projection
  const est_ebit      = last.nopat / (1 - f.tax_rate);
  const est_da_margin = 0.02; // hypothèse D&A ~2% du CA à maturité (conservateur)
  const est_ebitda    = est_ebit + last.revenue * est_da_margin;
  const em_tv         = est_ebitda * exitMultiple;
  const em_pv         = em_tv * df_last;

  // ── Cohérence Gordon ↔ Exit Multiple ─────────────────────────────────────
  // Multiple implicite Gordon : TV_gordon / EBITDA_terminal
  const gg_implied_multiple = gg_tv && est_ebitda > 0 ? gg_tv / est_ebitda : null;
  // g implicite Exit Multiple : g = WACC - FCFF1/TV_em
  const em_implied_g = em_tv > 0 ? last.wacc - gg_fcff1 / em_tv : null;

  // ── ROIC implicite en état stationnaire ───────────────────────────────────
  // En Gordon : g = ROIC_terminal × Taux_reinvestissement_terminal
  // → ROIC_implicite = g / taux_reinv_terminal
  const reinv_rate_t  = f.reinvestment_rate_terminal || (f.terminal_growth_rate / (last.roic || 0.10));
  const roic_implicit = reinv_rate_t > 0 ? f.terminal_growth_rate / reinv_rate_t : null;
  // Spread ROIC - WACC : >0 = création de valeur, <0 = destruction
  const roic_wacc_spread = roic_implicit ? roic_implicit - last.wacc : null;
  // Valeur du ROIC terminal pour que TV = 0 spread (ROIC = WACC)
  const roic_breakeven = last.wacc;

  // ── % TV dans la valeur totale ─────────────────────────────────────────────
  const pct_tv_gordon = gg_pv && r.enterprise_value > 0 ? gg_pv / r.enterprise_value : null;
  const pct_tv_exit   = r.enterprise_value > 0 ? em_pv / r.enterprise_value : null;

  // ── Sensibilité g × WACC (Gordon) ─────────────────────────────────────────
  const g_range    = [0.005, 0.010, 0.015, 0.020, 0.025, 0.030, 0.035];
  const wacc_range = [0.06, 0.07, 0.08, 0.09, 0.10, 0.11, 0.12];

  const Row = ({label, value, sub, color, highlight}) => (
    <div style={{
      display:"flex",justifyContent:"space-between",padding:"6px 8px",
      borderBottom:"1px solid rgba(255,255,255,0.04)",borderRadius: highlight ? 6 : 0,
      background: highlight ? "rgba(251,191,36,0.06)" : "transparent",
    }}>
      <div>
        <span style={{fontSize:12,color: highlight ? "#fbbf24" : "#e2e8f0"}}>{label}</span>
        {sub && <div style={{fontSize:10,color:"#475569",fontStyle:"italic"}}>{sub}</div>}
      </div>
      <span style={{fontSize:13,fontWeight:700,color:color||"#f1f5f9",fontFamily:"monospace"}}>{value}</span>
    </div>
  );

  const tvTabs = [
    {id:"gordon",      label:"Gordon Growth",     color:"#10b981"},
    {id:"exit",        label:"Exit Multiple",      color:"#8b5cf6"},
    {id:"roic",        label:"ROIC implicite",     color:"#f59e0b"},
    {id:"sensitivity", label:"Sensibilité g×WACC", color:"#3b82f6"},
  ];

  return (
    <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
      borderRadius:12,padding:16}}>

      {/* Header avec % TV en surbrillance */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",
            letterSpacing:"0.05em",marginBottom:4}}>
            📐 Terminal Value — Décomposition
          </div>
          <div style={{fontSize:10,color:"#475569"}}>
            Année {f.forecast_years} · WACC terminal {fP(last.wacc)} · g = {fP(f.terminal_growth_rate)}
          </div>
        </div>
        {/* % TV badges */}
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {pct_tv_gordon && (
            <div style={{textAlign:"center",background: pct_tv_gordon > 0.7 ? "rgba(239,68,68,0.1)" : "rgba(251,191,36,0.08)",
              border: `1px solid ${pct_tv_gordon > 0.7 ? "rgba(239,68,68,0.3)" : "rgba(251,191,36,0.2)"}`,
              borderRadius:8,padding:"6px 10px"}}>
              <div style={{fontSize:10,color:"#64748b"}}>TV / EV (Gordon)</div>
              <div style={{fontFamily:"monospace",fontSize:16,fontWeight:800,
                color: pct_tv_gordon > 0.7 ? "#ef4444" : "#fbbf24"}}>
                {fP(pct_tv_gordon)}
              </div>
            </div>
          )}
          {pct_tv_exit && (
            <div style={{textAlign:"center",background:"rgba(139,92,246,0.08)",
              border:"1px solid rgba(139,92,246,0.2)",borderRadius:8,padding:"6px 10px"}}>
              <div style={{fontSize:10,color:"#64748b"}}>TV / EV (Exit)</div>
              <div style={{fontFamily:"monospace",fontSize:16,fontWeight:800,color:"#8b5cf6"}}>
                {fP(pct_tv_exit)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alerte si TV > 70% */}
      {pct_tv_gordon > 0.70 && (
        <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
          borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:10,color:"#fca5a5"}}>
          ⚠️ La valeur terminale représente {fP(pct_tv_gordon)} de la valeur d'entreprise.
          Ce niveau élevé indique une forte sensibilité aux hypothèses de long terme —
          tester g et WACC dans l'onglet Sensibilité.
        </div>
      )}

      {/* Tab bar */}
      <div style={{display:"flex",gap:2,marginBottom:14,
        borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        {tvTabs.map(t => (
          <button key={t.id} onClick={()=>setTvTab(t.id)}
            style={{background:"none",border:"none",cursor:"pointer",padding:"5px 10px",
              fontSize:10,fontWeight:600,
              color: tvTab===t.id ? t.color : "#475569",
              borderBottom: tvTab===t.id ? `2px solid ${t.color}` : "2px solid transparent",
              marginBottom:"-1px",transition:"all 0.15s"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Gordon Growth ─────────────────────────────────────────────────── */}
      {tvTab === "gordon" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(16,185,129,0.04)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            TV = FCFF_n × (1+g) / (WACC − g)  ·  PV(TV) = TV / (1+WACC)^n
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            {/* Inputs */}
            <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12}}>
              <div style={{fontSize:10,fontWeight:700,color:"#10b981",marginBottom:8,textTransform:"uppercase"}}>
                Paramètres
              </div>
              <Row label={`FCFF année ${f.forecast_years}`}   value={`${fC(last.fcff)}€`}
                sub="Dernier flux projeté"/>
              <Row label="Taux de croissance g"               value={fP(f.terminal_growth_rate)}
                sub="PIB nominal LT ≈ 2-3%"/>
              <Row label="WACC terminal"                      value={fP(last.wacc)}
                sub="Convergé depuis WACC initial"/>
              <Row label="Taux de capitalisation (WACC−g)"   value={fP(gg_cap_rate)}
                color={gg_cap_rate <= 0 ? "#ef4444" : gg_cap_rate < 0.03 ? "#f59e0b" : "#94a3b8"}
                sub={gg_cap_rate <= 0 ? "⚠️ WACC < g — valeur infinie" : ""}/>
            </div>
            {/* Results */}
            <div style={{background:"rgba(16,185,129,0.04)",border:"1px solid rgba(16,185,129,0.12)",
              borderRadius:9,padding:12}}>
              <div style={{fontSize:10,fontWeight:700,color:"#10b981",marginBottom:8,textTransform:"uppercase"}}>
                Résultats
              </div>
              <Row label="FCFF normalisé FCFF×(1+g)"    value={`${fC(gg_fcff1)}€`}
                sub="Cash flow de la première année terminale"/>
              <Row label="Valeur Terminale (TV)"        value={gg_tv ? `${fC(gg_tv)}€` : "∞"}
                color="#10b981" highlight={!!gg_tv}/>
              <Row label={`PV(TV) — actualisée ${f.forecast_years} ans`}
                value={gg_pv ? `${fC(gg_pv)}€` : "—"}
                color="#10b981"
                sub={`Facteur d'actualisation : ${df_last.toFixed(4)}`}/>
              {pct_tv_gordon && (
                <Row label="TV / Valeur Entreprise"
                  value={fP(pct_tv_gordon)}
                  color={pct_tv_gordon > 0.7 ? "#ef4444" : "#fbbf24"}
                  highlight={pct_tv_gordon > 0.6}
                  sub={pct_tv_gordon > 0.7 ? "Élevé — tester la sensibilité" : ""}/>
              )}
            </div>
          </div>

          {/* Cohérence avec Exit Multiple */}
          {gg_implied_multiple && (
            <div style={{background:"rgba(255,255,255,0.02)",borderRadius:8,padding:"10px 12px",
              fontSize:10,color:"#64748b"}}>
              <span style={{fontWeight:700,color:"#94a3b8"}}>Cohérence → </span>
              Multiple EV/EBITDA implicite par Gordon : {" "}
              <span style={{fontFamily:"monospace",fontWeight:700,
                color: gg_implied_multiple > 25 ? "#ef4444" : gg_implied_multiple < 5 ? "#f59e0b" : "#10b981"}}>
                {gg_implied_multiple.toFixed(1)}x
              </span>
              {gg_implied_multiple > 25 && " ⚠️ Multiple très élevé — revoir g ou WACC"}
              {gg_implied_multiple < 5  && " ⚠️ Multiple bas — croissance terminale peut-être trop faible"}
            </div>
          )}
        </div>
      )}

      {/* ── Exit Multiple ─────────────────────────────────────────────────── */}
      {tvTab === "exit" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(139,92,246,0.04)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            TV = EBITDA_terminal × Multiple  ·  PV(TV) = TV / (1+WACC)^n
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12}}>
              <div style={{fontSize:10,fontWeight:700,color:"#8b5cf6",marginBottom:8,textTransform:"uppercase"}}>
                EBITDA terminal
              </div>
              <Row label="NOPAT année terminale"        value={`${fC(last.nopat)}€`}
                sub="EBIT × (1−IS) projeté"/>
              <Row label="EBIT estimé"                  value={`${fC(est_ebit)}€`}
                sub={`NOPAT / (1−${fP(f.tax_rate)})`}/>
              <Row label="D&A estimée (2% CA)"          value={`${fC(last.revenue * est_da_margin)}€`}
                sub="Hypothèse conservatrice à maturité"/>
              <Row label="EBITDA terminal estimé"       value={`${fC(est_ebitda)}€`}
                color="#8b5cf6" highlight/>
              <div style={{marginTop:10}}>
                <I lb="Multiple EV/EBITDA de sortie" v={exitMultiple} oc={setExitMultiple} s="0.5"
                  tip="Comparable transaction multiple at exit. Check consistency with sector benchmarks."/>
              </div>
            </div>
            <div style={{background:"rgba(139,92,246,0.04)",border:"1px solid rgba(139,92,246,0.12)",
              borderRadius:9,padding:12}}>
              <div style={{fontSize:10,fontWeight:700,color:"#8b5cf6",marginBottom:8,textTransform:"uppercase"}}>
                Résultats
              </div>
              <Row label="Valeur Terminale (TV)"        value={`${fC(em_tv)}€`}
                color="#8b5cf6" highlight/>
              <Row label={`PV(TV) — actualisée ${f.forecast_years} ans`}
                value={`${fC(em_pv)}€`}
                color="#8b5cf6"
                sub={`Facteur d'actualisation : ${df_last.toFixed(4)}`}/>
              {pct_tv_exit && (
                <Row label="TV / Valeur Entreprise"     value={fP(pct_tv_exit)}
                  color="#8b5cf6"/>
              )}
              {em_implied_g !== null && (
                <Row label="g implicite par ce multiple"
                  value={fP(em_implied_g)}
                  color={em_implied_g < 0 ? "#ef4444" : em_implied_g > 0.05 ? "#f59e0b" : "#94a3b8"}
                  sub={`Croissance implicite à WACC ${fP(last.wacc)}`}/>
              )}
            </div>
          </div>

          {/* Comparaison côte à côte */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"#475569",marginBottom:10,textTransform:"uppercase"}}>
              Comparaison Gordon ↔ Exit Multiple
            </div>
            <div style={{display:"flex",gap:8}}>
              {[
                {lb:"PV(TV) Gordon",  v:gg_pv,  c:"#10b981", pct:pct_tv_gordon},
                {lb:"PV(TV) Exit",    v:em_pv,  c:"#8b5cf6", pct:pct_tv_exit},
              ].map((m,i) => (
                <div key={i} style={{flex:1,textAlign:"center",padding:"10px 12px",
                  background:`${m.c}10`,borderRadius:8,border:`1px solid ${m.c}25`}}>
                  <div style={{fontSize:10,color:m.c,fontWeight:600}}>{m.lb}</div>
                  <div style={{fontSize:20,fontWeight:800,color:"#f1f5f9",
                    fontFamily:"monospace",margin:"6px 0"}}>
                    {m.v ? `${fC(m.v)}€` : "—"}
                  </div>
                  {m.pct && <div style={{fontSize:11,color:m.c}}>{fP(m.pct)} de l'EV</div>}
                </div>
              ))}
            </div>
            {gg_pv && em_pv && (
              <div style={{textAlign:"center",marginTop:10,fontSize:10,color:"#64748b"}}>
                Écart : {fC(Math.abs(gg_pv - em_pv))}€
                ({Math.abs((gg_pv - em_pv) / ((gg_pv + em_pv) / 2) * 100).toFixed(1)}%)
                {Math.abs(gg_pv - em_pv) / ((gg_pv + em_pv) / 2) > 0.20 &&
                  <span style={{color:"#f59e0b"}}> · Écart significatif — vérifier les hypothèses</span>
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ROIC implicite ────────────────────────────────────────────────── */}
      {tvTab === "roic" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(245,158,11,0.04)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            g = ROIC_terminal × Taux_réinvestissement  →  ROIC_implicite = g / taux_réinv
          </div>

          <div style={{background:"rgba(245,158,11,0.04)",border:"1px solid rgba(245,158,11,0.12)",
            borderRadius:9,padding:14,marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"#f59e0b",marginBottom:10,textTransform:"uppercase"}}>
              Décomposition de g — Équation fondamentale
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
              {[
                {lb:"g terminal",           v:fP(f.terminal_growth_rate),          c:"#f59e0b"},
                {lb:"Taux réinvestissement", v:fP(reinv_rate_t),                    c:"#94a3b8",
                 sub:"g / ROIC_projeté"},
                {lb:"ROIC implicite",        v:roic_implicit ? fP(roic_implicit) : "—", c:"#10b981",
                 sub:"g / taux_réinv"},
              ].map((item,i) => (
                <div key={i} style={{background:"rgba(255,255,255,0.03)",borderRadius:8,
                  padding:"10px 12px",textAlign:"center"}}>
                  <div style={{fontSize:10,color:"#64748b",marginBottom:4}}>{item.lb}</div>
                  {item.sub && <div style={{fontSize:10,color:"#334155",marginBottom:4,fontStyle:"italic"}}>{item.sub}</div>}
                  <div style={{fontFamily:"monospace",fontSize:16,fontWeight:800,color:item.c}}>{item.v}</div>
                </div>
              ))}
            </div>

            {/* Spread ROIC - WACC */}
            {roic_wacc_spread !== null && (
              <div style={{padding:"10px 14px",borderRadius:8,
                background: roic_wacc_spread > 0 ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
                border: `1px solid ${roic_wacc_spread > 0 ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}`,
              }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,
                      color: roic_wacc_spread > 0 ? "#10b981" : "#ef4444"}}>
                      {roic_wacc_spread > 0 ? "✓ Création de valeur en TV" : "✗ Destruction de valeur en TV"}
                    </div>
                    <div style={{fontSize:10,color:"#475569",marginTop:2}}>
                      ROIC implicite ({roic_implicit ? fP(roic_implicit) : "—"})
                      {roic_wacc_spread > 0 ? " > " : " < "}
                      WACC ({fP(last.wacc)})
                    </div>
                  </div>
                  <div style={{fontFamily:"monospace",fontSize:20,fontWeight:800,
                    color: roic_wacc_spread > 0 ? "#10b981" : "#ef4444"}}>
                    {roic_wacc_spread > 0 ? "+" : ""}{(roic_wacc_spread * 10000).toFixed(0)} bps
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sensibilité ROIC par g */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"#475569",marginBottom:8,textTransform:"uppercase"}}>
              ROIC implicite selon g (taux réinv. {fP(reinv_rate_t)} fixe)
            </div>
            <div style={{display:"flex",gap:4,alignItems:"flex-end",height:80}}>
              {[0.005,0.010,0.015,0.020,0.025,0.030,0.035].map(g => {
                const roic_g = reinv_rate_t > 0 ? g / reinv_rate_t : 0;
                const spread_g = roic_g - last.wacc;
                const maxRoic = 0.20;
                return (
                  <div key={g} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
                    <div style={{fontSize:10,color: spread_g > 0 ? "#10b981" : "#ef4444",
                      fontWeight:700,marginBottom:2}}>{fP(roic_g)}</div>
                    <div style={{width:"100%",
                      height:`${Math.max(roic_g/maxRoic*60,3)}px`,
                      background: spread_g > 0 ? "#10b981" : "#ef4444",
                      opacity: Math.abs(g - f.terminal_growth_rate) < 0.003 ? 1 : 0.5,
                      borderRadius:"3px 3px 0 0",
                      border: Math.abs(g - f.terminal_growth_rate) < 0.003 ?
                        "2px solid #fbbf24" : "none"}}/>
                    <div style={{fontSize:10,color:"#475569",marginTop:3}}>{fP(g)}</div>
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:10,color:"#334155",marginTop:6,textAlign:"center",fontStyle:"italic"}}>
              Barre surlignée = g actuel · Vert = ROIC &gt; WACC · Rouge = ROIC &lt; WACC
            </div>
          </div>
        </div>
      )}

      {/* ── Sensibilité g × WACC ─────────────────────────────────────────── */}
      {tvTab === "sensitivity" && (
        <div>
          <div style={{fontSize:10,color:"#475569",fontFamily:"monospace",
            background:"rgba(59,130,246,0.04)",borderRadius:7,padding:"6px 10px",marginBottom:12}}>
            PV(TV) Gordon selon g (croissance terminale) × WACC terminal
          </div>

          <div style={{overflowX:"auto",marginBottom:12}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"monospace"}}>
              <thead>
                <tr>
                  <th style={{padding:"5px 8px",color:"#475569",textAlign:"left",
                    borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                    g \ WACC
                  </th>
                  {wacc_range.map(w2 => (
                    <th key={w2} style={{padding:"5px 8px",textAlign:"right",
                      color: Math.abs(w2 - last.wacc) < 0.005 ? "#3b82f6" : "#475569",
                      fontWeight: Math.abs(w2 - last.wacc) < 0.005 ? 800 : 400,
                      borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                      {(w2*100).toFixed(0)}%
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g_range.map(g2 => (
                  <tr key={g2} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                    <td style={{padding:"5px 8px",
                      color: Math.abs(g2 - f.terminal_growth_rate) < 0.003 ? "#f59e0b" : "#475569",
                      fontWeight: Math.abs(g2 - f.terminal_growth_rate) < 0.003 ? 800 : 400}}>
                      {(g2*100).toFixed(1)}%
                    </td>
                    {wacc_range.map(w2 => {
                      const cap = w2 - g2;
                      if (cap <= 0) return (
                        <td key={w2} style={{padding:"5px 8px",textAlign:"right",color:"#ef4444"}}>∞</td>
                      );
                      const tv2   = gg_fcff1 / cap;
                      const pv2   = tv2 / Math.pow(1 + w2, f.forecast_years);
                      const pct2  = r.enterprise_value > 0 ? pv2 / r.enterprise_value : 0;
                      const isCur = Math.abs(w2-last.wacc)<0.005 && Math.abs(g2-f.terminal_growth_rate)<0.003;
                      // Color: heat map based on % of EV
                      const heat  = pct2 > 0.8 ? "#ef4444" : pct2 > 0.65 ? "#f59e0b" : pct2 < 0.40 ? "#10b981" : "#94a3b8";
                      return (
                        <td key={w2} style={{padding:"5px 8px",textAlign:"right",
                          color: isCur ? "#f1f5f9" : heat,
                          fontWeight: isCur ? 800 : 400,
                          background: isCur ? "rgba(59,130,246,0.15)" : "transparent",
                          borderRadius: isCur ? 4 : 0}}>
                          {fC(pv2)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{fontSize:10,color:"#334155",fontStyle:"italic",marginBottom:10}}>
            Valeur actuelle de la TV. Cellule bleue = paramètres courants.
            🟡 TV/EV &gt; 65% · 🔴 TV/EV &gt; 80% · 🟢 TV/EV &lt; 40%
          </div>

          {/* % TV heat map */}
          <div style={{background:"rgba(255,255,255,0.02)",borderRadius:9,padding:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"#475569",marginBottom:8,textTransform:"uppercase"}}>
              % TV / Valeur Entreprise
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"monospace"}}>
                <thead>
                  <tr>
                    <th style={{padding:"4px 6px",color:"#475569",textAlign:"left",
                      borderBottom:"1px solid rgba(255,255,255,0.06)"}}>g \ WACC</th>
                    {wacc_range.map(w2 => (
                      <th key={w2} style={{padding:"4px 6px",textAlign:"right",
                        color: Math.abs(w2-last.wacc)<0.005 ? "#3b82f6":"#475569",
                        fontWeight: Math.abs(w2-last.wacc)<0.005 ? 800 : 400,
                        borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                        {(w2*100).toFixed(0)}%
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g_range.map(g2 => (
                    <tr key={g2}>
                      <td style={{padding:"4px 6px",
                        color: Math.abs(g2-f.terminal_growth_rate)<0.003 ? "#f59e0b":"#475569",
                        fontWeight: Math.abs(g2-f.terminal_growth_rate)<0.003 ? 800:400}}>
                        {(g2*100).toFixed(1)}%
                      </td>
                      {wacc_range.map(w2 => {
                        const cap = w2 - g2;
                        if (cap <= 0) return <td key={w2} style={{padding:"4px 6px",textAlign:"right",color:"#ef4444",fontSize:10}}>∞</td>;
                        const tv2  = gg_fcff1 / cap;
                        const pv2  = tv2 / Math.pow(1+w2, f.forecast_years);
                        const pct2 = r.enterprise_value > 0 ? pv2 / r.enterprise_value : 0;
                        const isCur= Math.abs(w2-last.wacc)<0.005 && Math.abs(g2-f.terminal_growth_rate)<0.003;
                        const bgCol = pct2>0.80 ? "rgba(239,68,68,0.25)"
                                    : pct2>0.65 ? "rgba(245,158,11,0.2)"
                                    : pct2<0.40 ? "rgba(16,185,129,0.2)"
                                    : "rgba(255,255,255,0.03)";
                        const txCol = pct2>0.80 ? "#fca5a5"
                                    : pct2>0.65 ? "#fcd34d"
                                    : pct2<0.40 ? "#6ee7b7"
                                    : "#64748b";
                        return (
                          <td key={w2} style={{padding:"4px 6px",textAlign:"right",
                            background: isCur ? "rgba(59,130,246,0.2)" : bgCol,
                            color: isCur ? "#f1f5f9" : txCol,
                            fontWeight: isCur ? 800 : 500,fontSize:10}}>
                            {(pct2*100).toFixed(0)}%
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================================================
// DISCOUNT RATE BUILD-UP (for intangibles)
// ==========================================================================
const DiscountBuilder = ({onRateChange, currentRate}) => {
  const [open, setOpen] = useState(false);
  const [dr, setDr] = useState({
    wacc: 0.10,
    asset_risk_premium: 0.02,
    illiquidity_premium: 0.01,
    obsolescence_premium: 0.005,
  });
  const udr = k => v => setDr(p => ({...p, [k]: v}));
  const total = dr.wacc + dr.asset_risk_premium + dr.illiquidity_premium + dr.obsolescence_premium;

  const Row = ({label, value, color}) => (
    <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
      <span style={{fontSize:12,color:"#e2e8f0"}}>{label}</span>
      <span style={{fontSize:13,fontWeight:700,color:color||"#f1f5f9",fontFamily:"monospace"}}>{fP(value)}</span>
    </div>
  );

  if(!open) return (
    <div onClick={()=>setOpen(true)} style={{background:"rgba(245,158,11,0.05)",border:"1px solid rgba(245,158,11,0.15)",borderRadius:9,padding:"10px 14px",cursor:"pointer",marginBottom:12,transition:"all 0.2s"}}
      onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(245,158,11,0.3)"}
      onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(245,158,11,0.15)"}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:12,color:"#f59e0b",fontWeight:600}}>🔧 Discount Rate Build-up</span>
        <span style={{fontSize:10,color:"#64748b"}}>Click to expand ▼</span>
      </div>
    </div>
  );

  return (
    <div style={{background:"rgba(245,158,11,0.04)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:11,padding:16,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{fontSize:12,fontWeight:700,color:"#f59e0b"}}>🔧 Discount Rate Build-up</span>
        <button onClick={()=>setOpen(false)} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:12}}>▲</button>
      </div>
      <div style={{fontSize:10,color:"#64748b",fontStyle:"italic",marginBottom:10}}>Rate = WACC + Asset Risk Premium + Illiquidity + Obsolescence</div>
      <div style={g2}>
        <I lb="Base WACC" v={dr.wacc} oc={udr("wacc")} pct tip="Company WACC from build-up"/>
        <I lb="Asset Risk Premium" v={dr.asset_risk_premium} oc={udr("asset_risk_premium")} pct tip="Additional risk for intangible nature"/>
      </div>
      <div style={{...g2,marginTop:8}}>
        <I lb="Illiquidity Premium" v={dr.illiquidity_premium} oc={udr("illiquidity_premium")} pct tip="Premium for non-tradeable asset"/>
        <I lb="Obsolescence Premium" v={dr.obsolescence_premium} oc={udr("obsolescence_premium")} pct tip="Technology/market obsolescence risk"/>
      </div>
      <div style={{marginTop:10,background:"rgba(245,158,11,0.1)",borderRadius:7,padding:10}}>
        <Row label="WACC" value={dr.wacc}/>
        <Row label="+ Asset Risk" value={dr.asset_risk_premium}/>
        <Row label="+ Illiquidity" value={dr.illiquidity_premium}/>
        <Row label="+ Obsolescence" value={dr.obsolescence_premium}/>
        <div style={{borderTop:"2px solid rgba(245,158,11,0.3)",marginTop:4,paddingTop:4}}>
          <Row label="Discount Rate" value={total} color="#f59e0b"/>
        </div>
      </div>
      <button onClick={()=>{if(onRateChange)onRateChange(total);}} style={{width:"100%",marginTop:10,background:"linear-gradient(135deg,#b45309,#f59e0b)",border:"none",borderRadius:8,padding:"9px",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>
        Apply Rate: {fP(total)} →
      </button>
    </div>
  );
};

// ==========================================================================
// METHODOLOGY PANEL (dépliable)
// ==========================================================================
const MethodPanel = ({model}) => {
  const [open, setOpen] = useState(false);
  const methods = {
    dcf: {
      title: "Discounted Cash Flow (DCF)",
      formula: "Enterprise Value = Σ PV(FCFF) + PV(Terminal Value)",
      steps: [
        "1. Project revenues using convergence model: gₜ = g₀ + (g∞ - g₀) × min(t/T, 1)",
        "2. Compute NOPAT = Revenue × Operating Margin × (1 - Tax Rate)",
        "3. Calculate reinvestment = ΔRevenue / Sales-to-Capital ratio",
        "4. FCFF = NOPAT - Reinvestment",
        "5. Discount each FCFF at time-varying WACC",
        "6. Terminal Value (Gordon Growth): TV = FCFF_n × (1+g) / (WACC - g)",
        "7. Enterprise Value = Sum of PV(FCFF) + PV(Terminal Value)",
        "8. Equity Value = Enterprise Value - Net Debt - Minority Interests",
      ],
      notes: "The convergence model allows smooth transition from current growth/margins/WACC to terminal steady-state values."
    },
    exc: {
      title: "Excess Return / Economic Profit",
      formula: "EV = Invested Capital + Σ PV(Excess Returns)",
      steps: [
        "1. Start with current invested capital",
        "2. Compute NOPAT = Invested Capital × ROIC",
        "3. Capital Charge = Invested Capital × WACC",
        "4. Excess Return = NOPAT - Capital Charge",
        "5. Reinvestment = NOPAT × Reinvestment Rate",
        "6. Next period IC = IC + Reinvestment",
        "7. EV = IC₀ + Σ PV(Excess Returns)",
        "8. Value is created when ROIC > WACC",
      ],
      notes: "Equivalent to DCF but makes value creation explicit: only returns above cost of capital create value."
    },
    mul: {
      title: "Comparable Multiples",
      formula: "EV = Metric × Weighted Average Multiple × Adjustments",
      steps: [
        "1. Select peer companies with known valuation multiples",
        "2. Calculate weighted average for each multiple type (EV/Revenue, EV/EBITDA, P/E, etc.)",
        "3. Apply each multiple to the target's corresponding metric",
        "4. Adjust for: Illiquidity Discount, Control Premium, Size Discount",
        "5. Range: Low = minimum multiple result, High = maximum",
      ],
      notes: "Relative valuation — relies on market pricing of comparable companies. Most reliable with truly comparable peers."
    },
    rfr: {
      title: "Relief from Royalty",
      formula: "Fair Value = Σ PV(After-tax Royalty Savings) + TAB",
      steps: [
        "1. Project attributable revenues over forecast period",
        "2. Apply arm's length royalty rate to revenues",
        "3. Compute after-tax savings: Royalty × (1 - Tax Rate)",
        "4. Apply obsolescence factor: Savings × (1 - obs)^t",
        "5. Discount at asset-specific rate (WACC + risk premium)",
        "6. Add Tax Amortization Benefit (TAB)",
      ],
      notes: "Values the intangible by estimating what a licensee would pay in royalties. Commonly used for brands, patents, and technology."
    },
    mpe: {
      title: "Multi-Period Excess Earnings (MPEEM)",
      formula: "Fair Value = Σ PV(Adjusted Excess Earnings) + TAB",
      steps: [
        "1. Project total earnings from the asset's revenue stream",
        "2. Deduct contributory asset charges (working capital, fixed assets, workforce)",
        "3. Excess Earnings = After-tax Earnings - Σ Contributory Charges",
        "4. Apply customer attrition: Adjusted = Excess × (1 - attrition)^t",
        "5. Discount at asset-specific rate",
        "6. Add Tax Amortization Benefit (TAB)",
      ],
      notes: "Isolates earnings attributable to the specific intangible by removing returns earned by other assets. Standard for customer relationships."
    },
    ast: {
      title: "Asset-Based / Net Asset Value",
      formula: "Adjusted NAV = Fair Value Assets - Fair Value Liabilities - Deferred Tax",
      steps: [
        "1. Revalue each asset from book value to fair market value",
        "2. Revalue liabilities to fair value",
        "3. Compute deferred tax on unrealized gains: (FV - BV) × Tax Rate",
        "4. Adjusted NAV = Σ Fair Assets - Σ Fair Liabilities - Contingent Liab. - Deferred Tax",
        "5. Liquidation NAV applies additional liquidation discounts to each asset",
      ],
      notes: "Best suited for asset-heavy businesses, holding companies, or liquidation scenarios."
    },
  };

  const m = methods[model];
  if (!m) return null;

  return (
    <div style={{marginBottom:16}}>
      <div onClick={()=>setOpen(!open)} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:9,padding:"10px 14px",cursor:"pointer",transition:"all 0.2s"}}
        onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.15)"}
        onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12,color:"#94a3b8",fontWeight:600}}>📖 Methodology: {m.title}</span>
          <span style={{fontSize:10,color:"#64748b"}}>{open?"▲ Hide":"▼ Show"}</span>
        </div>
      </div>
      {open&&<div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.08)",borderTop:"none",borderRadius:"0 0 9px 9px",padding:16}}>
        <div style={{background:"rgba(59,130,246,0.06)",borderRadius:7,padding:"10px 14px",marginBottom:12}}>
          <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",fontWeight:600,marginBottom:4}}>Core Formula</div>
          <div style={{fontSize:13,color:"#3b82f6",fontFamily:"monospace",fontWeight:600}}>{m.formula}</div>
        </div>
        <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",fontWeight:600,marginBottom:8}}>Calculation Steps</div>
        {m.steps.map((step,i)=>(
          <div key={i} style={{fontSize:12,color:"#cbd5e1",lineHeight:1.7,paddingLeft:4}}>{step}</div>
        ))}
        <div style={{marginTop:12,padding:"8px 12px",background:"rgba(251,191,36,0.06)",borderRadius:7,border:"1px solid rgba(251,191,36,0.1)"}}>
          <div style={{fontSize:10,color:"#fbbf24",fontWeight:600,marginBottom:2}}>💡 Note</div>
          <div style={{fontSize:12,color:"#fcd34d",lineHeight:1.5}}>{m.notes}</div>
        </div>
      </div>}
    </div>
  );
};



// ==========================================================================
// VALIDATION ENGINE
// ==========================================================================
function validate(f, model) {
  const e = []; // erreurs bloquantes
  const w = []; // warnings informatifs

  // ── DCF ──────────────────────────────────────────────────
  if (model === "dcf") {
    const wacc_t = f.cost_of_capital_terminal || 0.09;
    const g      = f.terminal_growth_rate     || 0;
    if ((f.sales_to_capital_ratio || 0) <= 0)
      e.push("❌ Sales/Capital ratio must be > 0");
    if (g >= wacc_t)
      e.push(`❌ Terminal growth (${fP(g)}) ≥ Terminal WACC (${fP(wacc_t)}) — model diverges`);
    if ((f.forecast_years || 0) < 1)
      e.push("❌ Forecast years must be ≥ 1");
    if ((f.base_revenue || 0) <= 0)
      w.push("💡 Base revenue is zero — projections will be flat");
    if (g > 0.04)
      w.push(`⚠️ Terminal growth ${fP(g)} > 4% is unusually high`);
    if ((f.forecast_years || 0) > 20)
      w.push("💡 Forecast > 20 years reduces reliability");
    if ((f.cost_of_capital_initial || 0) < 0.04)
      w.push("⚠️ Initial WACC < 4% is unusually low");
    if (f.sales_to_capital_ratio > 5)
      w.push("💡 Sales/Capital > 5x is very capital-light");
    if (f.base_operating_margin > 0.5)
      w.push("💡 Operating margin > 50% is exceptional");
  }

  // ── EXCESS RETURN ─────────────────────────────────────────
  if (model === "exc") {
    const wacc_t = f.cost_of_capital_terminal || f.cost_of_capital || 0.09;
    const g      = f.terminal_growth_rate     || 0;
    if ((f.base_invested_capital || 0) <= 0)
      w.push("💡 Invested capital is zero — all value from excess returns");
    if (g >= wacc_t)
      e.push(`❌ Terminal growth (${fP(g)}) ≥ Terminal WACC (${fP(wacc_t)}) — model diverges`);
    if (g > 0.04)
      w.push(`⚠️ Terminal growth ${fP(g)} > 4% is unusually high`);
    if ((f.forecast_years || 0) > 20)
      w.push("💡 Forecast > 20 years reduces reliability");
    if ((f.cost_of_capital || 0) < 0.04)
      w.push("⚠️ WACC < 4% is unusually low");
  }

  // ── MULTIPLES ─────────────────────────────────────────────
  if (model === "mul") {
    const hasComps = (f.comparables || []).length > 0;
    if (!hasComps)
      e.push("❌ Add at least one comparable before calculating");
    if (f.illiquidity_discount > 0.35)
      w.push("💡 Illiquidity discount > 35% is aggressive");
    if ((f.illiquidity_discount || 0) + (f.size_discount || 0) > 0.5)
      w.push("⚠️ Combined discounts > 50% — review assumptions");
  }

  // ── RELIEF FROM ROYALTY ───────────────────────────────────
  if (model === "rfr") {
    if ((f.royalty_rate || 0) <= 0)
      e.push("❌ Royalty rate must be > 0");
    if ((f.base_revenue || 0) <= 0)
      e.push("❌ Base revenue must be > 0");
    if ((f.discount_rate || 0) <= 0)
      e.push("❌ Discount rate must be > 0");
    if (f.royalty_rate > 0.15)
      w.push("💡 Royalty rate > 15% — verify arm's length range");
    if ((f.discount_rate || 0) < (f.royalty_rate || 0))
      w.push("⚠️ Discount rate < royalty rate seems low");
    if ((f.obsolescence_factor || 0) > 0.20)
      w.push("💡 Obsolescence > 20%/yr — asset loses most value within 5 years");
  }

  // ── MPEEM ─────────────────────────────────────────────────
  if (model === "mpe") {
    if ((f.discount_rate || 0) <= 0)
      e.push("❌ Discount rate must be > 0");
    if (f.annual_attrition_rate > 0.25)
      w.push("⚠️ Attrition > 25% — very high churn");
    if ((f.operating_margin || 0) <= 0)
      w.push("💡 Operating margin ≤ 0 — verify contributory asset charges");
  }

  // ── ASSET-BASED ───────────────────────────────────────────
  if (model === "ast") {
    const hasAssets = (f.assets || []).length > 0;
    if (!hasAssets)
      e.push("❌ Add at least one asset before calculating");
    if ((f.deferred_tax_rate || 0) > 0.40)
      w.push("💡 Deferred tax rate > 40% — verify applicable rate");
    const totalFV = (f.assets || []).reduce((s,a) => s + (a.fair_value||0), 0);
    const totalFL = (f.liabilities || []).reduce((s,l) => s + (l.fair_value||l.book_value||0), 0);
    if (totalFL > totalFV)
      w.push("⚠️ Liabilities exceed total assets — negative NAV expected");
  }

  // ── DGI: VP ───────────────────────────────────────────────
  if (model === "vp") {
    const taux_cap = (f.taux_rf - f.taux_inflation) + (f.beta_risque * 0.05);
    if (taux_cap <= 0)
      e.push(`❌ Taux de capitalisation nul ou négatif (${(taux_cap*100).toFixed(2)}%) — vérifier Rf, inflation et β`);
    if ((f.nombre_titres || 0) <= 0)
      w.push("💡 Nombre de titres = 0 — valeur unitaire non calculable");
    if (f.beta_risque > 2)
      w.push("💡 β > 2 est exceptionnel (ex. nouvelles technologies)");
    if ((f.taux_rf || 0) > 0.10)
      w.push("⚠️ Taux OAT > 10% — vérifier la valeur saisie");
  }

  // ── DGI: VM ───────────────────────────────────────────────
  if (model === "vm") {
    if ((f.nombre_titres || 0) <= 0)
      w.push("💡 Nombre de titres = 0 — valeur unitaire non calculable");
    if ((f.capitaux_propres || 0) < 0)
      w.push("⚠️ Capitaux propres négatifs — société en situation de fonds propres insuffisants");
  }

  // ── DGI: VR ───────────────────────────────────────────────
  if (model === "vr") {
    const taux = f.taux_rendement_actions_cotees || 0;
    if (taux <= 0)
      e.push("❌ Taux de rendement des actions cotées doit être > 0");
    if ((f.dividende_n2 === 0) && (f.dividende_n1 === 0) && (f.dividende_n === 0))
      w.push("💡 Dividendes tous nuls — valeur de rendement sera nulle");
    if (taux > 0.10)
      w.push("⚠️ Taux rendement > 10% — vérifier la valeur saisie");
  }

  // ── DGI: MBA ──────────────────────────────────────────────
  if (model === "mba") {
    if ((f.coefficient_mba || 0) <= 0)
      e.push("❌ Coefficient MBA doit être > 0");
    if (f.coefficient_mba < 4 || f.coefficient_mba > 10)
      w.push("💡 Coefficient MBA varie généralement de 4 à 10 selon les investissements requis");
    const mba = (f.benefice_net||0) + (f.dotations_amortissements||0);
    if (mba <= 0)
      w.push("⚠️ MBA ≤ 0 — valeur d'entreprise sera nulle ou négative");
  }

  // ── DGI: EBE/RE ───────────────────────────────────────────
  if (model === "ebe") {
    if ((f.multiple_ebe || 0) <= 0)
      e.push("❌ Multiple EBE doit être > 0");
    if (f.multiple_ebe < 3 || f.multiple_ebe > 15)
      w.push("💡 Multiple EBE typiquement de 4x à 12x selon secteur");
  }
  if (model === "re") {
    if ((f.multiple_re || 0) <= 0)
      e.push("❌ Multiple RE doit être > 0");
    if (f.multiple_re < 5 || f.multiple_re > 20)
      w.push("💡 Multiple RE typiquement de 6x à 15x selon secteur");
  }

  // ── DGI: COMBINAISON ──────────────────────────────────────
  if (model === "comb") {
    if ((f.capitaux_propres || 0) < 0)
      w.push("⚠️ Capitaux propres négatifs — VM sera négative");
    if ((f.nombre_titres || 0) <= 0)
      w.push("💡 Nombre de titres = 0 — valeur unitaire non calculable");
    const taux_cap = (f.taux_rf - f.taux_inflation) + (f.beta_risque * 0.05);
    if (taux_cap <= 0)
      e.push(`❌ Taux de capitalisation nul ou négatif — vérifier Rf, inflation et β`);
  }

  // ── US: Earnings Capitalization ───────────────────────────
  if (model === "us") {
    const cap = (f.rf_rate + f.beta * f.erp + (f.size_premium||0) + (f.specific_premium||0)) - (f.sustainable_growth||0.02);
    if (cap <= 0) e.push("❌ Capitalization rate ≤ 0 — reduce growth or increase discount rate");
    if ((f.ni_n || 0) <= 0) w.push("💡 Year N net income ≤ 0 — normalized earnings may be negative");
    if ((f.beta || 0) > 2.5) w.push("💡 Beta > 2.5 is very high — verify comparable selection");
    if ((f.rf_rate || 0) < 0.01) w.push("⚠️ Risk-free rate < 1% — verify US Treasury rate");
    if ((f.sustainable_growth || 0) > 0.05) w.push("⚠️ Sustainable growth > 5% exceeds long-run GDP — Rev. Ruling 59-60 requires conservative assumptions");
  }

  // ── DE: IDW S1 Ertragswertverfahren ──────────────────────
  if (model === "de") {
    const s = f.abgeltungssteuer || 0.26375;
    const bl = (f.beta_unlevered||1) * (1 + (1-(f.tax_rate||0.25)) * (f.debt_to_equity||0));
    const ke_after = (f.basiszinssatz||0.025) * (1-s) + bl * (f.mrp_after_tax||0.055);
    const g = f.terminal_growth || 0;
    if (ke_after <= g) e.push("❌ Ke (after-tax) ≤ terminal growth — model diverges");
    if ((f.basiszinssatz||0) > 0.08) w.push("💡 Basiszinssatz > 8% is historically very high");
    if ((f.mrp_after_tax||0) > 0.08) w.push("💡 MRP after-tax > 8% — IDW guidance is typically 5–7.5%");
    if ((f.base_earnings||0) <= 0) w.push("⚠️ Base earnings ≤ 0 — Ertragswert may be negative");
  }

  // ── CH: CSI n°28 ──────────────────────────────────────────
  if (model === "ch") {
    if ((f.taux_capitalisation || 0) <= 0) e.push("❌ Le taux de capitalisation doit être > 0");
    if ((f.profit_n || 0) <= 0) w.push("💡 Bénéfice N ≤ 0 — valeur de rendement sera nulle ou négative");
    if ((f.taux_capitalisation || 0) > 0.15) w.push("💡 Taux > 15% est très conservateur selon les barèmes cantonaux");
    if ((f.hidden_reserves || 0) < 0) w.push("⚠️ Réserves latentes négatives (dépréciations non comptabilisées)");
  }

  // ── UK: HMRC P/E Earnings Basis ───────────────────────────
  if (model === "uk") {
    if ((f.sector_pe || 0) <= 0) e.push("❌ Sector P/E must be > 0");
    if ((f.maintainable_earnings || 0) <= 0) e.push("❌ Maintainable earnings must be > 0");
    if ((f.illiquidity_discount || 0) > 0.40) w.push("💡 Illiquidity discount > 40% is very aggressive for SAV purposes");
    const combined = (f.illiquidity_discount||0) + (f.size_discount||0);
    if (combined > 0.50) w.push("⚠️ Combined discounts > 50% — HMRC may challenge");
    if ((f.sector_pe || 0) > 40) w.push("💡 P/E > 40× is very high — verify comparables");
  }

  return { errors: e, warnings: w, blocking: e.length > 0 };
}

// ==========================================================================
// UI COMPONENTS (with tooltips, %, validation)
// ==========================================================================
const g2={display:"grid",gridTemplateColumns:"1fr 1fr",gap:10};
const g3={display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10};
const g4={display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8};

// Tooltip component
const Tip=({text})=>{
  const[show,setShow]=useState(false);
  if(!text)return null;
  return(<span style={{position:"relative",display:"inline-block",marginLeft:4}}>
    <span onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)} style={{cursor:"help",fontSize:10,color:"#475569",background:"rgba(255,255,255,0.06)",borderRadius:10,width:14,height:14,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>?</span>
    {show&&<div style={{position:"absolute",bottom:20,left:-80,width:200,background:"#1e293b",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"8px 10px",fontSize:11,color:"#cbd5e1",zIndex:99,lineHeight:1.4,boxShadow:"0 8px 24px rgba(0,0,0,0.5)"}}>{text}</div>}
  </span>);
};

// Input with % support and tooltips
const I=({lb,v,oc,t="number",s,p,sx,tip,pct,money})=>{
  const [editing,setEditing]=useState(false);
  const [raw,setRaw]=useState("");
  const display = money && !editing ? fMon(v) : pct && typeof v === "number" ? Math.round(v*10000)/100 : v;
  const handle = (e) => {
    const val = e.target.value;
    if(money){setRaw(val);return;}
    if(t==="number"){
      if(val==="")return oc("");
      const n = Number(val);
      oc(pct ? n/100 : n);
    } else oc(val);
  };
  const onBlurH = (e) => {
    e.target.style.borderColor="rgba(255,255,255,0.08)";
    if(money){setEditing(false);const parsed=parseMoney(raw||String(v));if(typeof parsed==="number")oc(parsed);}
  };
  const onFocusH = (e) => {
    e.target.style.borderColor="rgba(59,130,246,0.5)";
    if(money){setEditing(true);setRaw(v===0?"0":fMon(v));}
  };
  return(<div style={{display:"flex",flexDirection:"column",gap:4}}>
    <label style={{fontSize:12,fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",display:"flex",alignItems:"center"}}>{lb}<Tip text={tip||TIPS[lb.toLowerCase().replace(/[^a-z]/g,"_")]}/></label>
    <div style={{position:"relative"}}>
      <input type={money?"text":t} step={s||(pct?"0.1":"any")} placeholder={p||( money?"e.g. 1.5M":"")} value={money?(editing?raw:(display===0?"0":(display||""))):( display===0?0:(display||""))}
        onChange={handle} onFocus={onFocusH} onBlur={onBlurH}
        style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"8px 10px",paddingRight:(sx||pct||money)?32:10,color:"#e2e8f0",fontSize:13,fontFamily:"monospace",outline:"none",boxSizing:"border-box"}}/>
      {(sx||pct||money)&&<span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"#64748b"}}>{pct?"%":money?"€":sx}</span>}
    </div>
  </div>);
};

const Sc=({i,t})=>(<div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,marginTop:20}}><div style={{width:3,height:3,borderRadius:"50%",background:"#475569",flexShrink:0}}/><h3 style={{fontSize:13,fontWeight:700,color:"#94a3b8",margin:0,textTransform:"uppercase",letterSpacing:"0.07em"}}>{t}</h3><div style={{flex:1,height:"1px",background:"rgba(255,255,255,0.05)"}}/></div>);
const Cd=({lb,v,a="#3b82f6"})=>(<div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:11,padding:"14px 16px",position:"relative",overflow:"hidden"}}><div style={{position:"absolute",top:0,left:0,right:0,height:2,background:a}}/><div style={{fontSize:11,color:"#64748b",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>{lb}</div><div style={{fontSize:22,fontWeight:700,color:"#f1f5f9",fontFamily:"monospace"}}>{v}</div></div>);
const TT=({active,payload,label})=>{if(!active||!payload?.length)return null;return(<div style={{background:"#1e293b",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,padding:"6px 10px",fontSize:11}}><div style={{fontWeight:700,color:"#f1f5f9",marginBottom:2}}>{label}</div>{payload.map((p,i)=>(<div key={i} style={{color:p.color,display:"flex",justifyContent:"space-between",gap:10}}><span>{p.name}:</span><span style={{fontFamily:"monospace",fontWeight:600}}>{typeof p.value==="number"&&Math.abs(p.value)<1?fP(p.value):fC(p.value)}</span></div>))}</div>);};
const Bx=({t,children})=>(<div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:11,padding:16}}><div style={{fontSize:12,fontWeight:700,color:"#94a3b8",marginBottom:10,textTransform:"uppercase"}}>{t}</div>{children}</div>);
const Bt=({L,l,onC,onX,disabled})=>{const isDisabled=l||disabled;return(<div style={{display:"flex",gap:8,marginTop:20}}><button onClick={onC} disabled={isDisabled} title={disabled?"Fix errors above to calculate":undefined} style={{flex:1,background:isDisabled?(disabled?"rgba(127,29,29,0.4)":"#1e3a5f"):"linear-gradient(135deg,#2563eb,#3b82f6)",border:isDisabled&&disabled?"1px solid rgba(239,68,68,0.3)":"none",borderRadius:9,padding:"11px",color:isDisabled?"#94a3b8":"#fff",fontSize:13,fontWeight:700,cursor:isDisabled?"not-allowed":"pointer",transition:"all 0.2s"}}>{l?L.calcing:disabled?(L.fix_errors||"Fix errors to calculate"):L.calc}</button><button onClick={onX} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:9,padding:"11px 14px",color:"#64748b",fontSize:12,cursor:"pointer"}}>{L.clr}</button></div>);};
const Er=({e})=>e?<div style={{marginTop:10,background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:7,padding:"7px 10px",fontSize:12,color:"#ef4444"}}>⚠️ {e}</div>:null;
const Pan=({children,hr})=>(<div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:20,maxHeight:hr?"85vh":"none",overflowY:hr?"auto":"visible"}}>{children}</div>);

// Summary box
const Summary=({L,lines,color})=>(<div style={{background:`${color}08`,border:`1px solid ${color}25`,borderRadius:12,padding:"16px 20px",marginBottom:16}}>
  <div style={{fontSize:13,fontWeight:700,color,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>{L.summary}</div>
  {lines.map((ln,i)=>(<div key={i} style={{fontSize:13,color:"#e2e8f0",lineHeight:1.6}}>{ln}</div>))}
</div>);

// Warnings box
const Warns=({L,validation})=>{
  if(!validation)return null;
  const errs = validation.errors||[];
  const warns = validation.warnings||[];
  if(!errs.length&&!warns.length)return null;
  return(<div style={{marginTop:12}}>
    {errs.length>0&&<div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.35)",borderRadius:10,padding:"10px 14px",marginBottom:warns.length?8:0}}>
      <div style={{fontSize:12,fontWeight:700,color:"#ef4444",marginBottom:4}}>⛔ {L.errors||"Errors"}</div>
      {errs.map((e,i)=>(<div key={i} style={{fontSize:12,color:"#fca5a5",lineHeight:1.5}}>{e}</div>))}
    </div>}
    {warns.length>0&&<div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:10,padding:"10px 14px"}}>
      <div style={{fontSize:12,fontWeight:700,color:"#fbbf24",marginBottom:4}}>{L.warn}</div>
      {warns.map((w,i)=>(<div key={i} style={{fontSize:12,color:"#fcd34d",lineHeight:1.5}}>{w}</div>))}
    </div>}
  </div>);
};

// Page transition wrapper
const FadeIn=({children})=>{
  const[vis,setVis]=useState(false);
  useEffect(()=>{setVis(false);const t=setTimeout(()=>setVis(true),50);return()=>clearTimeout(t);},[]);
  return <div style={{opacity:vis?1:0,transform:vis?"translateY(0)":"translateY(12px)",transition:"all 0.35s ease"}}>{children}</div>;
};

// ==========================================================================
// DCF CALCULATOR (with %, tooltips, validation, summary)
// ==========================================================================
const DCF=({L,onResult,initialValues})=>{
  const d={company_name:"",base_revenue:1e6,base_ebit:12e4,base_operating_margin:.12,revenue_growth_initial:.15,revenue_growth_terminal:.03,growth_convergence_year:5,target_operating_margin:.18,margin_convergence_year:5,tax_rate:.25,sales_to_capital_ratio:1.5,cost_of_capital_initial:.1,cost_of_capital_terminal:.09,wacc_convergence_year:5,forecast_years:10,terminal_growth_rate:.02,net_debt:2e5,minority_interests:0,shares_outstanding:1e4};
  const[f,sF]=useState({...d,...(initialValues||{})});const u=k=>v=>sF(p=>({...p,[k]:v}));
  const[sector,setSector]=useState(null);
  const applySector=(k)=>{setSector(k);if(!k)return;const s=SECTORS[k];sF(p=>({...p,base_revenue:s.base_revenue,base_ebit:s.base_ebit,base_operating_margin:s.base_operating_margin,revenue_growth_initial:s.revenue_growth_initial,revenue_growth_terminal:s.revenue_growth_terminal,target_operating_margin:s.target_operating_margin,tax_rate:s.tax_rate,sales_to_capital_ratio:s.sales_to_capital_ratio,cost_of_capital_initial:s.cost_of_capital_initial,cost_of_capital_terminal:s.cost_of_capital_terminal,terminal_growth_rate:s.terminal_growth_rate}));};
  const{r,l,e,run,ref,sR,sE}=useCalc("dcf");
  const validation=validate(f,"dcf");

  const [exportLoading, setExportLoading] = React.useState(false);

  const handleExportDCF = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/dcf/export-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name:              f.company_name || "Company",
          base_revenue:              f.base_revenue,
          base_ebit:                 f.base_ebit || Math.round(f.base_revenue * f.base_operating_margin),
          base_operating_margin:     f.base_operating_margin,
          revenue_growth_initial:    f.revenue_growth_initial,
          revenue_growth_terminal:   f.revenue_growth_terminal,
          growth_convergence_year:   f.growth_convergence_year,
          target_operating_margin:   f.target_operating_margin,
          margin_convergence_year:   f.margin_convergence_year,
          tax_rate:                  f.tax_rate,
          sales_to_capital_ratio:    f.sales_to_capital_ratio,
          cost_of_capital_initial:   f.cost_of_capital_initial,
          cost_of_capital_terminal:  f.cost_of_capital_terminal,
          wacc_convergence_year:     f.wacc_convergence_year,
          forecast_years:            f.forecast_years,
          terminal_growth_rate:      f.terminal_growth_rate,
          net_debt:                  f.net_debt,
          minority_interests:        f.minority_interests || 0,
          shares_outstanding:        f.shares_outstanding,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || L.err_server);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Company").replace(/\s+/g, "_");
      a.href = url;
      a.download = `Valoris_DCF_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message + "\n\nVérifiez que le serveur backend tourne (uvicorn main:app --reload)");
    } finally {
      setExportLoading(false);
    }
  };
  return(<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"380px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <SectorPicker onSelect={applySector} current={sector}/>
      <Sc i="🏢" t="Company"/><I lb="Company Name" v={f.company_name} oc={u("company_name")} t="text" p="TechCorp" tip={TIPS.company_name}/>
      <Sc i="📋" t="Base Metrics"/><div style={g2}><I lb="Base Revenue" v={f.base_revenue} oc={u("base_revenue")} money tip={TIPS.base_revenue}/><I lb="Base EBIT" v={f.base_ebit} oc={u("base_ebit")} money tip={TIPS.base_ebit}/></div><div style={{marginTop:10}}><I lb="Operating Margin" v={f.base_operating_margin} oc={u("base_operating_margin")} pct tip={TIPS.base_operating_margin}/></div>
      <Sc i="🚀" t="Growth"/><div style={g3}><I lb="Initial Growth" v={f.revenue_growth_initial} oc={u("revenue_growth_initial")} pct tip={TIPS.revenue_growth_initial}/><I lb="Terminal Growth" v={f.revenue_growth_terminal} oc={u("revenue_growth_terminal")} pct tip={TIPS.revenue_growth_terminal}/><I lb="Conv. Year" v={f.growth_convergence_year} oc={u("growth_convergence_year")} tip={TIPS.growth_convergence_year}/></div>
      <Sc i="📐" t="Margins"/><div style={g2}><I lb="Target Margin" v={f.target_operating_margin} oc={u("target_operating_margin")} pct tip={TIPS.target_operating_margin}/><I lb="Conv. Year" v={f.margin_convergence_year} oc={u("margin_convergence_year")} tip={TIPS.margin_convergence_year}/></div><div style={{marginTop:10}}><I lb="Tax Rate" v={f.tax_rate} oc={u("tax_rate")} pct tip={TIPS.tax_rate}/></div>
      <WACCBuilder onWaccChange={(v)=>{sF(p=>({...p,cost_of_capital_initial:v}))}} onWaccTerminalChange={(v)=>{sF(p=>({...p,cost_of_capital_terminal:v}))}} L={L}/>
      <Sc i="💰" t="Capital"/><div style={g2}><I lb="Sales/Capital" v={f.sales_to_capital_ratio} oc={u("sales_to_capital_ratio")} s="0.1" tip={TIPS.sales_to_capital_ratio}/><I lb="Initial WACC" v={f.cost_of_capital_initial} oc={u("cost_of_capital_initial")} pct tip={TIPS.cost_of_capital_initial}/></div><div style={{...g2,marginTop:10}}><I lb="Terminal WACC" v={f.cost_of_capital_terminal} oc={u("cost_of_capital_terminal")} pct tip={TIPS.cost_of_capital_terminal}/><I lb="WACC Conv." v={f.wacc_convergence_year} oc={u("wacc_convergence_year")} tip={TIPS.wacc_convergence_year}/></div>
      <Sc i="🔮" t="Forecast"/><div style={g2}><I lb="Forecast Years" v={f.forecast_years} oc={u("forecast_years")} tip={TIPS.forecast_years}/><I lb="Terminal Growth" v={f.terminal_growth_rate} oc={u("terminal_growth_rate")} pct tip={TIPS.terminal_growth_rate}/></div>
      <Sc i="⚙️" t="Adjustments"/><div style={g3}><I lb="Net Debt" v={f.net_debt} oc={u("net_debt")} money tip={TIPS.net_debt}/><I lb="Minority Int." v={f.minority_interests} oc={u("minority_interests")} money tip={TIPS.minority_interests}/><I lb="Shares" v={f.shares_outstanding} oc={u("shares_outstanding")} tip={TIPS.shares_outstanding}/></div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f,company_name:f.company_name||"Company"},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/><Er e={e}/>
    </Pan>
    {r&&<div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <MethodPanel model="dcf"/>
      <Summary L={L} color="#3b82f6" lines={[
        `🏢 ${r.company_name||"Company"} — DCF Valuation`,
        `💰 Equity Value: ${fC(r.equity_value)}€ | Enterprise Value: ${fC(r.enterprise_value)}€`,
        r.value_per_share?`📊 Value per Share: ${fmt(r.value_per_share,2)}€`:"",
        `📈 Terminal value represents ${fP(r.terminal_value_pct)} of total value`,
      ].filter(Boolean)}/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExportDCF} disabled={exportLoading} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading?"rgba(16,185,129,0.06)":"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.35)",borderRadius:8,color:"#10b981",cursor:exportLoading?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s",opacity:exportLoading?0.7:1}}
          onMouseEnter={ev=>{if(!exportLoading){ev.currentTarget.style.background="rgba(16,185,129,0.22)";ev.currentTarget.style.borderColor="rgba(16,185,129,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading?"rgba(16,185,129,0.06)":"rgba(16,185,129,0.12)";ev.currentTarget.style.borderColor="rgba(16,185,129,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading ? "Génération en cours…" : "Exporter le modèle DCF (.xlsx)"}
        </button>
      </div>
      <div style={g3}><Cd lb={L.eqV} v={`${fC(r.equity_value)}€`} a="#3b82f6"/><Cd lb={L.evV} v={`${fC(r.enterprise_value)}€`} a="#8b5cf6"/><Cd lb={L.vps} v={r.value_per_share?`${fmt(r.value_per_share,2)}€`:"—"} a="#10b981"/></div>
      <div style={g4}><Cd lb={L.tv} v={`${fC(r.terminal_value)}€`} a="#f59e0b"/><Cd lb={L.spv} v={`${fC(r.sum_pv_fcff)}€`} a="#06b6d4"/><Cd lb={L.em} v={`${fmt(r.implied_exit_multiple,1)}x`} a="#ec4899"/><Cd lb={L.tp} v={fP(r.terminal_value_pct)} a="#f97316"/></div>
      <div style={g2}><Bx t="Revenue"><ResponsiveContainer width="100%" height={170}><AreaChart data={r.projections}><defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><Tooltip content={<TT/>}/><Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fill="url(#rg)" name="Revenue"/></AreaChart></ResponsiveContainer></Bx><Bx t="FCFF"><ResponsiveContainer width="100%" height={170}><BarChart data={r.projections}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><Tooltip content={<TT/>}/><Bar dataKey="fcff" name="FCFF" radius={[3,3,0,0]}>{r.projections.map((p,i)=><Cell key={i} fill={p.fcff>=0?"#10b981":"#ef4444"} fillOpacity={.7}/>)}</Bar></BarChart></ResponsiveContainer></Bx></div>
      <Bx t="ROIC vs WACC"><ResponsiveContainer width="100%" height={160}><LineChart data={r.projections}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={v=>`${(v*100).toFixed(0)}%`}/><Tooltip content={<TT/>}/><Line type="monotone" dataKey="roic" stroke="#10b981" strokeWidth={2} dot={{r:2}} name="ROIC"/><Line type="monotone" dataKey="wacc" stroke="#f59e0b" strokeWidth={2} dot={{r:2}} name="WACC" strokeDasharray="5 5"/></LineChart></ResponsiveContainer></Bx>
      <Bx t={L.proj}><div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"monospace"}}><thead><tr style={{borderBottom:"2px solid rgba(255,255,255,0.08)"}}>{["Yr","Rev","Grw","Mgn","NOPAT","FCFF","ROIC","WACC","PV"].map(h=><th key={h} style={{padding:"6px 4px",textAlign:"right",color:"#64748b",fontWeight:600,fontSize:10}}>{h}</th>)}</tr></thead><tbody>{r.projections.map((p,i)=><tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}><td style={{padding:"5px 4px",textAlign:"right",color:"#94a3b8",fontWeight:700}}>{p.year}</td><td style={{padding:"5px 4px",textAlign:"right",color:"#e2e8f0"}}>{fC(p.revenue)}</td><td style={{padding:"5px 4px",textAlign:"right",color:p.revenue_growth_rate>.1?"#10b981":"#94a3b8"}}>{fP(p.revenue_growth_rate)}</td><td style={{padding:"5px 4px",textAlign:"right",color:"#94a3b8"}}>{fP(p.operating_margin)}</td><td style={{padding:"5px 4px",textAlign:"right",color:"#e2e8f0"}}>{fC(p.nopat)}</td><td style={{padding:"5px 4px",textAlign:"right",color:p.fcff>=0?"#10b981":"#ef4444"}}>{fC(p.fcff)}</td><td style={{padding:"5px 4px",textAlign:"right",color:p.roic>p.wacc?"#10b981":"#f59e0b"}}>{fP(p.roic)}</td><td style={{padding:"5px 4px",textAlign:"right",color:"#94a3b8"}}>{fP(p.wacc)}</td><td style={{padding:"5px 4px",textAlign:"right",color:"#e2e8f0"}}>{fC(p.pv_fcff)}</td></tr>)}</tbody></table></div></Bx>
      <TVPanel r={r} f={f} L={L}/>
      <SensPanel baseParams={f} model="dcf" L={L}/>
      <ScenPanel baseParams={f} model="dcf" L={L}/>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================
// EXCESS RETURN (with improvements)
// ==========================================================================
const EXC=({L,onResult,initialValues})=>{
  const d={company_name:"",base_revenue:1e6,base_invested_capital:666667,revenue_growth_initial:.15,revenue_growth_terminal:.03,growth_convergence_year:5,roic_initial:.135,roic_terminal:.15,roic_convergence_year:5,cost_of_capital:.1,cost_of_capital_terminal:.09,wacc_convergence_year:5,reinvestment_rate_initial:.55,reinvestment_rate_terminal:.2,reinvestment_convergence_year:5,forecast_years:10,terminal_growth_rate:.02,net_debt:2e5,minority_interests:0,shares_outstanding:1e4};
  const[f,sF]=useState({...d,...(initialValues||{})});const u=k=>v=>sF(p=>({...p,[k]:v}));
  const[sector,setSector]=useState(null);
  const applySector=(k)=>{setSector(k);if(!k)return;const s=SECTORS[k];sF(p=>({...p,base_revenue:s.base_revenue,base_invested_capital:s.base_invested_capital||s.base_revenue/1.5,revenue_growth_initial:s.revenue_growth_initial,revenue_growth_terminal:s.revenue_growth_terminal,roic_initial:s.roic_initial,roic_terminal:s.roic_terminal,cost_of_capital:s.cost_of_capital_initial,cost_of_capital_terminal:s.cost_of_capital_terminal,terminal_growth_rate:s.terminal_growth_rate}));};
  const{r,l,e,run,ref,sR,sE}=useCalc("exc"); const validation=validate(f,"exc");
  const [exportLoadingExc, setExportLoadingExc] = React.useState(false);
  const handleExportExc = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoadingExc(true);
    try {
      const response = await fetch(`${API_BASE}/api/excess-return/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name: f.company_name||"Company",
          base_revenue: f.base_revenue, base_invested_capital: f.base_invested_capital,
          revenue_growth_initial: f.revenue_growth_initial, revenue_growth_terminal: f.revenue_growth_terminal,
          growth_convergence_year: f.growth_convergence_year,
          roic_initial: f.roic_initial, roic_terminal: f.roic_terminal, roic_convergence_year: f.roic_convergence_year,
          cost_of_capital: f.cost_of_capital, cost_of_capital_terminal: f.cost_of_capital_terminal,
          wacc_convergence_year: f.wacc_convergence_year,
          reinvestment_rate_initial: f.reinvestment_rate_initial, reinvestment_rate_terminal: f.reinvestment_rate_terminal,
          reinvestment_convergence_year: f.reinvestment_convergence_year,
          forecast_years: f.forecast_years, terminal_growth_rate: f.terminal_growth_rate,
          net_debt: f.net_debt, minority_interests: f.minority_interests||0, shares_outstanding: f.shares_outstanding,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Company").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_ExcessReturn_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoadingExc(false); }
  };
  return(<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"380px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <SectorPicker onSelect={applySector} current={sector}/>
      <Sc i="🏢" t="Company"/><I lb="Company Name" v={f.company_name} oc={u("company_name")} t="text" tip={TIPS.company_name}/>
      <Sc i="📋" t="Base"/><div style={g2}><I lb="Base Revenue" v={f.base_revenue} oc={u("base_revenue")} money tip={TIPS.base_revenue}/><I lb="Invested Capital" v={f.base_invested_capital} oc={u("base_invested_capital")} money tip={TIPS.base_invested_capital}/></div>
      <Sc i="📈" t="ROIC"/><div style={g3}><I lb="Initial ROIC" v={f.roic_initial} oc={u("roic_initial")} pct tip={TIPS.roic_initial}/><I lb="Terminal ROIC" v={f.roic_terminal} oc={u("roic_terminal")} pct tip={TIPS.roic_terminal}/><I lb="Conv. Yr" v={f.roic_convergence_year} oc={u("roic_convergence_year")}/></div>
      <Sc i="🚀" t="Growth"/><div style={g3}><I lb="Initial Growth" v={f.revenue_growth_initial} oc={u("revenue_growth_initial")} pct/><I lb="Terminal Growth" v={f.revenue_growth_terminal} oc={u("revenue_growth_terminal")} pct/><I lb="Conv." v={f.growth_convergence_year} oc={u("growth_convergence_year")}/></div>
      <Sc i="💰" t="WACC"/><div style={g3}><I lb="Initial WACC" v={f.cost_of_capital} oc={u("cost_of_capital")} pct tip={TIPS.cost_of_capital}/><I lb="Terminal WACC" v={f.cost_of_capital_terminal} oc={u("cost_of_capital_terminal")} pct/><I lb="Conv." v={f.wacc_convergence_year} oc={u("wacc_convergence_year")}/></div>
      <Sc i="🔄" t="Reinvestment"/><div style={g3}><I lb="Initial Reinv." v={f.reinvestment_rate_initial} oc={u("reinvestment_rate_initial")} pct tip={TIPS.reinvestment_rate_initial}/><I lb="Terminal Reinv." v={f.reinvestment_rate_terminal} oc={u("reinvestment_rate_terminal")} pct/><I lb="Conv." v={f.reinvestment_convergence_year} oc={u("reinvestment_convergence_year")}/></div>
      <Sc i="⚙️" t="Adjustments"/><div style={g2}><I lb="Forecast Yrs" v={f.forecast_years} oc={u("forecast_years")} tip={TIPS.forecast_years}/><I lb="Terminal Growth" v={f.terminal_growth_rate} oc={u("terminal_growth_rate")} pct/></div><div style={{...g3,marginTop:10}}><I lb="Net Debt" v={f.net_debt} oc={u("net_debt")} money/><I lb="Minority" v={f.minority_interests} oc={u("minority_interests")} money/><I lb="Shares" v={f.shares_outstanding} oc={u("shares_outstanding")}/></div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f,company_name:f.company_name||"Company"},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/><Er e={e}/>
    </Pan>
    {r&&<div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <MethodPanel model="exc"/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExportExc} disabled={exportLoadingExc} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoadingExc?"rgba(139,92,246,0.06)":"rgba(139,92,246,0.12)",border:"1px solid rgba(139,92,246,0.35)",borderRadius:8,color:"#a78bfa",cursor:exportLoadingExc?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoadingExc){ev.currentTarget.style.background="rgba(139,92,246,0.22)";ev.currentTarget.style.borderColor="rgba(139,92,246,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoadingExc?"rgba(139,92,246,0.06)":"rgba(139,92,246,0.12)";ev.currentTarget.style.borderColor="rgba(139,92,246,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoadingExc ? "Génération en cours…" : "Exporter Excess Return (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#8b5cf6" lines={[`🏢 ${f.company_name||"Company"} — Excess Return`,`💰 Equity: ${fC(r.equity_value)}€ | EV: ${fC(r.enterprise_value)}€`,`📈 Avg ROIC-WACC spread: ${fP(r.avg_roic_wacc_spread)} → ${r.avg_roic_wacc_spread>0?"Value creation":"Value destruction"}`]}/>
      <div style={g3}><Cd lb={L.eqV} v={`${fC(r.equity_value)}€`} a="#8b5cf6"/><Cd lb={L.evV} v={`${fC(r.enterprise_value)}€`} a="#3b82f6"/><Cd lb={L.vps} v={r.value_per_share?`${fmt(r.value_per_share,2)}€`:"—"} a="#10b981"/></div>
      <div style={g3}><Cd lb="Inv.Capital" v={`${fC(r.base_invested_capital)}€`} a="#f59e0b"/><Cd lb="Value Created" v={`${fC(r.total_value_created)}€`} a="#06b6d4"/><Cd lb="Avg Spread" v={fP(r.avg_roic_wacc_spread)} a="#ec4899"/></div>
      <div style={g2}><Bx t="Excess Return"><ResponsiveContainer width="100%" height={170}><BarChart data={r.projections}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><Tooltip content={<TT/>}/><Bar dataKey="excess_return" name="Excess" radius={[3,3,0,0]}>{r.projections.map((p,i)=><Cell key={i} fill={p.excess_return>=0?"#8b5cf6":"#ef4444"} fillOpacity={.7}/>)}</Bar></BarChart></ResponsiveContainer></Bx><Bx t="ROIC vs WACC"><ResponsiveContainer width="100%" height={170}><LineChart data={r.projections}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={v=>`${(v*100).toFixed(0)}%`}/><Tooltip content={<TT/>}/><Line type="monotone" dataKey="roic" stroke="#10b981" strokeWidth={2} dot={{r:2}} name="ROIC"/><Line type="monotone" dataKey="wacc" stroke="#f59e0b" strokeWidth={2} dot={{r:2}} name="WACC" strokeDasharray="5 5"/></LineChart></ResponsiveContainer></Bx></div>
      <SensPanel baseParams={f} model="exc" L={L}/>
      <ScenPanel baseParams={f} model="exc" L={L}/>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================
// MULTIPLES (with improvements)
// ==========================================================================
const MUL=({L,onResult,initialValues})=>{
  const d={company_name:"",revenue:1e6,ebitda:18e4,ebit:12e4,net_income:85e3,book_value_equity:4e5,comparables:[{name:"Peer A",ev_revenue:2.5,ev_ebitda:12,ev_ebit:16,pe_ratio:22,pb_ratio:3.5,weight:1},{name:"Peer B",ev_revenue:3,ev_ebitda:14,ev_ebit:18,pe_ratio:25,pb_ratio:4,weight:1},{name:"Peer C",ev_revenue:2,ev_ebitda:10,ev_ebit:13,pe_ratio:18,pb_ratio:2.8,weight:.8}],illiquidity_discount:.15,control_premium:0,size_discount:.1,net_debt:2e5,minority_interests:0,shares_outstanding:1e4};
  const[f,sF]=useState({...d,...(initialValues||{})});const u=k=>v=>sF(p=>({...p,[k]:v}));const uC=(i,k)=>v=>sF(p=>{const c=[...p.comparables];c[i]={...c[i],[k]:v};return{...p,comparables:c};});
  const[sector,setSector]=useState(null);
  const applySector=(k)=>{setSector(k);if(!k)return;const s=SECTORS[k];sF(p=>({...p,revenue:s.base_revenue,ebitda:s.ebitda,ebit:s.base_ebit||s.ebit,net_income:s.net_income,book_value_equity:s.book_value_equity}));};
  const{r,l,e,run,ref,sR,sE}=useCalc("mul"); const validation=validate(f,"mul");
  const [exportLoadingMul, setExportLoadingMul] = React.useState(false);

  const handleExportMul = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoadingMul(true);
    try {
      const response = await fetch(`${API_BASE}/api/multiples/export-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name:         f.company_name || "Company",
          revenue:              f.revenue,
          ebitda:               f.ebitda,
          ebit:                 f.ebit,
          net_income:           f.net_income,
          book_value_equity:    f.book_value_equity,
          comparables:          f.comparables.map(c => ({
            name:        c.name,
            ev_revenue:  c.ev_revenue  ?? null,
            ev_ebitda:   c.ev_ebitda   ?? null,
            ev_ebit:     c.ev_ebit     ?? null,
            pe_ratio:    c.pe_ratio    ?? null,
            pb_ratio:    c.pb_ratio    ?? null,
            weight:      c.weight      ?? 1,
          })),
          illiquidity_discount: f.illiquidity_discount,
          control_premium:      f.control_premium,
          size_discount:        f.size_discount,
          net_debt:             f.net_debt,
          minority_interests:   f.minority_interests || 0,
          shares_outstanding:   f.shares_outstanding,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || L.err_server);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Company").replace(/\s+/g, "_");
      a.href = url;
      a.download = `Valoris_Multiples_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message + "\n\nVérifiez que le serveur backend tourne (uvicorn main:app --reload)");
    } finally {
      setExportLoadingMul(false);
    }
  };

  return(<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"420px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <SectorPicker onSelect={applySector} current={sector}/>
      <Sc i="🏢" t="Financials"/><I lb="Company Name" v={f.company_name} oc={u("company_name")} t="text"/>
      <div style={{...g3,marginTop:10}}><I lb="Revenue" v={f.revenue} oc={u("revenue")} money/><I lb="EBITDA" v={f.ebitda} oc={u("ebitda")} money/><I lb="EBIT" v={f.ebit} oc={u("ebit")} money/></div>
      <div style={{...g2,marginTop:10}}><I lb="Net Income" v={f.net_income} oc={u("net_income")} money/><I lb="Book Equity" v={f.book_value_equity} oc={u("book_value_equity")} money/></div>
      <Sc i="⚖️" t="Comparables"/>
      {f.comparables.map((c,i)=>(<div key={i} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:9,padding:10,marginBottom:7}}><div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:6}}><I lb={`Peer ${i+1}`} v={c.name} oc={uC(i,"name")} t="text"/>{f.comparables.length>2&&<button onClick={()=>sF(p=>({...p,comparables:p.comparables.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14,padding:4}}>×</button>}</div><div style={g3}><I lb="EV/Rev" v={c.ev_revenue} oc={uC(i,"ev_revenue")}/><I lb="EV/EBITDA" v={c.ev_ebitda} oc={uC(i,"ev_ebitda")}/><I lb="EV/EBIT" v={c.ev_ebit} oc={uC(i,"ev_ebit")}/></div><div style={{...g3,marginTop:6}}><I lb="P/E" v={c.pe_ratio} oc={uC(i,"pe_ratio")}/><I lb="P/B" v={c.pb_ratio} oc={uC(i,"pb_ratio")}/><I lb="Weight" v={c.weight} oc={uC(i,"weight")} s="0.1"/></div></div>))}
      <button onClick={()=>sF(p=>({...p,comparables:[...p.comparables,{name:`Peer ${String.fromCharCode(65+p.comparables.length)}`,ev_revenue:null,ev_ebitda:null,ev_ebit:null,pe_ratio:null,pb_ratio:null,weight:1}]}))} style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px dashed rgba(255,255,255,0.1)",borderRadius:7,padding:8,color:"#64748b",fontSize:12,cursor:"pointer",marginBottom:6}}>+ Add Comparable</button>
      <Sc i="📉" t="Adjustments"/><div style={g3}><I lb="Illiquidity" v={f.illiquidity_discount} oc={u("illiquidity_discount")} pct tip={TIPS.illiquidity_discount}/><I lb="Control Prem." v={f.control_premium} oc={u("control_premium")} pct tip={TIPS.control_premium}/><I lb="Size Disc." v={f.size_discount} oc={u("size_discount")} pct tip={TIPS.size_discount}/></div><div style={{...g3,marginTop:10}}><I lb="Net Debt" v={f.net_debt} oc={u("net_debt")} money/><I lb="Minority" v={f.minority_interests} oc={u("minority_interests")} money/><I lb="Shares" v={f.shares_outstanding} oc={u("shares_outstanding")}/></div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f,company_name:f.company_name||"Company"},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/><Er e={e}/>
    </Pan>
    {r&&<div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <MethodPanel model="mul"/>
      <Summary L={L} color="#10b981" lines={[`⚖️ ${f.company_name||"Company"} — Comparable Multiples`,`💰 Equity range: ${fC(r.equity_value_low)}€ — ${fC(r.equity_value_high)}€`,`📊 Midpoint: ${fC(r.equity_value_mid)}€`]}/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExportMul} disabled={exportLoadingMul} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoadingMul?"rgba(16,185,129,0.06)":"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.35)",borderRadius:8,color:"#10b981",cursor:exportLoadingMul?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s",opacity:exportLoadingMul?0.7:1}}
          onMouseEnter={ev=>{if(!exportLoadingMul){ev.currentTarget.style.background="rgba(16,185,129,0.22)";ev.currentTarget.style.borderColor="rgba(16,185,129,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoadingMul?"rgba(16,185,129,0.06)":"rgba(16,185,129,0.12)";ev.currentTarget.style.borderColor="rgba(16,185,129,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoadingMul ? "Génération en cours…" : "Exporter le modèle Multiples (.xlsx)"}
        </button>
      </div>
      <div style={g3}><Cd lb="Equity Low" v={`${fC(r.equity_value_low)}€`} a="#f59e0b"/><Cd lb="Equity Mid" v={`${fC(r.equity_value_mid)}€`} a="#10b981"/><Cd lb="Equity High" v={`${fC(r.equity_value_high)}€`} a="#3b82f6"/></div>
      <Bx t="EV by Multiple"><ResponsiveContainer width="100%" height={200}><BarChart data={r.multiple_results} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis type="number" stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><YAxis type="category" dataKey="multiple_type" stroke="#475569" tick={{fontSize:10}} width={70}/><Tooltip content={<TT/>}/><Bar dataKey="enterprise_value" name="EV" fill="#8b5cf6" fillOpacity={.7} radius={[0,4,4,0]}/></BarChart></ResponsiveContainer></Bx>
      <SensPanel baseParams={f} model="mul" L={L}/>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================
// RFR + MPEEM + ASSET (with improvements)
// ==========================================================================
const RFR=({L,onResult,initialValues})=>{
  const d={asset_name:"",asset_type:"brand",base_revenue:1e6,revenue_growth_initial:.1,revenue_growth_terminal:.03,growth_convergence_year:5,royalty_rate:.05,tax_rate:.25,discount_rate:.13,forecast_years:10,obsolescence_factor:0,tax_amortization_years:10};
  const[f,sF]=useState({...d,...(initialValues||{})});const u=k=>v=>sF(p=>({...p,[k]:v}));
  const[sector,setSector]=useState(null);
  const applySector=(k)=>{setSector(k);if(!k)return;const s=SECTORS[k];sF(p=>({...p,base_revenue:s.base_revenue,revenue_growth_initial:s.revenue_growth_initial,revenue_growth_terminal:s.revenue_growth_terminal,royalty_rate:s.royalty_rate,tax_rate:s.tax_rate,discount_rate:s.discount_rate}));};
  const{r,l,e,run,ref,sR,sE}=useCalc("rfr"); const validation=validate(f,"rfr");
  const [exportLoadingRfR, setExportLoadingRfR] = React.useState(false);
  const handleExportRfR = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoadingRfR(true);
    try {
      const response = await fetch(`${API_BASE}/api/rfr/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          asset_name: f.asset_name||"Brand", asset_type: f.asset_type||"brand",
          base_revenue: f.base_revenue, revenue_growth_initial: f.revenue_growth_initial,
          revenue_growth_terminal: f.revenue_growth_terminal, growth_convergence_year: f.growth_convergence_year,
          royalty_rate: f.royalty_rate, tax_rate: f.tax_rate, discount_rate: f.discount_rate,
          forecast_years: f.forecast_years, obsolescence_factor: f.obsolescence_factor||0,
          tax_amortization_years: f.tax_amortization_years||10,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const nm = (f.asset_name || "Brand").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_RfR_${nm}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoadingRfR(false); }
  };
  return(<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"380px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <SectorPicker onSelect={applySector} current={sector}/>
      <Sc i="™️" t="Asset"/><div style={g2}><I lb="Asset Name" v={f.asset_name} oc={u("asset_name")} t="text" p="Brand XYZ"/><I lb="Asset Type" v={f.asset_type} oc={u("asset_type")} t="text"/></div>
      <Sc i="📋" t="Revenue"/><div style={g3}><I lb="Revenue" v={f.base_revenue} oc={u("base_revenue")} money/><I lb="Init. Growth" v={f.revenue_growth_initial} oc={u("revenue_growth_initial")} pct/><I lb="Term. Growth" v={f.revenue_growth_terminal} oc={u("revenue_growth_terminal")} pct/></div><div style={{marginTop:10}}><I lb="Conv. Year" v={f.growth_convergence_year} oc={u("growth_convergence_year")}/></div>
      <DiscountBuilder onRateChange={(v)=>{sF(p=>({...p,discount_rate:v}))}} currentRate={f.discount_rate}/>
      <Sc i="💎" t="Rates"/><div style={g3}><I lb="Royalty Rate" v={f.royalty_rate} oc={u("royalty_rate")} pct tip={TIPS.royalty_rate}/><I lb="Tax Rate" v={f.tax_rate} oc={u("tax_rate")} pct/><I lb="Discount Rate" v={f.discount_rate} oc={u("discount_rate")} pct tip={TIPS.discount_rate}/></div>
      <Sc i="⚙️" t="Parameters"/><div style={g3}><I lb="Forecast Yrs" v={f.forecast_years} oc={u("forecast_years")}/><I lb="Obsolescence" v={f.obsolescence_factor} oc={u("obsolescence_factor")} pct tip={TIPS.obsolescence_factor}/><I lb="Tax Amort." v={f.tax_amortization_years} oc={u("tax_amortization_years")} tip={TIPS.tax_amortization_years}/></div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f,asset_name:f.asset_name||"Brand"},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/><Er e={e}/>
    </Pan>
    {r&&<div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <MethodPanel model="rfr"/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExportRfR} disabled={exportLoadingRfR} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoadingRfR?"rgba(245,158,11,0.06)":"rgba(245,158,11,0.12)",border:"1px solid rgba(245,158,11,0.35)",borderRadius:8,color:"#fbbf24",cursor:exportLoadingRfR?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoadingRfR){ev.currentTarget.style.background="rgba(245,158,11,0.22)";ev.currentTarget.style.borderColor="rgba(245,158,11,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoadingRfR?"rgba(245,158,11,0.06)":"rgba(245,158,11,0.12)";ev.currentTarget.style.borderColor="rgba(245,158,11,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoadingRfR ? "Génération en cours…" : "Exporter RfR (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#f59e0b" lines={[`™️ ${f.asset_name||"Brand"} — Relief from Royalty`,`💰 Fair Value: ${fC(r.fair_value)}€`,`📊 Implied multiple: ${fmt(r.implied_royalty_multiple,1)}x annual royalty`]}/>
      <div style={g3}><Cd lb={L.fv} v={`${fC(r.fair_value)}€`} a="#f59e0b"/><Cd lb="PV Royalties" v={`${fC(r.sum_pv_royalties)}€`} a="#3b82f6"/><Cd lb="TAB" v={`${fC(r.tax_amortization_benefit)}€`} a="#10b981"/></div>
      <Bx t="Royalty Savings"><ResponsiveContainer width="100%" height={170}><AreaChart data={r.projections}><defs><linearGradient id="ryG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={.3}/><stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><Tooltip content={<TT/>}/><Area type="monotone" dataKey="adjusted_royalty" stroke="#f59e0b" strokeWidth={2} fill="url(#ryG)" name="Royalty"/></AreaChart></ResponsiveContainer></Bx>
      {r.sensitivity&&<Bx t="Royalty Rate Sensitivity"><ResponsiveContainer width="100%" height={150}><BarChart data={r.sensitivity}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="label" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><Tooltip content={<TT/>}/><Bar dataKey="fair_value" name="Value" fill="#f59e0b" fillOpacity={.7} radius={[3,3,0,0]}/></BarChart></ResponsiveContainer></Bx>}
      <SensPanel baseParams={f} model="rfr" L={L}/>
      <ScenPanel baseParams={f} model="rfr" L={L}/>
    </div>}
  </div></FadeIn>);
};

const MPE=({L,onResult,initialValues})=>{
  const d={asset_name:"",asset_type:"customer_relationships",base_revenue:8e5,revenue_growth_initial:.08,revenue_growth_terminal:.02,growth_convergence_year:5,operating_margin:.2,tax_rate:.25,contributory_assets:[{name:"Working Capital",value:15e4,charge_rate:.04},{name:"Fixed Assets",value:2e5,charge_rate:.08},{name:"Workforce",value:1e5,charge_rate:.12}],discount_rate:.14,forecast_years:10,annual_attrition_rate:.08,tax_amortization_years:7};
  const[f,sF]=useState({...d,...(initialValues||{})});const u=k=>v=>sF(p=>({...p,[k]:v}));const uCA=(i,k)=>v=>sF(p=>{const c=[...p.contributory_assets];c[i]={...c[i],[k]:v};return{...p,contributory_assets:c};});
  const[sector,setSector]=useState(null);
  const applySector=(k)=>{setSector(k);if(!k)return;const s=SECTORS[k];sF(p=>({...p,base_revenue:s.base_revenue*.8,revenue_growth_initial:s.revenue_growth_initial,revenue_growth_terminal:s.revenue_growth_terminal,operating_margin:s.operating_margin,tax_rate:s.tax_rate,discount_rate:s.discount_rate}));};
  const{r,l,e,run,ref,sR,sE}=useCalc("mpe"); const validation=validate(f,"mpe");
  const [exportLoadingMPE, setExportLoadingMPE] = React.useState(false);
  const handleExportMPE = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoadingMPE(true);
    try {
      const response = await fetch(`${API_BASE}/api/mpeem/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          asset_name: f.asset_name||"Customer Relationships", asset_type: f.asset_type||"customer_relationships",
          base_revenue: f.base_revenue, revenue_growth_initial: f.revenue_growth_initial,
          revenue_growth_terminal: f.revenue_growth_terminal, growth_convergence_year: f.growth_convergence_year,
          operating_margin: f.operating_margin, tax_rate: f.tax_rate, discount_rate: f.discount_rate,
          forecast_years: f.forecast_years, annual_attrition_rate: f.annual_attrition_rate,
          tax_amortization_years: f.tax_amortization_years||7,
          contributory_assets: f.contributory_assets.map(ca=>({name:ca.name,value:ca.value,charge_rate:ca.charge_rate})),
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const nm = (f.asset_name || "Asset").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_MPEEM_${nm}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoadingMPE(false); }
  };  return(<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"400px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <SectorPicker onSelect={applySector} current={sector}/>
      <Sc i="🤝" t="Asset"/><div style={g2}><I lb="Asset Name" v={f.asset_name} oc={u("asset_name")} t="text" p="Client Relationships"/><I lb="Type" v={f.asset_type} oc={u("asset_type")} t="text"/></div>
      <Sc i="📋" t="Revenue"/><div style={g3}><I lb="Revenue" v={f.base_revenue} oc={u("base_revenue")} money/><I lb="Op. Margin" v={f.operating_margin} oc={u("operating_margin")} pct tip={TIPS.operating_margin}/><I lb="Tax Rate" v={f.tax_rate} oc={u("tax_rate")} pct/></div>
      <Sc i="🚀" t="Growth"/><div style={g3}><I lb="Initial" v={f.revenue_growth_initial} oc={u("revenue_growth_initial")} pct/><I lb="Terminal" v={f.revenue_growth_terminal} oc={u("revenue_growth_terminal")} pct/><I lb="Conv." v={f.growth_convergence_year} oc={u("growth_convergence_year")}/></div>
      <Sc i="🏗️" t="Contributory Assets"/>
      {f.contributory_assets.map((ca,i)=>(<div key={i} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:9,padding:10,marginBottom:7}}><div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:6}}><I lb={`Asset ${i+1}`} v={ca.name} oc={uCA(i,"name")} t="text"/>{f.contributory_assets.length>1&&<button onClick={()=>sF(p=>({...p,contributory_assets:p.contributory_assets.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14,padding:4}}>×</button>}</div><div style={g2}><I lb="Value" v={ca.value} oc={uCA(i,"value")} money/><I lb="Charge Rate" v={ca.charge_rate} oc={uCA(i,"charge_rate")} pct/></div></div>))}
      <button onClick={()=>sF(p=>({...p,contributory_assets:[...p.contributory_assets,{name:"",value:0,charge_rate:.05}]}))} style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px dashed rgba(255,255,255,0.1)",borderRadius:7,padding:8,color:"#64748b",fontSize:12,cursor:"pointer",marginBottom:6}}>+ Add Asset</button>
      <DiscountBuilder onRateChange={(v)=>{sF(p=>({...p,discount_rate:v}))}} currentRate={f.discount_rate}/>
      <Sc i="⚙️" t="Parameters"/><div style={g3}><I lb="Discount Rate" v={f.discount_rate} oc={u("discount_rate")} pct tip={TIPS.discount_rate}/><I lb="Attrition" v={f.annual_attrition_rate} oc={u("annual_attrition_rate")} pct tip={TIPS.annual_attrition_rate}/><I lb="Tax Amort." v={f.tax_amortization_years} oc={u("tax_amortization_years")}/></div><div style={{marginTop:10}}><I lb="Forecast Yrs" v={f.forecast_years} oc={u("forecast_years")}/></div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f,asset_name:f.asset_name||"Clients"},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/><Er e={e}/>
    </Pan>
    {r&&<div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <MethodPanel model="mpe"/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExportMPE} disabled={exportLoadingMPE} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoadingMPE?"rgba(236,72,153,0.06)":"rgba(236,72,153,0.12)",border:"1px solid rgba(236,72,153,0.35)",borderRadius:8,color:"#f472b6",cursor:exportLoadingMPE?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoadingMPE){ev.currentTarget.style.background="rgba(236,72,153,0.22)";ev.currentTarget.style.borderColor="rgba(236,72,153,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoadingMPE?"rgba(236,72,153,0.06)":"rgba(236,72,153,0.12)";ev.currentTarget.style.borderColor="rgba(236,72,153,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoadingMPE ? "Génération en cours…" : "Exporter MPEEM (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#ec4899" lines={[`🤝 ${f.asset_name||"Clients"} — MPEEM`,`💰 Fair Value: ${fC(r.fair_value)}€`,r.payback_years?`⏱️ Payback in ${r.payback_years} years`:"",r.implied_multiple?`📊 Implied multiple: ${fmt(r.implied_multiple,1)}x`:""].filter(Boolean)}/>
      <div style={g3}><Cd lb={L.fv} v={`${fC(r.fair_value)}€`} a="#ec4899"/><Cd lb="PV Excess" v={`${fC(r.sum_pv_excess)}€`} a="#3b82f6"/><Cd lb="TAB" v={`${fC(r.tax_amortization_benefit)}€`} a="#10b981"/></div>
      <div style={g2}><Bx t="Excess Earnings"><ResponsiveContainer width="100%" height={170}><BarChart data={r.projections}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><Tooltip content={<TT/>}/><Bar dataKey="adjusted_excess" name="Excess" fill="#ec4899" fillOpacity={.7} radius={[3,3,0,0]}/></BarChart></ResponsiveContainer></Bx><Bx t="Attrition"><ResponsiveContainer width="100%" height={170}><LineChart data={r.projections}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={v=>`${(v*100).toFixed(0)}%`}/><Tooltip content={<TT/>}/><Line type="monotone" dataKey="attrition_factor" stroke="#f59e0b" strokeWidth={2} dot={{r:2}} name="Attrition"/></LineChart></ResponsiveContainer></Bx></div>
      <SensPanel baseParams={f} model="mpe" L={L}/>
      <ScenPanel baseParams={f} model="mpe" L={L}/>
    </div>}
  </div></FadeIn>);
};

const AST=({L,onResult,initialValues})=>{
  const mk=(n,c,b,f,ld)=>({name:n,category:c,book_value:b,fair_value:f,liquidation_discount:ld});const ml=(n,c,b,f)=>({name:n,category:c,book_value:b,fair_value:f});
  const d={company_name:"",assets:[mk("Cash","current",15e4,15e4,0),mk("Real Estate","tangible",3e5,5e5,.2),mk("Brand","intangible",0,3e5,.7)],liabilities:[ml("Bank Loan","financial",25e4,25e4),ml("Suppliers","operating",12e4,12e4)],contingent_liabilities:25e3,deferred_tax_rate:.25,apply_deferred_tax:true,shares_outstanding:1e4,include_liquidation:true};
  const[f,sF]=useState({...d,...(initialValues||{})});const u=k=>v=>sF(p=>({...p,[k]:v}));const uA=(i,k)=>v=>sF(p=>{const a=[...p.assets];a[i]={...a[i],[k]:v};return{...p,assets:a};});const uL=(i,k)=>v=>sF(p=>{const li=[...p.liabilities];li[i]={...li[i],[k]:v};return{...p,liabilities:li};});
  const{r,l,e,run,ref,sR,sE}=useCalc("ast");
  const validation = validate(f, "ast");
  const [exportLoadingAst, setExportLoadingAst] = React.useState(false);

  const handleExportAst = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoadingAst(true);
    try {
      const response = await fetch(`${API_BASE}/api/asset/export-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_name:           f.company_name || "Company",
          assets:                 f.assets.map(a => ({
            name:                 a.name,
            category:             a.category,
            book_value:           a.book_value   ?? 0,
            fair_value:           a.fair_value   ?? 0,
            liquidation_discount: a.liquidation_discount ?? 0,
          })),
          liabilities:            f.liabilities.map(l => ({
            name:       l.name,
            category:   l.category,
            book_value: l.book_value ?? 0,
            fair_value: l.fair_value ?? 0,
          })),
          contingent_liabilities: f.contingent_liabilities ?? 0,
          deferred_tax_rate:      f.deferred_tax_rate,
          apply_deferred_tax:     f.apply_deferred_tax,
          shares_outstanding:     f.shares_outstanding,
          include_liquidation:    f.include_liquidation ?? true,
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || L.err_server);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Company").replace(/\s+/g, "_");
      a.href = url;
      a.download = `Valoris_NAV_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message + "\n\nVérifiez que le serveur backend tourne (uvicorn main:app --reload)");
    } finally {
      setExportLoadingAst(false);
    }
  };

  return(<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"420px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t="Company"/><I lb="Company Name" v={f.company_name} oc={u("company_name")} t="text"/>
      <Sc i="📦" t="Assets"/>
      {f.assets.map((a,i)=>(<div key={i} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:9,padding:10,marginBottom:7}}><div style={{...g2,marginBottom:6}}><I lb="Name" v={a.name} oc={uA(i,"name")} t="text"/><I lb="Category" v={a.category} oc={uA(i,"category")} t="text"/></div><div style={g3}><I lb="Book Value" v={a.book_value} oc={uA(i,"book_value")} money/><I lb="Fair Value" v={a.fair_value} oc={uA(i,"fair_value")} money/><I lb="Liq. Disc." v={a.liquidation_discount} oc={uA(i,"liquidation_discount")} pct/></div>{f.assets.length>1&&<button onClick={()=>sF(p=>({...p,assets:p.assets.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:10,marginTop:4}}>Remove</button>}</div>))}
      <button onClick={()=>sF(p=>({...p,assets:[...p.assets,mk("","tangible",0,0,0)]}))} style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px dashed rgba(255,255,255,0.1)",borderRadius:7,padding:8,color:"#64748b",fontSize:12,cursor:"pointer",marginBottom:6}}>+ Add Asset</button>
      <Sc i="📑" t="Liabilities"/>
      {f.liabilities.map((li,i)=>(<div key={i} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:9,padding:10,marginBottom:7}}><div style={{...g2,marginBottom:6}}><I lb="Name" v={li.name} oc={uL(i,"name")} t="text"/><I lb="Category" v={li.category} oc={uL(i,"category")} t="text"/></div><div style={g2}><I lb="Book Value" v={li.book_value} oc={uL(i,"book_value")} money/><I lb="Fair Value" v={li.fair_value} oc={uL(i,"fair_value")} money/></div>{f.liabilities.length>1&&<button onClick={()=>sF(p=>({...p,liabilities:p.liabilities.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:10,marginTop:4}}>Remove</button>}</div>))}
      <button onClick={()=>sF(p=>({...p,liabilities:[...p.liabilities,ml("","operating",0,0)]}))} style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px dashed rgba(255,255,255,0.1)",borderRadius:7,padding:8,color:"#64748b",fontSize:12,cursor:"pointer",marginBottom:6}}>+ Add Liability</button>
      <Sc i="⚙️" t="Parameters"/><div style={g3}><I lb="Contingent Liab." v={f.contingent_liabilities} oc={u("contingent_liabilities")} money tip={TIPS.contingent_liabilities}/><I lb="Tax Rate" v={f.deferred_tax_rate} oc={u("deferred_tax_rate")} pct tip={TIPS.deferred_tax_rate}/><I lb="Shares" v={f.shares_outstanding} oc={u("shares_outstanding")}/></div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f,company_name:f.company_name||"Company"},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/><Er e={e}/>
    </Pan>
    {r&&<div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <MethodPanel model="ast"/>
      <Summary L={L} color="#06b6d4" lines={[`🏢 ${f.company_name||"Company"} — Asset-Based`,`💰 Adjusted NAV: ${fC(r.adjusted_nav)}€ | Liquidation: ${fC(r.liquidation_nav)}€`,`📊 Premium to book: ${fP(r.premium_to_book)}`]}/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExportAst} disabled={exportLoadingAst} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoadingAst?"rgba(6,182,212,0.06)":"rgba(6,182,212,0.12)",border:"1px solid rgba(6,182,212,0.35)",borderRadius:8,color:"#06b6d4",cursor:exportLoadingAst?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s",opacity:exportLoadingAst?0.7:1}}
          onMouseEnter={ev=>{if(!exportLoadingAst){ev.currentTarget.style.background="rgba(6,182,212,0.22)";ev.currentTarget.style.borderColor="rgba(6,182,212,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoadingAst?"rgba(6,182,212,0.06)":"rgba(6,182,212,0.12)";ev.currentTarget.style.borderColor="rgba(6,182,212,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoadingAst ? "Génération en cours…" : "Exporter le modèle NAV (.xlsx)"}
        </button>
      </div>
      <div style={g3}><Cd lb={L.anav} v={`${fC(r.adjusted_nav)}€`} a="#06b6d4"/><Cd lb={L.liq} v={`${fC(r.liquidation_nav)}€`} a="#f59e0b"/><Cd lb={L.bnav} v={`${fC(r.book_nav)}€`} a="#64748b"/></div>
      <div style={g3}><Cd lb={L.ptb} v={fP(r.premium_to_book)} a="#10b981"/><Cd lb="Def. Tax" v={`${fC(r.deferred_tax_on_gains)}€`} a="#ef4444"/><Cd lb="Per Share" v={r.value_per_share_anav!=null?`${fmt(r.value_per_share_anav,2)}€`:"—"} a="#8b5cf6"/></div>
      <Bx t="Book vs Fair vs Liquidation"><ResponsiveContainer width="100%" height={200}><BarChart data={r.assets_detail}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/><XAxis dataKey="name" stroke="#475569" tick={{fontSize:10}}/><YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/><Tooltip content={<TT/>}/><Bar dataKey="book_value" name="Book" fill="#64748b" fillOpacity={.5} radius={[3,3,0,0]}/><Bar dataKey="fair_value" name="Fair" fill="#06b6d4" fillOpacity={.7} radius={[3,3,0,0]}/><Bar dataKey="liquidation_value" name="Liq." fill="#f59e0b" fillOpacity={.5} radius={[3,3,0,0]}/></BarChart></ResponsiveContainer></Bx>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================

// ==========================================================================
// WAVE 2: SENSITIVITY ANALYSIS ENGINE
// ==========================================================================
function sensitivityDCF(baseParams, paramKey, values, pctMode=false) {
  return values.map(v => {
    const p = {...baseParams, [paramKey]: pctMode ? v/100 : v};
    try {
      const r = calcDCF(p);
      return { label: pctMode ? `${v}%` : String(v), value: v, equity_value: r.equity_value, enterprise_value: r.enterprise_value };
    } catch { return { label: String(v), value: v, equity_value: 0, enterprise_value: 0 }; }
  });
}

function sensitivityExcess(baseParams, paramKey, values, pctMode=false) {
  return values.map(v => {
    const p = {...baseParams, [paramKey]: pctMode ? v/100 : v};
    try {
      const r = calcExcess(p);
      return { label: pctMode ? `${v}%` : String(v), value: v, equity_value: r.equity_value, enterprise_value: r.enterprise_value };
    } catch { return { label: String(v), value: v, equity_value: 0, enterprise_value: 0 }; }
  });
}

// Generic sensitivity runner
function runSensitivity(calcFn, baseParams, paramKey, values, pctMode=false, resultKey="equity_value") {
  return values.map(v => {
    const p = {...baseParams, [paramKey]: pctMode ? v/100 : v};
    try {
      const r = calcFn(p);
      return { label: pctMode ? `${v}%` : String(v), value: r[resultKey] || r.fair_value || r.adjusted_nav || r.equity_value_mid || 0 };
    } catch { return { label: String(v), value: 0 }; }
  });
}

// 2D Sensitivity matrix (WACC vs Growth for DCF)
function sensitivity2D(baseParams, key1, vals1, key2, vals2, pct1=false, pct2=false) {
  return vals1.map(v1 => {
    const row = { label: pct1 ? `${v1}%` : String(v1) };
    vals2.forEach(v2 => {
      const p = {...baseParams, [key1]: pct1?v1/100:v1, [key2]: pct2?v2/100:v2};
      try { const r = calcDCF(p); row[pct2?`${v2}%`:String(v2)] = r.equity_value; }
      catch { row[pct2?`${v2}%`:String(v2)] = null; }
    });
    return row;
  });
}

// ==========================================================================
// WAVE 2: SCENARIOS ENGINE
// ==========================================================================

// ==========================================================================
// ÉTAPE 3 — SENSITIVITY & SCENARIO PANELS (fully rebuilt)
// ==========================================================================

// ── Generic 2D matrix runner (model-agnostic) ─────────────────────────────
function matrix2D(calcFn, base, key1, vals1, key2, vals2, pct1, pct2, resultKey="equity_value") {
  return vals1.map(v1 => {
    const row = { _v1: v1, label: pct1 ? `${v1}%` : String(v1) };
    vals2.forEach(v2 => {
      const p = {...base, [key1]: pct1?v1/100:v1, [key2]: pct2?v2/100:v2};
      try {
        const r = calcFn(p);
        row[String(v2)] = r[resultKey] ?? r.fair_value ?? r.adjusted_nav ?? r.equity_value_mid ?? 0;
      } catch { row[String(v2)] = null; }
    });
    return row;
  });
}

// ── Heat-map cell colour (based on deviation from base value) ─────────────
function heatColor(val, baseVal) {
  if (val === null || val === undefined) return { bg:"transparent", tx:"#ef4444" };
  const dev = baseVal > 0 ? (val - baseVal) / baseVal : 0;
  if (dev >  0.25) return { bg:"rgba(16,185,129,0.30)", tx:"#6ee7b7" };
  if (dev >  0.12) return { bg:"rgba(16,185,129,0.15)", tx:"#a7f3d0" };
  if (dev >  0.04) return { bg:"rgba(16,185,129,0.07)", tx:"#94a3b8" };
  if (dev < -0.25) return { bg:"rgba(239,68,68,0.30)",  tx:"#fca5a5" };
  if (dev < -0.12) return { bg:"rgba(239,68,68,0.15)",  tx:"#fca5a5" };
  if (dev < -0.04) return { bg:"rgba(239,68,68,0.07)",  tx:"#94a3b8" };
  return { bg:"rgba(59,130,246,0.15)", tx:"#93c5fd" }; // base ≈ current
}

// ── Matrix table component ────────────────────────────────────────────────
const MatrixTable = ({rows, colVals, colPct, rowLabel, colLabel, baseVal, baseRow, baseCol}) => {
  return (
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"monospace"}}>
        <thead>
          <tr>
            <th style={{padding:"5px 8px",color:"#475569",textAlign:"left",
              borderBottom:"1px solid rgba(255,255,255,0.08)",fontSize:10,whiteSpace:"nowrap"}}>
              {rowLabel} ↓ / {colLabel} →
            </th>
            {colVals.map(v => {
              const isBase = Math.abs(v - baseCol) < 0.001;
              return (
                <th key={v} style={{padding:"5px 6px",textAlign:"right",
                  color: isBase ? "#3b82f6" : "#475569",
                  fontWeight: isBase ? 800 : 400,
                  borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                  {colPct ? `${v}%` : v}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isBaseRow = Math.abs(row._v1 - baseRow) < 0.001;
            return (
              <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                <td style={{padding:"4px 8px",
                  color: isBaseRow ? "#f59e0b" : "#475569",
                  fontWeight: isBaseRow ? 800 : 400,
                  fontSize: 9}}>
                  {row.label}
                </td>
                {colVals.map(v => {
                  const val = row[String(v)];
                  const isCur = isBaseRow && Math.abs(v - baseCol) < 0.001;
                  const {bg, tx} = isCur
                    ? {bg:"rgba(59,130,246,0.20)", tx:"#f1f5f9"}
                    : heatColor(val, baseVal);
                  return (
                    <td key={v} style={{padding:"4px 6px",textAlign:"right",
                      background: bg, color: isCur ? "#f1f5f9" : tx,
                      fontWeight: isCur ? 800 : 500,
                      borderRadius: isCur ? 4 : 0}}>
                      {val !== null && val !== undefined ? fC(val) : "∞"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{fontSize:10,color:"#334155",marginTop:5,fontStyle:"italic"}}>
        Cellule bleue = paramètres courants · 🟢 +25% · 🟡 +12% · 🔴 −12% · 🟥 −25%
      </div>
    </div>
  );
};

// ==========================================================================
// SENSITIVITY PANEL — Étape 3
// ==========================================================================
const SensPanel = ({baseParams, model, L}) => {
  const [activeMatrix, setActiveMatrix] = useState(0);
  const calcFn = CALCS[model];
  const resultKey = model==="rfr"||model==="mpe" ? "fair_value"
                  : model==="ast" ? "adjusted_nav"
                  : model==="mul" ? "equity_value_mid"
                  : "equity_value";

  // Base value for heat-map reference
  let baseVal = 0;
  try {
    const rb = calcFn ? calcFn(baseParams) : {};
    baseVal = rb[resultKey] ?? rb.fair_value ?? rb.adjusted_nav ?? rb.equity_value_mid ?? 0;
  } catch {}

  // ── Matrix configs per model ──────────────────────────────────────────────
  const matrixConfigs = {
    dcf: [
      { title:"WACC terminal × Croissance terminale",
        key1:"cost_of_capital_terminal", vals1:[7,8,9,10,11,12], pct1:true, label1:"WACC term.",
        key2:"terminal_growth_rate",      vals2:[1,1.5,2,2.5,3,3.5], pct2:true, label2:"g",
        base1: Math.round(baseParams.cost_of_capital_terminal*100),
        base2: baseParams.terminal_growth_rate*100 },
      { title:"WACC initial × Croissance initiale CA",
        key1:"cost_of_capital_initial",   vals1:[8,9,10,11,12,13], pct1:true, label1:"WACC init.",
        key2:"revenue_growth_initial",     vals2:[5,10,15,20,25,30], pct2:true, label2:"Croiss. CA",
        base1: Math.round(baseParams.cost_of_capital_initial*100),
        base2: Math.round(baseParams.revenue_growth_initial*100) },
      { title:"Marge cible × Croissance terminale",
        key1:"target_operating_margin",   vals1:[10,14,18,22,26,30], pct1:true, label1:"Marge cible",
        key2:"terminal_growth_rate",       vals2:[1,1.5,2,2.5,3,3.5], pct2:true, label2:"g",
        base1: Math.round(baseParams.target_operating_margin*100),
        base2: baseParams.terminal_growth_rate*100 },
    ],
    exc: [
      { title:"WACC terminal × ROIC terminal",
        key1:"cost_of_capital_terminal", vals1:[7,8,9,10,11,12], pct1:true, label1:"WACC term.",
        key2:"roic_terminal",             vals2:[8,10,12,15,18,20], pct2:true, label2:"ROIC term.",
        base1: Math.round(baseParams.cost_of_capital_terminal*100),
        base2: Math.round((baseParams.roic_terminal||0.15)*100) },
      { title:"ROIC terminal × Croissance terminale",
        key1:"roic_terminal",            vals1:[8,10,12,15,18,20], pct1:true, label1:"ROIC term.",
        key2:"terminal_growth_rate",      vals2:[1,1.5,2,2.5,3,3.5], pct2:true, label2:"g",
        base1: Math.round((baseParams.roic_terminal||0.15)*100),
        base2: baseParams.terminal_growth_rate*100 },
    ],
    rfr: [
      { title:"Taux redevance × Taux d'actualisation",
        key1:"royalty_rate",   vals1:[2,3,4,5,6,7,8], pct1:true, label1:"Royalty rate",
        key2:"discount_rate",  vals2:[10,11,12,13,14,15,16], pct2:true, label2:"Taux actualis.",
        base1: Math.round((baseParams.royalty_rate||0.05)*100),
        base2: Math.round((baseParams.discount_rate||0.12)*100) },
    ],
    mpe: [
      { title:"Attrition × Taux d'actualisation",
        key1:"annual_attrition_rate", vals1:[3,5,8,10,15,20], pct1:true, label1:"Attrition",
        key2:"discount_rate",          vals2:[10,12,14,16,18,20], pct2:true, label2:"Taux actualis.",
        base1: Math.round((baseParams.annual_attrition_rate||0.1)*100),
        base2: Math.round((baseParams.discount_rate||0.12)*100) },
    ],
  };

  const matrices = matrixConfigs[model] || [];
  if (!matrices.length || !calcFn) return null;

  const cfg = matrices[activeMatrix];
  const rows = matrix2D(calcFn, baseParams,
    cfg.key1, cfg.vals1, cfg.key2, cfg.vals2,
    cfg.pct1, cfg.pct2, resultKey);

  // ── Tornado chart data (1D sensitivity on key params) ────────────────────
  const tornadoConfigs = {
    dcf: [
      { key:"terminal_growth_rate",    label:"g terminal",      pct:true,  vals:[0.01,0.015,0.02,0.025,0.03,0.035] },
      { key:"cost_of_capital_terminal",label:"WACC terminal",   pct:true,  vals:[0.07,0.08,0.09,0.10,0.11,0.12] },
      { key:"target_operating_margin", label:"Marge cible",     pct:true,  vals:[0.10,0.14,0.18,0.22,0.26,0.30] },
      { key:"revenue_growth_initial",  label:"Croiss. initiale",pct:true,  vals:[0.05,0.10,0.15,0.20,0.25,0.30] },
      { key:"sales_to_capital_ratio",  label:"Sales/Capital",   pct:false, vals:[0.8,1.0,1.2,1.5,2.0,2.5] },
    ],
    exc: [
      { key:"roic_terminal",           label:"ROIC terminal",   pct:true,  vals:[0.08,0.10,0.12,0.15,0.18,0.20] },
      { key:"cost_of_capital_terminal",label:"WACC terminal",   pct:true,  vals:[0.07,0.08,0.09,0.10,0.11,0.12] },
      { key:"terminal_growth_rate",    label:"g terminal",      pct:true,  vals:[0.01,0.015,0.02,0.025,0.03] },
    ],
    rfr: [
      { key:"royalty_rate",   label:"Royalty rate",    pct:true, vals:[0.02,0.03,0.04,0.05,0.06,0.07,0.08] },
      { key:"discount_rate",  label:"Taux actualis.",  pct:true, vals:[0.10,0.11,0.12,0.13,0.14,0.15,0.16] },
    ],
    mpe: [
      { key:"annual_attrition_rate", label:"Attrition", pct:true, vals:[0.03,0.05,0.08,0.10,0.15,0.20] },
      { key:"discount_rate",         label:"Taux actualis.", pct:true, vals:[0.10,0.12,0.14,0.16,0.18] },
    ],
  };

  const tornadoData = (tornadoConfigs[model] || []).map(tc => {
    const results = runSensitivity(calcFn, baseParams, tc.key, tc.vals, false, resultKey);
    const vals = results.map(r => r.value).filter(v => v > 0);
    return { label: tc.label, min: Math.min(...vals), max: Math.max(...vals), base: baseVal };
  }).filter(d => d.max > d.min).sort((a,b) => (b.max-b.min) - (a.max-a.min));

  const tornadoMin = Math.min(...tornadoData.map(d => d.min)) * 0.9;
  const tornadoMax = Math.max(...tornadoData.map(d => d.max)) * 1.05;
  const tScale = v => ((v - tornadoMin) / (tornadoMax - tornadoMin)) * 100;

  return (
    <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
      borderRadius:12,padding:16}}>
      <div style={{fontSize:12,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",
        letterSpacing:"0.05em",marginBottom:14}}>
        📊 Analyse de Sensibilité
      </div>

      {/* ── Tornado chart ───────────────────────────────────────────────── */}
      {tornadoData.length > 0 && (
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:700,color:"#64748b",marginBottom:8,
            textTransform:"uppercase",letterSpacing:"0.04em"}}>
            Diagramme Tornado — Impact sur la valeur
          </div>
          <div style={{fontSize:10,color:"#334155",marginBottom:10,fontStyle:"italic"}}>
            Étendue min/max de chaque paramètre toutes choses égales par ailleurs
          </div>
          {tornadoData.map((d, i) => {
            const leftPct  = tScale(d.min);
            const rightPct = tScale(d.max);
            const basePct  = tScale(d.base);
            return (
              <div key={i} style={{display:"flex",alignItems:"center",marginBottom:7,gap:8}}>
                <div style={{width:110,fontSize:10,color:"#64748b",textAlign:"right",
                  flexShrink:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {d.label}
                </div>
                <div style={{flex:1,position:"relative",height:20,
                  background:"rgba(255,255,255,0.02)",borderRadius:4}}>
                  {/* Bar */}
                  <div style={{position:"absolute",
                    left:`${leftPct}%`,width:`${rightPct-leftPct}%`,
                    height:"100%",background:"rgba(59,130,246,0.35)",
                    border:"1px solid rgba(59,130,246,0.5)",borderRadius:4}}/>
                  {/* Base marker */}
                  <div style={{position:"absolute",left:`${basePct}%`,
                    top:2,bottom:2,width:2,background:"#fbbf24",borderRadius:1,
                    transform:"translateX(-50%)"}}/>
                  {/* Labels */}
                  <div style={{position:"absolute",left:`${leftPct}%`,
                    top:"50%",transform:"translateY(-50%) translateX(-100%)",
                    fontSize:10,color:"#ef4444",fontFamily:"monospace",
                    paddingRight:3,whiteSpace:"nowrap"}}>
                    {fC(d.min)}
                  </div>
                  <div style={{position:"absolute",left:`${rightPct}%`,
                    top:"50%",transform:"translateY(-50%) translateX(4px)",
                    fontSize:10,color:"#10b981",fontFamily:"monospace",
                    whiteSpace:"nowrap"}}>
                    {fC(d.max)}
                  </div>
                </div>
              </div>
            );
          })}
          <div style={{display:"flex",gap:12,marginTop:6,paddingLeft:118,fontSize:10,color:"#334155"}}>
            <span style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{width:16,height:8,background:"rgba(59,130,246,0.35)",
                border:"1px solid rgba(59,130,246,0.5)",borderRadius:2,display:"inline-block"}}/>
              Étendue du paramètre
            </span>
            <span style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{width:2,height:10,background:"#fbbf24",borderRadius:1,display:"inline-block"}}/>
              Valeur de base
            </span>
          </div>
        </div>
      )}

      {/* ── Matrix selector ─────────────────────────────────────────────── */}
      {matrices.length > 1 && (
        <div style={{display:"flex",gap:4,marginBottom:10,flexWrap:"wrap"}}>
          {matrices.map((m,i) => (
            <button key={i} onClick={()=>setActiveMatrix(i)}
              style={{background: activeMatrix===i ? "rgba(59,130,246,0.15)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${activeMatrix===i ? "rgba(59,130,246,0.4)" : "rgba(255,255,255,0.08)"}`,
                borderRadius:7,padding:"4px 10px",fontSize:10,fontWeight:600,
                color: activeMatrix===i ? "#3b82f6" : "#475569",cursor:"pointer"}}>
              {m.title.split("×")[0].trim()} × {m.title.split("×")[1]?.trim()}
            </button>
          ))}
        </div>
      )}

      {/* ── Matrix table ────────────────────────────────────────────────── */}
      <div style={{marginBottom:8}}>
        <div style={{fontSize:10,fontWeight:700,color:"#64748b",marginBottom:8,
          textTransform:"uppercase",letterSpacing:"0.04em"}}>
          {cfg.title}
        </div>
        <MatrixTable
          rows={rows} colVals={cfg.vals2} colPct={cfg.pct2}
          rowLabel={cfg.label1} colLabel={cfg.label2}
          baseVal={baseVal}
          baseRow={cfg.base1} baseCol={cfg.base2}/>
      </div>
    </div>
  );
};

// ==========================================================================
// SCENARIO PANEL — Étape 3 (3 scénarios avec hypothèses explicites)
// ==========================================================================
function buildScenarios(base, model) {
  // Scénarios avec logique métier explicite — pas un simple ±%
  const scenarios = {
    dcf: {
      optimistic: {
        ...base,
        revenue_growth_initial:      Math.min(base.revenue_growth_initial * 1.30, 0.50),
        revenue_growth_terminal:     Math.min(base.revenue_growth_terminal * 1.25, 0.05),
        target_operating_margin:     Math.min(base.target_operating_margin * 1.20, 0.40),
        cost_of_capital_initial:     base.cost_of_capital_initial * 0.92,
        cost_of_capital_terminal:    base.cost_of_capital_terminal * 0.92,
        terminal_growth_rate:        Math.min(base.terminal_growth_rate * 1.25, 0.04),
      },
      pessimistic: {
        ...base,
        revenue_growth_initial:      base.revenue_growth_initial * 0.65,
        revenue_growth_terminal:     base.revenue_growth_terminal * 0.75,
        target_operating_margin:     base.target_operating_margin * 0.82,
        cost_of_capital_initial:     base.cost_of_capital_initial * 1.10,
        cost_of_capital_terminal:    base.cost_of_capital_terminal * 1.10,
        terminal_growth_rate:        base.terminal_growth_rate * 0.75,
      },
    },
    exc: {
      optimistic: {
        ...base,
        roic_initial:    Math.min((base.roic_initial||0.135) * 1.25, 0.35),
        roic_terminal:   Math.min((base.roic_terminal||0.15) * 1.20, 0.30),
        cost_of_capital: (base.cost_of_capital||0.10) * 0.92,
        cost_of_capital_terminal: (base.cost_of_capital_terminal||0.09) * 0.92,
        terminal_growth_rate: Math.min(base.terminal_growth_rate * 1.25, 0.04),
      },
      pessimistic: {
        ...base,
        roic_initial:    (base.roic_initial||0.135) * 0.78,
        roic_terminal:   (base.roic_terminal||0.15) * 0.80,
        cost_of_capital: (base.cost_of_capital||0.10) * 1.10,
        cost_of_capital_terminal: (base.cost_of_capital_terminal||0.09) * 1.10,
        terminal_growth_rate: base.terminal_growth_rate * 0.75,
      },
    },
    rfr: {
      optimistic:  { ...base, royalty_rate: Math.min((base.royalty_rate||0.05)*1.30, 0.12), discount_rate: (base.discount_rate||0.12)*0.90, revenue_growth_initial: Math.min((base.revenue_growth_initial||0.10)*1.25, 0.30) },
      pessimistic: { ...base, royalty_rate: (base.royalty_rate||0.05)*0.72, discount_rate: (base.discount_rate||0.12)*1.12, revenue_growth_initial: (base.revenue_growth_initial||0.10)*0.70 },
    },
    mpe: {
      optimistic:  { ...base, annual_attrition_rate: Math.max((base.annual_attrition_rate||0.10)*0.70, 0.02), discount_rate: (base.discount_rate||0.12)*0.90, revenue_growth_initial: Math.min((base.revenue_growth_initial||0.10)*1.25, 0.30) },
      pessimistic: { ...base, annual_attrition_rate: Math.min((base.annual_attrition_rate||0.10)*1.40, 0.35), discount_rate: (base.discount_rate||0.12)*1.12, revenue_growth_initial: (base.revenue_growth_initial||0.10)*0.70 },
    },
  };

  const sc = scenarios[model];
  if (!sc) {
    // Fallback générique
    const pctKeys = ["revenue_growth_initial","revenue_growth_terminal","target_operating_margin",
      "roic_initial","roic_terminal","royalty_rate","terminal_growth_rate"];
    const costKeys = ["cost_of_capital","cost_of_capital_initial","cost_of_capital_terminal",
      "discount_rate","tax_rate","annual_attrition_rate","obsolescence_factor"];
    const opt = {...base}, pes = {...base};
    [...pctKeys, ...costKeys].forEach(k => {
      if (typeof base[k] !== "number") return;
      const isCost = costKeys.includes(k);
      opt[k] = isCost ? base[k]*0.87 : base[k]*1.20;
      pes[k] = isCost ? base[k]*1.13 : base[k]*0.80;
    });
    return { optimistic: opt, base, pessimistic: pes };
  }
  return { optimistic: sc.optimistic, base, pessimistic: sc.pessimistic };
}

// ── Hypothesis diff helper ─────────────────────────────────────────────────
function scenHypotheses(base, scenario, model) {
  const keyLabels = {
    revenue_growth_initial:   "Croiss. CA initiale",
    revenue_growth_terminal:  "Croiss. CA terminale",
    target_operating_margin:  "Marge cible",
    cost_of_capital_initial:  "WACC initial",
    cost_of_capital_terminal: "WACC terminal",
    terminal_growth_rate:     "g terminal",
    roic_initial:             "ROIC initial",
    roic_terminal:            "ROIC terminal",
    cost_of_capital:          "Coût du capital",
    royalty_rate:             "Taux redevance",
    discount_rate:            "Taux actualis.",
    annual_attrition_rate:    "Attrition",
  };
  return Object.entries(keyLabels)
    .filter(([k]) => base[k] !== undefined && scenario[k] !== undefined
      && Math.abs(base[k] - scenario[k]) > 0.0001)
    .map(([k, lb]) => ({
      label: lb,
      base:  base[k],
      scen:  scenario[k],
      delta: scenario[k] - base[k],
    }));
}

const ScenPanel = ({baseParams, model, L}) => {
  const calcFn = CALCS[model];
  if (!calcFn || model === "mul" || model === "ast") return null;

  const {optimistic, base, pessimistic} = buildScenarios(baseParams, model);
  const resultKey = model==="rfr"||model==="mpe" ? "fair_value" : "equity_value";

  let rO, rB, rP;
  try { rO = calcFn(optimistic); } catch { rO = null; }
  try { rB = calcFn(base);       } catch { rB = null; }
  try { rP = calcFn(pessimistic);} catch { rP = null; }
  if (!rO || !rB || !rP) return null;

  const getVal = r => r[resultKey] ?? r.fair_value ?? r.adjusted_nav ?? 0;
  const vO = getVal(rO), vB = getVal(rB), vP = getVal(rP);

  const upside   = vB > 0 ? (vO - vB) / vB : 0;
  const downside = vB > 0 ? (vP - vB) / vB : 0;

  const hypO = scenHypotheses(baseParams, optimistic, model);
  const hypP = scenHypotheses(baseParams, pessimistic, model);

  const ScenCard = ({label, val, color, hyps, isBase}) => (
    <div style={{background:`${color}08`,border:`1px solid ${color}22`,borderRadius:10,padding:14}}>
      <div style={{fontSize:10,fontWeight:700,color,textTransform:"uppercase",
        letterSpacing:"0.05em",marginBottom:8}}>{label}</div>
      <div style={{fontFamily:"monospace",fontSize:22,fontWeight:800,
        color:"#f1f5f9",marginBottom:8}}>{fC(val)}€</div>
      {!isBase && vB > 0 && (
        <div style={{fontFamily:"monospace",fontSize:11,fontWeight:600,
          color: val > vB ? "#10b981" : "#ef4444",marginBottom:10}}>
          {val > vB ? "+" : ""}{((val-vB)/vB*100).toFixed(1)}% vs base
        </div>
      )}
      {/* Hypothèses */}
      {hyps && hyps.length > 0 && (
        <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:8}}>
          <div style={{fontSize:10,color:"#334155",textTransform:"uppercase",
            letterSpacing:"0.05em",marginBottom:5}}>Hypothèses modifiées</div>
          {hyps.map((h,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",
              fontSize:10,padding:"2px 0",borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
              <span style={{color:"#64748b"}}>{h.label}</span>
              <span style={{fontFamily:"monospace",
                color: h.delta > 0 ? (label.includes("pes")||label.includes("Pes") ? "#ef4444":"#10b981")
                                   : (label.includes("pes")||label.includes("Pes") ? "#10b981":"#ef4444")}}>
                {fP(h.base)} → {fP(h.scen)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Bar chart: range visualization ───────────────────────────────────────
  const rangeMin = vP * 0.85;
  const rangeMax = vO * 1.10;
  const sc = v => Math.max(0, Math.min(100, (v - rangeMin) / (rangeMax - rangeMin) * 100));

  return (
    <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
      borderRadius:12,padding:16}}>
      <div style={{fontSize:12,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",
        letterSpacing:"0.05em",marginBottom:14}}>
        🎯 Analyse de Scénarios
      </div>

      {/* Range bar */}
      <div style={{marginBottom:16,padding:"10px 12px",
        background:"rgba(255,255,255,0.02)",borderRadius:9}}>
        <div style={{fontSize:10,color:"#475569",marginBottom:8,textTransform:"uppercase"}}>
          Fourchette de valorisation
        </div>
        <div style={{position:"relative",height:28,borderRadius:6,
          background:"rgba(255,255,255,0.03)"}}>
          {/* Full range */}
          <div style={{position:"absolute",left:`${sc(vP)}%`,
            width:`${sc(vO)-sc(vP)}%`,height:"100%",
            background:"rgba(59,130,246,0.12)",
            border:"1px solid rgba(59,130,246,0.25)",borderRadius:6}}/>
          {/* Pessimistic marker */}
          <div style={{position:"absolute",left:`${sc(vP)}%`,
            top:3,bottom:3,width:3,background:"#ef4444",borderRadius:2,
            transform:"translateX(-50%)"}}/>
          {/* Base marker */}
          <div style={{position:"absolute",left:`${sc(vB)}%`,
            top:1,bottom:1,width:3,background:"#3b82f6",borderRadius:2,
            transform:"translateX(-50%)"}}/>
          {/* Optimistic marker */}
          <div style={{position:"absolute",left:`${sc(vO)}%`,
            top:3,bottom:3,width:3,background:"#10b981",borderRadius:2,
            transform:"translateX(-50%)"}}/>
          {/* Value labels */}
          {[{v:vP,c:"#ef4444"},{v:vB,c:"#3b82f6"},{v:vO,c:"#10b981"}].map((m,i)=>(
            <div key={i} style={{position:"absolute",left:`${sc(m.v)}%`,
              top:-16,fontSize:10,color:m.c,fontWeight:700,
              fontFamily:"monospace",transform:"translateX(-50%)",whiteSpace:"nowrap"}}>
              {fC(m.v)}
            </div>
          ))}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",marginTop:18,fontSize:10,color:"#334155"}}>
          <span style={{color:"#ef4444"}}>↑ Pessimiste {(downside*100).toFixed(1)}%</span>
          <span style={{color:"#3b82f6"}}>Base</span>
          <span style={{color:"#10b981"}}>Optimiste +{(upside*100).toFixed(1)}%</span>
        </div>
      </div>

      {/* 3 scenario cards */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
        <ScenCard label="Pessimiste" val={vP} color="#ef4444" hyps={hypP}/>
        <ScenCard label="Base Case"  val={vB} color="#3b82f6" isBase/>
        <ScenCard label="Optimiste"  val={vO} color="#10b981" hyps={hypO}/>
      </div>
    </div>
  );
};


// ==========================================================================
// WAVE 2: FOOTBALL FIELD CHART (multi-model comparison)
// ==========================================================================
const FootballField = ({results, L}) => {
  if (!results || Object.keys(results).filter(k => results[k]?.equity_value || results[k]?.fair_value || results[k]?.equity_value_mid).length < 2) return null;

  const colorMap = {
    dcf:"#3b82f6", exc:"#8b5cf6", mul:"#10b981",
    rfr:"#f59e0b", mpe:"#ec4899", ast:"#06b6d4",
    vp:"#f97316",  vm:"#a78bfa",  vr:"#34d399",
    mba:"#fb7185", ebe_re:"#38bdf8", comb:"#fbbf24",
    us:"#f87171",  de:"#fbbf24",  ch:"#f472b6", uk:"#34d399"
  };
  const nameMap = {
    dcf:"DCF",              exc:"Excess Return",  mul:"Multiples",
    rfr:"Relief/Royalty",   mpe:"MPEEM",          ast:"Asset-Based",
    vp:"V.Productivité",    vm:"V.Mathématique",  vr:"V.Rendement",
    mba:"Val.MBA",          ebe_re:"Val.EBE/RE",  comb:"Comb.DGI",
    us:"IRS 59-60",  de:"IDW S1",  ch:"CSI n°28", uk:"HMRC SAV"
  };
  const jurKeys = new Set(["vp","vm","vr","mba","ebe_re","comb","us","de","ch","uk"]);

  // ── Compute real sensitivity ranges from stored params ───────────────────
  const getRealRange = (k, v) => {
    const p = v._params;   // stored alongside result at calculation time
    const mid_ev = v.equity_value || v.fair_value || v.equity_value_mid || 0;

    if (k === "mul") {
      return { low: v.equity_value_low, mid: v.equity_value_mid, high: v.equity_value_high };
    }
    if (k === "ast") {
      return { low: v.liquidation_nav, mid: v.adjusted_nav, high: v.adjusted_nav * 1.05 };
    }

    if (!p) {
      // No params stored — fallback to fixed spread
      const spread = jurKeys.has(k) ? 0.10 : 0.15;
      return { low: mid_ev*(1-spread), mid: mid_ev, high: mid_ev*(1+spread) };
    }

    // ── For each model, run the 2 most impactful params through runSensitivity
    let allVals = [];
    try {
      if (k === "dcf") {
        // WACC terminal: ±2pp around base
        const baseW = p.cost_of_capital_terminal || 0.09;
        const waccVals = [baseW-0.02, baseW-0.01, baseW, baseW+0.01, baseW+0.02].filter(v=>v>0.01&&v<0.25);
        waccVals.forEach(w => {
          const bg = p.terminal_growth_rate || 0.02;
          const gVals = [Math.max(0.005, bg-0.01), bg, Math.min(0.05, bg+0.01)];
          gVals.forEach(g => {
            try {
              const r2 = calcDCF({...p, cost_of_capital_terminal:w, terminal_growth_rate:g});
              if (r2.equity_value > 0) allVals.push(r2.equity_value);
            } catch {}
          });
        });
      } else if (k === "exc") {
        const baseW = p.cost_of_capital_terminal || p.cost_of_capital || 0.09;
        const baseR = p.roic_terminal || 0.15;
        [baseW-0.02, baseW-0.01, baseW, baseW+0.01, baseW+0.02].filter(v=>v>0.01).forEach(w => {
          [baseR*0.75, baseR*0.875, baseR, baseR*1.125, baseR*1.25].forEach(r => {
            try {
              const r2 = calcExcess({...p, cost_of_capital_terminal:w, cost_of_capital:w, roic_terminal:r});
              if (r2.equity_value > 0) allVals.push(r2.equity_value);
            } catch {}
          });
        });
      } else if (k === "rfr") {
        const baseRoy = p.royalty_rate || 0.05;
        const baseDr  = p.discount_rate || 0.12;
        [baseRoy*0.70, baseRoy*0.85, baseRoy, baseRoy*1.15, baseRoy*1.30].forEach(roy => {
          [baseDr-0.02, baseDr-0.01, baseDr, baseDr+0.01, baseDr+0.02].filter(v=>v>0.01).forEach(dr => {
            try {
              const r2 = calcRfR({...p, royalty_rate:roy, discount_rate:dr});
              if (r2.fair_value > 0) allVals.push(r2.fair_value);
            } catch {}
          });
        });
      } else if (k === "mpe") {
        const baseAtt = p.annual_attrition_rate || 0.10;
        const baseDr  = p.discount_rate || 0.12;
        [baseAtt*0.65, baseAtt*0.825, baseAtt, baseAtt*1.175, baseAtt*1.35].forEach(att => {
          [baseDr-0.02, baseDr-0.01, baseDr, baseDr+0.01, baseDr+0.02].filter(v=>v>0.01).forEach(dr => {
            try {
              const r2 = calcMPEEM({...p, annual_attrition_rate:att, discount_rate:dr});
              if (r2.fair_value > 0) allVals.push(r2.fair_value);
            } catch {}
          });
        });
      } else if (k === "us") {
        // US: vary cap_rate via rf_rate ±1pp and beta ±0.2
        const baseRf = p.rf_rate || 0.043;
        const baseBeta = p.beta || 1.1;
        [baseRf-0.01, baseRf, baseRf+0.01].filter(v=>v>0).forEach(rf => {
          [Math.max(0.5, baseBeta-0.2), baseBeta, baseBeta+0.2].forEach(b => {
            try {
              const r2 = calcUSEarnings({...p, rf_rate:rf, beta:b});
              if (r2.equity_value > 0) allVals.push(r2.equity_value);
            } catch {}
          });
        });
      } else if (k === "de") {
        // DE: vary basiszinssatz ±50bp and mrp_after_tax ±1pp
        const baseBz = p.basiszinssatz || 0.025;
        const baseMrp = p.mrp_after_tax || 0.055;
        [baseBz-0.005, baseBz, baseBz+0.005].filter(v=>v>0).forEach(bz => {
          [baseMrp-0.01, baseMrp, baseMrp+0.01].forEach(mrp => {
            try {
              const r2 = calcIDW({...p, basiszinssatz:bz, mrp_after_tax:mrp});
              if (r2.equity_value > 0) allVals.push(r2.equity_value);
            } catch {}
          });
        });
      } else if (k === "ch") {
        // CH: vary taux_capitalisation ±1pp and hidden_reserves ±20%
        const baseTx = p.taux_capitalisation || 0.07;
        const baseHr = p.hidden_reserves || 0;
        [baseTx-0.01, baseTx, baseTx+0.01].filter(v=>v>0.01).forEach(tx => {
          [baseHr*0.8, baseHr, baseHr*1.2].forEach(hr => {
            try {
              const r2 = calcCSI({...p, taux_capitalisation:tx, hidden_reserves:hr});
              if (r2.equity_value > 0) allVals.push(r2.equity_value);
            } catch {}
          });
        });
      } else if (k === "uk") {
        // UK: vary sector_pe ±2 and illiquidity_discount ±5pp
        const basePe = p.sector_pe || 12;
        const baseIl = p.illiquidity_discount || 0.20;
        [basePe-2, basePe-1, basePe, basePe+1, basePe+2].filter(v=>v>2).forEach(pe => {
          [Math.max(0, baseIl-0.05), baseIl, Math.min(0.5, baseIl+0.05)].forEach(il => {
            try {
              const r2 = calcUKPE({...p, sector_pe:pe, illiquidity_discount:il});
              if (r2.equity_value > 0) allVals.push(r2.equity_value);
            } catch {}
          });
        });
      } else if (jurKeys.has(k)) {
        // DGI methods: vary taux_rf ±1pp and beta_risque ±0.3
        const calcFn = CALCS[k];
        if (calcFn) {
          const baseRf   = p.taux_rf || p.discount_rate || 0.04;
          const baseBeta = p.beta_risque || 1.0;
          [baseRf-0.01, baseRf, baseRf+0.01].filter(v=>v>0).forEach(rf => {
            [Math.max(0.3, baseBeta-0.3), baseBeta, baseBeta+0.3].forEach(beta => {
              try {
                const r2 = calcFn({...p, taux_rf:rf, beta_risque:beta, discount_rate:rf+beta*0.05});
                const val = r2.equity_value || r2.valeur_entreprise || 0;
                if (val > 0) allVals.push(val);
              } catch {}
            });
          });
        }
      }
    } catch {}

    if (allVals.length < 3) {
      // Not enough data — fall back to fixed spread
      const spread = jurKeys.has(k) ? 0.10 : 0.15;
      return { low: mid_ev*(1-spread), mid: mid_ev, high: mid_ev*(1+spread) };
    }

    allVals.sort((a,b) => a-b);
    // Use 10th–90th percentile to avoid outliers
    const p10 = allVals[Math.floor(allVals.length * 0.10)];
    const p90 = allVals[Math.floor(allVals.length * 0.90)];
    return { low: p10, mid: mid_ev, high: p90 };
  };

  // ── Build data rows ──────────────────────────────────────────────────────
  const data = Object.entries(results).map(([k,v]) => {
    if (!colorMap[k] || !nameMap[k]) return null;
    const {low, mid, high} = getRealRange(k, v);
    if (!mid || mid <= 0) return null;
    return {
      key:k, model:nameMap[k], low, mid, high,
      color:colorMap[k], isJur:jurKeys.has(k),
      isSensReal: !!v._params && k !== "mul" && k !== "ast",
    };
  }).filter(Boolean);

  if (data.length < 2) return null;

  const allVals = data.flatMap(x => [x.low, x.high]).filter(v => v > 0 && isFinite(v));
  const minV = Math.min(...allVals) * 0.88;
  const maxV = Math.max(...allVals) * 1.12;
  const scale = v => Math.max(0, Math.min(100, ((v - minV) / (maxV - minV)) * 100));

  const intlData = data.filter(d => !d.isJur);
  const jurData  = data.filter(d =>  d.isJur);
  const hasSensReal = data.some(d => d.isSensReal);

  const renderRow = (d) => {
    const barW = Math.max(0.5, scale(d.high) - scale(d.low));
    return (
      <div key={d.key} style={{display:"flex",alignItems:"center",marginBottom:9}}>
        <div style={{width:116,fontSize:10,fontWeight:600,color:d.color,
          textAlign:"right",paddingRight:12,whiteSpace:"nowrap",flexShrink:0,
          display:"flex",alignItems:"center",justifyContent:"flex-end",gap:5}}>
          {d.isSensReal &&
            <span title="Fourchette calculée par sensibilité réelle"
              style={{fontSize:10,color:d.color,opacity:0.7}}>◈</span>}
          {d.model}
        </div>
        <div style={{flex:1,position:"relative",height:26,
          background:"rgba(255,255,255,0.02)",borderRadius:5}}>
          {/* Range bar */}
          <div style={{
            position:"absolute",left:`${scale(d.low)}%`,width:`${barW}%`,
            height:"100%",background:`${d.color}20`,
            border:`1px solid ${d.color}55`,borderRadius:5,
            boxShadow: d.isSensReal ? `0 0 6px ${d.color}18` : "none",
          }}/>
          {/* Low label */}
          <div style={{position:"absolute",left:`${scale(d.low)}%`,bottom:-13,
            fontSize:10,color:"#475569",fontFamily:"monospace",
            transform:"translateX(-50%)",whiteSpace:"nowrap"}}>
            {fC(d.low)}
          </div>
          {/* High label */}
          <div style={{position:"absolute",left:`${scale(d.high)}%`,bottom:-13,
            fontSize:10,color:"#475569",fontFamily:"monospace",
            transform:"translateX(-50%)",whiteSpace:"nowrap"}}>
            {fC(d.high)}
          </div>
          {/* Mid marker */}
          <div style={{position:"absolute",left:`${scale(d.mid)}%`,
            top:2,bottom:2,width:2,background:d.color,borderRadius:2,
            transform:"translateX(-50%)"}}/>
          {/* Mid label — above */}
          <div style={{position:"absolute",left:`${scale(d.mid)}%`,top:-15,
            fontSize:10,color:d.color,fontWeight:700,fontFamily:"monospace",
            transform:"translateX(-50%)",whiteSpace:"nowrap"}}>
            {fC(d.mid)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Bx t="🏈 Football Field — Multi-Model Comparison">
      <div style={{padding:"22px 0 8px"}}>

        {hasSensReal && (
          <div style={{paddingLeft:128,marginBottom:12,fontSize:10,color:"#475569",
            display:"flex",alignItems:"center",gap:6}}>
            <span style={{color:"#64748b",fontSize:10}}>◈</span>
            <span>Fourchette calculée par analyse de sensibilité réelle (WACC × g, Royalty × Dr, etc.)</span>
          </div>
        )}

        {/* Méthodes Financières Internationales */}
        {intlData.length > 0 && <>
          <div style={{fontSize:10,fontWeight:700,color:"#475569",letterSpacing:"1.2px",
            textTransform:"uppercase",paddingLeft:128,marginBottom:12,
            display:"flex",alignItems:"center",gap:8}}>
            <span style={{flex:1,height:1,background:"rgba(255,255,255,0.05)"}}/>
            <span>{L.grp.intl}</span>
            <span style={{flex:1,height:1,background:"rgba(255,255,255,0.05)"}}/>
          </div>
          <div style={{marginBottom:20}}>{intlData.map(renderRow)}</div>
        </>}

        {/* Séparateur */}
        {intlData.length > 0 && jurData.length > 0 &&
          <div style={{height:1,background:"rgba(201,168,76,0.15)",
            margin:"4px 0 20px 116px",borderStyle:"dashed",borderWidth:"1px 0 0 0",
            borderColor:"rgba(201,168,76,0.15)"}}/>
        }

        {/* Méthodes par Juridiction */}
        {jurData.length > 0 && <>
          <div style={{fontSize:10,fontWeight:700,color:"#C9A84C",letterSpacing:"1.2px",
            textTransform:"uppercase",paddingLeft:128,marginBottom:12,
            display:"flex",alignItems:"center",gap:8}}>
            <span style={{flex:1,height:1,background:"rgba(201,168,76,0.12)"}}/>
            <span>{L.grp.jur}</span>
            <span style={{flex:1,height:1,background:"rgba(201,168,76,0.12)"}}/>
          </div>
          <div style={{marginBottom:20}}>{jurData.map(renderRow)}</div>
        </>}

        {/* Axe */}
        <div style={{display:"flex",marginTop:18,paddingLeft:116}}>
          <div style={{flex:1,display:"flex",justifyContent:"space-between",
            fontSize:10,color:"#334155",borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:5}}>
            <span>{fC(minV)}</span>
            <span style={{color:"#475569",fontSize:10}}>← Fourchette de valorisation →</span>
            <span>{fC(maxV)}</span>
          </div>
        </div>

        {/* Légende */}
        <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:12,
          fontSize:10,color:"#475569",flexWrap:"wrap"}}>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:20,height:8,background:"rgba(255,255,255,0.08)",
              border:"1px solid rgba(255,255,255,0.15)",borderRadius:2,display:"inline-block"}}/>
            Fourchette de valeur
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:2,height:10,background:"#94a3b8",borderRadius:1,display:"inline-block"}}/>
            Valeur centrale
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:10,color:"#64748b"}}>◈</span>
            Sensibilité réelle
          </span>
          <span style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:10,color:"#334155"}}>±%</span>
            Fourchette indicative (no params)
          </span>
        </div>
      </div>
    </Bx>
  );
};


// ==========================================================================
// MÉTHODES FISCALES FRANÇAISES — Composants UI
// ==========================================================================

// VP — Valeur de Productivité
const VP_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", nombre_titres:1000,
    benefice_n2:80000, benefice_n1:90000, benefice_n:100000,
    reintegration_dirigeant:0, elements_non_recurrents:0,
    taux_rf:0.0386, taux_inflation:0.012, beta_risque:1.0,
  };
  const [f, sF] = useState({...d,...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("vp");
  const [exportLoading_VP, setExportLoading_VP] = React.useState(false);
  const handleExport_VP = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_VP(true);
    try {
      const response = await fetch(`${API_BASE}/api/dgi/vp/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name: f.company_name||"Société", nombre_titres: f.nombre_titres,
          benefice_n2: f.benefice_n2, benefice_n1: f.benefice_n1, benefice_n: f.benefice_n,
          reintegration_dirigeant: f.reintegration_dirigeant||0, elements_non_recurrents: f.elements_non_recurrents||0,
          taux_rf: f.taux_rf, taux_inflation: f.taux_inflation, beta_risque: f.beta_risque,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Societe").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_VP_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoading_VP(false); }
  };
  const rf_d = f.taux_rf - f.taux_inflation;
  const prime = f.beta_risque * 0.05;
  const taux_cap = rf_d + prime;
  const validation = validate(f, "vp");

  return (<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"380px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.sec_company}/><I lb={L.companyName} v={f.company_name} oc={u("company_name")} t="text" p="Société X"/>
      <I lb={L.sharesOutstanding} v={f.nombre_titres} oc={u("nombre_titres")}/>
      <Sc i="📊" t="Bénéfices (résultat courant net après IS)"/>
      <div style={{fontSize:10,color:"#64748b",marginBottom:8,fontStyle:"italic"}}>Pondération : 1×(n-2) + 2×(n-1) + 3×(n) ÷ 6</div>
      <div style={g3}>
        <I lb="Bénéfice n-2" v={f.benefice_n2} oc={u("benefice_n2")} money/>
        <I lb="Bénéfice n-1" v={f.benefice_n1} oc={u("benefice_n1")} money/>
        <I lb="Bénéfice n" v={f.benefice_n} oc={u("benefice_n")} money/>
      </div>
      <Sc i="🔧" t="Retraitements"/>
      <div style={g2}>
        <I lb="Réintégration salaire dirigeant" v={f.reintegration_dirigeant} oc={u("reintegration_dirigeant")} money tip="To be added back in owner-managed SMEs"/>
        <I lb="Éléments non récurrents (à déduire)" v={f.elements_non_recurrents} oc={u("elements_non_recurrents")} money tip="Real estate gains, exceptional income, etc."/>
      </div>
      <Sc i="📈" t="Taux de capitalisation"/>
      <div style={{background:"rgba(59,130,246,0.05)",borderRadius:9,padding:12,marginBottom:10}}>
        <div style={{fontSize:10,color:"#64748b",fontStyle:"italic",marginBottom:8}}>Taux = (Rf déflaté) + β × 5% (prime historique France)</div>
        <div style={{fontSize:10,color:"#64748b",marginBottom:6}}>cf. Annexe 2 du guide DGI — taux OAT long terme déflaté de l'inflation sous-jacente</div>
        <div style={g3}>
          <I lb="Taux OAT (Rf)" v={f.taux_rf} oc={u("taux_rf")} pct tip="Taux rendement emprunts d'État long terme"/>
          <I lb="Inflation sous-jacente" v={f.taux_inflation} oc={u("taux_inflation")} pct tip="Hors tarifs publics et produits volatils"/>
          <I lb="Coefficient β" v={f.beta_risque} oc={u("beta_risque")} s="0.1" tip="0.3-0.5 faible | 0.5-0.8 moyen | 0.8-1.1 élevé | 1.1-1.5 très élevé"/>
        </div>
        <div style={{marginTop:10,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <div style={{background:"rgba(255,255,255,0.03)",borderRadius:7,padding:"8px 10px"}}>
            <div style={{fontSize:10,color:"#64748b"}}>Rf déflaté</div>
            <div style={{fontSize:13,fontWeight:700,color:"#f1f5f9",fontFamily:"monospace"}}>{fP(rf_d)}</div>
          </div>
          <div style={{background:"rgba(255,255,255,0.03)",borderRadius:7,padding:"8px 10px"}}>
            <div style={{fontSize:10,color:"#64748b"}}>Prime risque (β×5%)</div>
            <div style={{fontSize:13,fontWeight:700,color:"#f59e0b",fontFamily:"monospace"}}>{fP(prime)}</div>
          </div>
          <div style={{background:"rgba(59,130,246,0.08)",borderRadius:7,padding:"8px 10px",border:"1px solid rgba(59,130,246,0.2)"}}>
            <div style={{fontSize:10,color:"#64748b"}}>Taux de capitalisation</div>
            <div style={{fontSize:13,fontWeight:700,color:"#3b82f6",fontFamily:"monospace"}}>{fP(taux_cap)}</div>
          </div>
        </div>
      </div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>
    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_VP} disabled={exportLoading_VP} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_VP?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.35)",borderRadius:8,color:"#818cf8",cursor:exportLoading_VP?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_VP){ev.currentTarget.style.background="rgba(99,102,241,0.22)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_VP?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_VP ? "Génération en cours…" : "Exporter VP (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#3b82f6" lines={[
        `🏢 ${f.company_name||L.sec_company} — Valeur de Productivité`,
        `💰 Valeur de productivité : ${fC(r.valeur_productivite)}€`,
        `📊 Taux capitalisation : ${fP(r.taux_capitalisation)} | Bénéfice récurrent : ${fC(r.bef_recurrent)}€`,
        r.valeur_unitaire ? `🏷️ Valeur unitaire : ${fmt(r.valeur_unitaire,2)}€/titre` : "",
      ].filter(Boolean)}/>
      <div style={g2}>
        <Cd lb="Valeur de Productivité" v={`${fC(r.valeur_productivite)}€`} a="#3b82f6"/>
        <Cd lb="Bénéfice récurrent" v={`${fC(r.bef_recurrent)}€`} a="#10b981"/>
      </div>
      <div style={g3}>
        <Cd lb="Rf déflaté" v={fP(r.rf_deflate)} a="#64748b"/>
        <Cd lb="Prime de risque" v={fP(r.prime_risque)} a="#f59e0b"/>
        <Cd lb="Taux capitalisation" v={fP(r.taux_capitalisation)} a="#3b82f6"/>
      </div>
      {r.valeur_unitaire && <Cd lb="Valeur unitaire" v={`${fmt(r.valeur_unitaire,2)}€/titre`} a="#8b5cf6"/>}
      <Bx t="Décomposition du bénéfice moyen pondéré">
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,fontFamily:"monospace"}}>
            <thead><tr style={{borderBottom:"2px solid rgba(255,255,255,0.08)"}}>{["Exercice","Bénéfice","Coeff.","Contribution"].map(h=><th key={h} style={{padding:"6px 8px",textAlign:"right",color:"#64748b",fontSize:10}}>{h}</th>)}</tr></thead>
            <tbody>
              {[{yr:"n-2",b:f.benefice_n2,c:1},{yr:"n-1",b:f.benefice_n1,c:2},{yr:"n",b:f.benefice_n,c:3}].map((row,i)=>(
                <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                  <td style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8",fontWeight:700}}>{row.yr}</td>
                  <td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(row.b)}€</td>
                  <td style={{padding:"5px 8px",textAlign:"right",color:"#64748b"}}>{row.c}</td>
                  <td style={{padding:"5px 8px",textAlign:"right",color:"#10b981"}}>{fC(row.b*row.c)}€</td>
                </tr>
              ))}
              <tr style={{borderTop:"2px solid rgba(255,255,255,0.1)"}}>
                <td colSpan={3} style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8",fontSize:10}}>Bénéfice moy. pondéré (÷6)</td>
                <td style={{padding:"5px 8px",textAlign:"right",color:"#3b82f6",fontWeight:700}}>{fC(r.bef_moyen_pondere)}€</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};

// VM — Valeur Mathématique (Actif Net Réévalué + Survaleur)
const VM_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", nombre_titres:1000,
    capitaux_propres:1500000,
    revaluations:[
      {poste:"Constructions",valeur_comptable:300000,valeur_venale:500000,necessite_exploitation:true},
      {poste:"Fonds commercial",valeur_comptable:0,valeur_venale:200000,necessite_exploitation:true},
    ],
    apply_impot_latent:false, taux_is:0.25,
    passif_hors_bilan:0,
    include_survaleur:false,
    bef_recurrent:100000,
    taux_rf:0.0386, taux_inflation:0.012, beta_risque:1.0,
    duree_survaleur:8,
    valeur_fonds_commerce:0,
  };
  const [f, sF] = useState({...d,...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const uR = (i, k) => v => sF(p => {
    const r = [...p.revaluations];
    const pv = k==="valeur_venale"||k==="valeur_comptable" ? (parseMoney(v)||0) : v;
    const plus_value = k==="valeur_venale" ? pv - r[i].valeur_comptable : k==="valeur_comptable" ? r[i].valeur_venale - pv : r[i].plus_value;
    r[i] = {...r[i], [k]:pv, plus_value};
    return {...p, revaluations:r};
  });
  const {r, l, e, run, ref, sR, sE} = useCalc("vm");
  const validation = validate(f, "vm");
  const [exportLoading_VM, setExportLoading_VM] = React.useState(false);
  const handleExport_VM = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_VM(true);
    try {
      const response = await fetch(`${API_BASE}/api/dgi/vm/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name: f.company_name||"Société", nombre_titres: f.nombre_titres,
          capitaux_propres: f.capitaux_propres,
          revaluations: f.revaluations.map(rv=>({poste:rv.poste,valeur_comptable:rv.valeur_comptable,valeur_venale:rv.valeur_venale,necessite_exploitation:rv.necessite_exploitation,plus_value:rv.plus_value??rv.valeur_venale-rv.valeur_comptable})),
          apply_impot_latent: f.apply_impot_latent, taux_is: f.taux_is, passif_hors_bilan: f.passif_hors_bilan||0,
          include_survaleur: f.include_survaleur, bef_recurrent: f.bef_recurrent||0,
          taux_rf: f.taux_rf, taux_inflation: f.taux_inflation, beta_risque: f.beta_risque,
          duree_survaleur: f.duree_survaleur||8, valeur_fonds_commerce: f.valeur_fonds_commerce||0,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Societe").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_VM_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoading_VM(false); }
  };
  // Compute revaluations plus_value if not set
  const revsWithPV = f.revaluations.map(rv => ({...rv, plus_value: rv.plus_value != null ? rv.plus_value : rv.valeur_venale - rv.valeur_comptable}));
  const paramsForRun = {...f, revaluations:revsWithPV};

  return (<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"400px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.sec_company}/>
      <I lb={L.companyName} v={f.company_name} oc={u("company_name")} t="text"/>
      <I lb={L.sharesOutstanding} v={f.nombre_titres} oc={u("nombre_titres")}/>
      <Sc i="💼" t="Capitaux propres comptables"/>
      <I lb="Capitaux propres" v={f.capitaux_propres} oc={u("capitaux_propres")} money tip="Capital + Réserves + Résultat + Provisions réglementées"/>
      <Sc i="📦" t="Réévaluations des postes du bilan"/>
      <div style={{fontSize:10,color:"#64748b",marginBottom:8,fontStyle:"italic"}}>Corriger les valeurs comptables en valeurs vénales réelles</div>
      {revsWithPV.map((rv, i) => (
        <div key={i} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:9,padding:10,marginBottom:7}}>
          <div style={{...g2,marginBottom:6}}>
            <I lb="Poste" v={rv.poste} oc={uR(i,"poste")} t="text"/>
            <div style={{display:"flex",alignItems:"flex-end",gap:6}}>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
                <label style={{fontSize:10,fontWeight:600,color:"#94a3b8",textTransform:"uppercase"}}>Nécessaire à exploit.</label>
                <button onClick={()=>uR(i,"necessite_exploitation")(!rv.necessite_exploitation)} style={{background:rv.necessite_exploitation?"rgba(16,185,129,0.15)":"rgba(239,68,68,0.1)",border:`1px solid ${rv.necessite_exploitation?"rgba(16,185,129,0.3)":"rgba(239,68,68,0.3)"}`,borderRadius:7,padding:"7px 10px",color:rv.necessite_exploitation?"#10b981":"#ef4444",fontSize:12,cursor:"pointer",fontWeight:600}}>{rv.necessite_exploitation?"✓ Oui":"✗ Non"}</button>
              </div>
              {f.revaluations.length>1&&<button onClick={()=>sF(p=>({...p,revaluations:p.revaluations.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:14,padding:4,marginBottom:2}}>×</button>}
            </div>
          </div>
          <div style={g2}>
            <I lb="Valeur comptable nette" v={rv.valeur_comptable} oc={uR(i,"valeur_comptable")} money/>
            <I lb="Valeur vénale" v={rv.valeur_venale} oc={uR(i,"valeur_venale")} money/>
          </div>
          <div style={{marginTop:6,fontSize:10,color:(rv.valeur_venale||0)>=(rv.valeur_comptable||0)?"#10b981":"#ef4444",fontWeight:600}}>
            Plus-value latente : {fC((rv.valeur_venale||0)-(rv.valeur_comptable||0))}€
          </div>
        </div>
      ))}
      <button onClick={()=>sF(p=>({...p,revaluations:[...p.revaluations,{poste:"",valeur_comptable:0,valeur_venale:0,necessite_exploitation:true,plus_value:0}]}))} style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px dashed rgba(255,255,255,0.1)",borderRadius:7,padding:8,color:"#64748b",fontSize:12,cursor:"pointer",marginBottom:6}}>+ Ajouter un poste</button>
      <Sc i="💸" t="Fiscalité latente et passif hors bilan"/>
      <div style={g2}>
        <div>
          <label style={{fontSize:10,fontWeight:600,color:"#94a3b8",textTransform:"uppercase"}}>Appliquer l'IS latent</label>
          <button onClick={()=>u("apply_impot_latent")(!f.apply_impot_latent)} style={{width:"100%",marginTop:4,background:f.apply_impot_latent?"rgba(16,185,129,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${f.apply_impot_latent?"rgba(16,185,129,0.3)":"rgba(255,255,255,0.08)"}`,borderRadius:8,padding:"8px",color:f.apply_impot_latent?"#10b981":"#94a3b8",fontSize:12,cursor:"pointer"}}>{f.apply_impot_latent?"✓ IS latent appliqué":"IS latent non appliqué"}</button>
        </div>
        <I lb="Taux IS" v={f.taux_is} oc={u("taux_is")} pct tip="Uniquement sur actifs non nécessaires à l'exploitation"/>
      </div>
      <div style={{marginTop:10}}><I lb="Passif hors bilan (engagements, crédit-bail, retraites)" v={f.passif_hors_bilan} oc={u("passif_hors_bilan")} money/></div>
      <Sc i="⭐" t="Survaleur (Goodwill) — optionnel"/>
      <div>
        <label style={{fontSize:10,fontWeight:600,color:"#94a3b8",textTransform:"uppercase"}}>Inclure la survaleur</label>
        <button onClick={()=>u("include_survaleur")(!f.include_survaleur)} style={{width:"100%",marginTop:4,background:f.include_survaleur?"rgba(251,191,36,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${f.include_survaleur?"rgba(251,191,36,0.3)":"rgba(255,255,255,0.08)"}`,borderRadius:8,padding:"8px",color:f.include_survaleur?"#fbbf24":"#94a3b8",fontSize:12,cursor:"pointer"}}>{f.include_survaleur?"✓ Survaleur activée":"Survaleur non calculée"}</button>
      </div>
      {f.include_survaleur && <div style={{marginTop:10}}>
        <div style={g2}>
          <I lb="Bénéfice récurrent" v={f.bef_recurrent} oc={u("bef_recurrent")} money tip="Issu de la VP — bénéfice normatif après IS"/>
          <I lb="Durée rente (ans)" v={f.duree_survaleur} oc={u("duree_survaleur")} tip="5-8 ans courants | jusqu'à 1/i pour les grandes entreprises pérennes"/>
        </div>
        <div style={{...g3,marginTop:8}}>
          <I lb="Taux OAT (Rf)" v={f.taux_rf} oc={u("taux_rf")} pct/>
          <I lb="Inflation" v={f.taux_inflation} oc={u("taux_inflation")} pct/>
          <I lb="Coefficient β" v={f.beta_risque} oc={u("beta_risque")} s="0.1"/>
        </div>
        <div style={{marginTop:8}}><I lb="Valeur fonds commerce au bilan (à déduire de l'ANC)" v={f.valeur_fonds_commerce} oc={u("valeur_fonds_commerce")} money tip="Pour calculer l'ANC hors fonds commerciaux"/></div>
      </div>}
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...paramsForRun},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>
    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_VM} disabled={exportLoading_VM} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_VM?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.35)",borderRadius:8,color:"#818cf8",cursor:exportLoading_VM?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_VM){ev.currentTarget.style.background="rgba(99,102,241,0.22)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_VM?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_VM ? "Génération en cours…" : "Exporter VM (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#06b6d4" lines={[
        `🏢 ${f.company_name||L.sec_company} — Valeur Mathématique`,
        `💰 VM (avec survaleur) : ${fC(r.valeur_mathematique)}€`,
        `📊 Capitaux propres : ${fC(r.capitaux_propres)}€ + Plus-values : ${fC(r.total_plus_values)}€`,
        r.survaleur > 0 ? `⭐ Survaleur (goodwill) : ${fC(r.survaleur)}€` : "",
        r.valeur_unitaire ? `🏷️ Valeur unitaire : ${fmt(r.valeur_unitaire,2)}€/titre` : "",
      ].filter(Boolean)}/>
      <div style={g3}>
        <Cd lb="Valeur Mathématique" v={`${fC(r.valeur_mathematique)}€`} a="#06b6d4"/>
        <Cd lb="VM brute (hors survaleur)" v={`${fC(r.valeur_mathematique_brute)}€`} a="#3b82f6"/>
        {r.survaleur > 0 && <Cd lb="Survaleur (GW)" v={`${fC(r.survaleur)}€`} a="#fbbf24"/>}
      </div>
      {r.valeur_unitaire && <Cd lb="Valeur unitaire" v={`${fmt(r.valeur_unitaire,2)}€/titre`} a="#8b5cf6"/>}
      <Bx t="Détail des réévaluations">
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"monospace"}}>
          <thead><tr style={{borderBottom:"2px solid rgba(255,255,255,0.08)"}}>{["Poste","Valeur cpt.","Valeur vénale","Plus-value","Exploit."].map(h=><th key={h} style={{padding:"5px 8px",textAlign:"right",color:"#64748b"}}>{h}</th>)}</tr></thead>
          <tbody>{r.revaluations.map((rv,i)=>(
            <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
              <td style={{padding:"4px 8px",color:"#94a3b8",fontWeight:600}}>{rv.poste}</td>
              <td style={{padding:"4px 8px",textAlign:"right",color:"#64748b"}}>{fC(rv.valeur_comptable)}€</td>
              <td style={{padding:"4px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(rv.valeur_venale)}€</td>
              <td style={{padding:"4px 8px",textAlign:"right",color:(rv.plus_value||0)>=0?"#10b981":"#ef4444"}}>{fC(rv.plus_value||0)}€</td>
              <td style={{padding:"4px 8px",textAlign:"center"}}>{rv.necessite_exploitation?"✓":"—"}</td>
            </tr>
          ))}</tbody>
        </table>
      </Bx>
      {r.superprofit > 0 && <Bx t="Calcul de la survaleur">
        <div style={{display:"flex",flexDirection:"column",gap:6,fontSize:12,fontFamily:"monospace"}}>
          <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:"#64748b"}}>Bénéfice récurrent</span><span style={{color:"#e2e8f0"}}>{fC(r.valeur_mathematique_brute - (r.survaleur||0))} → {fC(f.bef_recurrent)}€</span></div>
          <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:"#64748b"}}>Rémunération des capitaux engagés ({fP(r.taux_survaleur)})</span><span style={{color:"#f59e0b"}}>{fC((r.valeur_mathematique_brute - (f.valeur_fonds_commerce||0))*r.taux_survaleur)}€</span></div>
          <div style={{display:"flex",justifyContent:"space-between",borderTop:"1px solid rgba(255,255,255,0.08)",paddingTop:4}}><span style={{color:"#64748b"}}>Superprofit</span><span style={{color:r.superprofit>=0?"#10b981":"#ef4444",fontWeight:700}}>{fC(r.superprofit)}€</span></div>
          <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:"#64748b"}}>Survaleur (goodwill)</span><span style={{color:"#fbbf24",fontWeight:700}}>{fC(r.survaleur)}€</span></div>
        </div>
      </Bx>}
    </div>}
  </div></FadeIn>);
};

// VR — Valeur de Rendement
const VR_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", nombre_titres:1000,
    dividende_n2:30000, dividende_n1:35000, dividende_n:40000,
    taux_rendement_actions_cotees:0.03,
    decote_non_liquidite:0.30,
  };
  const [f, sF] = useState({...d,...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("vr");
  const validation = validate(f, "vr");
  const [exportLoading_VR, setExportLoading_VR] = React.useState(false);
  const handleExport_VR = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_VR(true);
    try {
      const response = await fetch(`${API_BASE}/api/dgi/vr/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name: f.company_name||"Société", nombre_titres: f.nombre_titres,
          dividende_n2: f.dividende_n2, dividende_n1: f.dividende_n1, dividende_n: f.dividende_n,
          taux_rendement_actions_cotees: f.taux_rendement_actions_cotees, decote_non_liquidite: f.decote_non_liquidite,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Societe").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_VR_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoading_VR(false); }
  };
  const div_moy_pondere = (f.dividende_n2*1 + f.dividende_n1*2 + f.dividende_n*3) / 6;
  return (<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"380px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.sec_company}/>
      <div style={g2}><I lb="Nom" v={f.company_name} oc={u("company_name")} t="text"/><I lb={L.sharesOutstanding} v={f.nombre_titres} oc={u("nombre_titres")}/></div>
      <Sc i="💵" t="Dividendes distribués"/>
      <div style={{fontSize:10,color:"#64748b",marginBottom:8,fontStyle:"italic"}}>Exclure les distributions exceptionnelles (prélèvements sur réserves). Retenir uniquement les dividendes issus des bénéfices courants.</div>
      <div style={g3}>
        <I lb="Dividende n-2" v={f.dividende_n2} oc={u("dividende_n2")} money/>
        <I lb="Dividende n-1" v={f.dividende_n1} oc={u("dividende_n1")} money/>
        <I lb="Dividende n" v={f.dividende_n} oc={u("dividende_n")} money/>
      </div>
      <div style={{marginTop:8,padding:"8px 12px",background:"rgba(255,255,255,0.03)",borderRadius:8,fontSize:12,color:"#94a3b8"}}>
        Dividende moy. pondéré estimé : <span style={{color:"#f1f5f9",fontWeight:700}}>{fC(div_moy_pondere)}€</span>
      </div>
      <Sc i="📈" t="Taux de capitalisation"/>
      <div style={g2}>
        <I lb="Taux rendement actions cotées" v={f.taux_rendement_actions_cotees} oc={u("taux_rendement_actions_cotees")} pct tip="Historique 3% (30 ans). Taux CAC40 ~3% (2005). Source annexe 2 DGI."/>
        <I lb="Décote non-liquidité" v={f.decote_non_liquidite} oc={u("decote_non_liquidite")} pct tip="30% pour les PME non cotées"/>
      </div>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>
    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_VR} disabled={exportLoading_VR} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_VR?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.35)",borderRadius:8,color:"#818cf8",cursor:exportLoading_VR?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_VR){ev.currentTarget.style.background="rgba(99,102,241,0.22)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_VR?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_VR ? "Génération en cours…" : "Exporter VR (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#10b981" lines={[
        `🏢 ${f.company_name||L.sec_company} — Valeur de Rendement`,
        `💰 Valeur de rendement : ${fC(r.valeur_rendement)}€ (après décote ${fP(r.decote_liquidite)})`,
        r.valeur_unitaire ? `🏷️ Valeur unitaire : ${fmt(r.valeur_unitaire,2)}€/titre` : "",
      ].filter(Boolean)}/>
      <div style={g3}>
        <Cd lb="Valeur de Rendement" v={`${fC(r.valeur_rendement)}€`} a="#10b981"/>
        <Cd lb="Valeur brute (div/taux)" v={`${fC(r.valeur_brute)}€`} a="#3b82f6"/>
        <Cd lb="Dividende moyen" v={`${fC(r.dividende_moyen)}€`} a="#f59e0b"/>
      </div>
      {r.valeur_unitaire && <Cd lb="Valeur unitaire" v={`${fmt(r.valeur_unitaire,2)}€/titre`} a="#8b5cf6"/>}
      <Bx t="Formule de calcul">
        <div style={{fontSize:12,fontFamily:"monospace",display:"flex",flexDirection:"column",gap:4}}>
          <div style={{color:"#64748b"}}>Dividende moyen pondéré (÷6) = {fC(r.dividende_moyen)}€</div>
          <div style={{color:"#64748b"}}>Valeur brute = {fC(r.dividende_moyen)}€ ÷ {fP(r.taux_base)} = {fC(r.valeur_brute)}€</div>
          <div style={{color:"#64748b"}}>Décote non-liquidité ({fP(r.decote_liquidite)}) = {fC(r.valeur_brute*r.decote_liquidite)}€</div>
          <div style={{borderTop:"1px solid rgba(255,255,255,0.08)",paddingTop:4,color:"#10b981",fontWeight:700}}>Valeur de rendement = {fC(r.valeur_rendement)}€</div>
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};

// MBA — Marge Brute d'Autofinancement
const MBA_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", nombre_titres:1000,
    benefice_net:100000,
    dotations_amortissements:80000,
    dotations_provisions_reserves:10000,
    reprises_provisions:5000,
    coefficient_mba:8,
    endettement_financier_net:150000,
  };
  const [f, sF] = useState({...d,...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("mba");
  const validation = validate(f, "mba");
  const [exportLoading_MBA, setExportLoading_MBA] = React.useState(false);
  const handleExport_MBA = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_MBA(true);
    try {
      const response = await fetch(`${API_BASE}/api/dgi/mba/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name: f.company_name||"Société", nombre_titres: f.nombre_titres,
          benefice_net: f.benefice_net, dotations_amortissements: f.dotations_amortissements,
          dotations_provisions_reserves: f.dotations_provisions_reserves||0, reprises_provisions: f.reprises_provisions||0,
          coefficient_mba: f.coefficient_mba, endettement_financier_net: f.endettement_financier_net,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Societe").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_MBA_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoading_MBA(false); }
  };
  const mba_est = f.benefice_net + f.dotations_amortissements + (f.dotations_provisions_reserves||0) - (f.reprises_provisions||0);
  return (<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"380px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.sec_company}/><div style={g2}><I lb="Nom" v={f.company_name} oc={u("company_name")} t="text"/><I lb={L.sharesOutstanding} v={f.nombre_titres} oc={u("nombre_titres")}/></div>
      <Sc i="📊" t="Calcul de la MBA"/>
      <div style={{fontSize:10,color:"#64748b",marginBottom:8,fontStyle:"italic"}}>MBA = Bénéfice net + Dotations amort. + Δ Provisions réserves - Reprises. Adapté aux sociétés industrielles avec amortissements importants.</div>
      <div style={g2}>
        <I lb="Bénéfice net" v={f.benefice_net} oc={u("benefice_net")} money/>
        <I lb="Dotations amortissements" v={f.dotations_amortissements} oc={u("dotations_amortissements")} money/>
      </div>
      <div style={{...g2,marginTop:10}}>
        <I lb="Dotations provisions (à caractère réserves)" v={f.dotations_provisions_reserves} oc={u("dotations_provisions_reserves")} money tip="Provisions pour investissement, hausse prix, fluctuation cours"/>
        <I lb="Reprises provisions (déduire)" v={f.reprises_provisions} oc={u("reprises_provisions")} money/>
      </div>
      <div style={{marginTop:8,padding:"8px 12px",background:"rgba(255,255,255,0.03)",borderRadius:8,fontSize:12,color:"#94a3b8"}}>
        MBA estimée : <span style={{color:"#f1f5f9",fontWeight:700}}>{fC(mba_est)}€</span>
      </div>
      <Sc i="✖️" t="Coefficient multiplicateur"/>
      <div style={{fontSize:10,color:"#64748b",marginBottom:8,fontStyle:"italic"}}>De 4 à 10 — faible si investissements importants, élevé sinon. À calibrer par comparaison boursière.</div>
      <div style={g2}>
        <I lb="Coefficient MBA" v={f.coefficient_mba} oc={u("coefficient_mba")} s="0.5" tip="4-10 selon nature des investissements. Tiré des comparaisons boursières."/>
        <I lb="Endettement financier net" v={f.endettement_financier_net} oc={u("endettement_financier_net")} money tip="Dettes financières - Trésorerie"/>
      </div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f},(r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>
    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_MBA} disabled={exportLoading_MBA} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_MBA?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.35)",borderRadius:8,color:"#818cf8",cursor:exportLoading_MBA?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_MBA){ev.currentTarget.style.background="rgba(99,102,241,0.22)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_MBA?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_MBA ? "Génération en cours…" : "Exporter MBA (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#ec4899" lines={[
        `🏢 ${f.company_name||"Société"} — Valeur par la MBA`,
        `💰 Capitaux propres : ${fC(r.valeur_capitaux_propres)}€ (VE : ${fC(r.valeur_enterprise)}€)`,
        `📊 MBA : ${fC(r.mba)}€ × coeff. ${r.coefficient}`,
        r.valeur_unitaire ? `🏷️ Valeur unitaire : ${fmt(r.valeur_unitaire,2)}€/titre` : "",
      ].filter(Boolean)}/>
      <div style={g3}>
        <Cd lb="MBA" v={`${fC(r.mba)}€`} a="#ec4899"/>
        <Cd lb="Valeur d'entreprise" v={`${fC(r.valeur_enterprise)}€`} a="#8b5cf6"/>
        <Cd lb="Capitaux Propres" v={`${fC(r.valeur_capitaux_propres)}€`} a="#10b981"/>
      </div>
    </div>}
  </div></FadeIn>);
};

// EBE/RE — Valeur par l'EBE ou le Résultat d'Exploitation
const EBE_RE_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", nombre_titres:1000,
    ebe:150000, multiple_ebe:6.5,
    resultat_exploitation:120000, multiple_re:9,
    endettement_financier_net:100000,
  };
  const [f, sF] = useState({...d,...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const [mode, setMode] = useState("ebe"); // "ebe" or "re"
  const {r, l, e, run, ref, sR, sE} = useCalc(mode === "ebe" ? "ebe" : "re");
  const [exportLoading_EBERE, setExportLoading_EBERE] = React.useState(false);
  const handleExport_EBERE = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_EBERE(true);
    try {
      const response = await fetch(`${API_BASE}/api/dgi/ebe-re/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name: f.company_name||"Société", nombre_titres: f.nombre_titres,
          ebe: f.ebe||null, multiple_ebe: f.multiple_ebe,
          resultat_exploitation: f.resultat_exploitation||null, multiple_re: f.multiple_re,
          endettement_financier_net: f.endettement_financier_net, mode: mode,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Societe").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_EBERE_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoading_EBERE(false); }
  };
  const validation = validate(f, mode);
  return (<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"380px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.sec_company}/><div style={g2}><I lb="Nom" v={f.company_name} oc={u("company_name")} t="text"/><I lb={L.sharesOutstanding} v={f.nombre_titres} oc={u("nombre_titres")}/></div>
      <Sc i="🔀" t="Méthode à utiliser"/>
      <div style={{display:"flex",gap:8,marginBottom:14}}>
        {[{k:"ebe",l:"EBE (EBITDA)"},{k:"re",l:"RE (EBIT)"}].map(btn=>(
          <button key={btn.k} onClick={()=>{setMode(btn.k);sR(null);sE(null);}} style={{flex:1,padding:"9px",background:mode===btn.k?"linear-gradient(135deg,#2563eb,#3b82f6)":"rgba(255,255,255,0.04)",border:mode===btn.k?"none":"1px solid rgba(255,255,255,0.1)",borderRadius:9,color:mode===btn.k?"#fff":"#94a3b8",fontSize:12,fontWeight:mode===btn.k?700:400,cursor:"pointer"}}>{btn.l}</button>
        ))}
      </div>
      {mode==="ebe" ? <>
        <div style={{fontSize:10,color:"#64748b",marginBottom:8,fontStyle:"italic"}}>EBE = VA + Subv. exploitation - Impôts/taxes - Charges personnel (équivalent EBITDA)</div>
        <div style={g2}>
          <I lb="EBE (Excédent Brut d'Exploitation)" v={f.ebe} oc={u("ebe")} money tip="Solde intermédiaire de gestion — équivalent EBITDA"/>
          <I lb="Multiple EBE (VE/EBE)" v={f.multiple_ebe} oc={u("multiple_ebe")} s="0.5" tip="Tiré des comparaisons sectorielles ou boursières. Décoter si source boursière."/>
        </div>
      </> : <>
        <div style={{fontSize:10,color:"#64748b",marginBottom:8,fontStyle:"italic"}}>RE = Produits exploitation - Charges exploitation (équivalent EBIT)</div>
        <div style={g2}>
          <I lb="Résultat d'Exploitation (RE)" v={f.resultat_exploitation} oc={u("resultat_exploitation")} money tip="Proche de l'EBIT — résultat avant charges financières et IS"/>
          <I lb="Multiple RE (VE/RE)" v={f.multiple_re} oc={u("multiple_re")} s="0.5" tip="Tiré des comparaisons sectorielles ou boursières."/>
        </div>
      </>}
      <div style={{marginTop:10}}>
        <I lb="Endettement financier net" v={f.endettement_financier_net} oc={u("endettement_financier_net")} money tip="Dettes financières - (Trésorerie + VMP) → déduit de la VE"/>
      </div>
      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f},(r)=>onResult(r,{...f}))} onX={()=>{sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>
    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_EBERE} disabled={exportLoading_EBERE} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_EBERE?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.35)",borderRadius:8,color:"#818cf8",cursor:exportLoading_EBERE?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_EBERE){ev.currentTarget.style.background="rgba(99,102,241,0.22)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_EBERE?"rgba(99,102,241,0.06)":"rgba(99,102,241,0.12)";ev.currentTarget.style.borderColor="rgba(99,102,241,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_EBERE ? "Génération en cours…" : "Exporter EBE/RE (.xlsx)"}
        </button>
      </div>
      <Summary L={L} color="#f59e0b" lines={[
        `🏢 ${f.company_name||"Société"} — Valeur par ${mode==="ebe"?"l'EBE":"le RE"}`,
        `💰 Capitaux Propres : ${fC(r.valeur_capitaux_propres)}€ (VE : ${fC(r.valeur_enterprise)}€)`,
        `📊 ${mode==="ebe"?"EBE":"RE"} : ${fC(mode==="ebe"?r.ebe:r.re)}€ × coeff. ${r.multiple}`,
        r.valeur_unitaire ? `🏷️ Valeur unitaire : ${fmt(r.valeur_unitaire,2)}€/titre` : "",
      ].filter(Boolean)}/>
      <div style={g3}>
        <Cd lb={mode==="ebe"?"EBE":"RE"} v={`${fC(mode==="ebe"?r.ebe:r.re)}€`} a="#f59e0b"/>
        <Cd lb="Valeur d'entreprise" v={`${fC(r.valeur_enterprise)}€`} a="#8b5cf6"/>
        <Cd lb="Capitaux Propres" v={`${fC(r.valeur_capitaux_propres)}€`} a="#10b981"/>
      </div>
      {r.valeur_unitaire && <Cd lb="Valeur unitaire" v={`${fmt(r.valeur_unitaire,2)}€/titre`} a="#f59e0b"/>}
      <Bx t="Formule VE → Equity">
        <div style={{fontSize:12,fontFamily:"monospace",display:"flex",flexDirection:"column",gap:4}}>
          <div style={{color:"#64748b"}}>{mode==="ebe"?"EBE":"RE"} × Multiple = VE : {fC(r.valeur_enterprise)}€</div>
          <div style={{color:"#64748b"}}>− Endettement net : {fC(r.endettement_net)}€</div>
          <div style={{borderTop:"1px solid rgba(255,255,255,0.08)",paddingTop:4,color:"#10b981",fontWeight:700}}>= Capitaux Propres : {fC(r.valeur_capitaux_propres)}€</div>
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};

// COMBINAISON — Pondération DGI finale
const COMB_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", nombre_titres:1000,
    // VM
    capitaux_propres:1500000,
    revaluations:[{poste:"Fonds commercial",valeur_comptable:0,valeur_venale:300000,necessite_exploitation:true,plus_value:300000}],
    apply_impot_latent:false, taux_is:0.25, passif_hors_bilan:0,
    include_survaleur:false, bef_recurrent:100000, valeur_fonds_commerce:0,
    duree_survaleur:8,
    // VP
    benefice_n2:80000, benefice_n1:90000, benefice_n:100000,
    reintegration_dirigeant:0, elements_non_recurrents:0,
    taux_rf:0.0386, taux_inflation:0.012, beta_risque:1.0,
    // VR
    use_vr:false, dividende_n2:0, dividende_n1:0, dividende_n:0,
    taux_rendement_actions_cotees:0.03, decote_non_liquidite:0.30,
    // EBE
    use_ebe:true, ebe:150000, multiple_ebe:6.5,
    // RE
    use_re:false, resultat_exploitation:120000, multiple_re:9,
    // MBA
    use_mba:false, benefice_net:100000, dotations_amortissements:60000,
    dotations_provisions_reserves:0, reprises_provisions:0, coefficient_mba:8,
    // Commun
    endettement_financier_net:100000,
    // Pondérations
    poids_vp:1, poids_vr:1, poids_ebe:1, poids_re:1, poids_mba:1,
    poids_vm_final:1, poids_vrent_final:2,
    // Décotes
    decote_minorite:0, decote_clause_agrement:0,
  };
  const [f, sF] = useState({...d,...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("comb");
  const validation = validate(f, "comb");
  const [exportLoadingComb, setExportLoadingComb] = React.useState(false);
  const handleExportComb = async () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoadingComb(true);
    try {
      const response = await fetch(`${API_BASE}/api/dgi/comb/export-excel`, {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name: f.company_name||"Société", nombre_titres: f.nombre_titres,
          capitaux_propres: f.capitaux_propres,
          revaluations: f.revaluations.map(rv=>({poste:rv.poste,valeur_comptable:rv.valeur_comptable,valeur_venale:rv.valeur_venale,plus_value:(rv.valeur_venale||0)-(rv.valeur_comptable||0)})),
          apply_impot_latent: f.apply_impot_latent, taux_is: f.taux_is, passif_hors_bilan: f.passif_hors_bilan||0,
          include_survaleur: f.include_survaleur, bef_recurrent: f.bef_recurrent||0,
          valeur_fonds_commerce: f.valeur_fonds_commerce||0, duree_survaleur: f.duree_survaleur||8,
          benefice_n2: f.benefice_n2, benefice_n1: f.benefice_n1, benefice_n: f.benefice_n,
          reintegration_dirigeant: f.reintegration_dirigeant||0, elements_non_recurrents: f.elements_non_recurrents||0,
          taux_rf: f.taux_rf, taux_inflation: f.taux_inflation, beta_risque: f.beta_risque,
          use_vr: f.use_vr, dividende_n2: f.dividende_n2||0, dividende_n1: f.dividende_n1||0, dividende_n: f.dividende_n||0,
          taux_rendement_actions_cotees: f.taux_rendement_actions_cotees, decote_non_liquidite: f.decote_non_liquidite,
          use_ebe: f.use_ebe, ebe: f.ebe||0, multiple_ebe: f.multiple_ebe,
          use_re: f.use_re, resultat_exploitation: f.resultat_exploitation||0, multiple_re: f.multiple_re,
          use_mba: f.use_mba, benefice_net: f.benefice_net||0, dotations_amortissements: f.dotations_amortissements||0,
          dotations_provisions_reserves: f.dotations_provisions_reserves||0, reprises_provisions: f.reprises_provisions||0,
          coefficient_mba: f.coefficient_mba,
          endettement_financier_net: f.endettement_financier_net,
          poids_vp: f.poids_vp||1, poids_vr: f.poids_vr||1, poids_ebe: f.poids_ebe||1,
          poids_re: f.poids_re||1, poids_mba: f.poids_mba||1,
          poids_vm_final: f.poids_vm_final||1, poids_vrent_final: f.poids_vrent_final||2,
          decote_minorite: f.decote_minorite||0, decote_clause_agrement: f.decote_clause_agrement||0,
        }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || L.err_server); }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const co = (f.company_name || "Societe").replace(/\s+/g, "_");
      a.href = url; a.download = `Valoris_Comb_DGI_${co}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch(err) {
      alert(L.err_export + err.message);
    } finally { setExportLoadingComb(false); }
  };

  // Preset pondérations selon taille/nature
  const PONDERATION_PRESETS = [
    {label:"TPE/PME commerciale — majoritaire",vm:3,vrent:1},
    {label:"PME commerciale — minoritaire",vm:1,vrent:1},
    {label:"PMI moyenne — majoritaire",vm:2,vrent:1},
    {label:"PMI moyenne — minoritaire",vm:1,vrent:2},
    {label:"Grande entreprise commerciale",vm:1,vrent:1},
    {label:"Grande entreprise industrielle — majoritaire",vm:1,vrent:2},
    {label:"Holding patrimoniale",vm:4,vrent:1},
    {label:"Petite société commerciale < 3M€ CA",vm:3,vrent:1},
  ];

  return (<FadeIn><div style={{display:"grid",gridTemplateColumns:r?"420px 1fr":"1fr",gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏛️" t="Guide DGI — Combinaison de méthodes"/>
      <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.15)",borderRadius:9,padding:"10px 14px",marginBottom:14,fontSize:12,color:"#fcd34d"}}>
        <div style={{fontWeight:700,marginBottom:4}}>Méthode de l'administration fiscale française</div>
        Valeur vénale = pondération de la Valeur Mathématique (VM) et des Valeurs de Rentabilité (V.Rent) selon la taille, la nature et le pouvoir de décision.
      </div>
      <I lb={L.companyName} v={f.company_name} oc={u("company_name")} t="text"/>
      <I lb={L.sharesOutstanding} v={f.nombre_titres} oc={u("nombre_titres")}/>

      <Sc i="⚡" t="Preset de pondération (selon guide DGI)"/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:12}}>
        {PONDERATION_PRESETS.map((p,i)=>(
          <button key={i} onClick={()=>u("poids_vm_final")(p.vm)||u("poids_vrent_final")(p.vrent)||sF(prev=>({...prev,poids_vm_final:p.vm,poids_vrent_final:p.vrent}))} style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${f.poids_vm_final===p.vm&&f.poids_vrent_final===p.vrent?"rgba(251,191,36,0.4)":"rgba(255,255,255,0.08)"}`,borderRadius:7,padding:"7px 10px",color:f.poids_vm_final===p.vm&&f.poids_vrent_final===p.vrent?"#fbbf24":"#94a3b8",fontSize:10,cursor:"pointer",textAlign:"left",lineHeight:1.3}}>
            <div style={{fontWeight:600}}>{p.label}</div>
            <div style={{color:"#64748b",marginTop:2}}>VM×{p.vm} + VR×{p.vrent}</div>
          </button>
        ))}
      </div>

      <Sc i="🏛️" t="Valeur Mathématique (VM)"/>
      <I lb="Capitaux propres comptables" v={f.capitaux_propres} oc={u("capitaux_propres")} money/>
      <div style={{fontSize:10,color:"#64748b",margin:"4px 0 8px",fontStyle:"italic"}}>Plus-values latentes — saisir les réévaluations principales</div>
      {f.revaluations.map((rv, i) => {
        const pv = (rv.valeur_venale||0) - (rv.valeur_comptable||0);
        return (
        <div key={i} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:8,padding:8,marginBottom:6,display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:6,alignItems:"end"}}>
          <I lb="Poste" v={rv.poste} oc={v=>sF(p=>{const r=[...p.revaluations];r[i]={...r[i],poste:v};return{...p,revaluations:r};})} t="text"/>
          <I lb="Vl. cpt. nette" v={rv.valeur_comptable} oc={v=>{const vn=parseMoney(v)||0;sF(p=>{const r=[...p.revaluations];r[i]={...r[i],valeur_comptable:vn,plus_value:(r[i].valeur_venale||0)-vn};return{...p,revaluations:r};});}} money/>
          <I lb="Vl. vénale" v={rv.valeur_venale} oc={v=>{const vn=parseMoney(v)||0;sF(p=>{const r=[...p.revaluations];r[i]={...r[i],valeur_venale:vn,plus_value:vn-(r[i].valeur_comptable||0)};return{...p,revaluations:r};});}} money/>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
            <div style={{fontSize:10,color:pv>=0?"#10b981":"#ef4444",fontWeight:700}}>{pv>=0?"+":""}{fC(pv)}€</div>
            {f.revaluations.length>1&&<button onClick={()=>sF(p=>({...p,revaluations:p.revaluations.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:11,padding:2}}>×</button>}
          </div>
        </div>
      );})}
      <button onClick={()=>sF(p=>({...p,revaluations:[...p.revaluations,{poste:"",valeur_comptable:0,valeur_venale:0,necessite_exploitation:true,plus_value:0}]}))} style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px dashed rgba(255,255,255,0.1)",borderRadius:7,padding:6,color:"#64748b",fontSize:10,cursor:"pointer",marginBottom:8}}>+ Réévaluation</button>

      <Sc i="📈" t="Valeur de Productivité (VP) — obligatoire"/>
      <div style={g3}>
        <I lb="Bénéf. n-2" v={f.benefice_n2} oc={u("benefice_n2")} money/>
        <I lb="Bénéf. n-1" v={f.benefice_n1} oc={u("benefice_n1")} money/>
        <I lb="Bénéf. n" v={f.benefice_n} oc={u("benefice_n")} money/>
      </div>
      <div style={{...g3,marginTop:8}}>
        <I lb="Taux OAT (Rf)" v={f.taux_rf} oc={u("taux_rf")} pct/>
        <I lb="Inflation" v={f.taux_inflation} oc={u("taux_inflation")} pct/>
        <I lb="Coefficient β" v={f.beta_risque} oc={u("beta_risque")} s="0.1"/>
      </div>

      <Sc i="💵" t="Valeur de Rendement (VR) — optionnel"/>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <button onClick={()=>u("use_vr")(!f.use_vr)} style={{background:f.use_vr?"rgba(16,185,129,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${f.use_vr?"rgba(16,185,129,0.3)":"rgba(255,255,255,0.1)"}`,borderRadius:8,padding:"7px 14px",color:f.use_vr?"#10b981":"#64748b",fontSize:12,cursor:"pointer"}}>{f.use_vr?"✓ Activée":"Activer"}</button>
        <span style={{fontSize:10,color:"#64748b"}}>Pour titres minoritaires avec distribution régulière</span>
      </div>
      {f.use_vr && <div style={g3}><I lb="Div. n-2" v={f.dividende_n2} oc={u("dividende_n2")} money/><I lb="Div. n-1" v={f.dividende_n1} oc={u("dividende_n1")} money/><I lb="Div. n" v={f.dividende_n} oc={u("dividende_n")} money/></div>}

      <Sc i="📊" t="Valeur par l'EBE — optionnel"/>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <button onClick={()=>u("use_ebe")(!f.use_ebe)} style={{background:f.use_ebe?"rgba(245,158,11,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${f.use_ebe?"rgba(245,158,11,0.3)":"rgba(255,255,255,0.1)"}`,borderRadius:8,padding:"7px 14px",color:f.use_ebe?"#f59e0b":"#64748b",fontSize:12,cursor:"pointer"}}>{f.use_ebe?"✓ Activée":"Activer"}</button>
      </div>
      {f.use_ebe && <div style={g2}><I lb="EBE" v={f.ebe} oc={u("ebe")} money/><I lb="Multiple EBE" v={f.multiple_ebe} oc={u("multiple_ebe")} s="0.5"/></div>}

      <Sc i="📉" t="Valeur par le RE — optionnel"/>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <button onClick={()=>u("use_re")(!f.use_re)} style={{background:f.use_re?"rgba(139,92,246,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${f.use_re?"rgba(139,92,246,0.3)":"rgba(255,255,255,0.1)"}`,borderRadius:8,padding:"7px 14px",color:f.use_re?"#8b5cf6":"#64748b",fontSize:12,cursor:"pointer"}}>{f.use_re?"✓ Activée":"Activer"}</button>
      </div>
      {f.use_re && <div style={g2}><I lb="Résultat exploitation" v={f.resultat_exploitation} oc={u("resultat_exploitation")} money/><I lb="Multiple RE" v={f.multiple_re} oc={u("multiple_re")} s="0.5"/></div>}

      <Sc i="🏭" t="Valeur par la MBA — optionnel"/>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <button onClick={()=>u("use_mba")(!f.use_mba)} style={{background:f.use_mba?"rgba(236,72,153,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${f.use_mba?"rgba(236,72,153,0.3)":"rgba(255,255,255,0.1)"}`,borderRadius:8,padding:"7px 14px",color:f.use_mba?"#ec4899":"#64748b",fontSize:12,cursor:"pointer"}}>{f.use_mba?"✓ Activée":"Activer"}</button>
        <span style={{fontSize:10,color:"#64748b"}}>Adapté aux sociétés industrielles</span>
      </div>
      {f.use_mba && <div style={{...g3}}><I lb="Bénéfice net" v={f.benefice_net} oc={u("benefice_net")} money/><I lb="Dotations amort." v={f.dotations_amortissements} oc={u("dotations_amortissements")} money/><I lb="Coefficient" v={f.coefficient_mba} oc={u("coefficient_mba")} s="0.5"/></div>}

      <div style={{marginTop:8}}><I lb="Endettement financier net" v={f.endettement_financier_net} oc={u("endettement_financier_net")} money/></div>

      <Sc i="⚖️" t="Pondération finale (VM vs V.Rent)"/>
      <div style={g2}>
        <I lb="Poids VM" v={f.poids_vm_final} oc={u("poids_vm_final")} s="1"/>
        <I lb="Poids V.Rent" v={f.poids_vrent_final} oc={u("poids_vrent_final")} s="1"/>
      </div>
      <div style={{marginTop:4,fontSize:10,color:"#64748b",textAlign:"center"}}>Formule: ({f.poids_vm_final}×VM + {f.poids_vrent_final}×V.Rent) ÷ {f.poids_vm_final+f.poids_vrent_final}</div>

      <Sc i="📉" t="Décotes et corrections"/>
      <div style={g2}>
        <I lb="Décote minorité" v={f.decote_minorite} oc={u("decote_minorite")} pct tip="Variable selon situation (0-25%). Non cumulable si pondération déjà reflète minorité."/>
        <I lb="Décote clause agrément" v={f.decote_clause_agrement} oc={u("decote_clause_agrement")} pct tip="~10% pour parts sociales ou actions avec clause d'agrément statutaire"/>
      </div>
      <Bt L={L} l={l} disabled={validation.blocking} onC={()=>run({...f,revaluations:f.revaluations.map(rv=>({...rv,plus_value:(rv.valeur_venale||0)-(rv.valeur_comptable||0)}))}, (r)=>onResult(r,{...f}))} onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>
    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExportComb} disabled={exportLoadingComb} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoadingComb?"rgba(251,191,36,0.06)":"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.35)",borderRadius:8,color:"#fbbf24",cursor:exportLoadingComb?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoadingComb){ev.currentTarget.style.background="rgba(251,191,36,0.22)";ev.currentTarget.style.borderColor="rgba(251,191,36,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoadingComb?"rgba(251,191,36,0.06)":"rgba(251,191,36,0.12)";ev.currentTarget.style.borderColor="rgba(251,191,36,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoadingComb ? "Génération en cours…" : "Exporter Combinaison DGI (.xlsx)"}
        </button>
      </div>
      <div style={{background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.25)",borderRadius:12,padding:"16px 20px",marginBottom:4}}>
        <div style={{fontSize:12,fontWeight:700,color:"#fbbf24",textTransform:"uppercase",marginBottom:8}}>🏛️ Valeur Vénale — Guide DGI</div>
        <div style={{fontSize:22,fontWeight:800,color:"#f1f5f9",fontFamily:"monospace"}}>{fC(r.valeur_venale)}€</div>
        {r.valeur_unitaire && <div style={{fontSize:13,color:"#fbbf24",marginTop:4}}>Valeur unitaire : {fmt(r.valeur_unitaire,2)}€/titre</div>}
        <div style={{fontSize:10,color:"#64748b",marginTop:8}}>({r.ponderation.vm}×VM + {r.ponderation.vrent}×V.Rent) ÷ {r.ponderation.vm+r.ponderation.vrent}</div>
      </div>
      <div style={g3}>
        <Cd lb="Valeur Mathématique (VM)" v={`${fC(r.vm)}€`} a="#06b6d4"/>
        <Cd lb="V. Rentabilité (V.Rent)" v={`${fC(r.vrent)}€`} a="#3b82f6"/>
        <Cd lb="Avant décotes" v={`${fC(r.valeur_avant_decotes)}€`} a="#8b5cf6"/>
      </div>
      {(r.decote_minorite > 0 || r.decote_agrement > 0) && <div style={g2}>
        <Cd lb="Décote minorité" v={fP(r.decote_minorite)} a="#ef4444"/>
        <Cd lb="Décote agrément" v={fP(r.decote_agrement)} a="#ef4444"/>
      </div>}
      <Bx t="Décomposition de la V. Rentabilité">
        {r.vrent_sources && r.vrent_sources.length > 0 ? <div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={r.vrent_sources.map(s=>({name:s.label,value:s.v,weight:s.w}))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
              <XAxis dataKey="name" stroke="#475569" tick={{fontSize:10}}/>
              <YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="value" name="Valeur" fill="#3b82f6" fillOpacity={.7} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
          <div style={{marginTop:8}}>
            {r.vrent_sources.map((s,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:11}}>
                <span style={{color:"#94a3b8"}}>{s.label} (poids {s.w})</span>
                <span style={{color:"#e2e8f0",fontFamily:"monospace",fontWeight:600}}>{fC(s.v)}€</span>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",marginTop:2,fontSize:12,borderTop:"2px solid rgba(255,255,255,0.08)"}}>
              <span style={{color:"#64748b",fontWeight:600}}>V.Rent (moyenne pondérée)</span>
              <span style={{color:"#3b82f6",fontFamily:"monospace",fontWeight:700}}>{fC(r.vrent)}€</span>
            </div>
          </div>
        </div> : <div style={{color:"#64748b",fontSize:11}}>Aucune valeur de rentabilité calculée</div>}
      </Bx>
      <Bx t="Tableau de synthèse DGI">
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,fontFamily:"monospace"}}>
            <thead><tr style={{borderBottom:"2px solid rgba(255,255,255,0.08)"}}>{["Méthode","Valeur globale","Valeur unitaire","Poids"].map(h=><th key={h} style={{padding:"6px 8px",textAlign:"right",color:"#64748b",fontSize:10}}>{h}</th>)}</tr></thead>
            <tbody>
              {r.detail.vm&&<tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}><td style={{padding:"5px 8px",color:"#06b6d4",fontWeight:700}}>VM (+ survaleur)</td><td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(r.detail.vm?.equity_value)}€</td><td style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8"}}>{r.detail.vm?.valeur_unitaire?fmt(r.detail.vm.valeur_unitaire,2)+"€":"—"}</td><td style={{padding:"5px 8px",textAlign:"right",color:"#fbbf24"}}>{r.ponderation.vm}</td></tr>}
              {r.detail.vp&&<tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}><td style={{padding:"5px 8px",color:"#3b82f6",fontWeight:700}}>VP (productivité)</td><td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(r.detail.vp?.equity_value)}€</td><td style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8"}}>{r.detail.vp?.valeur_unitaire?fmt(r.detail.vp.valeur_unitaire,2)+"€":"—"}</td><td style={{padding:"5px 8px",textAlign:"right",color:"#64748b"}}>V.Rent</td></tr>}
              {r.detail.vr&&<tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}><td style={{padding:"5px 8px",color:"#10b981",fontWeight:700}}>VR (rendement)</td><td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(r.detail.vr?.equity_value)}€</td><td style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8"}}>{r.detail.vr?.valeur_unitaire?fmt(r.detail.vr.valeur_unitaire,2)+"€":"—"}</td><td style={{padding:"5px 8px",textAlign:"right",color:"#64748b"}}>V.Rent</td></tr>}
              {r.detail.ebe&&<tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}><td style={{padding:"5px 8px",color:"#f59e0b",fontWeight:700}}>V.EBE</td><td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(r.detail.ebe?.equity_value)}€</td><td style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8"}}>{r.detail.ebe?.valeur_unitaire?fmt(r.detail.ebe.valeur_unitaire,2)+"€":"—"}</td><td style={{padding:"5px 8px",textAlign:"right",color:"#64748b"}}>V.Rent</td></tr>}
              {r.detail.re&&<tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}><td style={{padding:"5px 8px",color:"#8b5cf6",fontWeight:700}}>V.RE</td><td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(r.detail.re?.equity_value)}€</td><td style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8"}}>{r.detail.re?.valeur_unitaire?fmt(r.detail.re.valeur_unitaire,2)+"€":"—"}</td><td style={{padding:"5px 8px",textAlign:"right",color:"#64748b"}}>V.Rent</td></tr>}
              {r.detail.mba&&<tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}><td style={{padding:"5px 8px",color:"#ec4899",fontWeight:700}}>V.MBA</td><td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(r.detail.mba?.equity_value)}€</td><td style={{padding:"5px 8px",textAlign:"right",color:"#94a3b8"}}>{r.detail.mba?.valeur_unitaire?fmt(r.detail.mba.valeur_unitaire,2)+"€":"—"}</td><td style={{padding:"5px 8px",textAlign:"right",color:"#64748b"}}>V.Rent</td></tr>}
              <tr style={{borderTop:"2px solid rgba(255,255,255,0.1)"}}>
                <td style={{padding:"7px 8px",color:"#fbbf24",fontWeight:800}}>Valeur Vénale DGI</td>
                <td style={{padding:"7px 8px",textAlign:"right",color:"#fbbf24",fontWeight:800,fontSize:14}}>{fC(r.valeur_venale)}€</td>
                <td style={{padding:"7px 8px",textAlign:"right",color:"#fbbf24",fontWeight:700}}>{r.valeur_unitaire?fmt(r.valeur_unitaire,2)+"€":"—"}</td>
                <td style={{padding:"7px 8px"}}/>
              </tr>
            </tbody>
          </table>
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};


// ==========================================================================
// DASHBOARD + MAIN APP (with Football Field)
// ==========================================================================
const colors={dcf:"#93c5fd",exc:"#60a5fa",mul:"#3b82f6",rfr:"#2563eb",mpe:"#1d4ed8",ast:"#1e40af",vp:"#C9A84C",vm:"#b8963e",vr:"#a07c2f",mba:"#C9A84C",ebe_re:"#b8963e",comb:"#d4a843",us:"#f87171",de:"#fbbf24",ch:"#f472b6",uk:"#34d399",fr:"#818cf8"};

// SVG icon components — geometric, monoline, 20×20 viewBox
const ModelIcon = ({id, color, size=22}) => {
  const s = size;
  const icons = {
    dcf: ( // Bar chart rising + discount curve
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="12" width="3" height="6" rx="0.5"/>
        <rect x="7" y="7" width="3" height="11" rx="0.5"/>
        <rect x="12" y="4" width="3" height="14" rx="0.5"/>
        <path d="M2 10 Q7 5 12 3 L17 2" strokeDasharray="1.5 1.5" opacity="0.5"/>
      </svg>
    ),
    exc: ( // Spread between two lines
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <path d="M2 14 Q6 12 10 10 Q14 8 18 6"/>
        <path d="M2 17 Q6 16 10 15 Q14 14 18 13" opacity="0.45"/>
        <line x1="13" y1="7" x2="13" y2="14" strokeWidth="1" strokeDasharray="1.5 1"/>
        <polygon points="13,6 11.5,8.5 14.5,8.5" fill={color} stroke="none"/>
      </svg>
    ),
    mul: ( // Ratio symbol — horizontal bars balanced
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <line x1="3" y1="8" x2="17" y2="8"/>
        <line x1="3" y1="12" x2="17" y2="12"/>
        <circle cx="7" cy="5.5" r="1.5" fill={color} stroke="none"/>
        <circle cx="13" cy="14.5" r="1.5" fill={color} stroke="none"/>
      </svg>
    ),
    rfr: ( // Royalty flow — coin stack + arrow
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <ellipse cx="8" cy="6" rx="5" ry="2"/>
        <path d="M3 6 Q3 9 8 9 Q13 9 13 6"/>
        <path d="M3 9 Q3 12 8 12 Q13 12 13 9"/>
        <path d="M14 10 L18 10 M16 8 L18 10 L16 12" strokeWidth="1.3"/>
      </svg>
    ),
    mpe: ( // Retention curve — funnel shape
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <path d="M2 4 L18 4 L14 9 L14 16 L6 16 L6 9 Z"/>
        <line x1="6" y1="12" x2="14" y2="12" opacity="0.45"/>
      </svg>
    ),
    ast: ( // Balance sheet — horizontal split
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <rect x="2" y="3" width="16" height="14" rx="1.5"/>
        <line x1="10" y1="3" x2="10" y2="17"/>
        <line x1="2" y1="10" x2="18" y2="10"/>
        <line x1="4" y1="6.5" x2="8" y2="6.5" opacity="0.6"/>
        <line x1="4" y1="13" x2="8" y2="13" opacity="0.6"/>
        <line x1="12" y1="6.5" x2="16" y2="6.5" opacity="0.6"/>
      </svg>
    ),
    // DGI methods — same language, gold color
    vp: ( // Weighted average — scale beam
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <line x1="10" y1="3" x2="10" y2="17"/>
        <line x1="5" y1="8" x2="15" y2="8"/>
        <circle cx="5" cy="11" r="2.5"/>
        <circle cx="15" cy="11" r="2.5"/>
        <line x1="5" y1="8" x2="5" y2="11"/>
        <line x1="15" y1="8" x2="15" y2="11"/>
      </svg>
    ),
    vm: ( // Building + upward revaluation arrow
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <rect x="3" y="8" width="10" height="9" rx="0.5"/>
        <path d="M3 8 L8 3 L13 8"/>
        <line x1="15" y1="14" x2="15" y2="5"/>
        <path d="M13 7 L15 5 L17 7"/>
      </svg>
    ),
    vr: ( // Dividend flow — recurring arrow
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <path d="M4 10 Q4 4 10 4 Q16 4 16 10 Q16 16 10 16 Q7 16 5.5 14"/>
        <path d="M3 11.5 L4 10 L5.5 11.5" strokeWidth="1.3"/>
        <line x1="10" y1="7" x2="10" y2="13"/>
        <line x1="7.5" y1="10" x2="12.5" y2="10" opacity="0.6"/>
      </svg>
    ),
    mba: ( // Cash generation — stacked horizontal fill
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <rect x="2" y="13" width="16" height="4" rx="0.5" opacity="0.4" fill={color} stroke={color}/>
        <rect x="2" y="8.5" width="12" height="3.5" rx="0.5"/>
        <rect x="2" y="4" width="8" height="3.5" rx="0.5"/>
      </svg>
    ),
    ebe_re: ( // EBITDA waterfall — descending blocks
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <rect x="2" y="4" width="4" height="13" rx="0.5"/>
        <rect x="7.5" y="7" width="4" height="10" rx="0.5"/>
        <rect x="13" y="11" width="4" height="6" rx="0.5"/>
        <line x1="6" y1="7" x2="7.5" y2="7" strokeDasharray="1 1"/>
        <line x1="11.5" y1="11" x2="13" y2="11" strokeDasharray="1 1"/>
      </svg>
    ),
    comb: ( // Weighted combination — pie + weights
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <circle cx="10" cy="10" r="7"/>
        <path d="M10 10 L10 3" />
        <path d="M10 10 L16.06 13.5"/>
        <path d="M10 10 L3.5 13" opacity="0.5"/>
        <circle cx="10" cy="10" r="1.5" fill={color} stroke="none"/>
      </svg>
    ),
    us: ( // Stars and stripes — cap rate funnel
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <circle cx="10" cy="8" r="5"/>
        <line x1="10" y1="13" x2="10" y2="18"/>
        <line x1="7" y1="16" x2="13" y2="16"/>
        <line x1="7.5" y1="7" x2="12.5" y2="7" opacity="0.5"/>
        <line x1="7.5" y1="9" x2="12.5" y2="9" opacity="0.5"/>
      </svg>
    ),
    de: ( // IDW eagle silhouette — earnings waterfall
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <rect x="2" y="5" width="16" height="11" rx="1.5"/>
        <line x1="2" y1="10" x2="18" y2="10"/>
        <path d="M6 10 L6 5"/>
        <path d="M10 10 L10 5"/>
        <path d="M14 10 L14 5" opacity="0.5"/>
        <path d="M5 14 Q10 12 15 14" opacity="0.6"/>
      </svg>
    ),
    ch: ( // Swiss cross — practitioner formula
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round">
        <rect x="3" y="3" width="14" height="14" rx="2"/>
        <line x1="10" y1="6" x2="10" y2="14"/>
        <line x1="6" y1="10" x2="14" y2="10"/>
      </svg>
    ),
    uk: ( // Union jack simplified — P/E ratio
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <circle cx="10" cy="10" r="7"/>
        <line x1="3" y1="10" x2="17" y2="10"/>
        <line x1="10" y1="3" x2="10" y2="17"/>
        <line x1="5" y1="5" x2="15" y2="15" opacity="0.4"/>
        <line x1="15" y1="5" x2="5" y2="15" opacity="0.4"/>
      </svg>
    ),
    fr: ( // Fleur-de-lis stylized — 3 petals + horizontal bar
      <svg width={s} height={s} viewBox="0 0 20 20" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 17 L10 10"/>
        <path d="M10 10 Q10 5 10 3"/>
        <path d="M10 10 Q7 7 5 5 Q6 8 10 10"/>
        <path d="M10 10 Q13 7 15 5 Q14 8 10 10"/>
        <line x1="7" y1="13" x2="13" y2="13" strokeWidth="1.6"/>
        <circle cx="10" cy="3" r="1.2" fill={color} stroke="none"/>
      </svg>
    ),
  };
  return icons[id] || null;
};
// ==========================================================================
// US — IRS Rev. Ruling 59-60 Earnings Capitalization
// ==========================================================================
const US_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", ni_n:500000, ni_n1:450000, ni_n2:400000,
    addback_owner_comp:50000, nonrecurring_items:0,
    rf_rate:0.043, erp:0.055, beta:1.1, size_premium:0.03, specific_premium:0.02,
    sustainable_growth:0.03, net_debt:100000, shares_outstanding:10000,
  };
  const [f, sF] = useState({...d, ...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("us");
  const validation = validate(f, "us");

  // computed preview
  const ke = f.rf_rate + f.beta * f.erp + (f.size_premium||0) + (f.specific_premium||0);
  const cap = ke - (f.sustainable_growth||0);
  const color = "#f87171";

  const [exportLoading_US, setExportLoading_US] = useState(false);
  const handleExport_US = () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_US(true);
    try {
      const S = JUR_XLS, A = "F87171", wb = XLSX.utils.book_new();
      const co = f.company_name || L.us_company, dt = new Date().toISOString().slice(0,10);
      // ── Sheet 1: Summary (inputs as values, results as formulas) ──
      const s1 = [
        ["VALORIS — IRS Rev. Ruling 59-60 §4","",""],
        [L.jur_exp_company, co, ""],
        [L.jur_exp_date, dt, ""],
        ["","",""],
        ["INPUTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, ""],
        ["Net Income N−2", f.ni_n2, ""],                     // B7
        ["Net Income N−1", f.ni_n1, ""],                     // B8
        ["Net Income N", f.ni_n, ""],                         // B9
        [L.us_addback, f.addback_owner_comp||0, ""],          // B10
        [L.us_nonrecurring, f.nonrecurring_items||0, ""],     // B11
        ["Rf", f.rf_rate, ""],                                // B12
        ["ERP", f.erp, ""],                                   // B13
        ["Beta", f.beta, ""],                                 // B14
        [L.us_size_prem, f.size_premium||0, ""],              // B15
        [L.us_spec_prem, f.specific_premium||0, ""],          // B16
        [L.jur_exp_growth, f.sustainable_growth||0, ""],      // B17
        [L.jur_exp_net_debt, f.net_debt||0, ""],              // B18
        [L.jur_exp_shares, f.shares_outstanding||0, ""],      // B19
        ["","",""],
        ["RESULTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, "Formula"],
        [L.jur_exp_weighted_avg, {f:"(B9*3+B8*2+B7*1)/6"}, "=(N×3 + N-1×2 + N-2×1) / 6"],
        [L.jur_exp_norm_earnings, {f:"B23+B10-B11"}, "=Weighted Avg + Add-back − Non-recurring"],
        [L.jur_exp_ke, {f:"B12+B14*B13+B15+B16"}, "=Rf + β×ERP + Size + Specific"],
        [L.jur_exp_cap_rate, {f:"B25-B17"}, "=Ke − Growth"],
        [L.jur_exp_earnings_value, {f:"B24/B26"}, "=Normalized Earnings / Cap Rate"],
        [L.jur_exp_equity_value, {f:"B27-B18"}, "=Earnings Value − Net Debt"],
        [L.jur_exp_vps, {f:'IF(B19>0,B28/B19,"—")'}, "=Equity / Shares"],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(s1);
      ws1["!cols"] = [{wch:28},{wch:20},{wch:36}];
      ws1["!merges"] = [{s:{r:0,c:0},e:{r:0,c:2}}];
      // Style title
      S.applyStyle(ws1,"A1",S.title(A)); S.applyStyle(ws1,"B1",S.title(A)); S.applyStyle(ws1,"C1",S.title(A));
      // Info rows
      [1,2].forEach(r => { S.applyStyle(ws1,`A${r+1}`,S.inputLabel()); S.applyStyle(ws1,`B${r+1}`,S.inputVal()); S.applyStyle(ws1,`C${r+1}`,S.spacer()); });
      // Section headers
      S.styleRow(ws1,4,3,S.sectionHdr(A)); S.styleRow(ws1,20,3,S.sectionHdr(A));
      // Column headers
      S.styleRow(ws1,5,3,S.colHdrL(A));  S.styleRow(ws1,21,3,S.colHdrL(A));
      // Input rows (6-18, 0-indexed)
      for (let i=6;i<=18;i++) { S.applyStyle(ws1,`A${i+1}`,S.inputLabel()); S.applyStyle(ws1,`C${i+1}`,S.spacer()); }
      [6,7,8,9,10,17].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValNum()));
      [11,12,14,15,16].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValPct()));
      S.applyStyle(ws1,"B15",S.inputValDec()); // beta
      S.applyStyle(ws1,"B20",S.inputValNum()); // shares
      // Result rows (22-29)
      for (let i=22;i<=29;i++) { S.applyStyle(ws1,`A${i+1}`,S.resultLabel(A)); S.applyStyle(ws1,`C${i+1}`,S.formulaLabel()); }
      [22,23,26,27].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.resultVal(A)));
      [24,25].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.resultValPct(A)));
      S.applyStyle(ws1,"B30",S.resultValDec(A)); // VPS
      // Spacer rows
      S.styleRow(ws1,3,3,S.spacer()); S.styleRow(ws1,19,3,S.spacer());
      // Background fill
      S.fillBg(ws1,0,0,29,2,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws1, L.jur_exp_sheet_summary);

      // ── Sheet 2: Calculation Detail (full formula chain) ──
      const s2 = [
        ["VALORIS — "+L.us_weighted_title,"","",""],
        [L.jur_exp_company, co, "", ""],
        ["","","",""],
        [L.us_tbl_year, L.us_tbl_ni, L.us_tbl_weight, L.us_tbl_weighted],
        ["N−2", f.ni_n2, 1, {f:"B5*C5"}],
        ["N−1", f.ni_n1, 2, {f:"B6*C6"}],
        ["N",   f.ni_n,  3, {f:"B7*C7"}],
        [L.us_tbl_wavg+" (÷6)", {f:"SUM(D5:D7)/6"}, "", ""],
        ["","","",""],
        [L.us_capm_title,"","",""],
        [L.jur_exp_step, L.jur_exp_value, "", ""],
        ["Rf (US Treasury 20Y)", f.rf_rate, "", ""],
        ["β × ERP", {f:"B14*B15"}, "", ""],
        ["Beta (β)", f.beta, "", ""],
        ["ERP", f.erp, "", ""],
        [L.us_size_prem, f.size_premium||0, "", ""],
        [L.us_spec_prem, f.specific_premium||0, "", ""],
        ["Ke = Rf + β×ERP + Size + Spec", {f:"B12+B13+B16+B17"}, "", ""],
        [L.jur_exp_growth, f.sustainable_growth||0, "", ""],
        [L.jur_exp_cap_rate+" = Ke − g", {f:"B18-B19"}, "", ""],
        ["","","",""],
        ["VALUATION","","",""],
        [L.jur_exp_step, L.jur_exp_value, "", ""],
        [L.jur_exp_norm_earnings, {f:"B8"}, "", "=Weighted Avg from above"],
        ["÷ "+L.jur_exp_cap_rate, {f:"B20"}, "", ""],
        ["= "+L.jur_exp_earnings_value, {f:"B24/B25"}, "", "=Norm. Earnings / Cap Rate"],
        ["− "+L.jur_exp_net_debt, f.net_debt||0, "", ""],
        ["= "+L.jur_exp_equity_value, {f:"B26-B27"}, "", "=Earnings Value − NFD"],
        ["÷ "+L.jur_exp_shares, f.shares_outstanding||0, "", ""],
        ["= "+L.jur_exp_vps, {f:'IF(B29>0,B28/B29,"—")'}, "", ""],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(s2);
      ws2["!cols"] = [{wch:34},{wch:18},{wch:10},{wch:36}];
      ws2["!merges"] = [{s:{r:0,c:0},e:{r:0,c:3}}];
      S.applyStyle(ws2,"A1",S.title(A)); S.applyStyle(ws2,"B1",S.title(A)); S.applyStyle(ws2,"C1",S.title(A)); S.applyStyle(ws2,"D1",S.title(A));
      S.styleRow(ws2,3,4,S.colHdrL(A)); S.styleRow(ws2,9,4,S.sectionHdr(A)); S.styleRow(ws2,10,4,S.colHdrL(A)); S.styleRow(ws2,21,4,S.sectionHdr(A)); S.styleRow(ws2,22,4,S.colHdrL(A));
      S.fillBg(ws2,0,0,29,3,S.spacer());
      for (let i=4;i<=7;i++) { S.applyStyle(ws2,`A${i+1}`,S.formulaLabel()); S.applyStyle(ws2,`B${i+1}`,S.formulaVal()); S.applyStyle(ws2,`C${i+1}`,S.inputVal()); S.applyStyle(ws2,`D${i+1}`,S.formulaVal()); }
      S.applyStyle(ws2,"A9",S.resultLabel(A)); S.applyStyle(ws2,"B9",S.resultVal(A));
      for (let i=11;i<=19;i++) { S.applyStyle(ws2,`A${i+1}`,S.formulaLabel()); S.applyStyle(ws2,`B${i+1}`,S.formulaValPct()); }
      S.applyStyle(ws2,"A19",S.resultLabel(A)); S.applyStyle(ws2,"B19",S.resultValPct(A));
      S.applyStyle(ws2,"A21",S.resultLabel(A)); S.applyStyle(ws2,"B21",S.resultValPct(A));
      for (let i=23;i<=29;i++) { S.applyStyle(ws2,`A${i+1}`,S.formulaLabel()); S.applyStyle(ws2,`B${i+1}`,S.formulaVal()); }
      [25,27].forEach(i => { S.applyStyle(ws2,`A${i+1}`,S.resultLabel(A)); S.applyStyle(ws2,`B${i+1}`,S.resultVal(A)); });
      XLSX.utils.book_append_sheet(wb, ws2, L.jur_exp_sheet_detail);

      // ── Sheet 3: Regulatory Reference ──
      const s3 = [[L.jur_exp_us_ref_title],[""],
        [L.jur_exp_us_ref_text],[""],
        ["Ref.: IRS Rev. Ruling 59-60 (1959) · §4 Factors · CAPM Build-Up"],
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(s3);
      ws3["!cols"] = [{wch:90}];
      S.applyStyle(ws3,"A1",S.refTitle()); S.applyStyle(ws3,"A3",S.refBody()); S.applyStyle(ws3,"A5",S.refFooter());
      S.fillBg(ws3,0,0,4,0,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws3, L.jur_exp_sheet_ref);

      XLSX.writeFile(wb, `Valoris_US_IRS59-60_${co.replace(/\s+/g,"_")}_${dt}.xlsx`);
    } catch(err) { alert(L.err_export + err.message); }
    finally { setExportLoading_US(false); }
  };

  return (<FadeIn><div style={{display:"grid", gridTemplateColumns: r ? "380px 1fr" : "1fr", gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.us_company}/>
      <I lb={L.us_company_name} v={f.company_name} oc={u("company_name")} t="text" p={L.us_company_name_ph}/>
      <div style={{...g2, marginTop:10}}>
        <I lb={L.us_shares} v={f.shares_outstanding} oc={u("shares_outstanding")} tip={L.us_shares_tip}/>
        <I lb={L.us_net_debt} v={f.net_debt} oc={u("net_debt")} money tip={L.us_net_debt_tip}/>
      </div>

      <Sc i="📊" t={L.us_ni_section}/>
      <div style={{background:"rgba(248,113,113,0.04)",border:"1px solid rgba(248,113,113,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#94a3b8",fontFamily:"monospace",marginBottom:8}}>
          {L.us_ni_formula}
        </div>
        <div style={g3}>
          <I lb={L.us_ni_n2} v={f.ni_n2} oc={u("ni_n2")} money tip={L.us_ni_n2_tip}/>
          <I lb={L.us_ni_n1} v={f.ni_n1} oc={u("ni_n1")} money tip={L.us_ni_n1_tip}/>
          <I lb={L.us_ni_n} v={f.ni_n} oc={u("ni_n")} money tip={L.us_ni_n_tip}/>
        </div>
      </div>

      <Sc i="⚙️" t={L.us_norm_section}/>
      <div style={g2}>
        <I lb={L.us_addback} v={f.addback_owner_comp} oc={u("addback_owner_comp")} money tip={L.us_addback_tip}/>
        <I lb={L.us_nonrecurring} v={f.nonrecurring_items} oc={u("nonrecurring_items")} money tip={L.us_nonrecurring_tip}/>
      </div>

      <Sc i="📈" t={L.us_capm_section}/>
      <div style={{background:"rgba(248,113,113,0.04)",border:"1px solid rgba(248,113,113,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#94a3b8",fontFamily:"monospace",marginBottom:8}}>
          {L.us_capm_formula}
        </div>
        <div style={g2}>
          <I lb={L.us_rf} v={f.rf_rate} oc={u("rf_rate")} pct tip={L.us_rf_tip}/>
          <I lb={L.us_erp} v={f.erp} oc={u("erp")} pct tip={L.us_erp_tip}/>
        </div>
        <div style={{...g3, marginTop:10}}>
          <I lb={L.us_beta} v={f.beta} oc={u("beta")} s="0.05" tip={L.us_beta_tip}/>
          <I lb={L.us_size_prem} v={f.size_premium} oc={u("size_premium")} pct tip={L.us_size_prem_tip}/>
          <I lb={L.us_spec_prem} v={f.specific_premium} oc={u("specific_premium")} pct tip={L.us_spec_prem_tip}/>
        </div>
        <div style={{marginTop:10}}>
          <I lb={L.us_growth} v={f.sustainable_growth} oc={u("sustainable_growth")} pct tip={L.us_growth_tip}/>
        </div>
        <div style={{display:"flex",gap:16,marginTop:10,padding:"8px 10px",background:"rgba(248,113,113,0.08)",borderRadius:7}}>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.us_ke}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#f87171"}}>{(ke*100).toFixed(2)}%</div></div>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.us_cap_rate}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:cap>0?"#f87171":"#ef4444"}}>{(cap*100).toFixed(2)}%</div></div>
        </div>
      </div>

      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking}
        onC={()=>run({...f},(res)=>onResult(res,{...f}))}
        onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>

    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_US} disabled={exportLoading_US} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_US?"rgba(248,113,113,0.06)":"rgba(248,113,113,0.12)",border:"1px solid rgba(248,113,113,0.35)",borderRadius:8,color:"#f87171",cursor:exportLoading_US?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_US){ev.currentTarget.style.background="rgba(248,113,113,0.22)";ev.currentTarget.style.borderColor="rgba(248,113,113,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_US?"rgba(248,113,113,0.06)":"rgba(248,113,113,0.12)";ev.currentTarget.style.borderColor="rgba(248,113,113,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_US ? L.jur_exp_generating : L.jur_exp_btn_us}
        </button>
      </div>
      <div style={{background:"rgba(248,113,113,0.06)",border:"1px solid rgba(248,113,113,0.2)",borderRadius:12,padding:"14px 18px"}}>
        <div style={{fontSize:12,fontWeight:700,color:"#f87171",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{L.us_result_title}</div>
        <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.6}}>
          {f.company_name||L.us_company} — {L.us_earnings_cap}. {L.us_norm_of} <strong style={{color:"#f1f5f9"}}>{fC(r.normalized_earnings)}€</strong> {L.us_cap_at} <strong style={{color:"#f87171"}}>{(r.cap_rate*100).toFixed(2)}%</strong>.
          {r.value_per_share ? ` ${L.us_vps} ${fmt(r.value_per_share,2)}€.` : ""}
        </div>
      </div>

      <div style={g3}>
        <Cd lb={L.us_equity_value}       v={`${fC(r.equity_value)}€`}       a={color}/>
        <Cd lb={L.us_earnings_value}     v={`${fC(r.earnings_value)}€`}      a="#fb923c"/>
        <Cd lb={L.us_value_per_share}    v={r.value_per_share ? `${fmt(r.value_per_share,2)}€` : "—"} a="#fbbf24"/>
      </div>
      <div style={g3}>
        <Cd lb={L.us_norm_earnings} v={`${fC(r.normalized_earnings)}€`} a="#94a3b8"/>
        <Cd lb={L.us_cap_rate}            v={`${(r.cap_rate*100).toFixed(2)}%`} a="#94a3b8"/>
        <Cd lb={L.us_ke_buildup}       v={`${(r.ke*100).toFixed(2)}%`}      a="#94a3b8"/>
      </div>

      <Bx t={L.us_weighted_title}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,fontFamily:"monospace"}}>
          <thead><tr style={{borderBottom:"2px solid rgba(255,255,255,0.08)"}}>
            {[L.us_tbl_year,L.us_tbl_ni,L.us_tbl_weight,L.us_tbl_weighted].map(h=><th key={h} style={{padding:"5px 8px",textAlign:"right",color:"#64748b",fontSize:10}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {r.weights.map((row,i)=>(
              <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                <td style={{padding:"5px 8px",color:"#94a3b8",fontWeight:700}}>{row.year}</td>
                <td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(row.ni)}€</td>
                <td style={{padding:"5px 8px",textAlign:"right",color:"#f87171"}}>{row.weight}×</td>
                <td style={{padding:"5px 8px",textAlign:"right",color:"#e2e8f0"}}>{fC(row.weighted)}€</td>
              </tr>
            ))}
            <tr style={{borderTop:"2px solid rgba(248,113,113,0.3)"}}>
              <td colSpan={3} style={{padding:"6px 8px",color:"#f87171",fontWeight:700}}>{L.us_tbl_wavg}</td>
              <td style={{padding:"6px 8px",textAlign:"right",color:"#f87171",fontWeight:700}}>{fC(r.ni_avg)}€</td>
            </tr>
          </tbody>
        </table>
      </Bx>

      <Bx t={L.us_capm_title}>
        <div style={{fontSize:10,fontFamily:"monospace",color:"#94a3b8",marginBottom:10}}>
          Ke = {(f.rf_rate*100).toFixed(2)}% (Rf) + {f.beta.toFixed(2)} × {(f.erp*100).toFixed(2)}% (ERP) + {(f.size_premium*100).toFixed(2)}% (size) + {((f.specific_premium||0)*100).toFixed(2)}% (spec.) = <span style={{color:"#f87171",fontWeight:700}}>{(r.ke*100).toFixed(2)}%</span>
          &nbsp;|&nbsp; {L.us_cap_rate} = {(r.ke*100).toFixed(2)}% − {((f.sustainable_growth||0)*100).toFixed(2)}% = <span style={{color:"#f87171",fontWeight:700}}>{(r.cap_rate*100).toFixed(2)}%</span>
        </div>
        <div style={{padding:"10px 14px",background:"rgba(248,113,113,0.06)",border:"1px solid rgba(248,113,113,0.15)",borderRadius:8,fontSize:12,color:"#94a3b8",lineHeight:1.7}}>
          <strong style={{color:"#f87171"}}>{L.us_note_title}</strong> {L.us_note_text}
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================
// DE — IDW S1 (2008) Ertragswertverfahren
// ==========================================================================
const DE_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", base_earnings:800000,
    earnings_growth_initial:0.05, earnings_growth_terminal:0.01, growth_convergence_year:5,
    basiszinssatz:0.025, mrp_after_tax:0.055,
    beta_unlevered:0.9, debt_to_equity:0.30, tax_rate:0.30,
    abgeltungssteuer:0.26375, terminal_growth:0.01,
    forecast_years:10, net_debt:200000, shares_outstanding:10000,
  };
  const [f, sF] = useState({...d, ...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("de");
  const validation = validate(f, "de");

  const bl = f.beta_unlevered * (1 + (1 - f.tax_rate) * (f.debt_to_equity||0));
  const s  = f.abgeltungssteuer || 0.26375;
  const ke_after = f.basiszinssatz * (1 - s) + bl * f.mrp_after_tax;
  const color = "#fbbf24";

  const [exportLoading_DE, setExportLoading_DE] = useState(false);
  const handleExport_DE = () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_DE(true);
    try {
      const S = JUR_XLS, A = "FBBF24", wb = XLSX.utils.book_new();
      const co = f.company_name || L.de_company, dt = new Date().toISOString().slice(0,10);
      const nY = f.forecast_years || 10;
      // ── Sheet 1: Summary ──
      const s1 = [
        ["VALORIS — IDW S1 (2008) Ertragswertverfahren","",""],
        [L.jur_exp_company, co, ""], [L.jur_exp_date, dt, ""], ["","",""],
        ["INPUTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, ""],
        [L.de_base_earnings, f.base_earnings, ""],            // B7
        [L.de_growth_initial, f.earnings_growth_initial, ""], // B8
        [L.de_growth_terminal, f.earnings_growth_terminal,""],// B9
        [L.de_conv_year, f.growth_convergence_year||5, ""],   // B10
        [L.de_forecast_years, nY, ""],                        // B11
        [L.de_basiszinssatz, f.basiszinssatz, ""],            // B12
        ["MRP after-tax", f.mrp_after_tax, ""],               // B13
        ["β unlevered", f.beta_unlevered, ""],                // B14
        ["D/E", f.debt_to_equity, ""],                        // B15
        [L.de_tax+" (IS)", f.tax_rate, ""],                   // B16
        [L.de_abgelt+" (s)", f.abgeltungssteuer||0.26375,""],// B17
        [L.de_terminal_gordon+" (g)", f.terminal_growth||0,""],// B18
        [L.jur_exp_net_debt, f.net_debt||0, ""],              // B19
        [L.jur_exp_shares, f.shares_outstanding||0, ""],      // B20
        ["","",""],
        ["RESULTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, "Formula"],
        [L.jur_exp_beta_levered, {f:"B14*(1+(1-B16)*B15)"}, "=β_u × [1+(1−IS)×D/E]"],
        [L.jur_exp_ke_after_tax, {f:"B12*(1-B17)+B24*B13"}, "=Basiszins×(1−s) + β_lev×MRP"],
        [L.jur_exp_sum_pv, r.sum_pv_earnings, ""],
        [L.jur_exp_terminal_value, r.terminal_value, ""],
        [L.jur_exp_ertragswert, {f:"B26+B27"}, "=Sum PV + PV(TV)"],
        [L.jur_exp_equity_value, {f:"B28-B19"}, "=Ertragswert − NFD"],
        [L.jur_exp_vps, {f:'IF(B20>0,B29/B20,"—")'}, "=Equity / Shares"],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(s1);
      ws1["!cols"] = [{wch:30},{wch:20},{wch:36}];
      ws1["!merges"] = [{s:{r:0,c:0},e:{r:0,c:2}}];
      S.applyStyle(ws1,"A1",S.title(A)); S.applyStyle(ws1,"B1",S.title(A)); S.applyStyle(ws1,"C1",S.title(A));
      [1,2].forEach(rw => { S.applyStyle(ws1,`A${rw+1}`,S.inputLabel()); S.applyStyle(ws1,`B${rw+1}`,S.inputVal()); });
      S.styleRow(ws1,4,3,S.sectionHdr(A)); S.styleRow(ws1,5,3,S.colHdrL(A));
      S.styleRow(ws1,21,3,S.sectionHdr(A)); S.styleRow(ws1,22,3,S.colHdrL(A));
      for (let i=6;i<=19;i++) { S.applyStyle(ws1,`A${i+1}`,S.inputLabel()); S.applyStyle(ws1,`C${i+1}`,S.spacer()); }
      [6,18].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValNum()));
      [7,8,11,12,14,15,16,17].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValPct()));
      [13].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValDec()));
      [9,10,19].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputVal()));
      for (let i=23;i<=29;i++) { S.applyStyle(ws1,`A${i+1}`,S.resultLabel(A)); S.applyStyle(ws1,`C${i+1}`,S.formulaLabel()); }
      [23,24].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.resultValDec(A)));
      [25,26,27,28].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.resultVal(A)));
      S.applyStyle(ws1,"B25",S.resultValPct(A));
      S.fillBg(ws1,0,0,30,2,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws1, L.jur_exp_sheet_summary);

      // ── Sheet 2: Calculation Detail (projections table with formulas) ──
      const s2rows = [
        ["VALORIS — "+L.de_proj_title,"","","",""],
        [L.jur_exp_company, co, "", "", ""],
        ["","","","",""],
        ["HAMADA BETA","","","",""],
        ["β unlevered", f.beta_unlevered, "", "", ""],       // B5
        [L.de_tax+" (IS)", f.tax_rate, "", "", ""],          // B6
        ["D/E", f.debt_to_equity, "", "", ""],               // B7
        ["β levered = β_u×[1+(1−IS)×D/E]", {f:"B5*(1+(1-B6)*B7)"}, "", "", ""],
        ["","","","",""],
        ["DISCOUNT RATE (IDW S1)","","","",""],
        [L.de_basiszinssatz, f.basiszinssatz, "", "", ""],   // B11
        [L.de_abgelt+" (s)", f.abgeltungssteuer||0.26375,"","",""], // B12
        ["Basiszins×(1−s)", {f:"B11*(1-B12)"}, "", "", ""],
        ["β_lev × MRP", {f:"B8*"+f.mrp_after_tax}, "", "", ""],
        ["Ke after-tax", {f:"B13+B14"}, "", "", ""],
        ["","","","",""],
        ["EARNINGS PROJECTIONS","","","",""],
        [L.jur_exp_year, L.jur_exp_earnings, "Growth", "Discount factor", L.jur_exp_pv],
      ];
      // projection rows: use actual values since the multi-year convergence formula is complex
      r.projections.forEach(p => {
        s2rows.push([p.year, p.earnings, p.growth, null, p.pv]);
      });
      const pvSumRow = s2rows.length + 1;
      s2rows.push(["","","","",""]);
      s2rows.push([L.jur_exp_sum_pv, {f:`SUM(E19:E${18+nY})`}, "", "", ""]);
      s2rows.push([L.jur_exp_terminal_value+" (Gordon)", r.terminal_value, "", "", ""]);
      s2rows.push([L.jur_exp_ertragswert, {f:`B${pvSumRow+1}+B${pvSumRow+2}`}, "", "", ""]);
      s2rows.push(["− "+L.jur_exp_net_debt, f.net_debt||0, "", "", ""]);
      s2rows.push(["= "+L.jur_exp_equity_value, {f:`B${pvSumRow+3}-B${pvSumRow+4}`}, "", "", ""]);
      const ws2 = XLSX.utils.aoa_to_sheet(s2rows);
      ws2["!cols"] = [{wch:34},{wch:16},{wch:12},{wch:16},{wch:16}];
      ws2["!merges"] = [{s:{r:0,c:0},e:{r:0,c:4}}];
      S.applyStyle(ws2,"A1",S.title(A)); for(let c=1;c<5;c++) S.applyStyle(ws2,XLSX.utils.encode_cell({r:0,c}),S.title(A));
      S.styleRow(ws2,3,5,S.sectionHdr(A)); S.styleRow(ws2,9,5,S.sectionHdr(A)); S.styleRow(ws2,16,5,S.sectionHdr(A));
      S.styleRow(ws2,17,5,S.colHdrL(A));
      // Style projection data rows
      for (let i=18;i<18+nY;i++) {
        S.applyStyle(ws2,`A${i+1}`,S.formulaLabel());
        S.applyStyle(ws2,`B${i+1}`,S.formulaVal());
        S.applyStyle(ws2,`C${i+1}`,S.formulaValPct());
        S.applyStyle(ws2,`E${i+1}`,S.formulaVal());
      }
      // Style beta & Ke rows
      [4,5,6].forEach(i=>{S.applyStyle(ws2,`A${i+1}`,S.formulaLabel());S.applyStyle(ws2,`B${i+1}`,S.formulaValPct());});
      S.applyStyle(ws2,"A8",S.resultLabel(A)); S.applyStyle(ws2,"B8",S.resultValDec(A));
      [10,11].forEach(i=>{S.applyStyle(ws2,`A${i+1}`,S.formulaLabel());S.applyStyle(ws2,`B${i+1}`,S.formulaValPct());});
      [12,13].forEach(i=>{S.applyStyle(ws2,`A${i+1}`,S.formulaLabel());S.applyStyle(ws2,`B${i+1}`,S.formulaValPct());});
      S.applyStyle(ws2,"A15",S.resultLabel(A)); S.applyStyle(ws2,"B15",S.resultValPct(A));
      // Style totals
      for(let i=pvSumRow;i<=pvSumRow+4;i++) { S.applyStyle(ws2,`A${i+1}`,S.resultLabel(A)); S.applyStyle(ws2,`B${i+1}`,S.resultVal(A)); }
      S.fillBg(ws2,0,0,s2rows.length-1,4,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws2, L.jur_exp_sheet_detail);

      // ── Sheet 3: Regulatory Reference ──
      const s3 = [[L.jur_exp_de_ref_title],[""],[L.jur_exp_de_ref_text],[""],
        ["Ref.: IDW S1 i.d.F. 2008 §§100–167 · IDW RS HFA 10 · Bundesbank Svensson"]];
      const ws3 = XLSX.utils.aoa_to_sheet(s3);
      ws3["!cols"] = [{wch:90}];
      S.applyStyle(ws3,"A1",S.refTitle()); S.applyStyle(ws3,"A3",S.refBody()); S.applyStyle(ws3,"A5",S.refFooter());
      S.fillBg(ws3,0,0,4,0,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws3, L.jur_exp_sheet_ref);

      XLSX.writeFile(wb, `Valoris_DE_IDW-S1_${co.replace(/\s+/g,"_")}_${dt}.xlsx`);
    } catch(err) { alert(L.err_export + err.message); }
    finally { setExportLoading_DE(false); }
  };

  return (<FadeIn><div style={{display:"grid", gridTemplateColumns: r ? "380px 1fr" : "1fr", gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.de_company}/>
      <I lb={L.de_company_name} v={f.company_name} oc={u("company_name")} t="text" p={L.de_company_name_ph}/>
      <div style={{...g2, marginTop:10}}>
        <I lb={L.de_shares} v={f.shares_outstanding} oc={u("shares_outstanding")}/>
        <I lb={L.de_net_debt} v={f.net_debt} oc={u("net_debt")} money/>
      </div>

      <Sc i="📊" t={L.de_earnings_section}/>
      <div style={{background:"rgba(251,191,36,0.04)",border:"1px solid rgba(251,191,36,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#94a3b8",fontFamily:"monospace",marginBottom:8}}>
          {L.de_earnings_formula}
        </div>
        <I lb={L.de_base_earnings} v={f.base_earnings} oc={u("base_earnings")} money tip={L.de_base_earnings_tip}/>
        <div style={{...g3, marginTop:10}}>
          <I lb={L.de_growth_initial} v={f.earnings_growth_initial} oc={u("earnings_growth_initial")} pct tip={L.de_growth_initial_tip}/>
          <I lb={L.de_growth_terminal} v={f.earnings_growth_terminal} oc={u("earnings_growth_terminal")} pct tip={L.de_growth_terminal_tip}/>
          <I lb={L.de_conv_year} v={f.growth_convergence_year} oc={u("growth_convergence_year")} tip={L.de_conv_year_tip}/>
        </div>
        <div style={{marginTop:10}}>
          <I lb={L.de_terminal_gordon} v={f.terminal_growth} oc={u("terminal_growth")} pct tip={L.de_terminal_gordon_tip}/>
        </div>
        <div style={{marginTop:6}}>
          <I lb={L.de_forecast_years} v={f.forecast_years} oc={u("forecast_years")} tip={L.de_forecast_years_tip}/>
        </div>
      </div>

      <Sc i="📈" t={L.de_capm_section}/>
      <div style={{background:"rgba(251,191,36,0.04)",border:"1px solid rgba(251,191,36,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#94a3b8",fontFamily:"monospace",marginBottom:8}}>
          {L.de_capm_formula}
        </div>
        <div style={g2}>
          <I lb={L.de_basiszinssatz} v={f.basiszinssatz} oc={u("basiszinssatz")} pct tip={L.de_basiszinssatz_tip}/>
          <I lb={L.de_mrp} v={f.mrp_after_tax} oc={u("mrp_after_tax")} pct tip={L.de_mrp_tip}/>
        </div>
        <div style={{...g3, marginTop:10}}>
          <I lb={L.de_beta} v={f.beta_unlevered} oc={u("beta_unlevered")} s="0.05" tip={L.de_beta_tip}/>
          <I lb={L.de_de_ratio} v={f.debt_to_equity} oc={u("debt_to_equity")} s="0.05" tip={L.de_de_ratio_tip}/>
          <I lb={L.de_tax} v={f.tax_rate} oc={u("tax_rate")} pct tip={L.de_tax_tip}/>
        </div>
        <div style={{marginTop:10}}>
          <I lb={L.de_abgelt} v={f.abgeltungssteuer} oc={u("abgeltungssteuer")} pct tip={L.de_abgelt_tip}/>
        </div>
        <div style={{display:"flex",gap:16,marginTop:10,padding:"8px 10px",background:"rgba(251,191,36,0.08)",borderRadius:7}}>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.de_beta_lev}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#fbbf24"}}>{bl.toFixed(3)}</div></div>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.de_ke_after}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#fbbf24"}}>{(ke_after*100).toFixed(2)}%</div></div>
        </div>
      </div>

      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking}
        onC={()=>run({...f},(res)=>onResult(res,{...f}))}
        onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>

    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_DE} disabled={exportLoading_DE} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_DE?"rgba(251,191,36,0.06)":"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.35)",borderRadius:8,color:"#fbbf24",cursor:exportLoading_DE?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_DE){ev.currentTarget.style.background="rgba(251,191,36,0.22)";ev.currentTarget.style.borderColor="rgba(251,191,36,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_DE?"rgba(251,191,36,0.06)":"rgba(251,191,36,0.12)";ev.currentTarget.style.borderColor="rgba(251,191,36,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_DE ? L.jur_exp_generating : L.jur_exp_btn_de}
        </button>
      </div>
      <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:12,padding:"14px 18px"}}>
        <div style={{fontSize:12,fontWeight:700,color:"#fbbf24",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{L.de_result_title}</div>
        <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.6}}>
          {f.company_name||L.de_company} — {L.de_ertragswert}. {L.de_ke_after} <strong style={{color:"#fbbf24"}}>{(r.ke_after*100).toFixed(2)}%</strong>
          {" · "}{L.de_beta_lev} <strong style={{color:"#fbbf24"}}>{r.beta_levered.toFixed(3)}</strong>
          {" · "}{L.de_terminal_value} <strong style={{color:"#fbbf24"}}>{r.terminal_value_pct ? `${(r.terminal_value_pct*100).toFixed(1)}%` : "—"}</strong> {L.de_of_ertragswert}.
        </div>
      </div>

      <div style={g3}>
        <Cd lb={L.de_equity_value}    v={`${fC(r.equity_value)}€`}    a={color}/>
        <Cd lb={L.de_ertragswert_lbl}     v={`${fC(r.ertragswert)}€`}     a="#f59e0b"/>
        <Cd lb={L.de_value_per_share} v={r.value_per_share ? `${fmt(r.value_per_share,2)}€` : "—"} a="#fbbf24"/>
      </div>
      <div style={g3}>
        <Cd lb={L.de_sum_pv} v={`${fC(r.sum_pv_earnings)}€`} a="#94a3b8"/>
        <Cd lb={L.de_terminal_lbl}  v={`${fC(r.terminal_value)}€`}   a="#94a3b8"/>
        <Cd lb={L.de_terminal_pct}      v={r.terminal_value_pct ? `${(r.terminal_value_pct*100).toFixed(1)}%` : "—"} a="#94a3b8"/>
      </div>

      <Bx t={L.de_proj_title}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"monospace"}}>
            <thead><tr style={{borderBottom:"2px solid rgba(255,255,255,0.08)"}}>
              {[L.de_tbl_yr,L.de_tbl_earnings,L.de_tbl_growth,L.de_tbl_pv].map(h=><th key={h} style={{padding:"5px 6px",textAlign:"right",color:"#64748b",fontSize:10}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {r.projections.map((p,i)=>(
                <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                  <td style={{padding:"4px 6px",color:"#94a3b8",fontWeight:700}}>{p.year}</td>
                  <td style={{padding:"4px 6px",textAlign:"right",color:"#e2e8f0"}}>{fC(p.earnings)}€</td>
                  <td style={{padding:"4px 6px",textAlign:"right",color:p.growth>0?"#10b981":"#f87171"}}>{(p.growth*100).toFixed(1)}%</td>
                  <td style={{padding:"4px 6px",textAlign:"right",color:"#e2e8f0"}}>{fC(p.pv)}€</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Bx>

      <Bx t={L.de_note_title}>
        <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.8}}>
          <div style={{marginBottom:6}}><strong style={{color:"#fbbf24"}}>{L.de_note_ke}</strong> {L.de_note_ke_formula}</div>
          <div style={{marginBottom:6}}><strong style={{color:"#fbbf24"}}>{L.de_note_beta}</strong> {L.de_note_beta_text} {r.beta_levered.toFixed(3)}</div>
          <div style={{marginBottom:6}}><strong style={{color:"#fbbf24"}}>{L.de_note_abgelt}</strong> {((f.abgeltungssteuer||0.26375)*100).toFixed(3)}% {L.de_note_abgelt_text}</div>
          <div style={{color:"#475569",fontSize:10}}>{L.de_note_ref}</div>
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================
// CH — CSI Circulaire n°28 Formule Praticien
// ==========================================================================
const CANTON_RATES = {
  ge:{label:"Genève",rate:0.07}, vd:{label:"Vaud",rate:0.08},
  zh:{label:"Zurich",rate:0.075}, be:{label:"Berne",rate:0.08},
  bs:{label:"Bâle-Ville",rate:0.075}, lu:{label:"Lucerne",rate:0.08},
  ag:{label:"Argovie",rate:0.085}, st:{label:"Saint-Gall",rate:0.085},
  fr:{label:"Fribourg",rate:0.08}, ne:{label:"Neuchâtel",rate:0.085},
};

const CH_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", profit_n:300000, profit_n1:260000,
    taux_capitalisation:0.07, equity_book:1200000, hidden_reserves:200000,
    hidden_reserves_tax_rate:0.215, minority_discount:0, shares_outstanding:10000,
    canton:"ge",
  };
  const [f, sF] = useState({...d, ...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("ch");
  const validation = validate(f, "ch");
  const color = "#f472b6";

  // Apply canton default rate when canton changes
  const applyCanton = (k) => {
    const c = CANTON_RATES[k];
    sF(p => ({...p, canton:k, taux_capitalisation: c?.rate ?? p.taux_capitalisation}));
  };

  // live preview
  const bm = (2 * f.profit_n + 1 * (f.profit_n1||0)) / 3;
  const vr_preview = f.taux_capitalisation > 0 ? bm / f.taux_capitalisation : 0;
  const vi_preview = (f.equity_book||0) + (f.hidden_reserves||0)*(1-(f.hidden_reserves_tax_rate||0.215));

  const [exportLoading_CH, setExportLoading_CH] = useState(false);
  const handleExport_CH = () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_CH(true);
    try {
      const S = JUR_XLS, A = "F472B6", wb = XLSX.utils.book_new();
      const co = f.company_name || L.sec_company, dt = new Date().toISOString().slice(0,10);
      // ── Sheet 1: Summary ──
      const s1 = [
        ["VALORIS — CSI Circulaire n°28 — Formule Praticien","",""],
        [L.jur_exp_company, co, ""], [L.jur_exp_date, dt, ""],
        [L.ch_canton_label, CANTON_RATES[f.canton]?.label||f.canton, ""],
        ["","",""],
        ["INPUTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, ""],
        [L.ch_profit_n+" (×2)", f.profit_n, ""],                  // B8
        [L.ch_profit_n1+" (×1)", f.profit_n1||0, ""],             // B9
        [L.jur_exp_cap_rate_cantonal, f.taux_capitalisation, ""],  // B10
        [L.ch_equity_book, f.equity_book||0, ""],                  // B11
        [L.ch_reserves, f.hidden_reserves||0, ""],                 // B12
        [L.ch_taux_is, f.hidden_reserves_tax_rate||0.215, ""],    // B13
        [L.ch_decote_min, f.minority_discount||0, ""],            // B14
        [L.jur_exp_shares, f.shares_outstanding||0, ""],          // B15
        ["","",""],
        ["RESULTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, "Formula"],
        [L.jur_exp_avg_profit, {f:"(2*B8+B9)/3"}, "=(2×N + N-1) / 3"],
        [L.jur_exp_vr, {f:"B19/B10"}, "=Bénéfice moyen / Taux cantonal"],
        [L.jur_exp_reserves_net, {f:"B12*(1-B13)"}, "=Réserves × (1 − IS)"],
        [L.jur_exp_vi, {f:"B11+B21"}, "=Equity book + Réserves nettes"],
        [L.jur_exp_gross_value, {f:"(2*B20+B22)/3"}, "=(2×VR + VI) / 3"],
        [L.jur_exp_minority_discount, {f:"B23*B14"}, "=Valeur brute × Décote"],
        [L.jur_exp_tax_value, {f:"B23-B24"}, "=Valeur brute − Décote"],
        [L.jur_exp_vps, {f:'IF(B15>0,B25/B15,"—")'}, "=Valeur fiscale / Titres"],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(s1);
      ws1["!cols"] = [{wch:30},{wch:20},{wch:36}];
      ws1["!merges"] = [{s:{r:0,c:0},e:{r:0,c:2}}];
      S.applyStyle(ws1,"A1",S.title(A)); S.applyStyle(ws1,"B1",S.title(A)); S.applyStyle(ws1,"C1",S.title(A));
      [1,2,3].forEach(rw => { S.applyStyle(ws1,`A${rw+1}`,S.inputLabel()); S.applyStyle(ws1,`B${rw+1}`,S.inputVal()); });
      S.styleRow(ws1,5,3,S.sectionHdr(A)); S.styleRow(ws1,6,3,S.colHdrL(A));
      S.styleRow(ws1,16,3,S.sectionHdr(A)); S.styleRow(ws1,17,3,S.colHdrL(A));
      for (let i=7;i<=14;i++) { S.applyStyle(ws1,`A${i+1}`,S.inputLabel()); S.applyStyle(ws1,`C${i+1}`,S.spacer()); }
      [7,8,10,11,14].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValNum()));
      [9,12,13].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValPct()));
      for (let i=18;i<=25;i++) { S.applyStyle(ws1,`A${i+1}`,S.resultLabel(A)); S.applyStyle(ws1,`C${i+1}`,S.formulaLabel()); }
      [18,19,20,21,22,23,24].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.resultVal(A)));
      S.applyStyle(ws1,"B26",S.resultValDec(A));
      S.fillBg(ws1,0,0,25,2,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws1, L.jur_exp_sheet_summary);

      // ── Sheet 2: Calculation Detail ──
      const hasDiscount = (f.minority_discount||0) > 0;
      const s2 = [
        ["VALORIS — Formule Praticien — "+L.jur_exp_sheet_detail,""],
        [L.jur_exp_company, co], ["",""],
        [L.ch_vr_section.toUpperCase(),""],
        [L.jur_exp_step, L.jur_exp_value],
        [L.ch_profit_n+" (N, ×2)", f.profit_n],                  // B6
        [L.ch_profit_n1+" (N−1, ×1)", f.profit_n1||0],           // B7
        [L.jur_exp_avg_profit+" = (2×N + N-1) / 3", {f:"(2*B6+B7)/3"}],
        [L.jur_exp_cap_rate_cantonal, f.taux_capitalisation],     // B9
        ["VR = "+L.jur_exp_avg_profit+" / Taux", {f:"B8/B9"}],
        ["",""],
        [L.ch_vi_section.toUpperCase(),""],
        [L.jur_exp_step, L.jur_exp_value],
        [L.ch_equity_book, f.equity_book||0],                    // B14
        [L.ch_reserves, f.hidden_reserves||0],                   // B15
        [L.ch_taux_is, f.hidden_reserves_tax_rate||0.215],       // B16
        [L.jur_exp_reserves_net+" = Rés. × (1−IS)", {f:"B15*(1-B16)"}],
        ["VI = Equity + Rés. nettes", {f:"B14+B17"}],
        ["",""],
        ["FORMULE PRATICIEN",""],
        [L.jur_exp_step, L.jur_exp_value],
        ["(2 × VR + VI) / 3", {f:"(2*B10+B18)/3"}],
        ...(hasDiscount ? [
          [L.ch_decote_min, f.minority_discount||0],
          [L.jur_exp_tax_value+" = Brute × (1−décote)", {f:"B22*(1-B23)"}],
        ] : [
          [L.jur_exp_tax_value, {f:"B22"}],
        ]),
      ];
      const taxRow = hasDiscount ? 24 : 23;
      s2.push([L.jur_exp_shares, f.shares_outstanding||0]);
      s2.push([L.jur_exp_vps, {f:`IF(B${taxRow+1}>0,B${taxRow}/B${taxRow+1},"—")`}]);
      const ws2 = XLSX.utils.aoa_to_sheet(s2);
      ws2["!cols"] = [{wch:40},{wch:20}];
      ws2["!merges"] = [{s:{r:0,c:0},e:{r:0,c:1}}];
      S.applyStyle(ws2,"A1",S.title(A)); S.applyStyle(ws2,"B1",S.title(A));
      S.styleRow(ws2,3,2,S.sectionHdr(A)); S.styleRow(ws2,4,2,S.colHdrL(A));
      S.styleRow(ws2,11,2,S.sectionHdr(A)); S.styleRow(ws2,12,2,S.colHdrL(A));
      S.styleRow(ws2,19,2,S.sectionHdr(A)); S.styleRow(ws2,20,2,S.colHdrL(A));
      // Key result cells
      S.applyStyle(ws2,"A10",S.resultLabel(A)); S.applyStyle(ws2,"B10",S.resultVal(A));
      S.applyStyle(ws2,"A18",S.resultLabel(A)); S.applyStyle(ws2,"B18",S.resultVal(A));
      S.applyStyle(ws2,`A${taxRow+1}`,S.resultLabel(A)); S.applyStyle(ws2,`B${taxRow+1}`,S.resultVal(A));
      S.fillBg(ws2,0,0,s2.length-1,1,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws2, L.jur_exp_sheet_detail);

      // ── Sheet 3: Regulatory Reference ──
      const s3 = [[L.jur_exp_ch_ref_title],[""],[L.jur_exp_ch_ref_text],[""],
        ["Ref.: CSI Circular n°28 §3.1 (2023) · FTA Circular n°37"]];
      const ws3 = XLSX.utils.aoa_to_sheet(s3);
      ws3["!cols"] = [{wch:90}];
      S.applyStyle(ws3,"A1",S.refTitle()); S.applyStyle(ws3,"A3",S.refBody()); S.applyStyle(ws3,"A5",S.refFooter());
      S.fillBg(ws3,0,0,4,0,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws3, L.jur_exp_sheet_ref);

      XLSX.writeFile(wb, `Valoris_CH_CSI-28_${co.replace(/\s+/g,"_")}_${dt}.xlsx`);
    } catch(err) { alert(L.err_export + err.message); }
    finally { setExportLoading_CH(false); }
  };

  return (<FadeIn><div style={{display:"grid", gridTemplateColumns: r ? "380px 1fr" : "1fr", gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.sec_company}/>
      <I lb={L.ch_nom_societe} v={f.company_name} oc={u("company_name")} t="text" p="Dupont SA"/>
      <div style={{...g2, marginTop:10}}>
        <I lb={L.ch_nb_titres} v={f.shares_outstanding} oc={u("shares_outstanding")}/>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          <label style={{fontSize:10,fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>{L.ch_canton_label}</label>
          <select value={f.canton} onChange={e=>applyCanton(e.target.value)}
            style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"8px 10px",color:"#e2e8f0",fontSize:12,cursor:"pointer"}}>
            {Object.entries(CANTON_RATES).map(([k,c])=>(
              <option key={k} value={k}>{c.label} ({(c.rate*100).toFixed(1)}%)</option>
            ))}
          </select>
        </div>
      </div>

      <Sc i="💰" t={L.ch_vr_section}/>
      <div style={{background:"rgba(244,114,182,0.04)",border:"1px solid rgba(244,114,182,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#94a3b8",fontFamily:"monospace",marginBottom:8}}>
          VR = (2×N + N−1) / 3 ÷ taux cantonal
        </div>
        <div style={g2}>
          <I lb={L.ch_profit_n} v={f.profit_n} oc={u("profit_n")} money tip={L.ch_profit_n_tip}/>
          <I lb={L.ch_profit_n1} v={f.profit_n1} oc={u("profit_n1")} money tip={L.ch_profit_n1_tip}/>
        </div>
        <div style={{marginTop:10}}>
          <I lb={L.ch_taux_cap} v={f.taux_capitalisation} oc={u("taux_capitalisation")} pct tip={L.ch_taux_cap_tip}/>
        </div>
        <div style={{display:"flex",gap:16,marginTop:10,padding:"8px 10px",background:"rgba(244,114,182,0.08)",borderRadius:7}}>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.ch_benefice_moyen}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#f472b6"}}>{fC(bm)}€</div></div>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.ch_vr_preview}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#f472b6"}}>{fC(vr_preview)}€</div></div>
        </div>
      </div>

      <Sc i="🏛️" t={L.ch_vi_section}/>
      <div style={{background:"rgba(244,114,182,0.04)",border:"1px solid rgba(244,114,182,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#94a3b8",fontFamily:"monospace",marginBottom:8}}>
          {L.ch_vi_formula}
        </div>
        <div style={g2}>
          <I lb={L.ch_equity_book} v={f.equity_book} oc={u("equity_book")} money tip={L.ch_equity_book_tip}/>
          <I lb={L.ch_reserves} v={f.hidden_reserves} oc={u("hidden_reserves")} money tip={L.ch_reserves_tip}/>
        </div>
        <div style={{marginTop:10}}>
          <I lb={L.ch_taux_is} v={f.hidden_reserves_tax_rate} oc={u("hidden_reserves_tax_rate")} pct tip={L.ch_taux_is_tip}/>
        </div>
        <div style={{display:"flex",gap:16,marginTop:10,padding:"8px 10px",background:"rgba(244,114,182,0.08)",borderRadius:7}}>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.ch_vi_preview}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#f472b6"}}>{fC(vi_preview)}€</div></div>
        </div>
      </div>

      <Sc i="⚙️" t={L.sec_adjustments}/>
      <I lb={L.ch_decote_min} v={f.minority_discount} oc={u("minority_discount")} pct tip={L.ch_decote_min_tip}/>

      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking}
        onC={()=>run({...f},(res)=>onResult(res,{...f}))}
        onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>

    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_CH} disabled={exportLoading_CH} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_CH?"rgba(244,114,182,0.06)":"rgba(244,114,182,0.12)",border:"1px solid rgba(244,114,182,0.35)",borderRadius:8,color:"#f472b6",cursor:exportLoading_CH?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_CH){ev.currentTarget.style.background="rgba(244,114,182,0.22)";ev.currentTarget.style.borderColor="rgba(244,114,182,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_CH?"rgba(244,114,182,0.06)":"rgba(244,114,182,0.12)";ev.currentTarget.style.borderColor="rgba(244,114,182,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_CH ? L.jur_exp_generating : L.jur_exp_btn_ch}
        </button>
      </div>
      <div style={{background:"rgba(244,114,182,0.06)",border:"1px solid rgba(244,114,182,0.2)",borderRadius:12,padding:"14px 18px"}}>
        <div style={{fontSize:12,fontWeight:700,color:"#f472b6",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{L.ch_result_title}</div>
        <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.6}}>
          {f.company_name||L.sec_company} — {L.ch_valeur_fiscale} <strong style={{color:"#f1f5f9"}}>{fC(r.valeur_fiscale)}€</strong>
          {r.value_per_share ? ` · ${L.ch_valeur_titre} ${fmt(r.value_per_share,2)}€` : ""}.
          {r.minority_discount > 0 ? ` ${L.ch_decote_de} ${(r.minority_discount*100).toFixed(0)}%.` : ""}
        </div>
      </div>

      <div style={g4}>
        <Cd lb={L.ch_lbl_vf}      v={`${fC(r.valeur_fiscale)}€`}       a={color}/>
        <Cd lb={L.ch_lbl_vt}      v={r.value_per_share ? `${fmt(r.value_per_share,2)}€` : "—"} a="#ec4899"/>
        <Cd lb={L.ch_lbl_vr} v={`${fC(r.valeur_rendement)}€`}     a="#94a3b8"/>
        <Cd lb={L.ch_lbl_vi}  v={`${fC(r.valeur_intrinseque)}€`}   a="#94a3b8"/>
      </div>

      <Bx t={L.ch_calc_title}>
        <div style={{fontSize:10,fontFamily:"monospace",color:"#94a3b8",marginBottom:12,background:"rgba(244,114,182,0.06)",borderRadius:7,padding:"8px 12px",lineHeight:1.8}}>
          <div>{L.ch_benefice_moyen} = (2 × {fC(f.profit_n)} + {fC(f.profit_n1||0)}) / 3 = <strong style={{color:"#f1f5f9"}}>{fC(r.benefice_moyen)}</strong></div>
          <div>VR = {fC(r.benefice_moyen)} ÷ {(r.taux_capitalisation*100).toFixed(1)}% = <strong style={{color:"#f472b6"}}>{fC(r.valeur_rendement)}</strong></div>
          <div>{L.ch_reserves_nettes} = {fC(f.hidden_reserves||0)} × (1 − {((f.hidden_reserves_tax_rate||0.215)*100).toFixed(1)}%) = <strong style={{color:"#f1f5f9"}}>{fC(r.reserves_nettes)}</strong></div>
          <div>VI = {fC(f.equity_book||0)} + {fC(r.reserves_nettes)} = <strong style={{color:"#f472b6"}}>{fC(r.valeur_intrinseque)}</strong></div>
          <div style={{marginTop:4,padding:"4px 0",borderTop:"1px solid rgba(244,114,182,0.2)"}}>
            <strong style={{color:"#f472b6"}}>{L.ch_gross_value} (2 × {fC(r.valeur_rendement)} + {fC(r.valeur_intrinseque)}) ÷ 3 = {fC(r.valeur_brute)}</strong>
          </div>
          {r.minority_discount > 0 && <div>{L.ch_after_discount} ({(r.minority_discount*100).toFixed(0)}%) : <strong style={{color:"#f472b6"}}>{fC(r.valeur_fiscale)}</strong></div>}
        </div>
        <div style={{fontSize:12,color:"#475569",lineHeight:1.6}}>
          {L.ch_note_ref}
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================
// UK — HMRC SAV P/E Earnings Basis (SVM107070)
// ==========================================================================
const UK_COMP = ({L, onResult, initialValues}) => {
  const d = {
    company_name:"", maintainable_earnings:400000, sector_pe:12,
    stake_pct:1.0, control_premium:0.25,
    illiquidity_discount:0.20, size_discount:0.10,
    total_shares:10000, shares_outstanding:10000,
  };
  const [f, sF] = useState({...d, ...(initialValues||{})});
  const u = k => v => sF(p => ({...p, [k]: v}));
  const {r, l, e, run, ref, sR, sE} = useCalc("uk");
  const validation = validate(f, "uk");
  const color = "#34d399";

  const gross = (f.maintainable_earnings||0) * (f.sector_pe||0);
  const stake = f.stake_pct || 1;
  const ctrl = stake >= 0.5 ? (f.control_premium||0) : 0;
  const adj_preview = gross * (1+ctrl) * (1-(f.illiquidity_discount||0)) * (1-(f.size_discount||0));

  const [exportLoading_UK, setExportLoading_UK] = useState(false);
  const handleExport_UK = () => {
    if (!r) { alert(L.alert_run_first); return; }
    setExportLoading_UK(true);
    try {
      const S = JUR_XLS, A = "34D399", wb = XLSX.utils.book_new();
      const co = f.company_name || L.uk_company, dt = new Date().toISOString().slice(0,10);
      // ── Sheet 1: Summary ──
      const s1 = [
        ["VALORIS — HMRC SAV — Open Market Value (SVM107070)","",""],
        [L.jur_exp_company, co, ""], [L.jur_exp_date, dt, ""], ["","",""],
        ["INPUTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, ""],
        [L.jur_exp_maint_earnings, f.maintainable_earnings, ""],  // B7
        [L.jur_exp_sector_pe, f.sector_pe, ""],                  // B8
        [L.jur_exp_stake, f.stake_pct||1, ""],                   // B9
        [L.jur_exp_control_prem, f.control_premium||0, ""],      // B10
        [L.jur_exp_illiquidity, f.illiquidity_discount||0, ""],   // B11
        [L.jur_exp_size_disc, f.size_discount||0, ""],            // B12
        [L.uk_total_shares, f.total_shares||0, ""],               // B13
        ["","",""],
        ["RESULTS","",""],
        [L.jur_exp_parameter, L.jur_exp_value, "Formula"],
        [L.jur_exp_gross_omv, {f:"B7*B8"}, "=Earnings × P/E"],
        [L.jur_exp_control_prem+" (adj.)", {f:'IF(B9>=0.5,B17*(1+B10),B17)'}, "=if stake≥50%: +premium"],
        [L.jur_exp_illiquidity+" (adj.)", {f:"B18*(1-B11)"}, "=after illiquidity"],
        [L.jur_exp_adj_omv, {f:"B19*(1-B12)"}, "=after size discount"],
        [L.jur_exp_eff_pe, {f:"B20/B7"}, "=Adj. OMV / Earnings"],
        [L.jur_exp_vps, {f:'IF(B13>0,B20/B13,"—")'}, "=Adj. OMV / Shares"],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(s1);
      ws1["!cols"] = [{wch:30},{wch:20},{wch:36}];
      ws1["!merges"] = [{s:{r:0,c:0},e:{r:0,c:2}}];
      S.applyStyle(ws1,"A1",S.title(A)); S.applyStyle(ws1,"B1",S.title(A)); S.applyStyle(ws1,"C1",S.title(A));
      [1,2].forEach(rw => { S.applyStyle(ws1,`A${rw+1}`,S.inputLabel()); S.applyStyle(ws1,`B${rw+1}`,S.inputVal()); });
      S.styleRow(ws1,4,3,S.sectionHdr(A)); S.styleRow(ws1,5,3,S.colHdrL(A));
      S.styleRow(ws1,14,3,S.sectionHdr(A)); S.styleRow(ws1,15,3,S.colHdrL(A));
      for (let i=6;i<=12;i++) { S.applyStyle(ws1,`A${i+1}`,S.inputLabel()); S.applyStyle(ws1,`C${i+1}`,S.spacer()); }
      [6,12].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValNum()));
      S.applyStyle(ws1,"B9",S.inputValDec()); // P/E
      [8,9,10,11].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.inputValPct()));
      for (let i=16;i<=22;i++) { S.applyStyle(ws1,`A${i+1}`,S.resultLabel(A)); S.applyStyle(ws1,`C${i+1}`,S.formulaLabel()); }
      [16,17,18,19].forEach(i => S.applyStyle(ws1,`B${i+1}`,S.resultVal(A)));
      S.applyStyle(ws1,"B22",S.resultValDec(A)); // eff P/E
      S.applyStyle(ws1,"B23",S.resultValDec(A)); // VPS
      S.fillBg(ws1,0,0,22,2,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws1, L.jur_exp_sheet_summary);

      // ── Sheet 2: Adjustment Waterfall (formulas) ──
      const s2 = [
        ["VALORIS — "+L.uk_waterfall_title,"",""],
        [L.jur_exp_company, co, ""], ["","",""],
        ["WATERFALL","",""],
        [L.jur_exp_step, L.jur_exp_value, "Adjustment"],
        [L.jur_exp_maint_earnings, f.maintainable_earnings, ""],   // B6
        [L.jur_exp_sector_pe, f.sector_pe, ""],                   // B7
        [L.jur_exp_gross_omv+" = Earnings × P/E", {f:"B6*B7"}, ""],
        [L.jur_exp_stake, f.stake_pct||1, ""],                    // B9
        [L.jur_exp_control_prem, f.control_premium||0, ""],       // B10
        ["After Control Premium", {f:'IF(B9>=0.5,B8*(1+B10),B8)'}, {f:'IF(B9>=0.5,"+"&TEXT(B10,"0%"),"N/A minority")'}],
        [L.jur_exp_illiquidity, f.illiquidity_discount||0, ""],   // B12
        ["After Illiquidity", {f:"B11*(1-B12)"}, {f:'"-"&TEXT(B12,"0%")'}],
        [L.jur_exp_size_disc, f.size_discount||0, ""],            // B14
        ["After Size Discount", {f:"B13*(1-B14)"}, {f:'"-"&TEXT(B14,"0%")'}],
        ["","",""],
        ["= "+L.jur_exp_adj_omv, {f:"B15"}, ""],
        [L.uk_total_shares, f.total_shares||0, ""],               // B18
        [L.jur_exp_eff_pe, {f:"B15/B6"}, ""],
        [L.jur_exp_vps, {f:'IF(B18>0,B15/B18,"—")'}, ""],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(s2);
      ws2["!cols"] = [{wch:36},{wch:20},{wch:22}];
      ws2["!merges"] = [{s:{r:0,c:0},e:{r:0,c:2}}];
      S.applyStyle(ws2,"A1",S.title(A)); S.applyStyle(ws2,"B1",S.title(A)); S.applyStyle(ws2,"C1",S.title(A));
      S.styleRow(ws2,3,3,S.sectionHdr(A)); S.styleRow(ws2,4,3,S.colHdrL(A));
      // Waterfall rows
      for (let i=5;i<=14;i++) { S.applyStyle(ws2,`A${i+1}`,S.formulaLabel()); S.applyStyle(ws2,`B${i+1}`,S.formulaVal()); }
      [7,10,12,14].forEach(i => { S.applyStyle(ws2,`A${i+1}`,S.resultLabel(A)); S.applyStyle(ws2,`B${i+1}`,S.resultVal(A)); });
      S.applyStyle(ws2,"A17",S.resultLabel(A)); S.applyStyle(ws2,"B17",S.resultVal(A));
      S.applyStyle(ws2,"A20",S.resultLabel(A)); S.applyStyle(ws2,"B20",S.resultValDec(A));
      S.applyStyle(ws2,"A21",S.resultLabel(A)); S.applyStyle(ws2,"B21",S.resultValDec(A));
      S.fillBg(ws2,0,0,20,2,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws2, L.jur_exp_sheet_detail);

      // ── Sheet 3: Regulatory Reference ──
      const s3 = [[L.jur_exp_uk_ref_title],[""],[L.jur_exp_uk_ref_text],[""],
        ["Ref.: HMRC SAV Manual SVM107070 · TCGA 1992 §272 · CG68300 · ITEPA 2003"]];
      const ws3 = XLSX.utils.aoa_to_sheet(s3);
      ws3["!cols"] = [{wch:90}];
      S.applyStyle(ws3,"A1",S.refTitle()); S.applyStyle(ws3,"A3",S.refBody()); S.applyStyle(ws3,"A5",S.refFooter());
      S.fillBg(ws3,0,0,4,0,S.spacer());
      XLSX.utils.book_append_sheet(wb, ws3, L.jur_exp_sheet_ref);

      XLSX.writeFile(wb, `Valoris_UK_HMRC-SAV_${co.replace(/\s+/g,"_")}_${dt}.xlsx`);
    } catch(err) { alert(L.err_export + err.message); }
    finally { setExportLoading_UK(false); }
  };

  return (<FadeIn><div style={{display:"grid", gridTemplateColumns: r ? "380px 1fr" : "1fr", gap:20}}>
    <Pan hr={!!r}>
      <Sc i="🏢" t={L.uk_company}/>
      <I lb={L.uk_company_name} v={f.company_name} oc={u("company_name")} t="text" p={L.uk_company_name_ph}/>
      <div style={{...g2, marginTop:10}}>
        <I lb={L.uk_total_shares} v={f.total_shares} oc={u("total_shares")} tip={L.uk_total_shares_tip}/>
        <I lb={L.uk_stake} v={f.stake_pct} oc={u("stake_pct")} pct tip={L.uk_stake_tip}/>
      </div>

      <Sc i="💰" t={L.uk_earnings_section}/>
      <div style={{background:"rgba(52,211,153,0.04)",border:"1px solid rgba(52,211,153,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#94a3b8",fontFamily:"monospace",marginBottom:8}}>
          {L.uk_earnings_formula}
        </div>
        <div style={g2}>
          <I lb={L.uk_maintainable} v={f.maintainable_earnings} oc={u("maintainable_earnings")} money tip={L.uk_maintainable_tip}/>
          <I lb={L.uk_pe} v={f.sector_pe} oc={u("sector_pe")} s="0.5" tip={L.uk_pe_tip}/>
        </div>
        <div style={{display:"flex",gap:16,marginTop:10,padding:"8px 10px",background:"rgba(52,211,153,0.08)",borderRadius:7}}>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.uk_gross_omv}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#34d399"}}>{fC(gross)}£</div></div>
          <div><div style={{fontSize:10,color:"#94a3b8"}}>{L.uk_adj_omv}</div><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#34d399"}}>{fC(adj_preview)}£</div></div>
        </div>
      </div>

      <Sc i="⚙️" t={L.uk_adj_section}/>
      <div style={{background:"rgba(52,211,153,0.04)",border:"1px solid rgba(52,211,153,0.15)",borderRadius:9,padding:12,marginBottom:8}}>
        <div style={{fontSize:10,color:"#64748b",marginBottom:8}}>
          {L.uk_control_note_pre} <strong style={{color:stake>=0.5?"#34d399":"#94a3b8"}}>{(stake*100).toFixed(0)}% — {stake>=0.5?L.uk_majority:L.uk_minority}</strong>)
        </div>
        <div style={g3}>
          <I lb={L.uk_control} v={f.control_premium} oc={u("control_premium")} pct tip={L.uk_control_tip}/>
          <I lb={L.uk_illiquidity} v={f.illiquidity_discount} oc={u("illiquidity_discount")} pct tip={L.uk_illiquidity_tip}/>
          <I lb={L.uk_size} v={f.size_discount} oc={u("size_discount")} pct tip={L.uk_size_tip}/>
        </div>
      </div>

      <Warns L={L} validation={validation}/>
      <Bt L={L} l={l} disabled={validation.blocking}
        onC={()=>run({...f},(res)=>onResult(res,{...f}))}
        onX={()=>{sF({...d});sR(null);sE(null);}}/>
      <Er e={e}/>
    </Pan>

    {r && <div ref={ref} style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-6,marginBottom:2}}>
        <button onClick={handleExport_UK} disabled={exportLoading_UK} style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",background:exportLoading_UK?"rgba(52,211,153,0.06)":"rgba(52,211,153,0.12)",border:"1px solid rgba(52,211,153,0.35)",borderRadius:8,color:"#34d399",cursor:exportLoading_UK?"wait":"pointer",fontSize:12,fontWeight:600,transition:"all 0.15s"}}
          onMouseEnter={ev=>{if(!exportLoading_UK){ev.currentTarget.style.background="rgba(52,211,153,0.22)";ev.currentTarget.style.borderColor="rgba(52,211,153,0.6)";}}}
          onMouseLeave={ev=>{ev.currentTarget.style.background=exportLoading_UK?"rgba(52,211,153,0.06)":"rgba(52,211,153,0.12)";ev.currentTarget.style.borderColor="rgba(52,211,153,0.35)";}}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          {exportLoading_UK ? L.jur_exp_generating : L.jur_exp_btn_uk}
        </button>
      </div>
      <div style={{background:"rgba(52,211,153,0.06)",border:"1px solid rgba(52,211,153,0.2)",borderRadius:12,padding:"14px 18px"}}>
        <div style={{fontSize:12,fontWeight:700,color:"#34d399",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{L.uk_result_title}</div>
        <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.6}}>
          {f.company_name||L.uk_company} — {L.uk_pe_basis}. {L.uk_gross_omv} <strong style={{color:"#f1f5f9"}}>{fC(r.gross_omv)}£</strong> {L.uk_adj_to} <strong style={{color:"#34d399"}}>{fC(r.adjusted_omv)}£</strong>.
          {L.uk_eff_pe} <strong style={{color:"#34d399"}}>{r.effective_pe.toFixed(1)}×</strong>.
          {r.value_per_share ? ` ${L.uk_vps} ${fmt(r.value_per_share,2)}£.` : ""}
        </div>
      </div>

      <div style={g4}>
        <Cd lb={L.uk_adj_omv_lbl}    v={`${fC(r.adjusted_omv)}£`}     a={color}/>
        <Cd lb={L.uk_vps_lbl}   v={r.value_per_share ? `${fmt(r.value_per_share,2)}£` : "—"} a="#10b981"/>
        <Cd lb={L.uk_gross_omv_lbl}       v={`${fC(r.gross_omv)}£`}         a="#94a3b8"/>
        <Cd lb={L.uk_eff_pe_lbl}   v={`${r.effective_pe.toFixed(1)}×`} a="#94a3b8"/>
      </div>

      <Bx t={L.uk_waterfall_title}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,fontFamily:"monospace"}}>
          <tbody>
            {[
              {label:L.uk_wf_me_pe, value: r.gross_omv, adj:null, note:L.uk_wf_gross},
              {label:L.uk_wf_control, value: r.gross_omv*(1+r.control_adj), adj:r.control_adj>0?`+${(r.control_adj*100).toFixed(0)}%`:L.uk_wf_na_minority, note:L.uk_wf_majority_only},
              {label:L.uk_wf_illiquidity, value: r.gross_omv*(1+r.control_adj)*(1-r.illiquidity_discount), adj:`−${(r.illiquidity_discount*100).toFixed(0)}%`, note:L.uk_wf_illiquidity_ref},
              {label:L.uk_wf_size, value: r.adjusted_omv, adj:`−${(r.size_discount*100).toFixed(0)}%`, note:L.uk_wf_size_ref},
            ].map((row,i)=>(
              <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                <td style={{padding:"7px 8px",color:"#94a3b8"}}>{row.label}</td>
                <td style={{padding:"7px 8px",textAlign:"right",color:"#e2e8f0",fontWeight:600}}>{fC(row.value)}£</td>
                <td style={{padding:"7px 8px",textAlign:"right",color:row.adj?.startsWith("+")?"#10b981":"#f87171",fontSize:10}}>{row.adj||""}</td>
                <td style={{padding:"7px 8px",color:"#475569",fontSize:10}}>{row.note}</td>
              </tr>
            ))}
            <tr style={{borderTop:"2px solid rgba(52,211,153,0.3)"}}>
              <td colSpan={3} style={{padding:"8px 8px",color:"#34d399",fontWeight:700}}>{L.uk_wf_omv}</td>
              <td style={{padding:"8px 8px",textAlign:"right",color:"#34d399",fontWeight:700}}>{fC(r.adjusted_omv)}£</td>
            </tr>
          </tbody>
        </table>
      </Bx>

      <Bx t={L.uk_note_title}>
        <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.8}}>
          <div style={{marginBottom:6}}><strong style={{color:"#34d399"}}>{L.uk_note_standard}</strong> {L.uk_note_standard_text}</div>
          <div style={{marginBottom:6}}><strong style={{color:"#34d399"}}>{L.uk_note_goodwill}</strong> {L.uk_note_goodwill_text}</div>
          <div style={{marginBottom:6}}><strong style={{color:"#34d399"}}>{L.uk_note_crosscheck}</strong> {L.uk_note_crosscheck_text}</div>
          <div style={{color:"#475569",fontSize:10}}>{L.uk_note_ref}</div>
        </div>
      </Bx>
    </div>}
  </div></FadeIn>);
};

// ==========================================================================
// FR — Guide DGI Nov. 2006 — 6 Official French Tax Valuation Methods
// ==========================================================================
const FR_COMP = ({L, onResult, initialValues}) => {
  const [activeMethod, setActiveMethod] = useState("vp");
  const [frResults, setFrResults] = useState({});

  const DGI_TABS = [
    {id:"vp",    label:"Productivité",    Comp:VP_COMP,     color:"#C9A84C"},
    {id:"vm",    label:"Mathématique",    Comp:VM_COMP,     color:"#b8963e"},
    {id:"vr",    label:"Rendement",       Comp:VR_COMP,     color:"#a07c2f"},
    {id:"mba",   label:"MBA",             Comp:MBA_COMP,    color:"#C9A84C"},
    {id:"ebe_re",label:"EBE / RE",        Comp:EBE_RE_COMP, color:"#b8963e"},
    {id:"comb",  label:"Combinaison DGI", Comp:COMB_COMP,   color:"#d4a843"},
  ];

  const handleSubResult = (method, r) => {
    setFrResults(prev => {
      const next = {...prev, [method]: r};
      onResult(next);
      return next;
    });
  };

  const calcCount = Object.keys(frResults).length;

  return (
    <FadeIn>
      <div>
        {/* Info banner */}
        <div style={{background:"rgba(201,168,76,0.06)",border:"1px solid rgba(201,168,76,0.15)",borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <span style={{fontSize:20}}>🇫🇷</span>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontSize:13,fontWeight:700,color:"#C9A84C",marginBottom:2}}>Guide DGI Novembre 2006</div>
            <div style={{fontSize:12,color:"#64748b",lineHeight:1.5}}>
              Administration fiscale française — 6 méthodes officielles pour donations, successions et contrôles prix de transfert
            </div>
          </div>
          {calcCount > 0 && (
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {DGI_TABS.filter(t => frResults[t.id]).map(t => (
                <span key={t.id} style={{fontSize:10,padding:"2px 7px",background:`${t.color}18`,borderRadius:4,color:t.color,fontWeight:700}}>{t.label} ✓</span>
              ))}
            </div>
          )}
        </div>

        {/* Method tabs */}
        <div style={{display:"flex",gap:0,borderBottom:"1px solid rgba(255,255,255,0.08)",marginBottom:24,overflowX:"auto",scrollbarWidth:"none"}}>
          {DGI_TABS.map(t => {
            const active = activeMethod === t.id;
            const done   = !!frResults[t.id];
            return (
              <button key={t.id} onClick={()=>setActiveMethod(t.id)} style={{
                display:"flex",alignItems:"center",gap:5,padding:"9px 14px",
                background: active ? "rgba(201,168,76,0.07)" : "transparent",
                border:"none",borderBottom: active ? `2px solid ${t.color}` : "2px solid transparent",
                color: active ? t.color : done ? "#94a3b8" : "#475569",
                fontSize:12,fontWeight:active?700:500,cursor:"pointer",transition:"all 0.15s",
                whiteSpace:"nowrap",marginBottom:"-1px",
              }}>
                {t.label}
                {done && <span style={{fontSize:10,padding:"1px 5px",background:`${t.color}20`,borderRadius:3,color:t.color,fontWeight:700}}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Active sub-component */}
        {DGI_TABS.map(({id, Comp}) => activeMethod === id && (
          <Comp key={id} L={L}
            onResult={(r) => handleSubResult(id, r)}
            initialValues={initialValues?.[id]}
          />
        ))}

        {/* Summary panel — appears when ≥2 methods calculated */}
        {calcCount >= 2 && (
          <div style={{marginTop:24,background:"rgba(201,168,76,0.04)",border:"1px solid rgba(201,168,76,0.15)",borderRadius:10,padding:"16px 20px"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#C9A84C",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>
              Synthèse DGI — {calcCount} méthode{calcCount>1?"s":""} calculée{calcCount>1?"s":""}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8}}>
              {DGI_TABS.filter(t => frResults[t.id]).map(t => {
                const r = frResults[t.id];
                const ev = r?.equity_value ?? null;
                const pu = r?.valeur_unitaire ?? null;
                return (
                  <div key={t.id} style={{background:"rgba(0,0,0,0.2)",borderRadius:8,padding:"10px 12px",borderLeft:`2px solid ${t.color}`}}>
                    <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{t.label}</div>
                    <div style={{fontSize:14,fontWeight:700,color:"#e2e8f0",fontFamily:"monospace",marginBottom:2}}>{ev!=null?fC(ev):"—"}</div>
                    {pu!=null&&<div style={{fontSize:10,color:t.color,fontFamily:"monospace"}}>{fC(pu)} / titre</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </FadeIn>
  );
};

const comps={dcf:DCF,exc:EXC,mul:MUL,rfr:RFR,mpe:MPE,ast:AST,vp:VP_COMP,vm:VM_COMP,vr:VR_COMP,mba:MBA_COMP,ebe_re:EBE_RE_COMP,comb:COMB_COMP,us:US_COMP,de:DE_COMP,ch:CH_COMP,uk:UK_COMP,fr:FR_COMP};

// ==========================================================================
// HTV LOGO COMPONENT
// ==========================================================================
const HtvLogo = ({size=32, showText=false}) => {
  const s = size;
  return (
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <svg width={s} height={s} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Outer diamond frame */}
        <polygon points="16,1 31,16 16,31 1,16"
          fill="none" stroke="#C9A84C" strokeWidth="1.5"/>
        {/* Inner filled diamond */}
        <polygon points="16,6 26,16 16,26 6,16"
          fill="#0D1B3E"/>
        {/* H letterform — two vertical bars + horizontal crossbar */}
        <rect x="10" y="10" width="2.5" height="12" rx="1.2" fill="#C9A84C"/>
        <rect x="19.5" y="10" width="2.5" height="12" rx="1.2" fill="#C9A84C"/>
        <rect x="10" y="14.75" width="12" height="2.5" rx="1.2" fill="#C9A84C"/>
      </svg>
      {showText && (
        <div style={{display:"flex",flexDirection:"column",lineHeight:1}}>
          <span style={{fontSize:11,fontWeight:800,color:"#C9A84C",letterSpacing:"1px"}}>HtV</span>
          <span style={{fontSize:10,color:"#64748b",letterSpacing:"0.5px",textTransform:"uppercase"}}>Hard to Value</span>
        </div>
      )}
    </div>
  );
};


// ==========================================================================
// EXCEL IMPORT ENGINE — HardToValue_Template.xlsx mapper
// ==========================================================================
function xlCell(sheet, row, col) {
  const addr = XLSX.utils.encode_cell({ r: row, c: col });
  const cell = sheet[addr];
  if (!cell) return null;
  const v = cell.v;
  if (v === null || v === undefined || v === "") return null;
  return v;
}
function xlNum(sheet, row, col, factor = 1) {
  const v = xlCell(sheet, row, col);
  if (v == null || typeof v !== "number") return null;
  return v * factor;
}

function parseSovWorkbook(wb) {
  const s1 = wb.Sheets["1_Identification"];
  const s2 = wb.Sheets["2_Compte_Résultat"];
  const s3 = wb.Sheets["3_Bilan"];
  const s4 = wb.Sheets["4_Paramètres_Marché"];
  const s5 = wb.Sheets["5_Actifs_Incorporels"];
  const s6 = wb.Sheets["6_Multiples_Comp"];
  const s9  = wb.Sheets["9_Diagnostic_Strategique"];
  const s7  = wb.Sheets["7_US_IRS_5960"];
  const s8  = wb.Sheets["8_DE_IDW_S1"];
  const s10 = wb.Sheets["10_CH_CSI_28"];
  const s11 = wb.Sheets["11_UK_HMRC"];

  const factor = xlNum(s1, 20, 2) || 1000;
  const company_name  = xlCell(s1, 5, 4)  || "";
  const nombre_titres = xlNum(s1, 21, 4)  || 0;
  const shares        = nombre_titres;
  const fiscal_year   = xlNum(s1, 14, 2)  || null;

  const rev      = [2,3,4,5,6].map(c => xlNum(s2, 6,  c, factor));
  const ebitda_a = [2,3,4,5,6].map(c => xlNum(s2, 17, c, factor));
  const ebit_a   = [2,3,4,5,6].map(c => xlNum(s2, 18, c, factor));
  const net_inc  = [2,3,4,5,6].map(c => xlNum(s2, 25, c, factor));
  const da_a     = [2,3,4,5,6].map(c => xlNum(s2, 14, c, factor));
  const div_a    = [2,3,4,5,6].map(c => xlNum(s2, 26, c, factor));
  const tax_a    = [2,3,4,5,6].map(c => xlNum(s2, 24, c, factor));
  const prov_a   = [2,3,4,5,6].map(c => xlNum(s2, 15, c, factor));
  const remun_dir = xlNum(s2, 11, 6, factor);

  const base_revenue  = rev[4];
  const ebitda_n      = ebitda_a[4];
  const ebit_n        = ebit_a[4];
  const net_income_n  = net_inc[4];
  const da_n          = da_a[4];
  const div_n         = div_a[4];

  const ebt_n = xlNum(s2, 20, 6, factor) || 0;
  const tax_rate_raw = (tax_a[4] != null && ebt_n !== 0) ? Math.abs(tax_a[4] / ebt_n) : 0.25;
  const tax_rate = Math.min(Math.max(tax_rate_raw, 0.10), 0.40);

  const growths = [];
  for (let i=1;i<5;i++) {
    if (rev[i]!=null && rev[i-1]!=null && rev[i-1]!==0) growths.push((rev[i]-rev[i-1])/rev[i-1]);
  }
  const revenue_growth_initial = Math.min(Math.max(growths.length>0?growths.reduce((a,b)=>a+b,0)/growths.length:0.05, -0.2), 0.5);
  const base_operating_margin  = (base_revenue && ebit_n!=null && base_revenue!==0) ? ebit_n/base_revenue : 0.10;

  const immob_inc  = xlNum(s3, 6,  4, factor);
  const goodwill   = xlNum(s3, 7,  4, factor);
  const immob_corp = xlNum(s3, 9,  4, factor);
  const stocks     = xlNum(s3, 12, 4, factor);
  const creances   = xlNum(s3, 13, 4, factor);
  const cash       = xlNum(s3, 15, 4, factor);
  const capitaux_propres = xlNum(s3, 25, 4, factor);
  const provisions = xlNum(s3, 26, 4, factor);
  const dettes_lt  = xlNum(s3, 28, 4, factor);
  const dettes_ct  = xlNum(s3, 29, 4, factor);
  const fourn      = xlNum(s3, 31, 4, factor);
  const total_debt = (dettes_lt||0)+(dettes_ct||0);
  const net_debt   = total_debt-(cash||0);
  const nwc        = (stocks||0)+(creances||0)-(fourn||0);
  const base_invested_capital = (capitaux_propres||0)+net_debt;
  const sales_to_capital = (base_revenue && base_invested_capital>0) ? base_revenue/base_invested_capital : 1.5;

  const rf        = xlNum(s4, 6,  2) || 0.035;
  const erp       = xlNum(s4, 7,  2) || 0.055;
  const beta_u    = xlNum(s4, 8,  2) || 1.0;
  const size_prem = xlNum(s4, 10, 2) || 0.02;
  const spec_prem = xlNum(s4, 11, 2) || 0.0;
  const kd_pretax = xlNum(s4, 15, 2) || 0.045;
  const tax_wacc  = xlNum(s4, 16, 2) || 0.25;
  const eq_weight = xlNum(s4, 18, 2) || 0.77;
  const inflation = xlNum(s4, 23, 2) || 0.02;
  const div_yield = xlNum(s4, 24, 2) || 0.03;
  const dlom      = xlNum(s4, 25, 2) || 0.30;
  const ctrl_prem = xlNum(s4, 26, 2) || 0;
  const size_disc = xlNum(s4, 27, 2) || 0;
  const beta_dgi  = xlNum(s4, 28, 2) || 1.0;
  const taux_is_vm= xlNum(s4, 29, 2) || 0.25;

  const d_over_e   = eq_weight>0?(1-eq_weight)/eq_weight:0.3;
  const beta_lev   = beta_u*(1+(1-tax_wacc)*d_over_e);
  const ke         = rf+beta_lev*erp+size_prem+spec_prem;
  const kd_net     = kd_pretax*(1-tax_wacc);
  const wacc_computed = eq_weight*ke+(1-eq_weight)*kd_net;

  const intangibles = [];
  if (s5) {
    for (let r=6;r<=10;r++) {
      const name = xlCell(s5,r,2);
      if (!name) continue;
      intangibles.push({
        name:String(name), type:xlCell(s5,r,3)||"Marque",
        book_value:xlNum(s5,r,4,factor)||0, life:xlNum(s5,r,5)||10,
        royalty_rate:xlNum(s5,r,6)||0.04, attrition:xlNum(s5,r,7)||0.10,
        rev_attributable:xlNum(s5,r,8,factor)||base_revenue,
        discount_rate:xlNum(s5,r,9)||(wacc_computed+0.02),
      });
    }
  }
  const contrib_labels=["BFR normatif","Immob. corporelles","Marque","Technologie","Autre actif"];
  const contrib_assets=[];
  if (s5) {
    for (let r=15;r<=19;r++) {
      const val=xlNum(s5,r,2,factor), rate=xlNum(s5,r,3);
      if(val!=null&&rate!=null) contrib_assets.push({name:contrib_labels[r-15],value:val,charge_rate:rate});
    }
  }

  const comparables=[];
  if (s6) {
    for (let r=6;r<=15;r++) {
      const name=xlCell(s6,r,1);
      if (!name) continue;
      comparables.push({name:String(name),ev_revenue:xlNum(s6,r,2),ev_ebitda:xlNum(s6,r,3),ev_ebit:xlNum(s6,r,4),pe_ratio:xlNum(s6,r,5),pb_ratio:xlNum(s6,r,6),weight:xlNum(s6,r,7)||1});
    }
  }

  const ben_n2=net_inc[2]??(net_income_n??0)*0.85;
  const ben_n1=net_inc[3]??(net_income_n??0)*0.92;
  const div_n2=div_a[2]??0, div_n1=div_a[3]??0;

  const p=(n,d=0)=>parseFloat((n||0).toFixed(d));

  return {
    _meta:{company_name,fiscal_year,factor,has_comparables:comparables.length>0,has_intangibles:intangibles.length>0,
      fields_filled:[base_revenue,ebitda_n,ebit_n,net_income_n,capitaux_propres,net_debt,rf,beta_u].filter(v=>v!=null).length},
    company_name, shares_outstanding:shares, nombre_titres:shares, base_revenue, tax_rate, net_debt,
    dcf:{company_name,shares_outstanding:shares,base_revenue,base_ebit:ebit_n,base_operating_margin:p(base_operating_margin,4),target_operating_margin:p(base_operating_margin*1.15,4),revenue_growth_initial:p(revenue_growth_initial,4),revenue_growth_terminal:0.025,growth_convergence_year:5,margin_convergence_year:5,tax_rate:p(tax_rate,4),sales_to_capital_ratio:p(sales_to_capital,3),cost_of_capital_initial:p(wacc_computed,4),cost_of_capital_terminal:p(wacc_computed-0.01,4),wacc_convergence_year:5,forecast_years:10,terminal_growth_rate:0.025,net_debt:p(net_debt),minority_interests:0},
    exc:{company_name,shares_outstanding:shares,base_revenue,base_invested_capital:p(base_invested_capital),roic_initial:p(ebit_n!=null&&base_invested_capital>0?(ebit_n*(1-tax_rate))/base_invested_capital:0.12,4),roic_terminal:0.12,roic_convergence_year:5,revenue_growth_initial:p(revenue_growth_initial,4),revenue_growth_terminal:0.025,growth_convergence_year:5,cost_of_capital:p(wacc_computed,4),cost_of_capital_terminal:p(wacc_computed-0.01,4),wacc_convergence_year:5,reinvestment_rate_initial:0.30,reinvestment_rate_terminal:0.20,reinvestment_convergence_year:5,forecast_years:10,net_debt:p(net_debt),minority_interests:0},
    mul:{company_name,shares_outstanding:shares,revenue:base_revenue,ebitda:ebitda_n,ebit:ebit_n,net_income:net_income_n,book_value_equity:capitaux_propres,net_debt:p(net_debt),minority_interests:0,illiquidity_discount:0,control_premium:ctrl_prem||0,size_discount:size_disc||0,comparables:comparables.length>0?comparables:[{name:"Comp 1",ev_ebitda:8,ev_ebit:12,pe_ratio:15,weight:1},{name:"Comp 2",ev_ebitda:9,ev_ebit:13,pe_ratio:16,weight:1},{name:"Comp 3",ev_ebitda:7,ev_ebit:11,pe_ratio:14,weight:1}]},
    rfr:intangibles.length>0?{asset_name:intangibles[0].name,asset_type:intangibles[0].type,base_revenue,royalty_rate:intangibles[0].royalty_rate,discount_rate:intangibles[0].discount_rate,tax_rate:p(tax_rate,4),forecast_years:intangibles[0].life,revenue_growth_initial:p(revenue_growth_initial,4),revenue_growth_terminal:0.025,growth_convergence_year:5,obsolescence_factor:0.02}:null,
    mpe:intangibles.length>0?{asset_name:intangibles[0].name,asset_type:intangibles[0].type,base_revenue,operating_margin:p(base_operating_margin,4),tax_rate:p(tax_rate,4),discount_rate:intangibles[0].discount_rate,annual_attrition_rate:intangibles[0].attrition,forecast_years:intangibles[0].life,revenue_growth_initial:p(revenue_growth_initial,4),revenue_growth_terminal:0.025,growth_convergence_year:5,contributory_assets:contrib_assets.length>0?contrib_assets:[{name:"BFR normatif",value:Math.abs(nwc||0),charge_rate:0.10},{name:"Immob. corporelles",value:immob_corp||0,charge_rate:0.08}]}:null,
    ast:{company_name,shares_outstanding:shares,assets:[{name:"Immobilisations incorporelles",category:"Intangible",book_value:immob_inc||0,fair_value:immob_inc||0,liquidation_discount:0.30},{name:"Immobilisations corporelles",category:"Tangible",book_value:immob_corp||0,fair_value:immob_corp||0,liquidation_discount:0.20},{name:"Stocks",category:"Circulant",book_value:stocks||0,fair_value:stocks||0,liquidation_discount:0.30},{name:"Créances",category:"Circulant",book_value:creances||0,fair_value:creances||0,liquidation_discount:0.10},{name:"Trésorerie",category:"Circulant",book_value:cash||0,fair_value:cash||0,liquidation_discount:0}].filter(a=>a.book_value>0),liabilities:[{name:"Dettes financières",book_value:total_debt||0,fair_value:total_debt||0},{name:"Dettes fournisseurs",book_value:fourn||0,fair_value:fourn||0},{name:"Provisions",book_value:provisions||0,fair_value:provisions||0}].filter(l=>l.book_value>0),apply_deferred_tax:true,deferred_tax_rate:taux_is_vm,contingent_liabilities:0,net_debt:p(net_debt)},
    vp:{company_name,nombre_titres:shares,benefice_n2:p(ben_n2),benefice_n1:p(ben_n1),benefice_n:p(net_income_n),reintegration_dirigeant:p(remun_dir),elements_non_recurrents:0,taux_rf:rf,taux_inflation:inflation,beta_risque:beta_dgi},
    vm:{company_name,nombre_titres:shares,capitaux_propres:p(capitaux_propres),revaluations:[{poste:"Immobilisations corporelles",valeur_comptable:immob_corp||0,valeur_venale:immob_corp||0,plus_value:0,necessite_exploitation:true},{poste:"Fonds commercial",valeur_comptable:goodwill||0,valeur_venale:goodwill||0,plus_value:0,necessite_exploitation:true}],apply_impot_latent:false,taux_is:taux_is_vm,passif_hors_bilan:0,include_survaleur:false,bef_recurrent:p(net_income_n),taux_rf:rf,taux_inflation:inflation,beta_risque:beta_dgi,duree_survaleur:8,valeur_fonds_commerce:goodwill||0},
    vr:{company_name,nombre_titres:shares,dividende_n2:p(div_n2),dividende_n1:p(div_n1),dividende_n:p(div_n),taux_rendement_actions_cotees:div_yield,decote_non_liquidite:dlom},
    mba:{company_name,nombre_titres:shares,benefice_net:p(net_income_n),dotations_amortissements:p(da_n),dotations_provisions_reserves:p(prov_a[4]),reprises_provisions:0,coefficient_mba:5,endettement_financier_net:p(net_debt)},
    ebe_re:{company_name,nombre_titres:shares,ebe:p(ebitda_n),resultat_exploitation:p(ebit_n),multiple_ebe:6,multiple_re:8,endettement_financier_net:p(net_debt)},
    comb:{company_name,nombre_titres:shares,capitaux_propres:p(capitaux_propres),revaluations:[{poste:"Immobilisations corporelles",valeur_comptable:immob_corp||0,valeur_venale:immob_corp||0,plus_value:0,necessite_exploitation:true}],apply_impot_latent:false,taux_is:taux_is_vm,passif_hors_bilan:0,include_survaleur:false,bef_recurrent:p(net_income_n),taux_rf:rf,taux_inflation:inflation,beta_risque:beta_dgi,duree_survaleur:8,valeur_fonds_commerce:goodwill||0,benefice_n2:p(ben_n2),benefice_n1:p(ben_n1),benefice_n:p(net_income_n),reintegration_dirigeant:p(remun_dir),elements_non_recurrents:0,use_vr:(div_n||0)>0,dividende_n2:p(div_n2),dividende_n1:p(div_n1),dividende_n:p(div_n),taux_rendement_actions_cotees:div_yield,decote_non_liquidite:dlom,use_ebe:ebitda_n!=null,ebe:p(ebitda_n),multiple_ebe:6,use_re:ebit_n!=null,resultat_exploitation:p(ebit_n),multiple_re:8,use_mba:false,benefice_net:p(net_income_n),dotations_amortissements:p(da_n),coefficient_mba:5,endettement_financier_net:p(net_debt),poids_vm_final:1,poids_vrent_final:2,decote_minorite:0,decote_clause_agrement:0},
    fr:{
      vp:{company_name,nombre_titres:shares,benefice_n2:p(ben_n2),benefice_n1:p(ben_n1),benefice_n:p(net_income_n),reintegration_dirigeant:p(remun_dir),elements_non_recurrents:0,taux_rf:rf,taux_inflation:inflation,beta_risque:beta_dgi},
      vm:{company_name,nombre_titres:shares,capitaux_propres:p(capitaux_propres),revaluations:[{poste:"Immobilisations corporelles",valeur_comptable:immob_corp||0,valeur_venale:immob_corp||0,plus_value:0,necessite_exploitation:true},{poste:"Fonds commercial",valeur_comptable:goodwill||0,valeur_venale:goodwill||0,plus_value:0,necessite_exploitation:true}],apply_impot_latent:false,taux_is:taux_is_vm,passif_hors_bilan:0,include_survaleur:false,bef_recurrent:p(net_income_n),taux_rf:rf,taux_inflation:inflation,beta_risque:beta_dgi,duree_survaleur:8,valeur_fonds_commerce:goodwill||0},
      vr:{company_name,nombre_titres:shares,dividende_n2:p(div_n2),dividende_n1:p(div_n1),dividende_n:p(div_n),taux_rendement_actions_cotees:div_yield,decote_non_liquidite:dlom},
      mba:{company_name,nombre_titres:shares,benefice_net:p(net_income_n),dotations_amortissements:p(da_n),dotations_provisions_reserves:p(prov_a[4]),reprises_provisions:0,coefficient_mba:5,endettement_financier_net:p(net_debt)},
      ebe_re:{company_name,nombre_titres:shares,ebe:p(ebitda_n),resultat_exploitation:p(ebit_n),multiple_ebe:6,multiple_re:8,endettement_financier_net:p(net_debt)},
      comb:{company_name,nombre_titres:shares,capitaux_propres:p(capitaux_propres),revaluations:[{poste:"Immobilisations corporelles",valeur_comptable:immob_corp||0,valeur_venale:immob_corp||0,plus_value:0,necessite_exploitation:true}],apply_impot_latent:false,taux_is:taux_is_vm,passif_hors_bilan:0,include_survaleur:false,bef_recurrent:p(net_income_n),taux_rf:rf,taux_inflation:inflation,beta_risque:beta_dgi,duree_survaleur:8,valeur_fonds_commerce:goodwill||0,benefice_n2:p(ben_n2),benefice_n1:p(ben_n1),benefice_n:p(net_income_n),reintegration_dirigeant:p(remun_dir),elements_non_recurrents:0,use_vr:(div_n||0)>0,dividende_n2:p(div_n2),dividende_n1:p(div_n1),dividende_n:p(div_n),taux_rendement_actions_cotees:div_yield,decote_non_liquidite:dlom,use_ebe:ebitda_n!=null,ebe:p(ebitda_n),multiple_ebe:6,use_re:ebit_n!=null,resultat_exploitation:p(ebit_n),multiple_re:8,use_mba:false,benefice_net:p(net_income_n),dotations_amortissements:p(da_n),coefficient_mba:5,endettement_financier_net:p(net_debt),poids_vm_final:1,poids_vrent_final:2,decote_minorite:0,decote_clause_agrement:0},
    },
    diag_fin:{
      years:["N-4","N-3","N-2","N-1","N"],
      revenue:rev, ebitda:ebitda_a, ebit:ebit_a, net_income:net_inc, da:da_a,
      net_debt:p(net_debt), capitaux_propres:p(capitaux_propres), total_debt:p(total_debt),
    },
    // ── 🇺🇸 US IRS Rev. Ruling 59-60 ──
    us:{
      company_name, shares_outstanding:shares, net_debt:p(net_debt),
      ni_n:p(net_income_n), ni_n1:p(ben_n1), ni_n2:p(ben_n2),
      addback_owner_comp: s7 ? (xlNum(s7,8,2,factor)||0) : p(remun_dir),
      nonrecurring_items: s7 ? (xlNum(s7,9,2,factor)||0) : 0,
      rf_rate:   s7 ? (xlNum(s7,3,2)||0.043) : rf,
      erp:       s7 ? (xlNum(s7,4,2)||0.055) : erp,
      beta:      s7 ? (xlNum(s7,5,2)||1.1)   : beta_lev,
      size_premium:    s7 ? (xlNum(s7,6,2)||0.03) : size_prem,
      specific_premium:s7 ? (xlNum(s7,7,2)||0.02) : spec_prem,
      sustainable_growth: s7 ? (xlNum(s7,10,2)||0.03) : 0.03,
    },
    // ── 🇩🇪 DE IDW S1 ──
    de:{
      company_name, shares_outstanding:shares, net_debt:p(net_debt),
      base_earnings: p(net_income_n),
      earnings_growth_initial:  s8 ? (xlNum(s8,9,2)||0.05)  : p(revenue_growth_initial,4),
      earnings_growth_terminal: s8 ? (xlNum(s8,10,2)||0.01) : 0.01,
      growth_convergence_year: 5,
      basiszinssatz:    s8 ? (xlNum(s8,3,2)||0.025)   : 0.025,
      mrp_after_tax:    s8 ? (xlNum(s8,4,2)||0.055)   : 0.055,
      beta_unlevered:   s8 ? (xlNum(s8,5,2)||0.9)     : beta_u,
      debt_to_equity:   s8 ? (xlNum(s8,6,2)||0.30)    : (eq_weight>0?(1-eq_weight)/eq_weight:0.30),
      tax_rate:         s8 ? (xlNum(s8,7,2)||0.30)     : 0.30,
      abgeltungssteuer: s8 ? (xlNum(s8,8,2)||0.26375)  : 0.26375,
      terminal_growth:  s8 ? (xlNum(s8,11,2)||0.01)    : 0.01,
      forecast_years:   s8 ? (xlNum(s8,12,2)||10)      : 10,
    },
    // ── 🇨🇭 CH CSI Circular n°28 ──
    ch:{
      company_name, shares_outstanding:shares,
      profit_n:  p(net_income_n),
      profit_n1: p(ben_n1),
      equity_book: p(capitaux_propres),
      taux_capitalisation:      s10 ? (xlNum(s10,4,2)||0.07)  : 0.07,
      hidden_reserves:          s10 ? (xlNum(s10,5,2,factor)||0) : 0,
      hidden_reserves_tax_rate: s10 ? (xlNum(s10,6,2)||0.215) : 0.215,
      minority_discount:        s10 ? (xlNum(s10,7,2)||0)     : 0,
      canton: s10 ? (String(xlCell(s10,3,2)||"ge").toLowerCase()) : "ge",
    },
    // ── 🇬🇧 UK HMRC SAV ──
    uk:{
      company_name, total_shares:shares, shares_outstanding:shares,
      maintainable_earnings: p(net_income_n),
      sector_pe:           s11 ? (xlNum(s11,3,2)||12)   : 12,
      stake_pct:           s11 ? (xlNum(s11,4,2)||1.0)  : 1.0,
      control_premium:     s11 ? (xlNum(s11,5,2)||0.25) : (ctrl_prem||0.25),
      illiquidity_discount:s11 ? (xlNum(s11,6,2)||0.20) : (dlom||0.20),
      size_discount:       s11 ? (xlNum(s11,7,2)||0.10) : (size_disc||0.10),
    },
    diag_strat: s9 ? (() => {
      const nature    = xlCell(s9, 5, 2) || "";
      const parties   = xlCell(s9, 6, 2) || "";
      const enjeux    = xlCell(s9, 7, 2) || "";
      const contexte  = xlCell(s9, 8, 2) || "";
      const forces       = xlCell(s9, 11, 2) || "";
      const faiblesses   = xlCell(s9, 12, 2) || "";
      const opportunites = xlCell(s9, 13, 2) || "";
      const menaces      = xlCell(s9, 14, 2) || "";
      const pouvoir_acheteurs    = xlNum(s9, 17, 2) || 3;
      const pouvoir_fournisseurs = xlNum(s9, 18, 2) || 3;
      const substituts           = xlNum(s9, 19, 2) || 3;
      const nouveaux_entrants    = xlNum(s9, 20, 2) || 3;
      const rivalite             = xlNum(s9, 21, 2) || 3;
      const marche_cible     = xlCell(s9, 24, 2) || "";
      const avantage_concurrentiel = xlCell(s9, 25, 2) || "";
      const part_marche      = xlCell(s9, 26, 2) || "";
      const differenciateurs = xlCell(s9, 27, 2) || "";
      const risques = [];
      for (let r = 30; r <= 40; r++) {
        const label = xlCell(s9, r, 2);
        if (!label) continue;
        risques.push({ label: String(label), probabilite: xlNum(s9, r, 3) || 2, impact: xlNum(s9, r, 4) || 2 });
      }
      const hasSomething = nature || forces || marche_cible || risques.length > 0;
      if (!hasSomething) return null;
      const filled = [nature, forces, pouvoir_acheteurs != null, marche_cible, risques.length > 0].filter(Boolean).length;
      return {
        _filled: filled,
        analyse: { nature: String(nature), parties: String(parties), enjeux: String(enjeux), contexte: String(contexte) },
        swot: { forces: String(forces), faiblesses: String(faiblesses), opportunites: String(opportunites), menaces: String(menaces) },
        porter: { pouvoir_acheteurs, pouvoir_fournisseurs, substituts, nouveaux_entrants, rivalite },
        positionnement: { marche_cible: String(marche_cible), avantage_concurrentiel: String(avantage_concurrentiel), part_marche: String(part_marche), differenciateurs: String(differenciateurs) },
        risques: risques.length > 0 ? risques : undefined,
      };
    })() : null,
  };
}

// ==========================================================================
// CLIENT DCF PARSER — Heuristic extraction for Revue de Valorisation
// 2-pass: sheet classification → label-proximity scanning
// ==========================================================================
function parseClientDCF(wb) {
  const sheets = wb.SheetNames || [];
  if (!sheets.length) return null;

  // ── Pass 1: classify sheets ──
  const classified = {};
  const dcfKeywords = ["dcf","cash flow","fcf","fcff","fcfe","flux","wacc","actualisation","discount"];
  const bsKeywords  = ["bilan","balance","actif","passif","assets","liabilities","equity"];
  const plKeywords  = ["compte","résultat","income","p&l","pnl","profit","loss","revenue","ca ","ebitda"];
  const waccKeywords = ["wacc","cmpc","coût du capital","cost of capital","beta","prime","risk"];
  const tvKeywords   = ["terminal","perpetuité","perpetuity","gordon","exit multiple","valeur résiduelle"];
  const compKeywords = ["comparable","multiple","peer","transaction","benchmark"];

  const classify = (name) => {
    const low = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (dcfKeywords.some(k => low.includes(k)))  return "dcf";
    if (waccKeywords.some(k => low.includes(k)))  return "wacc";
    if (tvKeywords.some(k => low.includes(k)))    return "terminal";
    if (compKeywords.some(k => low.includes(k)))  return "comparables";
    if (plKeywords.some(k => low.includes(k)))    return "pl";
    if (bsKeywords.some(k => low.includes(k)))    return "bs";
    return "other";
  };

  const sheetMap = {};
  sheets.forEach(name => {
    const cat = classify(name);
    if (!sheetMap[cat]) sheetMap[cat] = [];
    sheetMap[cat].push(name);
  });

  // ── Pass 2: scan cells for labeled values ──
  const assumptions = {};
  const rawCells = {};

  const labelPatterns = [
    { key: "wacc",             re: /wacc|cmpc|co[uû]t.*(capital|moyen)/i },
    { key: "risk_free_rate",   re: /taux.*(sans risque|risk.?free)|risk.?free.?rate|oat|bund/i },
    { key: "equity_risk_premium", re: /prime.*(risque|march[eé])|equity.?risk.?premium|erp|mrp/i },
    { key: "beta",             re: /^beta$|b[eê]ta.*(lever|unlever|relever|endett)/i },
    { key: "cost_of_equity",   re: /co[uû]t.*capitaux.propres|cost.?of.?equity|ke\b/i },
    { key: "cost_of_debt",     re: /co[uû]t.*dette|cost.?of.?debt|kd\b/i },
    { key: "tax_rate",         re: /taux.*(is|imposition|imp[oô]t)|tax.?rate|corporate.?tax/i },
    { key: "terminal_growth",  re: /croissance.*(terminale|perp[eé]tuit)|terminal.?growth|g\s*=|gordon/i },
    { key: "exit_multiple",    re: /multiple.*(sortie|exit)|exit.?multiple/i },
    { key: "revenue_growth",   re: /croissance.*ca|revenue.?growth|growth.?rate/i },
    { key: "ebitda_margin",    re: /marge.*ebitda|ebitda.?margin/i },
    { key: "capex",            re: /capex|investissement|capital.?expenditure/i },
    { key: "net_debt",         re: /dette.*(nette|financ)|net.?debt|endettement/i },
    { key: "shares",           re: /nombre.*(titres|actions)|shares.?outstanding|nb.?titres/i },
    { key: "dcf_value",        re: /valeur.*dcf|dcf.?value|enterprise.?value|valeur.*(entreprise|totale)/i },
    { key: "equity_value",     re: /capitaux.*propres.*march|equity.?value|valeur.*fonds/i },
    { key: "terminal_value",   re: /valeur.*(terminale|r[eé]siduelle)|terminal.?value/i },
    { key: "discount_period",  re: /dur[eé]e.*projection|forecast.*(period|years|horizon)|horizon/i },
    { key: "d_e_ratio",        re: /d\s*\/\s*e|dette.*capitaux|debt.*equity|levier|leverage/i },
    { key: "nwc",              re: /bfr|besoin.*fonds.*roulement|net.?working.?capital|nwc/i },
  ];

  const isNumeric = (v) => typeof v === "number" && isFinite(v);
  const isPercent = (key) => ["wacc","risk_free_rate","equity_risk_premium","cost_of_equity","cost_of_debt","tax_rate","terminal_growth","revenue_growth","ebitda_margin","exit_multiple","d_e_ratio"].includes(key);

  // Scan priority sheets first, then others
  const priority = [...(sheetMap.dcf||[]), ...(sheetMap.wacc||[]), ...(sheetMap.terminal||[]), ...(sheetMap.pl||[]), ...(sheetMap.bs||[]), ...(sheetMap.comparables||[])];
  const others = sheets.filter(s => !priority.includes(s));
  const scanOrder = [...priority, ...others];

  for (const sName of scanOrder) {
    const ws = wb.Sheets[sName];
    if (!ws || !ws["!ref"]) continue;
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const maxR = Math.min(range.e.r, 200);
    const maxC = Math.min(range.e.c, 30);

    for (let r = range.s.r; r <= maxR; r++) {
      for (let c = range.s.c; c <= maxC; c++) {
        const cell = ws[XLSX.utils.encode_cell({r, c})];
        if (!cell || typeof cell.v !== "string") continue;
        const label = String(cell.v).trim();
        if (label.length < 2 || label.length > 120) continue;

        for (const {key, re} of labelPatterns) {
          if (assumptions[key] !== undefined) continue;
          if (!re.test(label)) continue;

          // Look right (up to 4 cells) then down (up to 2 cells) for a number
          let found = null;
          for (let dc = 1; dc <= 4 && !found; dc++) {
            const adj = ws[XLSX.utils.encode_cell({r, c: c+dc})];
            if (adj && isNumeric(adj.v)) found = { v: adj.v, sheet: sName, cell: XLSX.utils.encode_cell({r, c: c+dc}), label };
          }
          for (let dr = 1; dr <= 2 && !found; dr++) {
            const adj = ws[XLSX.utils.encode_cell({r: r+dr, c})];
            if (adj && isNumeric(adj.v)) found = { v: adj.v, sheet: sName, cell: XLSX.utils.encode_cell({r: r+dr, c}), label };
          }
          if (found) {
            assumptions[key] = found.v;
            rawCells[key] = { value: found.v, sheet: found.sheet, cell: found.cell, label: found.label };
          }
          break;
        }
      }
    }
  }

  // ── Also extract yearly projection rows if found ──
  const projections = [];
  const projSheets = [...(sheetMap.dcf||[]), ...(sheetMap.pl||[])];
  for (const sName of projSheets) {
    const ws = wb.Sheets[sName];
    if (!ws || !ws["!ref"]) continue;
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const maxR = Math.min(range.e.r, 100);
    const maxC = Math.min(range.e.c, 20);

    // Find a row with consecutive years (e.g. 2024, 2025, 2026...)
    for (let r = range.s.r; r <= maxR && !projections.length; r++) {
      const years = [];
      for (let c = range.s.c; c <= maxC; c++) {
        const cell = ws[XLSX.utils.encode_cell({r, c})];
        if (cell && isNumeric(cell.v) && cell.v >= 2015 && cell.v <= 2040) {
          years.push({ col: c, year: cell.v });
        }
      }
      if (years.length >= 3) {
        // Found year header — scan rows below for revenue, ebitda, fcf etc.
        const projLabels = [
          { key: "revenue", re: /^(ca|chiffre|revenue|ventes|sales|turnover)/i },
          { key: "ebitda",  re: /ebitda|ebe|exc[eé]dent/i },
          { key: "ebit",    re: /ebit\b|r[eé]sultat.*exploitation|operating/i },
          { key: "fcf",     re: /fcf|free.?cash|flux.*(disponible|libre)|cash.?flow/i },
          { key: "capex",   re: /capex|investissement|capital.?exp/i },
        ];
        for (let pr = r + 1; pr <= Math.min(r + 30, maxR); pr++) {
          const lCell = ws[XLSX.utils.encode_cell({r: pr, c: range.s.c})] || ws[XLSX.utils.encode_cell({r: pr, c: range.s.c + 1})];
          if (!lCell || typeof lCell.v !== "string") continue;
          const lbl = String(lCell.v).trim();
          for (const {key, re} of projLabels) {
            if (re.test(lbl)) {
              const vals = years.map(({col, year}) => {
                const vc = ws[XLSX.utils.encode_cell({r: pr, c: col})];
                return { year, value: vc && isNumeric(vc.v) ? vc.v : null };
              }).filter(v => v.value !== null);
              if (vals.length >= 2) {
                projections.push({ metric: key, label: lbl, sheet: sName, values: vals });
              }
              break;
            }
          }
        }
        break;
      }
    }
  }

  const nAssumptions = Object.keys(assumptions).length;
  const nSheets = Object.keys(sheetMap).filter(k => k !== "other").length;
  if (nAssumptions === 0 && projections.length === 0) return null;

  return {
    sheets: sheetMap,
    sheetNames: sheets,
    nClassified: nSheets,
    assumptions,
    rawCells,
    projections,
    nAssumptions,
  };
}

// ==========================================================================
// EXCEL IMPORTER COMPONENT
// ==========================================================================
const ExcelImporter = ({ onImport, lang }) => {
  const [state, setState] = useState("idle");
  const [meta, setMeta]   = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i)) {
      setError(lang.xl_format_err);
      setState("error"); return;
    }
    setState("parsing"); setError(null);
    try {
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, { type: "array" });
      const required = ["1_Identification","2_Compte_Résultat","3_Bilan","4_Paramètres_Marché"];
      const missing  = required.filter(s => !wb.Sheets[s]);
      if (missing.length>0) throw new Error(`${lang.xl_sheets_missing} ${missing.join(", ")}`);
      const data = parseSovWorkbook(wb);
      setMeta(data._meta);
      setState("done");
      onImport(data);
    } catch(e) {
      setError(e.message);
      setState("error");
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); };
  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  if (state === "done" && meta) {
    const score = Math.round((meta.fields_filled/8)*100);
    return (
      <div style={{background:"rgba(16,185,129,0.05)",border:"1px solid rgba(16,185,129,0.2)",borderRadius:10,padding:"14px 18px",display:"flex",alignItems:"center",gap:16,marginBottom:24,flexWrap:"wrap"}}>
        <div style={{width:36,height:36,borderRadius:8,flexShrink:0,background:"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.25)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width={18} height={18} viewBox="0 0 20 20" fill="none" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4,10 8,14 16,6"/></svg>
        </div>
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:12,fontWeight:700,color:"#e2e8f0",marginBottom:2}}>
            {meta.company_name||lang.xl_societe_importee}
            {meta.fiscal_year&&<span style={{fontWeight:400,color:"#64748b",fontSize:11}}> — {lang.xl_exercice} {meta.fiscal_year}</span>}
          </div>
          <div style={{fontSize:12,color:"#64748b"}}>
            {lang.xl_donnees_importees} {score}%
            {meta.has_comparables&&` · ${lang.xl_comparables}`}
            {meta.has_intangibles&&` · ${lang.xl_actifs_incorp}`}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:80,height:4,borderRadius:4,background:"rgba(255,255,255,0.08)",overflow:"hidden"}}>
            <div style={{width:`${score}%`,height:"100%",background:"#10b981",borderRadius:4}}/>
          </div>
          <span style={{fontSize:10,color:"#10b981",fontWeight:700,fontFamily:"monospace"}}>{score}%</span>
        </div>
        <button onClick={()=>{setState("idle");setMeta(null);onImport(null);}} style={{background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,padding:"4px 10px",color:"#475569",fontSize:12,cursor:"pointer"}}>{lang.xl_effacer}</button>
      </div>
    );
  }

  return (
    <div onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
      onClick={()=>state!=="parsing"&&inputRef.current?.click()}
      style={{border:`1px dashed ${dragging?"rgba(99,155,250,0.6)":"rgba(255,255,255,0.1)"}`,borderRadius:10,padding:"18px 22px",marginBottom:24,cursor:"pointer",background:dragging?"rgba(59,130,246,0.04)":"rgba(255,255,255,0.01)",transition:"all 0.2s",display:"flex",alignItems:"center",gap:16}}>
      <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{display:"none"}} onChange={e=>processFile(e.target.files[0])}/>
      <div style={{width:36,height:36,borderRadius:8,flexShrink:0,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        {state==="parsing"
          ? <div style={{width:14,height:14,border:"2px solid rgba(99,155,250,0.3)",borderTop:"2px solid #60a5fa",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
          : <svg width={18} height={18} viewBox="0 0 20 20" fill="none" stroke={state==="error"?"#ef4444":"#475569"} strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="13" width="14" height="4" rx="1"/><path d="M10 11 L10 3 M7 6 L10 3 L13 6"/></svg>
        }
      </div>
      <div style={{flex:1}}>
        {state==="parsing"&&<div style={{fontSize:13,color:"#60a5fa"}}>{lang.xl_lecture}</div>}
        {state==="error"&&<div><div style={{fontSize:13,color:"#ef4444",fontWeight:600,marginBottom:2}}>{lang.xl_erreur_import}</div><div style={{fontSize:13,color:"#ef4444",opacity:0.8}}>{error}</div></div>}
        {(state==="idle")&&<div>
          <div style={{fontSize:13,fontWeight:600,color:"#94a3b8",marginBottom:2}}>{lang.xl_deposez} <span style={{color:"#60a5fa",fontFamily:"monospace"}}>{lang.xl_fichier}</span></div>
          <div style={{fontSize:13,color:"#475569"}}>{lang.xl_auto_fill}</div>
        </div>}
      </div>
      {(state==="idle"||state==="error")&&<div style={{fontSize:12,color:"#334155",border:"1px solid rgba(255,255,255,0.06)",borderRadius:5,padding:"3px 8px",whiteSpace:"nowrap",flexShrink:0}}>{lang.xl_parcourir}</div>}
      <style>{`@keyframes spin{to{transform:rotate(360deg);}} @keyframes fadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
};

// ==========================================================================
// DIAGNOSTIC STRATÉGIQUE
// ==========================================================================
const DEFAULT_STRAT = {
  _filled:0,
  analyse:{nature:"",parties:"",enjeux:"",contexte:""},
  swot:{forces:"",faiblesses:"",opportunites:"",menaces:""},
  porter:{pouvoir_acheteurs:3,pouvoir_fournisseurs:3,substituts:3,nouveaux_entrants:3,rivalite:3},
  positionnement:{marche_cible:"",avantage_concurrentiel:"",part_marche:"",differenciateurs:""},
  risques:[
    {label:"Risque de marché",probabilite:2,impact:3},
    {label:"Risque opérationnel",probabilite:2,impact:2},
    {label:"Risque réglementaire",probabilite:1,impact:3},
    {label:"Risque technologique",probabilite:2,impact:2},
  ],
};

// -- Debounced textarea: local state, propagates to parent after 300ms or on blur --
const DebouncedTextarea = React.memo(({value, onChange, placeholder, rows, style}) => {
  const [local, setLocal] = useState(value || "");
  const flush = useRef(null);
  useEffect(() => { setLocal(value || ""); }, [value]);
  useEffect(() => () => clearTimeout(flush.current), []);
  return <textarea value={local} onChange={e => {
    setLocal(e.target.value);
    clearTimeout(flush.current);
    flush.current = setTimeout(() => onChange(e.target.value), 300);
  }} onBlur={() => { clearTimeout(flush.current); onChange(local); }}
  placeholder={placeholder} rows={rows} style={style}/>;
});

// -- Debounced input: same pattern for single-line inputs --
const DebouncedInput = React.memo(({value, onChange, style}) => {
  const [local, setLocal] = useState(value || "");
  const flush = useRef(null);
  useEffect(() => { setLocal(value || ""); }, [value]);
  useEffect(() => () => clearTimeout(flush.current), []);
  return <input value={local} onChange={e => {
    setLocal(e.target.value);
    clearTimeout(flush.current);
    flush.current = setTimeout(() => onChange(e.target.value), 300);
  }} onBlur={() => { clearTimeout(flush.current); onChange(local); }}
  style={style}/>;
});

// -- Accordion module: stable identity, state via props --
const StratModule = React.memo(({id, label, icon, children, isOpen, onToggle, ai, onAskAI, L}) => (
  <div style={{border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,marginBottom:10,overflow:"hidden"}}>
    <button onClick={() => onToggle(id)} style={{width:"100%",background:"rgba(255,255,255,0.025)",border:"none",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",color:"#e2e8f0"}}>
      <span style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700}}><span>{icon}</span>{label}</span>
      <span style={{color:"#475569",fontSize:11,display:"inline-block",transition:"transform 0.2s",transform:isOpen?"rotate(180deg)":"none"}}>▼</span>
    </button>
    {isOpen && (
      <div style={{padding:"16px",borderTop:"1px solid rgba(255,255,255,0.05)"}}>
        {children}
        <div style={{display:"flex",gap:10,alignItems:"center",marginTop:14,flexWrap:"wrap"}}>
          <button onClick={() => onAskAI(id)} disabled={!!ai?.loading} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.25)",borderRadius:7,color:"#a78bfa",fontSize:12,fontWeight:600,cursor:ai?.loading?"not-allowed":"pointer",opacity:ai?.loading?0.7:1}}>
            {ai?.loading?L.ai_analyse_loading:L.ai_suggestions}
          </button>
          {ai?.error&&<span style={{fontSize:11,color:"#f87171"}}>{L.ai_backend_err}</span>}
        </div>
        {ai?.suggestion&&<div style={{marginTop:10,padding:"10px 14px",background:"rgba(167,139,250,0.06)",border:"1px solid rgba(167,139,250,0.15)",borderRadius:8,fontSize:12,color:"#c4b5fd",lineHeight:1.7}}>{ai.suggestion}</div>}
      </div>
    )}
  </div>
));

// -- Labelled textarea field --
const StratTA = React.memo(({label, val, ph, onChange, rows = 2}) => (
  <div>
    <div style={{fontSize:12,color:"#64748b",marginBottom:4}}>{label}</div>
    <DebouncedTextarea value={val} onChange={onChange} placeholder={ph} rows={rows}
      style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,padding:"7px 9px",color:"#e2e8f0",fontSize:13,resize:"vertical",boxSizing:"border-box",fontFamily:"inherit"}}/>
  </div>
));

const riskHeatColor = (p,i) => { const s=p*i; return s>=6?"#ef4444":s>=4?"#f97316":s>=2?"#eab308":"#22c55e"; };

const DiagnosticStrategique = ({data, onChange, xlData, onXlImport, L, lang, aiSuggestions, onAiSuggestionsChange, onResetSession}) => {
  const d = data || DEFAULT_STRAT;
  const dRef = useRef(d);
  dRef.current = d;
  const [open, setOpen] = useState({analyse:true,swot:false,porter:false,positionnement:false,risques:false});
  const [showResetModal, setShowResetModal] = useState(false);
  const cleanSuggestionText = (raw) => {
    if (!raw || typeof raw !== "string") return raw;
    let s = raw.replace(/^```json\s*/i, "").replace(/```\s*$/g, "").trim();
    if (s.startsWith("{")) {
      try { const obj = JSON.parse(s); if (obj.suggestion) return obj.suggestion; } catch {}
    }
    return s;
  };
  const [aiState, setAiState] = useState(() => {
    // Initialize from persisted suggestions, cleaning any raw JSON artifacts
    const init = {};
    if (aiSuggestions) Object.keys(aiSuggestions).forEach(k => { if (aiSuggestions[k]) init[k] = {suggestion: cleanSuggestionText(aiSuggestions[k])}; });
    return init;
  });
  // Sync aiState when aiSuggestions prop changes (e.g. after session reset)
  useEffect(() => {
    const next = {};
    if (aiSuggestions) Object.keys(aiSuggestions).forEach(k => { if (aiSuggestions[k]) next[k] = {suggestion: cleanSuggestionText(aiSuggestions[k])}; });
    setAiState(next);
  }, [aiSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Excel import banner ──
  const [showXlBanner, setShowXlBanner] = useState(false);
  const xlBannerTimer = useRef(null);
  const handleXlImport = useCallback((data) => {
    onXlImport(data);
    if (data) {
      setShowXlBanner(true);
      if (xlBannerTimer.current) clearTimeout(xlBannerTimer.current);
      xlBannerTimer.current = setTimeout(() => setShowXlBanner(false), 6000);
    } else {
      setShowXlBanner(false);
    }
  }, [onXlImport]);
  useEffect(() => () => { if (xlBannerTimer.current) clearTimeout(xlBannerTimer.current); }, []);

  const stratProfile = useMemo(() => computeStratProfile(d), [d]);

  const upd = useCallback((section, updates) => {
    const curr = dRef.current;
    const next = {...curr, [section]: Array.isArray(updates) ? updates : {...curr[section], ...updates}};
    next._filled = [
      next.analyse?.nature,
      next.swot?.forces,
      next.porter?.rivalite != null,
      next.positionnement?.marche_cible,
      (next.risques||[]).length > 0,
    ].filter(Boolean).length;
    onChange(next);
  }, [onChange]);

  const handleToggle = useCallback((id) => {
    setOpen(p => ({...p, [id]: !p[id]}));
  }, []);

  const askAI = useCallback(async (module) => {
    setAiState(p=>({...p,[module]:{loading:true,suggestion:null,error:null}}));
    try {
      const json = await callAI("suggest", {
        module,
        context: dRef.current[module],
        fullDiagnostic: dRef.current,
        company: xlData?._meta?.company_name || "",
        lang,
      });
      const text = cleanSuggestionText(json.suggestion || json.raw || "");
      setAiState(p=>({...p,[module]:{loading:false,suggestion:text}}));
      if (onAiSuggestionsChange) {
        const next = {...(aiSuggestions||{}), [module]: text};
        onAiSuggestionsChange(next);
      }
    } catch(e) {
      setAiState(p=>({...p,[module]:{loading:false,error:e.message}}));
    }
  }, [xlData, lang, aiSuggestions, onAiSuggestionsChange]);

  const porterData = useMemo(() => [
    {subject:L.ds_pouvoir_acheteurs,    score:d.porter?.pouvoir_acheteurs||3},
    {subject:L.ds_pouvoir_fournisseurs, score:d.porter?.pouvoir_fournisseurs||3},
    {subject:L.ds_substituts,           score:d.porter?.substituts||3},
    {subject:L.ds_nouveaux_entrants,    score:d.porter?.nouveaux_entrants||3},
    {subject:L.ds_rivalite,             score:d.porter?.rivalite||3},
  ], [d.porter, L]);

  return (
    <div>
      {/* Reset confirmation modal */}
      {showResetModal && (
        <div style={{position:"fixed",inset:0,zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans','Segoe UI',sans-serif"}} onClick={()=>setShowResetModal(false)}>
          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)"}}/>
          <div onClick={e=>e.stopPropagation()} style={{position:"relative",zIndex:1,background:"#0f1729",border:"1px solid rgba(239,68,68,0.25)",borderRadius:16,padding:"28px 32px",maxWidth:400,width:"90%",textAlign:"center"}}>
            <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.25)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:20}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <div style={{fontSize:15,fontWeight:700,color:"#f1f5f9",marginBottom:8}}>{L.reset_btn}</div>
            <div style={{fontSize:13,color:"#94a3b8",lineHeight:1.6,marginBottom:24}}>
              {L.reset_confirm_msg}
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button onClick={()=>setShowResetModal(false)} style={{flex:1,padding:"10px 0",borderRadius:10,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",color:"#94a3b8",fontSize:13,fontWeight:600,cursor:"pointer",transition:"all 0.2s"}}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}>{L.reset_cancel}</button>
              <button onClick={()=>{setShowResetModal(false);onResetSession();}} style={{flex:1,padding:"10px 0",borderRadius:10,background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontSize:13,fontWeight:700,cursor:"pointer",transition:"all 0.2s"}}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(239,68,68,0.25)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(239,68,68,0.15)"}>{L.reset_clear}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:8,background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>📋</div>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"#f1f5f9"}}>{L.ds_title}</div>
            <div style={{fontSize:13,color:"#64748b"}}>{L.ds_subtitle}</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <button onClick={()=>setShowResetModal(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:8,background:"rgba(239,68,68,0.06)",border:"1px solid rgba(239,68,68,0.18)",color:"#ef4444",fontSize:11,fontWeight:600,cursor:"pointer",transition:"all 0.2s"}}
            onMouseEnter={e=>{e.currentTarget.style.background="rgba(239,68,68,0.14)";e.currentTarget.style.borderColor="rgba(239,68,68,0.35)";}}
            onMouseLeave={e=>{e.currentTarget.style.background="rgba(239,68,68,0.06)";e.currentTarget.style.borderColor="rgba(239,68,68,0.18)";}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 105.64-11.36L1 10"/></svg>
            {L.reset_btn}
          </button>
          <div style={{fontSize:12,color:"#64748b",background:"rgba(167,139,250,0.08)",padding:"4px 12px",borderRadius:20,border:"1px solid rgba(167,139,250,0.15)"}}>
            {d._filled||0}{L.ds_modules}
          </div>
        </div>
      </div>

      {/* ── Excel drag-and-drop import ── */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:0}}>
        <div style={{flex:1}}><ExcelImporter onImport={handleXlImport} lang={L}/></div>
        <a href="/HardToValue_Template.xlsx" download style={{fontSize:11,color:"#64748b",border:"1px solid rgba(255,255,255,0.08)",borderRadius:7,padding:"8px 14px",textDecoration:"none",whiteSpace:"nowrap",transition:"all 0.15s",background:"rgba(255,255,255,0.03)"}} onMouseEnter={e=>{e.currentTarget.style.color="#C9A84C";e.currentTarget.style.borderColor="rgba(201,168,76,0.3)"}} onMouseLeave={e=>{e.currentTarget.style.color="#64748b";e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"}}>{L.xl_download_tpl}</a>
      </div>
      {showXlBanner && (
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",marginTop:-12,marginBottom:20,borderRadius:10,background:"rgba(16,185,129,0.06)",border:"1px solid rgba(16,185,129,0.2)",animation:"fadeIn 0.3s ease"}}>
          <div style={{width:28,height:28,borderRadius:7,flexShrink:0,background:"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.25)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width={14} height={14} viewBox="0 0 20 20" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="4,10 8,14 16,6"/></svg>
          </div>
          <div style={{flex:1,fontSize:13,color:"#10b981",fontWeight:600}}>{L.ds_xl_banner}</div>
          <button onClick={()=>setShowXlBanner(false)} style={{background:"none",border:"none",color:"#10b981",cursor:"pointer",opacity:0.6,fontSize:16,lineHeight:1,padding:2}}>×</button>
        </div>
      )}

      <StratModule id="analyse" label={L.ds_analyse} icon="🔍" isOpen={open.analyse} onToggle={handleToggle} ai={aiState.analyse} onAskAI={askAI} L={L}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <StratTA label={L.ds_nature} val={d.analyse?.nature} ph={L.ds_nature_ph} onChange={v=>upd("analyse",{nature:v})}/>
          <StratTA label={L.ds_parties} val={d.analyse?.parties} ph={L.ds_parties_ph} onChange={v=>upd("analyse",{parties:v})}/>
          <StratTA label={L.ds_enjeux} val={d.analyse?.enjeux} ph={L.ds_enjeux_ph} onChange={v=>upd("analyse",{enjeux:v})}/>
          <StratTA label={L.ds_contexte} val={d.analyse?.contexte} ph={L.ds_contexte_ph} onChange={v=>upd("analyse",{contexte:v})}/>
        </div>
      </StratModule>

      <StratModule id="swot" label={L.ds_swot} icon="⊕" isOpen={open.swot} onToggle={handleToggle} ai={aiState.swot} onAskAI={askAI} L={L}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[{k:"forces",color:"#10b981",label:L.ds_forces,ph:L.ds_forces_ph},{k:"faiblesses",color:"#f87171",label:L.ds_faiblesses,ph:L.ds_faiblesses_ph},{k:"opportunites",color:"#60a5fa",label:L.ds_opportunites,ph:L.ds_opportunites_ph},{k:"menaces",color:"#fbbf24",label:L.ds_menaces,ph:L.ds_menaces_ph}].map(({k,color,label,ph}) => (
            <div key={k} style={{background:`${color}08`,border:`1px solid ${color}20`,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:11,fontWeight:700,color,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
              <DebouncedTextarea value={d.swot?.[k]||""} onChange={v=>upd("swot",{[k]:v})} placeholder={ph} rows={3}
                style={{width:"100%",background:"transparent",border:"none",outline:"none",color:"#e2e8f0",fontSize:13,resize:"vertical",boxSizing:"border-box",fontFamily:"inherit"}}/>
            </div>
          ))}
        </div>
      </StratModule>

      <StratModule id="porter" label={L.ds_porter} icon="⬡" isOpen={open.porter} onToggle={handleToggle} ai={aiState.porter} onAskAI={askAI} L={L}>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:20,alignItems:"start"}}>
          <div>
            {[{k:"pouvoir_acheteurs",label:L.ds_pouvoir_acheteurs,tip:L.ds_tip_acheteurs},{k:"pouvoir_fournisseurs",label:L.ds_pouvoir_fournisseurs,tip:L.ds_tip_fournisseurs},{k:"substituts",label:L.ds_substituts,tip:L.ds_tip_substituts},{k:"nouveaux_entrants",label:L.ds_nouveaux_entrants,tip:L.ds_tip_entrants},{k:"rivalite",label:L.ds_rivalite,tip:L.ds_tip_rivalite}].map(({k,label,tip}) => (
              <div key={k} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:12,color:"#94a3b8",cursor:"help"}} title={`${tip} (${d.porter?.[k]||3}/5)`}>{label}</span>
                  <span style={{fontSize:12,color:"#a78bfa",fontWeight:700,fontFamily:"monospace"}}>{d.porter?.[k]||3}/5</span>
                </div>
                <input type="range" min={1} max={5} value={d.porter?.[k]||3} onChange={e=>upd("porter",{[k]:+e.target.value})} style={{width:"100%",accentColor:"#a78bfa",cursor:"pointer"}}/>
              </div>
            ))}
          </div>
          <div style={{width:200,height:200,flexShrink:0}}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={porterData}>
                <PolarGrid stroke="rgba(255,255,255,0.08)"/>
                <PolarAngleAxis dataKey="subject" tick={{fontSize:10,fill:"#64748b"}}/>
                <PolarRadiusAxis domain={[0,5]} tick={false} axisLine={false}/>
                <Radar dataKey="score" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.2} strokeWidth={1.5}/>
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </StratModule>

      <StratModule id="positionnement" label={L.ds_positionnement} icon="🎯" isOpen={open.positionnement} onToggle={handleToggle} ai={aiState.positionnement} onAskAI={askAI} L={L}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <StratTA label={L.ds_marche_cible} val={d.positionnement?.marche_cible} ph={L.ds_marche_cible_ph} onChange={v=>upd("positionnement",{marche_cible:v})}/>
          <StratTA label={L.ds_avantage} val={d.positionnement?.avantage_concurrentiel} ph={L.ds_avantage_ph} onChange={v=>upd("positionnement",{avantage_concurrentiel:v})}/>
          <StratTA label={L.ds_part_marche} val={d.positionnement?.part_marche} ph={L.ds_part_marche_ph} onChange={v=>upd("positionnement",{part_marche:v})}/>
          <StratTA label={L.ds_facteurs} val={d.positionnement?.differenciateurs} ph={L.ds_facteurs_ph} onChange={v=>upd("positionnement",{differenciateurs:v})}/>
        </div>
      </StratModule>

      <StratModule id="risques" label={L.ds_risques} icon="⚠" isOpen={open.risques} onToggle={handleToggle} ai={aiState.risques} onAskAI={askAI} L={L}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <thead>
              <tr style={{borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                {[L.ds_risque_col,L.ds_prob_col,L.ds_impact_col,L.ds_crit_col,""].map((h,i) => (
                  <th key={i} style={{textAlign:i===0?"left":"center",padding:"6px 8px",color:"#64748b",fontWeight:600,fontSize:11}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(d.risques||[]).map((r,i) => (
                <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                  <td style={{padding:"5px 8px"}}>
                    <DebouncedInput value={r.label||""} onChange={v=>{const rs=[...dRef.current.risques];rs[i]={...rs[i],label:v};upd("risques",rs);}}
                      style={{background:"transparent",border:"1px solid rgba(255,255,255,0.08)",borderRadius:4,padding:"3px 6px",color:"#e2e8f0",fontSize:12,width:"100%"}}/>
                  </td>
                  {["probabilite","impact"].map(f => (
                    <td key={f} style={{padding:"5px 8px",textAlign:"center"}}>
                      <select value={r[f]||2} onChange={e=>{const rs=[...d.risques];rs[i]={...rs[i],[f]:+e.target.value};upd("risques",rs);}} style={{background:"#1e293b",border:"1px solid rgba(255,255,255,0.1)",borderRadius:4,color:"#e2e8f0",fontSize:12,padding:"2px 4px"}}>
                        <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                      </select>
                    </td>
                  ))}
                  <td style={{padding:"5px 8px",textAlign:"center"}}>
                    <span style={{fontSize:10,padding:"2px 7px",borderRadius:4,fontWeight:700,background:`${riskHeatColor(r.probabilite||2,r.impact||2)}20`,color:riskHeatColor(r.probabilite||2,r.impact||2)}}>
                      {(r.probabilite||2)*(r.impact||2)>=6?L.ds_critique:(r.probabilite||2)*(r.impact||2)>=4?L.ds_eleve:(r.probabilite||2)*(r.impact||2)>=2?L.ds_moyen:L.ds_faible}
                    </span>
                  </td>
                  <td style={{padding:"5px 8px",textAlign:"center"}}>
                    <button onClick={()=>{const rs=d.risques.filter((_,j)=>j!==i);upd("risques",rs);}} style={{background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={()=>upd("risques",[...(d.risques||[]),{label:L.ds_nouveau_risque,probabilite:2,impact:2}])} style={{marginTop:10,padding:"5px 12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"#64748b",fontSize:12,cursor:"pointer"}}>
            {L.ds_ajouter_risque}
          </button>
        </div>
      </StratModule>

      {/* ── Profil de l'opération (read-only, visible when ≥2 modules filled) ── */}
      {(d._filled||0) >= 2 && (
        <div style={{marginTop:16,border:"1px solid rgba(167,139,250,0.15)",borderRadius:10,padding:"16px 18px",background:"rgba(167,139,250,0.04)"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#a78bfa",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16}}>🎯</span>{L.ds_profil}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:14}}>
            {/* Operation type badge */}
            <div>
              <div style={{fontSize:11,color:"#64748b",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{L.ds_type_op}</div>
              <div style={{display:"inline-block",padding:"4px 12px",borderRadius:20,fontSize:12,fontWeight:600,
                background:stratProfile.opType?"rgba(167,139,250,0.15)":"rgba(100,116,139,0.15)",
                color:stratProfile.opType?"#c4b5fd":"#64748b",
                border:`1px solid ${stratProfile.opType?"rgba(167,139,250,0.25)":"rgba(100,116,139,0.2)"}`}}>
                {stratProfile.opType ? (L[`ds_op_${stratProfile.opType}`]||stratProfile.opType) : L.ds_non_detecte}
              </div>
            </div>
            {/* Risk score gauge */}
            <div>
              <div style={{fontSize:11,color:"#64748b",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{L.ds_niveau_risque}</div>
              <div style={{display:"inline-block",padding:"4px 12px",borderRadius:20,fontSize:12,fontWeight:700,
                background:stratProfile.riskScore==="high"?"rgba(239,68,68,0.15)":stratProfile.riskScore==="medium"?"rgba(249,115,22,0.15)":"rgba(34,197,94,0.15)",
                color:stratProfile.riskScore==="high"?"#f87171":stratProfile.riskScore==="medium"?"#fb923c":"#4ade80",
                border:`1px solid ${stratProfile.riskScore==="high"?"rgba(239,68,68,0.25)":stratProfile.riskScore==="medium"?"rgba(249,115,22,0.25)":"rgba(34,197,94,0.25)"}`}}>
                {stratProfile.riskScore==="high"?L.ds_risk_high:stratProfile.riskScore==="medium"?L.ds_risk_medium:L.ds_risk_low}
              </div>
              <div style={{fontSize:11,color:"#475569",marginTop:4}}>{L.ds_porter_avg} {stratProfile.porterIntensity.toFixed(1)}/5</div>
            </div>
            {/* Modules filled */}
            <div>
              <div style={{fontSize:11,color:"#64748b",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{L.ds_completude}</div>
              <div style={{fontSize:20,fontWeight:700,color:"#f1f5f9",fontFamily:"monospace"}}>{stratProfile.filledModules}/5</div>
            </div>
          </div>

          {/* Method family tags */}
          <div style={{marginBottom:stratProfile.riskFlags.length>0?12:0}}>
            <div style={{fontSize:11,color:"#64748b",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>{L.ds_famille_methodes}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {stratProfile.methodFamily.map((k,i) => (
                <span key={k} style={{padding:"3px 10px",borderRadius:14,fontSize:11,fontWeight:600,
                  background:i===0?"rgba(96,165,250,0.15)":"rgba(255,255,255,0.06)",
                  color:i===0?"#60a5fa":"#94a3b8",
                  border:`1px solid ${i===0?"rgba(96,165,250,0.25)":"rgba(255,255,255,0.08)"}`}}>
                  {L[`ds_meth_${k}`]||k}
                </span>
              ))}
            </div>
          </div>

          {/* Risk flags */}
          {stratProfile.riskFlags.length > 0 && (
            <div>
              <div style={{fontSize:11,color:"#64748b",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>{L.ds_signaux_risque}</div>
              {stratProfile.riskFlags.map((f,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",marginBottom:4,background:"rgba(239,68,68,0.06)",border:"1px solid rgba(239,68,68,0.12)",borderRadius:6}}>
                  <span style={{fontSize:14}}>⚠</span>
                  <div style={{flex:1}}>
                    <span style={{fontSize:12,color:"#f87171",fontWeight:600}}>{f.label}</span>
                    <span style={{fontSize:11,color:"#94a3b8",marginLeft:8}}>{L[`ds_risk_impact_${f.category}`]||f.impact} ({L[`ds_risk_adj_${f.category}`]||f.adjustment})</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================================================
// DIAGNOSTIC FINANCIER
// ==========================================================================
const DiagnosticFinancier = ({data, onChange, xlData, onXlImport, L, lang}) => {
  const fin = xlData?.diag_fin;
  const [aiState, setAiState] = useState({loading:false,commentary:data?.commentary||null,error:null});
  const finProfile = useMemo(() => computeFinProfile(fin), [fin]);

  const askAI = async () => {
    setAiState(p=>({...p,loading:true,error:null}));
    try {
      const json = await callAI("commentary", {
        lang,
        company: xlData?._meta?.company_name || "",
        financials: fin,
        computedProfile: finProfile,
      });
      setAiState({loading:false,commentary:json.commentary,error:null});
      onChange({...data,commentary:json.commentary});
    } catch(e) {
      setAiState(p=>({...p,loading:false,error:e.message}));
    }
  };

  if (!fin) {
    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:8,background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>📊</div>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"#f1f5f9"}}>{L.df_title}</div>
            <div style={{fontSize:13,color:"#64748b"}}>{L.df_import_msg}</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:0}}>
          <div style={{flex:1}}><ExcelImporter onImport={onXlImport} lang={L}/></div>
          <a href="/HardToValue_Template.xlsx" download style={{fontSize:11,color:"#64748b",border:"1px solid rgba(255,255,255,0.08)",borderRadius:7,padding:"8px 14px",textDecoration:"none",whiteSpace:"nowrap",transition:"all 0.15s",background:"rgba(255,255,255,0.03)"}} onMouseEnter={e=>{e.currentTarget.style.color="#C9A84C";e.currentTarget.style.borderColor="rgba(201,168,76,0.3)"}} onMouseLeave={e=>{e.currentTarget.style.color="#64748b";e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"}}>{L.xl_download_tpl}</a>
        </div>
      </div>
    );
  }

  const safe = v => (v==null||isNaN(v))?null:v;
  const ebitda_m = fin.ebitda.map((e,i)=>fin.revenue[i]?safe(e/fin.revenue[i]):null);
  const ebit_m   = fin.ebit.map((e,i)=>fin.revenue[i]?safe(e/fin.revenue[i]):null);
  const net_m    = fin.net_income.map((e,i)=>fin.revenue[i]?safe(e/fin.revenue[i]):null);
  const gearing  = fin.capitaux_propres>0?safe(fin.net_debt/fin.capitaux_propres):null;
  const roe      = fin.capitaux_propres>0?safe((fin.net_income[4]||0)/fin.capitaux_propres):null;
  const roce     = (fin.capitaux_propres+fin.net_debt)>0?safe((fin.ebit[4]||0)/(fin.capitaux_propres+fin.net_debt)):null;

  const chartData = fin.years.map((y,i)=>({
    year:y,revenue:fin.revenue[i],ebitda:fin.ebitda[i],ebit:fin.ebit[i],net_income:fin.net_income[i],
    ebitda_margin:ebitda_m[i]!=null?+(ebitda_m[i]*100).toFixed(1):null,
    ebit_margin:ebit_m[i]!=null?+(ebit_m[i]*100).toFixed(1):null,
    net_margin:net_m[i]!=null?+(net_m[i]*100).toFixed(1):null,
  })).filter(d=>d.revenue!=null);

  const KPI = ({label,val,fmt,color="#f1f5f9",accent}) => (
    <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:9,padding:"12px 14px",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:accent||color}}/>
      <div style={{fontSize:11,color:"#475569",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:5}}>{label}</div>
      <div style={{fontSize:22,fontWeight:700,color,fontFamily:"monospace"}}>{val!=null?fmt(val):"—"}</div>
    </div>
  );

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:8,background:"rgba(96,165,250,0.1)",border:"1px solid rgba(96,165,250,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>📊</div>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"#f1f5f9"}}>{L.df_title}</div>
            <div style={{fontSize:13,color:"#64748b"}}>{xlData._meta?.company_name||L.df_societe} · {L.df_exercice} {xlData._meta?.fiscal_year||"N"}</div>
          </div>
        </div>
        <button onClick={askAI} disabled={!!aiState.loading} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.25)",borderRadius:7,color:"#a78bfa",fontSize:12,fontWeight:600,cursor:aiState.loading?"not-allowed":"pointer"}}>
          {aiState.loading?L.ai_analyse_loading:`✦ ${L.df_commentaire_ia}`}
        </button>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
        <KPI label={L.df_marge_ebitda} val={ebitda_m[4]} fmt={v=>`${(v*100).toFixed(1)}%`} color={ebitda_m[4]>0.15?"#10b981":"#f87171"} accent={ebitda_m[4]>0.15?"#10b981":"#f87171"}/>
        <KPI label={L.df_marge_ebit}  val={ebit_m[4]}   fmt={v=>`${(v*100).toFixed(1)}%`} color={ebit_m[4]>0.08?"#60a5fa":"#f87171"} accent={ebit_m[4]>0.08?"#60a5fa":"#f87171"}/>
        <KPI label={L.df_marge_nette} val={net_m[4]}    fmt={v=>`${(v*100).toFixed(1)}%`} color={net_m[4]>0.05?"#a78bfa":"#f87171"} accent={net_m[4]>0.05?"#a78bfa":"#f87171"}/>
        <KPI label={L.df_gearing}     val={gearing}     fmt={v=>`${v.toFixed(2)}×`}        color={gearing!=null&&gearing<1?"#10b981":"#f97316"} accent={gearing!=null&&gearing<1?"#10b981":"#f97316"}/>
        <KPI label={L.df_roe}         val={roe}          fmt={v=>`${(v*100).toFixed(1)}%`} color={roe!=null&&roe>0.10?"#60a5fa":"#f87171"} accent={roe!=null&&roe>0.10?"#60a5fa":"#f87171"}/>
        <KPI label={L.df_roce}        val={roce}         fmt={v=>`${(v*100).toFixed(1)}%`} color={roce!=null&&roce>0.08?"#34d399":"#f87171"} accent={roce!=null&&roce>0.08?"#34d399":"#f87171"}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"14px"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#94a3b8",marginBottom:10}}>{L.df_ca_ebitda}</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
              <XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/>
              <YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={fC}/>
              <Tooltip content={<TT/>}/>
              <Bar dataKey="revenue" name={L.df_ca} fill="#3b82f6" fillOpacity={0.65} radius={[3,3,0,0]}/>
              <Bar dataKey="ebitda" name={L.df_ebitda} fill="#10b981" fillOpacity={0.65} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"14px"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#94a3b8",marginBottom:10}}>{L.df_evol_marges}</div>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
              <XAxis dataKey="year" stroke="#475569" tick={{fontSize:10}}/>
              <YAxis stroke="#475569" tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
              <Tooltip content={<TT/>}/>
              <Line type="monotone" dataKey="ebitda_margin" stroke="#10b981" name={L.df_ebitda_pct} strokeWidth={2} dot={{r:3,fill:"#10b981"}}/>
              <Line type="monotone" dataKey="ebit_margin" stroke="#60a5fa" name={L.df_ebit_pct} strokeWidth={2} dot={{r:3,fill:"#60a5fa"}}/>
              <Line type="monotone" dataKey="net_margin" stroke="#a78bfa" name={L.df_net_pct} strokeWidth={2} dot={{r:3,fill:"#a78bfa"}}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Computed Metrics Panel (visible when finProfile available) ── */}
      {finProfile && (
        <div style={{marginBottom:20,border:"1px solid rgba(96,165,250,0.15)",borderRadius:10,padding:"16px 18px",background:"rgba(96,165,250,0.04)"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#60a5fa",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16}}>📈</span>{L.df_metriques}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:14}}>
            <div style={{background:"rgba(255,255,255,0.03)",borderRadius:8,padding:"10px 12px",border:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>{L.df_tcam_ca}</div>
              <div style={{fontSize:18,fontWeight:700,color:finProfile.revenueCagr!=null&&finProfile.revenueCagr>0?"#10b981":"#f87171",fontFamily:"monospace"}}>
                {finProfile.revenueCagr!=null?`${(finProfile.revenueCagr*100).toFixed(1)}%`:"—"}
              </div>
            </div>
            <div style={{background:"rgba(255,255,255,0.03)",borderRadius:8,padding:"10px 12px",border:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>{L.df_tcam_ebitda}</div>
              <div style={{fontSize:18,fontWeight:700,color:finProfile.ebitdaCagr!=null&&finProfile.ebitdaCagr>0?"#10b981":"#f87171",fontFamily:"monospace"}}>
                {finProfile.ebitdaCagr!=null?`${(finProfile.ebitdaCagr*100).toFixed(1)}%`:"—"}
              </div>
            </div>
            <div style={{background:"rgba(255,255,255,0.03)",borderRadius:8,padding:"10px 12px",border:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>{L.df_stabilite}</div>
              <div style={{display:"inline-block",padding:"3px 10px",borderRadius:14,fontSize:12,fontWeight:600,marginTop:2,
                background:finProfile.marginStability==="stable"?"rgba(34,197,94,0.15)":finProfile.marginStability==="volatile"?"rgba(239,68,68,0.15)":"rgba(249,115,22,0.15)",
                color:finProfile.marginStability==="stable"?"#4ade80":finProfile.marginStability==="volatile"?"#f87171":"#fb923c"}}>
                {finProfile.marginStability==="stable"?L.df_stable:finProfile.marginStability==="volatile"?L.df_volatile:L.df_moderee}
              </div>
              {finProfile.ebitdaMarginStdDev!=null&&<div style={{fontSize:10,color:"#475569",marginTop:3}}>σ = {(finProfile.ebitdaMarginStdDev*100).toFixed(1)}%</div>}
            </div>
            <div style={{background:"rgba(255,255,255,0.03)",borderRadius:8,padding:"10px 12px",border:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>{L.df_endettement}</div>
              <div style={{fontSize:18,fontWeight:700,color:finProfile.netDebtToEbitda!=null&&finProfile.netDebtToEbitda<3?"#10b981":"#f97316",fontFamily:"monospace"}}>
                {finProfile.netDebtToEbitda!=null?`${finProfile.netDebtToEbitda.toFixed(1)}×`:"—"}
              </div>
              <div style={{fontSize:10,color:"#475569"}}>{L.df_dn_ebitda}</div>
            </div>
          </div>

          {/* Auto-suggestions row */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {finProfile.suggestedGrowth!=null && (
              <div style={{background:"rgba(34,197,94,0.06)",border:"1px solid rgba(34,197,94,0.12)",borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontSize:11,color:"#64748b",marginBottom:3}}>{L.df_croissance_sugg}</div>
                <div style={{fontSize:16,fontWeight:700,color:"#4ade80",fontFamily:"monospace"}}>{(finProfile.suggestedGrowth*100).toFixed(1)}%</div>
              </div>
            )}
            {finProfile.suggestedMargin!=null && (
              <div style={{background:"rgba(96,165,250,0.06)",border:"1px solid rgba(96,165,250,0.12)",borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontSize:11,color:"#64748b",marginBottom:3}}>{L.df_marge_sugg}</div>
                <div style={{fontSize:16,fontWeight:700,color:"#60a5fa",fontFamily:"monospace"}}>{(finProfile.suggestedMargin*100).toFixed(1)}%</div>
              </div>
            )}
          </div>

          {finProfile.assetMethodRelevant && (
            <div style={{marginTop:10,padding:"8px 12px",background:"rgba(239,68,68,0.06)",border:"1px solid rgba(239,68,68,0.12)",borderRadius:6,display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:14}}>⚠</span>
              <span style={{fontSize:12,color:"#f87171"}}>{L[finProfile.assetMethodReasonKey]||""} — {L.df_anr_warning}</span>
            </div>
          )}
        </div>
      )}

      {(aiState.commentary||aiState.error) && (
        <div style={{background:"rgba(167,139,250,0.06)",border:"1px solid rgba(167,139,250,0.15)",borderRadius:10,padding:"14px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#a78bfa",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{L.df_commentaire_ia}</div>
          {aiState.error
            ?<div style={{fontSize:12,color:"#f87171"}}>{L.df_backend_err} {aiState.error}</div>
            :<div style={{fontSize:13,color:"#c4b5fd",lineHeight:1.8,whiteSpace:"pre-line"}}>{aiState.commentary}</div>
          }
        </div>
      )}
    </div>
  );
};

// ==========================================================================
// BLOC SYNTHÈSE — Method Selection, Hypotheses & AI Enrichment
// ==========================================================================
const BlocSynthese = ({stratProfile, finProfile, diagStratData, synthesisData, onSynthesisChange, onApplyHypotheses, xlData, L, lang}) => {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const local = useMemo(() => computeSynthesisLocal(stratProfile, finProfile, L), [stratProfile, finProfile, L]);

  const merged = useMemo(() => {
    if (!synthesisData?.aiSynthesis) return local;
    const ai = synthesisData.aiSynthesis;
    // AI methods: keep AI rationales but always use TR-based names from local
    const localByKey = Object.fromEntries(local.recommendedMethods.map(m => [m.key, m]));
    const aiMethods = ai.recommendedMethods
      ? ai.recommendedMethods.map(m => ({
          ...m,
          name: L[`ds_meth_${m.key}`] || localByKey[m.key]?.name || m.name,
        }))
      : local.recommendedMethods;
    // AI hypotheses: keep AI sources (language-specific from backend), merge with local structure
    const aiHyp = ai.suggestedHypotheses || {};
    const mergedHyp = { ...local.suggestedHypotheses };
    for (const [k, v] of Object.entries(aiHyp)) {
      if (v && typeof v === "object") mergedHyp[k] = { ...(mergedHyp[k] || {}), ...v };
    }
    return {
      recommendedMethods: aiMethods,
      suggestedHypotheses: mergedHyp,
      alerts: [...local.alerts, ...(ai.additionalAlerts || []).map(a => typeof a === "string" ? { type: "info", message: a } : a)],
      aiCommentary: ai.commentary || null,
    };
  }, [local, synthesisData, L]);

  const askAISynthesis = async () => {
    setAiLoading(true); setAiError(null);
    try {
      const company = xlData?._meta?.company_name || "";
      const fin = xlData?.diag_fin || {};
      const strat = diagStratData || {};

      const json = await callAI("synthesis", {
        lang,
        company,
        // Raw qualitative data for genuine interpretation
        operation: {
          nature: strat.analyse?.nature || null,
          parties: strat.analyse?.parties || null,
          enjeux: strat.analyse?.enjeux || null,
          contexte: strat.analyse?.contexte || null,
        },
        swot: strat.swot || null,
        porter: strat.porter || null,
        positionnement: strat.positionnement || null,
        risques: strat.risques || null,
        // Computed strategic profile
        stratProfile: {
          opType: stratProfile?.opType,
          methodFamily: stratProfile?.methodFamily,
          riskScore: stratProfile?.riskScore,
          porterIntensity: stratProfile?.porterIntensity,
          filledModules: stratProfile?.filledModules,
          riskFlags: stratProfile?.riskFlags,
        },
        // Financial data + computed metrics
        financials: {
          years: fin.years || [],
          revenue: fin.revenue || [],
          ebitda: fin.ebitda || [],
          ebit: fin.ebit || [],
          net_income: fin.net_income || [],
          capitaux_propres: fin.capitaux_propres,
          net_debt: fin.net_debt,
        },
        finProfile: finProfile ? {
          revenueCagr: finProfile.revenueCagr,
          ebitdaCagr: finProfile.ebitdaCagr,
          marginStability: finProfile.marginStability,
          ebitdaMarginStdDev: finProfile.ebitdaMarginStdDev,
          netDebtToEbitda: finProfile.netDebtToEbitda,
          gearing: finProfile.gearing,
          suggestedGrowth: finProfile.suggestedGrowth,
          suggestedMargin: finProfile.suggestedMargin,
          lastMargin: finProfile.lastMargin,
          assetMethodRelevant: finProfile.assetMethodRelevant,
          assetMethodReasonKey: finProfile.assetMethodReasonKey,
        } : null,
        // Local pre-computation for reference
        localSynthesis: {
          recommendedMethods: local.recommendedMethods,
          alerts: local.alerts.map(a => a.message),
        },
      });

      if (json.recommendedMethods || json.suggestedHypotheses || json.additionalAlerts) {
        onSynthesisChange({ ...synthesisData, aiSynthesis: json });
      } else {
        onSynthesisChange({ ...synthesisData, aiSynthesis: { commentary: json.commentary || JSON.stringify(json), additionalAlerts: [] } });
      }
    } catch (e) { setAiError(e.message); }
    finally { setAiLoading(false); }
  };

  const handleApply = () => {
    const hyp = merged.suggestedHypotheses;
    const payload = {};
    if (hyp.revenueGrowth?.value != null) payload.revenue_growth_initial = hyp.revenueGrowth.value;
    if (hyp.ebitdaMargin?.value != null) payload.target_operating_margin = hyp.ebitdaMargin.value;
    if (hyp.discountRateRange) payload.cost_of_capital_initial = (hyp.discountRateRange.low + hyp.discountRateRange.high) / 2;
    if (hyp.terminalGrowth?.value != null) payload.terminal_growth_rate = hyp.terminalGrowth.value;
    onApplyHypotheses(payload);
  };

  const riskColor = (type) => type === "error" ? "#ef4444" : type === "warning" ? "#f97316" : "#60a5fa";

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:8,background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🧭</div>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"#f1f5f9"}}>{L.synth_title}</div>
            <div style={{fontSize:13,color:"#64748b"}}>{L.syn_subtitle}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={askAISynthesis} disabled={aiLoading} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"rgba(167,139,250,0.1)",border:"1px solid rgba(167,139,250,0.25)",borderRadius:7,color:"#a78bfa",fontSize:12,fontWeight:600,cursor:aiLoading?"not-allowed":"pointer",opacity:aiLoading?0.7:1}}>
            {aiLoading ? L.syn_analyse_loading : `✦ ${L.synth_ai_enrich}`}
          </button>
          <button onClick={handleApply} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"rgba(201,168,76,0.12)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:7,color:"#C9A84C",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            {L.synth_apply}
          </button>
        </div>
      </div>

      {aiError && <div style={{marginBottom:14,padding:"8px 12px",background:"rgba(239,68,68,0.06)",border:"1px solid rgba(239,68,68,0.15)",borderRadius:6,fontSize:12,color:"#f87171"}}>{L.ai_backend_err}: {aiError}</div>}

      {/* Alerts */}
      {merged.alerts.length > 0 && (
        <div style={{marginBottom:18}}>
          <div style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{L.synth_alerts}</div>
          {merged.alerts.filter(a => a.message).map((a, i) => (
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 10px",marginBottom:4,background:`${riskColor(a.type)}08`,border:`1px solid ${riskColor(a.type)}20`,borderRadius:6}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:riskColor(a.type),marginTop:5,flexShrink:0}}/>
              <span style={{fontSize:12,color:"#e2e8f0",lineHeight:1.5}}>{a.message}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        {/* Recommended Methods */}
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"16px"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#60a5fa",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>{L.synth_recommended}</div>
          {merged.recommendedMethods.map((m) => (
            <div key={m.key} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",marginBottom:6,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:7}}>
              <div style={{width:24,height:24,borderRadius:"50%",background:m.rank===1?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.06)",border:`1px solid ${m.rank===1?"rgba(201,168,76,0.3)":"rgba(255,255,255,0.1)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:m.rank===1?"#C9A84C":"#94a3b8",flexShrink:0}}>
                {m.rank}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:"#f1f5f9"}}>{m.name}</div>
                <div style={{fontSize:11,color:"#64748b"}}>{m.rationale}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Suggested Hypotheses */}
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"16px"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#C9A84C",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>{L.synth_hypotheses}</div>
          {[
            {key:"revenueGrowth",label:L.synth_revenue_growth,fmt:v=>`${(v*100).toFixed(1)}%`},
            {key:"ebitdaMargin",label:L.synth_ebitda_margin,fmt:v=>`${(v*100).toFixed(1)}%`},
            {key:"discountRateRange",label:L.synth_discount_rate,fmt:null,isRange:true},
            {key:"terminalGrowth",label:L.synth_terminal_growth,fmt:v=>`${(v*100).toFixed(1)}%`},
          ].map(({key,label,fmt,isRange}) => {
            const h = merged.suggestedHypotheses[key];
            if (!h) return null;
            return (
              <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",marginBottom:6,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:7}}>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:"#e2e8f0"}}>{label}</div>
                  <div style={{fontSize:10,color:"#64748b"}}>{h.source}</div>
                </div>
                <div style={{fontSize:16,fontWeight:700,color:"#C9A84C",fontFamily:"monospace"}}>
                  {isRange ? `${(h.low*100).toFixed(0)}–${(h.high*100).toFixed(0)}%` : fmt(h.value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Commentary */}
      {merged.aiCommentary && (
        <div style={{marginTop:16,background:"rgba(167,139,250,0.06)",border:"1px solid rgba(167,139,250,0.15)",borderRadius:10,padding:"14px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#a78bfa",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{L.syn_commentaire_ia}</div>
          <div style={{fontSize:13,color:"#c4b5fd",lineHeight:1.8,whiteSpace:"pre-line"}}>{merged.aiCommentary}</div>
        </div>
      )}
    </div>
  );
};

// ==========================================================================
// REVUE DE VALORISATION — AI-powered critical review of client DCF models
// ==========================================================================
const SEVERITY_COLORS = { critical: "#ef4444", major: "#f59e0b", minor: "#60a5fa", ok: "#22c55e" };
const SEVERITY_BG     = { critical: "rgba(239,68,68,0.08)", major: "rgba(245,158,11,0.08)", minor: "rgba(96,165,250,0.08)", ok: "rgba(34,197,94,0.08)" };
const SEVERITY_BORDER = { critical: "rgba(239,68,68,0.2)", major: "rgba(245,158,11,0.2)", minor: "rgba(96,165,250,0.2)", ok: "rgba(34,197,94,0.2)" };

const RevueValorisation = ({ L, lang }) => {
  const [clientFile, setClientFile] = useState(null); // parseClientDCF result
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const inputRef = useRef(null);

  // Context form
  const [ctx, setCtx] = useState({ company: "", sector: "", purpose: "", date: "", notes: "" });
  const updateCtx = (k, v) => setCtx(prev => ({ ...prev, [k]: v }));

  // AI review state
  const [review, setReview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [openAxis, setOpenAxis] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ── File handling ──
  const handleFile = async (file) => {
    if (!file || !file.name.match(/\.xlsx?$/i)) { setParseError(L.rv_parse_err); return; }
    setParsing(true); setParseError(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const result = parseClientDCF(wb);
      if (!result) { setParseError(L.rv_parse_err); setParsing(false); return; }
      setClientFile(result);
      setParsing(false);
    } catch (e) {
      setParseError(L.rv_parse_err); setParsing(false);
    }
  };

  const clearFile = () => { setClientFile(null); setReview(null); setAiError(null); setParseError(null); };

  // ── AI Review call ──
  const runReview = async () => {
    if (!clientFile) return;
    setAiLoading(true); setAiError(null); setReview(null);
    try {
      const payload = {
        lang,
        context: ctx,
        assumptions: clientFile.assumptions,
        rawCells: clientFile.rawCells,
        projections: clientFile.projections,
        sheets: clientFile.sheetNames,
        classified: clientFile.sheets,
      };
      const res = await callAI("review", payload);
      if (res.error) throw new Error(res.error);
      setReview(res);
      setOpenAxis(0);
    } catch (e) {
      setAiError(e.message || String(e));
    } finally {
      setAiLoading(false);
    }
  };

  // ── PDF export ──
  const exportPDF = async () => {
    if (!review) return;
    setPdfLoading(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210, ph = 297, mx = 18;
      let y = 20;

      const addPage = () => { doc.addPage(); y = 20; };
      const checkY = (need) => { if (y + need > ph - 20) addPage(); };

      // Title
      doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(30, 41, 59);
      doc.text(L.rv_title, mx, y); y += 8;
      doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
      if (ctx.company) { doc.text(ctx.company + (ctx.sector ? ` — ${ctx.sector}` : ""), mx, y); y += 5; }
      if (ctx.purpose) { doc.text(ctx.purpose, mx, y); y += 5; }
      if (ctx.date) { doc.text(ctx.date, mx, y); y += 5; }
      y += 6;

      // Overall assessment
      if (review.overall) {
        doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(30, 41, 59);
        doc.text(L.rv_overall, mx, y); y += 7;
        doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
        const lines = doc.splitTextToSize(review.overall, pw - 2 * mx);
        checkY(lines.length * 4 + 4);
        doc.text(lines, mx, y); y += lines.length * 4 + 6;
      }

      // Axes
      const axes = review.axes || [];
      const axisLabels = L.rv_axes;
      axes.forEach((axis, i) => {
        checkY(20);
        doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30, 41, 59);
        doc.text(`${i + 1}. ${axisLabels[i] || `Axis ${i + 1}`}`, mx, y); y += 6;

        const findings = axis.findings || [];
        if (!findings.length) {
          doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(148, 163, 184);
          doc.text(L.rv_no_findings, mx + 4, y); y += 5;
        } else {
          findings.forEach(f => {
            checkY(16);
            const sevLabel = L[`rv_severity_${f.severity}`] || f.severity;
            const color = f.severity === "critical" ? [239, 68, 68] : f.severity === "major" ? [245, 158, 11] : f.severity === "minor" ? [96, 165, 250] : [34, 197, 94];
            doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...color);
            doc.text(`[${sevLabel.toUpperCase()}]`, mx + 4, y);
            doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
            const desc = doc.splitTextToSize(f.description || "", pw - 2 * mx - 30);
            doc.text(desc, mx + 28, y); y += Math.max(desc.length * 4, 5);
            if (f.recommendation) {
              checkY(8);
              doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(148, 163, 184);
              const rec = doc.splitTextToSize(`→ ${f.recommendation}`, pw - 2 * mx - 30);
              doc.text(rec, mx + 28, y); y += rec.length * 3.5 + 2;
            }
            y += 2;
          });
        }
        y += 4;
      });

      doc.save(`${ctx.company || "Revue"}_ValuationReview.pdf`);
    } catch (e) {
      console.error("PDF export error", e);
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Render helpers ──
  const sevLabel = (sev) => L[`rv_severity_${sev}`] || sev;

  const inputStyle = { width: "100%", padding: "8px 10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#e2e8f0", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div>
      {/* ── Drop zone ── */}
      {!clientFile && !parsing && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? "rgba(96,165,250,0.5)" : "rgba(255,255,255,0.08)"}`,
            borderRadius: 12, padding: "32px 20px", textAlign: "center", cursor: "pointer",
            background: dragging ? "rgba(96,165,250,0.04)" : "rgba(255,255,255,0.015)",
            transition: "all 0.2s", marginBottom: 20,
          }}
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
          <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0", marginBottom: 4 }}>{L.rv_drop_label}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>{L.rv_drop_hint}</div>
          <div style={{ marginTop: 10 }}>
            <span style={{ fontSize: 11, padding: "4px 12px", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.2)", borderRadius: 5, color: "#60a5fa", cursor: "pointer" }}>{L.rv_browse}</span>
          </div>
        </div>
      )}

      {parsing && (
        <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8", fontSize: 13 }}>
          <div style={{ animation: "spin 1s linear infinite", display: "inline-block", marginBottom: 8 }}>⏳</div>
          <div>{L.rv_reading}</div>
        </div>
      )}

      {parseError && (
        <div style={{ marginBottom: 14, padding: "10px 14px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", borderRadius: 8, fontSize: 12, color: "#f87171" }}>
          {parseError}
        </div>
      )}

      {/* ── Parsed file summary ── */}
      {clientFile && (
        <div style={{ marginBottom: 18, padding: "12px 16px", background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#22c55e" }}>✓ {L.rv_parsed}</span>
            <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 10 }}>
              {clientFile.nClassified} {L.rv_sheets} · {clientFile.nAssumptions} {L.rv_assumptions}
              {clientFile.projections.length > 0 && ` · ${clientFile.projections.length} ${L.rv_projections}`}
            </span>
          </div>
          <button onClick={clearFile} style={{ fontSize: 11, padding: "4px 10px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 5, color: "#f87171", cursor: "pointer" }}>{L.rv_clear}</button>
        </div>
      )}

      {/* ── Context form ── */}
      {clientFile && (
        <div style={{ marginBottom: 20, padding: "16px 18px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", marginBottom: 12 }}>{L.rv_ctx_title}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 3 }}>{L.rv_ctx_company}</label>
              <input value={ctx.company} onChange={e => updateCtx("company", e.target.value)} placeholder={L.rv_ctx_company_ph} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 3 }}>{L.rv_ctx_sector}</label>
              <input value={ctx.sector} onChange={e => updateCtx("sector", e.target.value)} placeholder={L.rv_ctx_sector_ph} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 3 }}>{L.rv_ctx_purpose}</label>
              <input value={ctx.purpose} onChange={e => updateCtx("purpose", e.target.value)} placeholder={L.rv_ctx_purpose_ph} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 3 }}>{L.rv_ctx_date}</label>
              <input type="date" value={ctx.date} onChange={e => updateCtx("date", e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 3 }}>{L.rv_ctx_notes}</label>
            <textarea value={ctx.notes} onChange={e => updateCtx("notes", e.target.value)} placeholder={L.rv_ctx_notes_ph} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
        </div>
      )}

      {/* ── Run button ── */}
      {clientFile && !review && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <button onClick={runReview} disabled={aiLoading}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 24px",
              background: aiLoading ? "rgba(139,92,246,0.08)" : "linear-gradient(135deg,rgba(139,92,246,0.15),rgba(96,165,250,0.15))",
              border: "1px solid rgba(139,92,246,0.3)", borderRadius: 8,
              color: "#a78bfa", fontSize: 13, fontWeight: 700, cursor: aiLoading ? "not-allowed" : "pointer",
              opacity: aiLoading ? 0.7 : 1, transition: "all 0.2s",
            }}>
            {aiLoading ? (<><span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</span> {L.rv_running}</>) : (<>✦ {L.rv_run}</>)}
          </button>
        </div>
      )}

      {aiError && (
        <div style={{ marginBottom: 14, padding: "10px 14px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)", borderRadius: 8, fontSize: 12, color: "#f87171" }}>
          {L.rv_err}: {aiError}
        </div>
      )}

      {/* ── AI Review results ── */}
      {review && (
        <div>
          {/* Overall */}
          {review.overall && (
            <div style={{ marginBottom: 18, padding: "14px 18px", background: "rgba(139,92,246,0.05)", border: "1px solid rgba(139,92,246,0.15)", borderRadius: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#a78bfa", marginBottom: 8 }}>{L.rv_overall}</div>
              <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.7, whiteSpace: "pre-line" }}>{review.overall}</div>
            </div>
          )}

          {/* Axes — accordion */}
          {(review.axes || []).map((axis, i) => {
            const isOpen = openAxis === i;
            const findings = axis.findings || [];
            const worstSev = findings.reduce((w, f) => {
              const order = { critical: 0, major: 1, minor: 2, ok: 3 };
              return (order[f.severity] ?? 3) < (order[w] ?? 3) ? f.severity : w;
            }, "ok");
            const badge = findings.length;

            return (
              <div key={i} style={{ marginBottom: 8, border: `1px solid ${isOpen ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.06)"}`, borderRadius: 8, overflow: "hidden", transition: "all 0.2s" }}>
                <div
                  onClick={() => setOpenAxis(isOpen ? -1 : i)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", cursor: "pointer",
                    background: isOpen ? "rgba(139,92,246,0.04)" : "rgba(255,255,255,0.015)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: "#94a3b8", transition: "transform 0.2s", transform: isOpen ? "rotate(90deg)" : "rotate(0)" }}>▸</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{L.rv_axes[i] || `Axis ${i + 1}`}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {badge > 0 && (
                      <span style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 4,
                        background: SEVERITY_BG[worstSev], color: SEVERITY_COLORS[worstSev],
                        fontWeight: 700,
                      }}>
                        {badge} — {sevLabel(worstSev)}
                      </span>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div style={{ padding: "8px 14px 12px" }}>
                    {!findings.length && (
                      <div style={{ fontSize: 12, color: "#64748b", fontStyle: "italic", padding: "6px 0" }}>{L.rv_no_findings}</div>
                    )}
                    {findings.map((f, fi) => (
                      <div key={fi} style={{ marginBottom: 8, padding: "8px 12px", background: SEVERITY_BG[f.severity], border: `1px solid ${SEVERITY_BORDER[f.severity]}`, borderRadius: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: SEVERITY_COLORS[f.severity], textTransform: "uppercase", padding: "1px 5px", background: `${SEVERITY_COLORS[f.severity]}15`, borderRadius: 3 }}>
                            {sevLabel(f.severity)}
                          </span>
                          {f.cell_ref && <span style={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>{f.cell_ref}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: "#e2e8f0", lineHeight: 1.6, marginBottom: f.recommendation ? 4 : 0 }}>{f.description}</div>
                        {f.recommendation && (
                          <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", marginTop: 2 }}>→ {f.recommendation}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* PDF export + re-run */}
          <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16 }}>
            <button onClick={exportPDF} disabled={pdfLoading}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 18px",
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 7,
                color: "#f87171", fontSize: 12, fontWeight: 600, cursor: pdfLoading ? "not-allowed" : "pointer",
              }}>
              {pdfLoading ? L.rv_exporting : L.rv_export_pdf}
            </button>
            <button onClick={runReview} disabled={aiLoading}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 18px",
                background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: 7,
                color: "#a78bfa", fontSize: 12, fontWeight: 600, cursor: aiLoading ? "not-allowed" : "pointer",
              }}>
              {aiLoading ? L.rv_running : `↻ ${L.rv_run}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const INTL_METHODS = ["dcf","exc","mul","rfr","mpe","ast"];
const JUR_METHODS  = ["fr","us","de","ch","uk"];

const Dash=({L,lang,onSel,modelResults,xlData,onXlImport,diagStratData,diagFinData,onDiagStratChange,onDiagFinChange,synthesisData,onSynthesisChange,onApplyHypotheses,aiSuggestions,onAiSuggestionsChange,onResetSession,dashTab,setDashTab})=>{

  // Which model cards have data pre-filled from Excel or diagFinData
  const hasXlData = (k) => (xlData && xlData[k] != null) || (diagFinData && diagFinData[k] != null);

  const ModelCard = ({k}) => {
    const hasResult = !!modelResults[k];
    const hasXl    = hasXlData(k);
    const color = colors[k];
    const isJur = JUR_METHODS.includes(k);

    return (
      <div
        key={k}
        onClick={()=>onSel(k)}
        style={{
          background: hasResult
            ? `rgba(${isJur?"167,139,250":"59,130,246"},0.05)`
            : hasXl ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.02)",
          border:`1px solid ${hasResult ? color+"50" : hasXl ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.07)"}`,
          borderRadius:10,
          padding:"18px 16px 16px",
          cursor:"pointer",
          transition:"all 0.2s cubic-bezier(.4,0,.2,1)",
          position:"relative",
          overflow:"hidden",
          display:"flex",
          flexDirection:"column",
          gap:0,
        }}
        onMouseEnter={e=>{
          e.currentTarget.style.borderColor=color+"90";
          e.currentTarget.style.background=`rgba(${isJur?"167,139,250":"59,130,246"},0.07)`;
          e.currentTarget.style.transform="translateY(-1px)";
        }}
        onMouseLeave={e=>{
          e.currentTarget.style.borderColor=hasResult?color+"50":hasXl?"rgba(255,255,255,0.14)":"rgba(255,255,255,0.07)";
          e.currentTarget.style.background=hasResult?`rgba(${isJur?"167,139,250":"59,130,246"},0.05)`:hasXl?"rgba(255,255,255,0.025)":"rgba(255,255,255,0.02)";
          e.currentTarget.style.transform="none";
        }}
      >
        {/* Left accent bar */}
        <div style={{
          position:"absolute",left:0,top:12,bottom:12,
          width:2.5,borderRadius:"0 2px 2px 0",
          background: hasResult ? color : hasXl ? color+"70" : color+"30",
          transition:"all 0.2s",
        }}/>

        {/* Icon + status row */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10,paddingLeft:10}}>
          <div style={{
            width:34,height:34,borderRadius:7,
            background:`rgba(${isJur?"167,139,250":"59,130,246"},0.1)`,
            border:`1px solid ${color}25`,
            display:"flex",alignItems:"center",justifyContent:"center",
            flexShrink:0,
          }}>
            <ModelIcon id={k} color={color} size={18}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
            {hasResult && (
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.04em",textTransform:"uppercase",color:color,background:`${color}18`,padding:"2px 8px",borderRadius:4}}>
                {L.calcdone}
              </div>
            )}
            {!hasResult && hasXl && (
              <div style={{fontSize:10,fontWeight:600,letterSpacing:"0.03em",color:"#60a5fa",background:"rgba(96,165,250,0.1)",padding:"2px 8px",borderRadius:4,display:"flex",alignItems:"center",gap:4}}>
                <svg width={8} height={8} viewBox="0 0 8 8" fill="none"><rect x="1" y="1" width="6" height="4" rx="0.5" stroke="#60a5fa" strokeWidth="1"/><line x1="1" y1="3" x2="7" y2="3" stroke="#60a5fa" strokeWidth="0.8"/><line x1="3" y1="1" x2="3" y2="5" stroke="#60a5fa" strokeWidth="0.8"/></svg>
                Données Excel
              </div>
            )}
          </div>
        </div>

        {/* Name + subtitle */}
        <div style={{paddingLeft:10}}>
          <div style={{fontSize:15,fontWeight:700,color:"#e2e8f0",lineHeight:1.3,marginBottom:4}}>
            {L.m[k].n}
          </div>
          <div style={{fontSize:12,color:color,fontWeight:600,marginBottom:7,lineHeight:1.25}}>
            {L.m[k].d}
          </div>
          <div style={{fontSize:13,color:"#64748b",lineHeight:1.55}}>
            {L.m[k].x}
          </div>
        </div>
      </div>
    );
  };

  const jurCalcCount = JUR_METHODS.filter(k=>!!modelResults[k]).length;
  const intlCalcCount = INTL_METHODS.filter(k=>!!modelResults[k]).length;

  const DashTab = ({id, label, icon, badge}) => {
    const active = dashTab === id;
    return (
      <button onClick={()=>setDashTab(id)} style={{
        display:"flex",alignItems:"center",gap:8,padding:"10px 18px",flexShrink:0,
        background: active ? "rgba(255,255,255,0.06)" : "transparent",
        border:"none",borderBottom: active ? "2px solid #a78bfa" : "2px solid transparent",
        color: active ? "#f1f5f9" : "#64748b",fontSize:13,fontWeight: active?700:500,
        cursor:"pointer",transition:"all 0.15s",whiteSpace:"nowrap",marginBottom:"-1px",
      }}>
        <span>{icon}</span>{label}
        {badge > 0 && <span style={{fontSize:10,padding:"2px 7px",background:"rgba(167,139,250,0.2)",borderRadius:10,color:"#a78bfa",fontWeight:700}}>{badge}</span>}
      </button>
    );
  };

  return (
    <FadeIn>
      <div style={{padding:"32px 0 20px"}}>

        {/* Header */}
        <div style={{marginBottom:20}}>
          <h2 style={{fontSize:24,fontWeight:800,color:"#f1f5f9",margin:"0 0 6px",letterSpacing:"-0.5px"}}>{L.dash}</h2>
          <p style={{color:"#475569",fontSize:15,margin:0}}>{L.sel}</p>
        </div>

        {/* ── Tab bar ── */}
        <div style={{display:"flex",borderBottom:"1px solid rgba(255,255,255,0.07)",marginBottom:28,gap:2,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
          <DashTab id="strat"  icon="📋" label={L.grp.diagStrat} badge={diagStratData?._filled||0}/>
          <DashTab id="fin"    icon="📊" label={L.grp.diagFin}    badge={diagFinData?._ready?1:0}/>
          <DashTab id="synth"  icon="🧭" label={L.grp.diagSynth}             badge={0}/>
          <DashTab id="revue"  icon="🔍" label={L.grp.revue}                  badge={0}/>
          <DashTab id="models" icon="📐" label={L.grp.models}  badge={intlCalcCount+jurCalcCount}/>
        </div>

        {/* ── ONGLET : Diagnostic Stratégique ── */}
        {dashTab === "strat" && (
          <DiagnosticStrategique
            data={diagStratData}
            onChange={onDiagStratChange}
            xlData={xlData}
            onXlImport={onXlImport}
            L={L}
            lang={lang}
            aiSuggestions={aiSuggestions}
            onAiSuggestionsChange={onAiSuggestionsChange}
            onResetSession={onResetSession}
          />
        )}

        {/* ── ONGLET : Diagnostic Financier ── */}
        {dashTab === "fin" && (
          <DiagnosticFinancier
            data={diagFinData}
            onChange={onDiagFinChange}
            xlData={xlData}
            onXlImport={onXlImport}
            L={L}
            lang={lang}
          />
        )}

        {/* ── ONGLET : Synthèse ── */}
        {dashTab === "synth" && (
          <BlocSynthese
            stratProfile={computeStratProfile(diagStratData)}
            finProfile={computeFinProfile(xlData?.diag_fin)}
            diagStratData={diagStratData}
            synthesisData={synthesisData}
            onSynthesisChange={onSynthesisChange}
            onApplyHypotheses={(payload) => { onApplyHypotheses(payload); setDashTab("models"); }}
            xlData={xlData}
            L={L}
            lang={lang}
          />
        )}

        {/* ── ONGLET : Revue de Valorisation ── */}
        {dashTab === "revue" && (
          <RevueValorisation L={L} lang={lang} />
        )}

        {/* ── ONGLET : Modèles ── */}
        {dashTab === "models" && (<>
          {/* Universal methods */}
          <div style={{marginBottom:28}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#60a5fa",whiteSpace:"nowrap"}}>
                {L.grp.intl}
              </div>
              <div style={{flex:1,height:"1px",background:"rgba(96,165,250,0.15)"}}/>
              <span style={{fontSize:12,color:"#475569"}}>{intlCalcCount}/{INTL_METHODS.length} {L.dash_calculés}</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(250px,1fr))",gap:12}}>
              {INTL_METHODS.map(k => <ModelCard key={k} k={k}/>)}
            </div>
          </div>

          {/* Jurisdiction calculators (incl. France/DGI) */}
          <div style={{marginBottom:28}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#a78bfa",whiteSpace:"nowrap"}}>
                {L.grp.jur}
              </div>
              <div style={{flex:1,height:"1px",background:"rgba(167,139,250,0.15)"}}/>
              <span style={{fontSize:12,color:"#475569"}}>{jurCalcCount}/{JUR_METHODS.length} {L.dash_calculés}</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(250px,1fr))",gap:12}}>
              {JUR_METHODS.map(k => <ModelCard key={k} k={k}/>)}
            </div>
          </div>

          <FootballField results={modelResults} L={L}/>

          {Object.keys(modelResults).length >= 2 && (
            <div style={{display:"flex",justifyContent:"center",marginTop:20,paddingBottom:8}}>
              <button onClick={()=>onSel("rapport")}
                style={{display:"flex",alignItems:"center",gap:10,padding:"12px 28px",
                  background:"linear-gradient(135deg,rgba(59,130,246,0.12),rgba(139,92,246,0.12))",
                  border:"1px solid rgba(99,102,241,0.35)",borderRadius:10,
                  color:"#a5b4fc",fontSize:13,fontWeight:700,cursor:"pointer",transition:"all 0.2s",
                  boxShadow:"0 0 24px rgba(99,102,241,0.08)"}}
                onMouseEnter={e=>{e.currentTarget.style.background="linear-gradient(135deg,rgba(59,130,246,0.2),rgba(139,92,246,0.2))";e.currentTarget.style.borderColor="rgba(99,102,241,0.6)";e.currentTarget.style.color="#c7d2fe";}}
                onMouseLeave={e=>{e.currentTarget.style.background="linear-gradient(135deg,rgba(59,130,246,0.12),rgba(139,92,246,0.12))";e.currentTarget.style.borderColor="rgba(99,102,241,0.35)";e.currentTarget.style.color="#a5b4fc";}}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
                {L.btn_report}
                <span style={{fontSize:10,padding:"2px 8px",background:"rgba(99,102,241,0.2)",borderRadius:4,color:"#818cf8"}}>
                  {Object.keys(modelResults).length} {L.lbl_methods}
                </span>
              </button>
            </div>
          )}
        </>)}
      </div>
    </FadeIn>
  );
};


// ==========================================================================
// RAPPORT DE SYNTHÈSE MULTI-MODÈLES — v2
// Football field : barres empilées min|central|max
// Exports : Excel (SheetJS), PDF (pure JS), PNG (Canvas 2D)
// ==========================================================================
const SynthesisReport = ({results, xlData, L, onBack}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [pngLoading, setPngLoading]   = useState(false);
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [pdfCabinet, setPdfCabinet]   = useState('');
  const [pdfAnalyst, setPdfAnalyst]   = useState('');
  const [pdfLogoB64, setPdfLogoB64]   = useState(null); // base64 string of cabinet logo
  const [showPdfModal, setShowPdfModal] = useState(false);
  const ffRef = useRef(null);

  const colorMap = {
    dcf:"#3b82f6", exc:"#8b5cf6", mul:"#10b981",
    rfr:"#f59e0b", mpe:"#ec4899", ast:"#06b6d4",
    vp:"#f97316",  vm:"#a78bfa",  vr:"#34d399",
    mba:"#fb7185", ebe_re:"#38bdf8", comb:"#fbbf24",
    us:"#f87171",  de:"#fbbf24",  ch:"#f472b6", uk:"#34d399"
  };
  const nameMap = {
    dcf:L.sr_nm_dcf, exc:L.sr_nm_exc, mul:L.sr_nm_mul,
    rfr:L.sr_nm_rfr, mpe:L.sr_nm_mpe, ast:L.sr_nm_ast,
    vp:L.sr_nm_vp, vm:L.sr_nm_vm, vr:L.sr_nm_vr,
    mba:L.sr_nm_mba, ebe_re:L.sr_nm_ebe_re, comb:L.sr_nm_comb,
    us:L.sr_nm_us, de:L.sr_nm_de, ch:L.sr_nm_ch, uk:L.sr_nm_uk
  };
  const categoryMap = {
    dcf:L.sr_cat_financial, exc:L.sr_cat_financial, mul:L.sr_cat_financial,
    rfr:L.sr_cat_intangible, mpe:L.sr_cat_intangible, ast:L.sr_cat_asset,
    vp:L.sr_cat_jur_fr, vm:L.sr_cat_jur_fr, vr:L.sr_cat_jur_fr,
    mba:L.sr_cat_jur_fr, ebe_re:L.sr_cat_jur_fr, comb:L.sr_cat_jur_fr,
    us:L.sr_cat_jur_us, de:L.sr_cat_jur_de, ch:L.sr_cat_jur_ch, uk:L.sr_cat_jur_uk
  };
  // Suggested weights for display
  const weightMap = {
    dcf:"25–30%", exc:"10–15%", mul:"20–25%",
    rfr:"5–10%",  mpe:"5–10%",  ast:"10–15%",
    vp:"10–15%",  vm:"10–15%",  vr:"5–10%",
    mba:"5–10%",  ebe_re:"5–10%", comb:"15–20%",
    us:"10–15%",  de:"10–15%",  ch:"10–15%", uk:"10–15%"
  };
  const jurKeys = new Set(["vp","vm","vr","mba","ebe_re","comb","us","de","ch","uk"]);

  const companyName = xlData?._meta?.company_name
    || Object.values(results).find(r=>r.company_name)?.company_name
    || L.sec_company;
  // Source netDebt and shares from xlData first, then fall back to any model's stored params
  const netDebt = xlData?.dcf?.net_debt
    ?? xlData?.net_debt
    ?? Object.values(results).find(r => r._params?.net_debt != null)?._params?.net_debt
    ?? 0;
  const shares = xlData?.dcf?.shares
    || xlData?.vp?.nombre_titres
    || xlData?.shares_outstanding
    || Object.values(results).find(r => (r._params?.shares_outstanding || r._params?.nombre_titres) > 0)?._params?.shares_outstanding
    || Object.values(results).find(r => r._params?.nombre_titres > 0)?._params?.nombre_titres
    || 0;

  // ── Normalise result into {eq_low, eq_mid, eq_high, ev_*} ────────────────
  // Use each model's own net_debt from params when available, else shared netDebt
  const getModelData = (k, v) => {
    const mDebt = v._params?.net_debt ?? v._params?.endettement_financier_net ?? netDebt;
    let eq_mid = v.equity_value || v.fair_value || v.equity_value_mid || v.valeur_entreprise || 0;
    let eq_low  = v.equity_value_low  || eq_mid * 0.85;
    let eq_high = v.equity_value_high || eq_mid * 1.15;
    if (k === "mul") { eq_low = v.equity_value_low||eq_mid*0.85; eq_mid = v.equity_value_mid||eq_mid; eq_high = v.equity_value_high||eq_mid*1.15; }
    if (k === "ast") { eq_low = v.liquidation_nav||eq_mid*0.85; eq_mid = v.adjusted_nav||eq_mid; eq_high = (v.adjusted_nav||eq_mid)*1.05; }
    // For models that produce an explicit enterprise value, use it; otherwise derive from equity + debt
    let ev_mid, ev_low, ev_high;
    if (k === "us" && v.earnings_value) {
      // US: earnings_value is the pre-debt enterprise value
      ev_mid = v.earnings_value; ev_low = eq_low + mDebt; ev_high = eq_high + mDebt;
    } else if (k === "de" && v.ertragswert) {
      // DE: ertragswert is the pre-debt enterprise value
      ev_mid = v.ertragswert; ev_low = eq_low + mDebt; ev_high = eq_high + mDebt;
    } else if (k === "ch" || k === "uk") {
      // CH/UK: equity-level methods — no meaningful EV (no debt deduction in formula)
      ev_mid = eq_mid + mDebt; ev_low = eq_low + mDebt; ev_high = eq_high + mDebt;
    } else {
      ev_mid = eq_mid + mDebt; ev_low = eq_low + mDebt; ev_high = eq_high + mDebt;
    }
    const mShares = v._params?.shares_outstanding || v._params?.nombre_titres || v._params?.total_shares || shares;
    const vpa = mShares > 0 ? eq_mid / mShares : 0;
    return {
      key:k, name:nameMap[k]||k, color:colorMap[k]||"#94a3b8",
      category:categoryMap[k]||"Autre", isJur:jurKeys.has(k),
      weight: weightMap[k]||"—",
      eq_mid, eq_low, eq_high, ev_mid, ev_low, ev_high, vpa
    };
  };

  const rows = Object.entries(results)
    .filter(([k,v]) => nameMap[k] && (v.equity_value||v.fair_value||v.equity_value_mid||v.valeur_entreprise) > 0)
    .map(([k,v]) => getModelData(k,v))
    .filter(r => r.eq_mid > 0)
    .sort((a,b) => a.eq_mid - b.eq_mid);  // ascending for football field (bottom = lowest)

  const fV = v => {
    if (!v || !isFinite(v)) return "—";
    if (Math.abs(v) >= 1e6) return `${(v/1e6).toFixed(2)}M€`;
    if (Math.abs(v) >= 1e3) return `${(v/1e3).toFixed(0)}k€`;
    return `${Math.round(v)}€`;
  };
  const fVpa = v => v > 0 ? `${v.toFixed(2)}€` : "—";

  // ── Stats ────────────────────────────────────────────────────────────────
  const allMids = rows.map(r=>r.eq_mid).sort((a,b)=>a-b);
  const n = allMids.length;
  const median = n % 2 === 0 ? (allMids[n/2-1]+allMids[n/2])/2 : allMids[Math.floor(n/2)];
  const q1 = allMids[Math.floor(n*0.25)] || allMids[0];
  const q3 = allMids[Math.floor(n*0.75)] || allMids[n-1];
  const globalEqMin = Math.min(...rows.map(r=>r.eq_low));
  const globalEqMax = Math.max(...rows.map(r=>r.eq_high));
  const chartMin = globalEqMin * 0.90;
  const chartMax = globalEqMax * 1.10;
  const scaleX = v => Math.max(0, Math.min(100, ((v-chartMin)/(chartMax-chartMin))*100));

  // ── Export PNG — draws football field on canvas (no external deps) ────────
  // ── Helper: trigger download from blob ───────────────────────────────────
  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
  };

  // ── Export PNG — pure Canvas 2D, zero deps ────────────────────────────────
  const exportPNG = () => {
    setPngLoading(true);
    try {
      const BAR_H = 30, LABEL_W = 148, RIGHT_W = shares>0 ? 78 : 0, PAD = 18;
      const intlR = rows.filter(r=>!r.isJur);
      const dgiR  = rows.filter(r=> r.isJur);
      const secs  = [];
      if (intlR.length) secs.push({title:L.sr_png_methods_header, color:"#3b82f6", rows:intlR});
      if (dgiR.length)  secs.push({title:L.sr_png_jur_header, color:"#C9A84C", rows:dgiR});
      const canvasW = 880;
      const canvasH = PAD*2 + 50 + rows.length*(BAR_H+16) + secs.length*26 + 56 + 24;
      const trackW  = canvasW - LABEL_W - RIGHT_W - PAD*2;

      const cv = document.createElement("canvas");
      cv.width  = canvasW * 2;
      cv.height = canvasH * 2;
      const c = cv.getContext("2d");
      c.scale(2, 2);

      // Background
      c.fillStyle = "#0d1117";
      c.fillRect(0, 0, canvasW, canvasH);

      // Title
      c.fillStyle = "#f1f5f9"; c.font = "bold 14px Arial, sans-serif"; c.textAlign = "left";
      c.fillText(`${companyName} — Football Field`, PAD, PAD + 14);
      c.fillStyle = "#475569"; c.font = "10px Arial, sans-serif";
      c.fillText(`${rows.length} ${L.sr_methods} · Equity Value · ${new Date().toLocaleDateString()}`, PAD, PAD + 28);

      // Median badge top-right
      c.fillStyle = "#818cf8"; c.font = "bold 11px Arial, sans-serif"; c.textAlign = "right";
      c.fillText(`${L.sr_png_median} ${fV(median)}`, canvasW - PAD, PAD + 14);
      if (shares > 0) { c.font = "9px Arial,sans-serif"; c.fillStyle="#94a3b8"; c.fillText(`${(median/shares).toFixed(2)}€${L.sr_png_per_share}`, canvasW - PAD, PAD + 27); }

      let y = PAD + 46;
      const scX = v => LABEL_W + PAD + Math.max(0, Math.min(trackW, ((v-chartMin)/(chartMax-chartMin)) * trackW));
      const hex2rgb = h => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];

      const drawBar = r => {
        const pL = scX(r.eq_low), pM = scX(r.eq_mid), pH = scX(r.eq_high);
        const [rr,rg,rb] = hex2rgb(r.color);

        // Track bg
        c.fillStyle = "rgba(255,255,255,0.025)";
        c.fillRect(LABEL_W + PAD, y, trackW, BAR_H);

        // Q1–Q3 zone
        c.fillStyle = "rgba(99,102,241,0.08)";
        c.fillRect(scX(q1), y, Math.max(1, scX(q3)-scX(q1)), BAR_H);

        // Segment A: low→mid
        c.fillStyle = `rgba(${rr},${rg},${rb},0.20)`;
        c.strokeStyle = `rgba(${rr},${rg},${rb},0.55)`;
        c.lineWidth = 1;
        const bY = y + BAR_H*0.18, bH = BAR_H*0.64;
        c.fillRect(pL, bY, Math.max(1, pM-pL), bH);
        c.strokeRect(pL, bY, Math.max(1, pM-pL), bH);

        // Segment B: mid→high
        c.fillStyle = `rgba(${rr},${rg},${rb},0.42)`;
        c.fillRect(pM, bY, Math.max(1, pH-pM), bH);
        c.strokeRect(pM, bY, Math.max(1, pH-pM), bH);

        // Mid tick
        c.shadowColor = r.color; c.shadowBlur = 6;
        c.fillStyle = r.color;
        c.fillRect(pM - 1.5, y + 3, 3, BAR_H - 6);
        c.shadowBlur = 0;

        // Labels
        c.fillStyle = r.color; c.font = "bold 8.5px monospace"; c.textAlign = "center";
        c.fillText(fV(r.eq_mid), pM, y - 2);
        c.fillStyle = `rgba(${rr},${rg},${rb},0.7)`; c.font = "7px monospace";
        c.fillText(fV(r.eq_low),  pL, y + BAR_H + 10);
        c.fillText(fV(r.eq_high), pH, y + BAR_H + 10);

        // Model name left
        c.textAlign = "right"; c.fillStyle = r.color; c.font = "bold 9.5px Arial,sans-serif";
        c.fillText(r.name, LABEL_W + PAD - 8, y + BAR_H/2 + 4);

        // V/action right
        if (shares > 0) {
          c.textAlign = "left"; c.fillStyle = "#64748b"; c.font = "8px monospace";
          c.fillText(fVpa(r.vpa), LABEL_W + PAD + trackW + 8, y + BAR_H/2 + 4);
        }
        y += BAR_H + 16;
      };

      // Sections
      secs.forEach((sec, si) => {
        c.strokeStyle = sec.color + "55"; c.lineWidth = 0.7;
        c.setLineDash([3,3]);
        c.beginPath(); c.moveTo(LABEL_W+PAD, y+10); c.lineTo(LABEL_W+PAD+trackW, y+10); c.stroke();
        c.setLineDash([]);
        c.fillStyle = sec.color; c.font = "bold 7px Arial,sans-serif"; c.textAlign = "center";
        c.fillText(sec.title, LABEL_W+PAD+trackW/2, y+8);
        y += 22;
        sec.rows.forEach(drawBar);
        if (si < secs.length-1) y += 4;
      });

      // Median summary bar
      c.fillStyle = "rgba(99,102,241,0.07)";
      c.strokeStyle = "rgba(99,102,241,0.35)"; c.lineWidth = 1;
      c.fillRect(LABEL_W+PAD, y+4, trackW, 22);
      c.strokeRect(LABEL_W+PAD, y+4, trackW, 22);
      c.fillStyle = "rgba(99,102,241,0.75)";
      c.fillRect(scX(median)-1, y+4, 2, 22);
      c.fillStyle = "#818cf8"; c.font = "bold 9px monospace"; c.textAlign = "center";
      c.fillText(`${L.sr_png_median} ${fV(median)}   ·   Q1–Q3 : ${fV(q1)} – ${fV(q3)}`, LABEL_W+PAD+trackW/2, y+18);
      y += 32;

      // X-axis
      c.strokeStyle = "rgba(255,255,255,0.08)"; c.lineWidth = 1;
      c.beginPath(); c.moveTo(LABEL_W+PAD, y); c.lineTo(LABEL_W+PAD+trackW, y); c.stroke();
      [0, 25, 50, 75, 100].forEach(pct => {
        const v = chartMin + (chartMax-chartMin)*pct/100;
        const x = LABEL_W + PAD + trackW*pct/100;
        c.strokeStyle = "rgba(255,255,255,0.05)";
        c.beginPath(); c.moveTo(x, y); c.lineTo(x, y+4); c.stroke();
        c.fillStyle = "#475569"; c.font = "7px monospace"; c.textAlign = "center";
        c.fillText(fV(v), x, y+13);
      });

      // Watermark
      c.fillStyle = "rgba(201,168,76,0.25)"; c.font = "bold 9px Arial,sans-serif"; c.textAlign = "right";
      c.fillText("Hard to Value", canvasW-PAD, canvasH-6);

      cv.toBlob(blob => {
        triggerDownload(blob, `SoV_FootballField_${companyName.replace(/\s+/g,"_")}.png`);
        setPngLoading(false);
      }, "image/png");
    } catch(e) {
      console.error("PNG failed", e);
      setPngLoading(false);
    }
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const date = new Date().toLocaleDateString();
    const R0 = 5; // 1-based row of first data row in Synthèse sheet

    // Onglet 1 — Synthèse
    const aoa1 = [
      [L.sr_xls_title,...Array(10).fill("")],
      [`${L.sr_xls_company} ${companyName}`,``,`${L.sr_xls_date} ${date}`,``,`${L.sr_xls_methods} ${rows.length}`,``,`${L.sr_xls_nfd} ${Math.round(netDebt)} k€`,``,`${L.sr_xls_shares} ${shares>0?shares.toLocaleString():"—"}`,``],
      Array(11).fill(""),
      Array(11).fill(""),
      [L.exp_methode,L.exp_categorie,L.exp_ev_basse,L.exp_ev_centrale,L.exp_ev_haute,L.exp_eq_basse,L.exp_eq_centrale,L.exp_eq_haute,L.exp_fourchette,shares>0?L.exp_v_action:"—",L.exp_poids],
      ...rows.map((r,i)=>{
        const rn=R0+1+i;
        return [r.name,r.category,
          Math.round(r.ev_low),Math.round(r.ev_mid),Math.round(r.ev_high),
          Math.round(r.eq_low),Math.round(r.eq_mid),Math.round(r.eq_high),
          `=IFERROR((H${rn}-F${rn})/G${rn}*100,0)`,
          shares>0?`=IFERROR(G${rn}/${shares},0)`:"-",
          r.weight];
      }),
      Array(11).fill(""),
      [L.sr_xls_median,"",
        `=MEDIAN(C${R0+1}:C${R0+rows.length})`,`=MEDIAN(D${R0+1}:D${R0+rows.length})`,`=MEDIAN(E${R0+1}:E${R0+rows.length})`,
        `=MEDIAN(F${R0+1}:F${R0+rows.length})`,`=MEDIAN(G${R0+1}:G${R0+rows.length})`,`=MEDIAN(H${R0+1}:H${R0+rows.length})`,
        "",shares>0?`=IFERROR(MEDIAN(G${R0+1}:G${R0+rows.length})/${shares},0)`:"",""],
      ["MIN","",
        `=MIN(C${R0+1}:C${R0+rows.length})`,`=MIN(D${R0+1}:D${R0+rows.length})`,`=MIN(E${R0+1}:E${R0+rows.length})`,
        `=MIN(F${R0+1}:F${R0+rows.length})`,`=MIN(G${R0+1}:G${R0+rows.length})`,`=MIN(H${R0+1}:H${R0+rows.length})`,
        "","",""],
      ["MAX","",
        `=MAX(C${R0+1}:C${R0+rows.length})`,`=MAX(D${R0+1}:D${R0+rows.length})`,`=MAX(E${R0+1}:E${R0+rows.length})`,
        `=MAX(F${R0+1}:F${R0+rows.length})`,`=MAX(G${R0+1}:G${R0+rows.length})`,`=MAX(H${R0+1}:H${R0+rows.length})`,
        "","",""],
    ];
    const ws1=XLSX.utils.aoa_to_sheet(aoa1);
    ws1["!cols"]=[{wch:22},{wch:20},{wch:14},{wch:14},{wch:14},{wch:14},{wch:14},{wch:14},{wch:14},{wch:14},{wch:14}];
    ws1["!merges"]=[{s:{r:0,c:0},e:{r:0,c:10}}];
    XLSX.utils.book_append_sheet(wb,ws1,L.sr_xls_sheet1);

    // Onglet 2 — Bridge EV → Equity
    const R0b=5;
    const aoa2=[
      [L.sr_xls_br_title,...Array(4).fill("")],
      [`${L.sr_xls_company} ${companyName}`,``,`${L.sr_xls_nfd} ${Math.round(netDebt)} k€`,``,`${L.sr_xls_shares} ${shares>0?shares.toLocaleString():"—"}`],
      Array(5).fill(""),Array(5).fill(""),
      [L.exp_methode,L.sr_xls_br_ev,L.sr_xls_br_nfd,L.sr_xls_br_eq,L.exp_v_action],
      ...rows.map((r,i)=>{const rn=R0b+1+i;return[r.name,Math.round(r.ev_mid),Math.round(netDebt),`=B${rn}-C${rn}`,shares>0?`=IFERROR(D${rn}/${shares},0)`:"-"];}),
      Array(5).fill(""),
      [L.sr_xls_median,`=MEDIAN(B${R0b+1}:B${R0b+rows.length})`,Math.round(netDebt),`=MEDIAN(D${R0b+1}:D${R0b+rows.length})`,shares>0?`=IFERROR(MEDIAN(D${R0b+1}:D${R0b+rows.length})/${shares},0)`:"—"],
    ];
    const ws2=XLSX.utils.aoa_to_sheet(aoa2);
    ws2["!cols"]=[{wch:22},{wch:18},{wch:14},{wch:18},{wch:14}];
    XLSX.utils.book_append_sheet(wb,ws2,L.sr_xls_sheet2);

    // Onglet 3 — Chart data
    const aoa3=[
      [L.exp_methode,L.exp_ev_basse,L.exp_ev_centrale,L.exp_ev_haute,L.exp_categorie],
      ...rows.map(r=>[r.name,Math.round(r.eq_low),Math.round(r.eq_mid),Math.round(r.eq_high),r.category])
    ];
    const ws3=XLSX.utils.aoa_to_sheet(aoa3);
    ws3["!cols"]=[{wch:22},{wch:12},{wch:12},{wch:12},{wch:22}];
    XLSX.utils.book_append_sheet(wb,ws3,L.sr_xls_sheet3);

    XLSX.writeFile(wb,`SoV_Synthese_${companyName.replace(/\s+/g,"_")}.xlsx`);
  };


  // ── Export PDF — Rapport complet multi-pages ──────────────────────────────
  // Portrait A4, jsPDF-style via raw PDF stream, zéro dépendance externe
  const exportPDF = async () => {
    setPdfLoading(true);
    try {
      // ── Config page ──────────────────────────────────────────────────────
      const W = 595.28, H = 841.89; // A4 portrait
      const mL = 42, mR = 42, mT = 36;
      const contentW = W - mL - mR;
      const date = new Date().toLocaleDateString(undefined, {day:'2-digit',month:'long',year:'numeric'});
      const dateShort = new Date().toLocaleDateString();

      // ── Text safety (ASCII only pour PDF Type1) ───────────────────────────
      const safe = s => String(s || '')
        .replace(/[àâä]/g,'a').replace(/[éèêë]/g,'e').replace(/[îï]/g,'i')
        .replace(/[ôö]/g,'o').replace(/[ùûü]/g,'u').replace(/ç/g,'c')
        .replace(/[ÀÂÄÁÃ]/g,'A').replace(/[ÉÈÊËẼ]/g,'E').replace(/[ÎÏ]/g,'I')
        .replace(/[ÔÖÕÓ]/g,'O').replace(/[ÙÛÜÚ]/g,'U').replace(/Ç/g,'C')
        .replace(/[Ññ]/g,'n').replace(/€/g,'EUR').replace(/–/g,'-')
        .replace(/→/g,'->').replace(/·/g,'.').replace(/«|»/g,'"')
        .replace(/[^\x20-\x7E]/g,'?');
      const esc = s => safe(s).replace(/\\/g,'\\\\').replace(/\(/g,'\\(').replace(/\)/g,'\\)');

      // ── Formatters ────────────────────────────────────────────────────────
      const fV2 = v => {
        if (!v || !isFinite(v)) return '--';
        const a = Math.abs(v);
        if (a >= 1e9) return `${(v/1e9).toFixed(2)}Md EUR`;
        if (a >= 1e6) return `${(v/1e6).toFixed(2)}M EUR`;
        if (a >= 1e3) return `${(v/1e3).toFixed(0)}k EUR`;
        return `${Math.round(v)} EUR`;
      };
      const fShort = v => {
        if (!v || !isFinite(v)) return '--';
        const a = Math.abs(v);
        if (a >= 1e9) return `${(v/1e9).toFixed(1)}Md`;
        if (a >= 1e6) return `${(v/1e6).toFixed(2)}M`;
        if (a >= 1e3) return `${(v/1e3).toFixed(0)}k`;
        return `${Math.round(v)}`;
      };
      const pct = v => isFinite(v) ? `${(v*100).toFixed(1)}%` : '--';

      // ── Colors → PDF RGB ──────────────────────────────────────────────────
      const rgb = h => {
        const r=parseInt(h.slice(1,3),16)/255,g=parseInt(h.slice(3,5),16)/255,b=parseInt(h.slice(5,7),16)/255;
        return `${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)}`;
      };
      const GOLD='#C9A84C', BLUE='#3b82f6', SLATE='#64748b', WHITE='#f1f5f9', DIM='#475569';

      // ── Narrative auto-générée enrichie ─────────────────────────────────────
      const spread = (globalEqMax - globalEqMin) / median;
      const dispersion = spread < 0.30 ? 'low' : spread < 0.60 ? 'moderate' : 'high';

      // Analyse par catégorie DGI vs Intl
      const intlR4   = rows.filter(r => !r.isJur);
      const dgiR4    = rows.filter(r =>  r.isJur);
      const intlMids4 = intlR4.map(r => r.eq_mid).sort((a,b) => a-b);
      const dgiMids4  = dgiR4.map(r  => r.eq_mid).sort((a,b) => a-b);
      const intlMed4  = intlMids4.length ? (intlMids4.length % 2 === 0
        ? (intlMids4[intlMids4.length/2-1]+intlMids4[intlMids4.length/2])/2
        : intlMids4[Math.floor(intlMids4.length/2)]) : 0;
      const dgiMed4   = dgiMids4.length  ? (dgiMids4.length  % 2 === 0
        ? (dgiMids4[dgiMids4.length/2-1]+dgiMids4[dgiMids4.length/2])/2
        : dgiMids4[Math.floor(dgiMids4.length/2)]) : 0;

      // Outliers (> 1.5x ou < 0.5x mediane)
      const outlierHigh4 = rows.filter(r => r.eq_mid > median * 1.5);
      const outlierLow4  = rows.filter(r => r.eq_mid < median * 0.5);
      const closest4 = [...rows].sort((a,b) => Math.abs(a.eq_mid-median)-Math.abs(b.eq_mid-median))[0];

      const dispersTxt = spread < 0.30
        ? safe(L.sr_exp_narr_low_disp)
        : spread < 0.60
        ? safe(L.sr_exp_narr_mod_disp)
        : safe(L.sr_exp_narr_high_disp);

      const recLow  = fShort(q1);
      const recHigh = fShort(q3);

      const narrative1 = safe(L.sr_exp_narr_base).replace('{0}',rows.length).replace('{1}',rows.length>1?'s':'').replace('{2}',safe(companyName)).replace('{3}',fV2(median)).replace('{4}',dispersTxt);

      const narrative2 = (() => {
        let t = safe(L.sr_exp_narr_range).replace('{0}',recLow).replace('{1}',recHigh);
        if (intlR4.length > 0 && dgiR4.length > 0) {
          const gap = Math.abs(intlMed4 - dgiMed4) / (median||1) * 100;
          if (gap < 15)
            t += safe(L.sr_exp_narr_aligned).replace('{0}',fShort(intlMed4)).replace('{1}',fShort(dgiMed4));
          else
            t += safe(L.sr_exp_narr_gap).replace('{0}',gap.toFixed(0)).replace('{1}',fShort(intlMed4)).replace('{2}',fShort(dgiMed4));
        }
        return t;
      })();

      const narrative3 = (() => {
        let t = netDebt > 0
          ? safe(L.sr_exp_narr_nfd_pos).replace('{0}',fShort(netDebt))
          : safe(L.sr_exp_narr_nfd_zero);
        if (outlierHigh4.length > 0)
          t += " "+safe(L.sr_exp_narr_outlier_high).replace('{0}',outlierHigh4.map(r=>safe(r.name)).join(", ")).replace('{1}',outlierHigh4.length>1?'nt':'').replace('{2}',outlierHigh4.length>1?'vent':'').replace('{3}',outlierHigh4.length>1?'es':'');
        if (outlierLow4.length > 0)
          t += safe(L.sr_exp_narr_outlier_low).replace('{0}',outlierLow4.map(r=>safe(r.name)).join(", ")).replace('{1}',outlierLow4.length>1?'ent':'');
        if (closest4)
          t += safe(L.sr_exp_narr_closest).replace('{0}',safe(closest4.name)).replace('{1}',fShort(closest4.eq_mid));
        return t;
      })();
      // ── Descriptions méthodes DGI ─────────────────────────────────────────
      const dgiDesc = {
        vp: 'Valeur de Productivite: capitalisation du benefice retraite moyen par un taux issu du taux sans risque, de l\'inflation et du beta sectoriel. Methode principale du guide DGI 2006.',
        vm: 'Valeur Mathematique: actif net comptable corrige par revaluation des actifs (plus-values latentes) et prise en compte de l\'impot latent sur les revaluations.',
        vr: 'Valeur de Rendement: capitalisation du dividende moyen distribue au taux de rendement des actions cotees de meme secteur, ajuste d\'une decote de non-liquidite.',
        mba: 'Methode du Benefice Ajuste (MBA): capitalisation de la capacite beneficiaire ajustee (benefice + dotations + provisions) par un coefficient sectoriel de 4 a 10.',
        ebe_re: 'Methode des multiples EBE/RE: application d\'un multiple de marche a l\'Excedent Brut d\'Exploitation ou au Resultat d\'Exploitation, deduit de la dette financiere nette.',
        comb: 'Combinaison DGI: moyenne ponderee des methodes VP (patrimoniale) et methodes de rendement (VR, MBA, EBE/RE) selon les ponderations prescrites par le guide DGI 2006.',
        us: 'IRS Rev. Ruling 59-60: capitalisation du benefice net normalise moyen pondere (3 ans) au taux CAPM build-up (Rf + beta x ERP + primes). Methode de reference pour les societes non cotees aux Etats-Unis.',
        de: 'IDW S1 Ertragswertverfahren: actualisation des benefices distribuables au taux CAPM apres impot personnel (Basiszinssatz + beta x MRP - Abgeltungssteuer). Methode dominante en droit allemand.',
        ch: 'Formule Praticien CSI n°28: (2 x Valeur de Rendement + Valeur Intrinseque) / 3. Formule canonique suisse applicable dans toutes les administrations cantonales.',
        uk: 'HMRC SAV P/E Earnings Basis: benefice maintenable x P/E sectoriel, ajuste pour illiquidite, prime de controle et taille. Approche standard HMRC pour les titres non cotes (TCGA 1992 §272).',
      };
      const intlDesc = {
        dcf: 'Discounted Cash Flow: actualisation des flux de tresorerie libres sur la periode de projection, plus valeur terminale par le modele de Gordon-Shapiro. Methode de reference en finance d\'entreprise.',
        exc: 'Excess Return / EVA: valeur initiale du capital investi augmentee de la somme actualisee des survaleurs (rendement excedentaire au-dela du WACC). Met en evidence la creation de valeur.',
        mul: 'Comparables boursiers: application des multiples de valorisation (VE/CA, VE/EBITDA, VE/EBIT, PER, PBR) d\'un echantillon de societes comparables, apres ajustements.',
        rfr: 'Relief from Royalty: valorise un actif incorporel en estimant les redevances qu\'il aurait fallu payer si l\'actif avait ete licencie a un tiers (methode OCDE).',
        mpe: 'MPEEM (Multi-Period Excess Earnings): isole les flux de tresorerie attribuables specifiquement a l\'actif incorporel, apres deduction des charges des actifs contributifs.',
        ast: 'Actif Net Comptable Corrige: somme des valeurs de marche des actifs, diminuee des dettes financieres et de l\'impot latent. Approche patrimoniale pure.',
      };

      // ── Multi-page PDF builder ────────────────────────────────────────────
      const pages = [];    // each page = array of ops strings
      let ops = [];        // current page ops

      const newPage = () => { pages.push(ops); ops = []; };

      // Primitives — all Y coords are top-down (converted to PDF bottom-up)
      const fillR  = (x,y,w,h,col) => ops.push(`${rgb(col)} rg ${x.toFixed(1)} ${(H-y-h).toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)} re f`);
      const strokeR= (x,y,w,h,col,lw=0.5) => ops.push(`${rgb(col)} RG ${lw} w ${x.toFixed(1)} ${(H-y-h).toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)} re S`);
      const hline  = (x1,y,x2,col,lw=0.4) => ops.push(`${rgb(col)} RG ${lw} w ${x1.toFixed(1)} ${(H-y).toFixed(1)} m ${x2.toFixed(1)} ${(H-y).toFixed(1)} l S`);
      const txt    = (s,x,y,sz,bold,col='#e2e8f0',align='l') => {
        const safeStr = esc(s);
        if (!safeStr) return;
        const xOff = align==='r' ? x - safeStr.length*sz*0.52 : x;
        ops.push(`BT /${bold?'Hb':'Hr'} ${sz} Tf ${rgb(col)} rg ${xOff.toFixed(1)} ${(H-y).toFixed(1)} Td (${safeStr}) Tj ET`);
      };
      const txtW   = (s,sz) => String(s||'').length * sz * 0.52; // approx width

      // ── Helper: wrap long text into lines ────────────────────────────────
      const wrapText = (text, maxW, sz) => {
        const words = safe(text).split(' ');
        const lines2 = [];
        let cur = '';
        for (const w of words) {
          const test = cur ? cur+' '+w : w;
          if (txtW(test, sz) > maxW && cur) {
            lines2.push(cur);
            cur = w;
          } else {
            cur = test;
          }
        }
        if (cur) lines2.push(cur);
        return lines2;
      };

      // ── Standard header/footer ────────────────────────────────────────────
      const drawHeader = (pageNum, totalPages, subtitle='') => {
        fillR(0, 0, W, 28, '#0a0f1a');
        hline(0, 28, W, '#1e2d4a', 0.5);
        txt('Hard to Value', mL, 18, 7, true, GOLD);
        txt(`| ${safe(companyName)}`, mL+120, 18, 7, false, SLATE);
        if (subtitle) txt(subtitle, mL, 24, 6, false, '#334155');
        txt(`${pageNum}/${totalPages}`, W-mR, 18, 7, false, SLATE, 'r');
        txt(dateShort, W-mR, 24, 6, false, '#334155', 'r');
      };

      const drawFooter = () => {
        fillR(0, H-20, W, 20, '#0a0f1a');
        hline(0, H-20, W, '#1e2d4a', 0.5);
        txt(safe(L.sr_exp_footer_conf), mL, H-7, 6, false, '#334155');
        txt(safe(L.sr_exp_footer_disclaimer), mL, H-13, 5.5, false, '#1e2d4a');
      };

      // ── Cabinet config ────────────────────────────────────────────────────
      const cabinetName = pdfCabinet || 'Hard to Value';
      const analystName = pdfAnalyst || '';

      // ── Compter les pages totales ─────────────────────────────────────────
      // P1: cover, P2: exec summary, P3: football field, P4: tableau, P5+: 1/method, Pn: annexe
      const totalPages = 4 + rows.length + 1;

      // ──────────────────────────────────────────────────────────────────────
      // PAGE 1 — COUVERTURE
      // ──────────────────────────────────────────────────────────────────────
      fillR(0,0,W,H,'#060b14');
      // Fond décoratif
      fillR(0,0,W,240,'#080e1a');
      // Bande latérale gold
      fillR(0,0,4,H,GOLD);
      // Titre société
      txt(safe(companyName), mL+8, 120, 28, true, WHITE);
      hline(mL+8, 136, mL+8+Math.min(txtW(safe(companyName),28)+20, contentW), GOLD, 1.5);
      // Sous-titre rapport
      txt(safe(L.sr_exp_valuation_report), mL+8, 158, 11, true, GOLD);
      txt(safe(L.sr_exp_synthesis_multimethods), mL+8, 174, 9, false, '#94a3b8');
      // Médiane
      fillR(mL+8, 200, contentW, 56, '#0d1825');
      strokeR(mL+8, 200, contentW, 56, GOLD+'44');
      txt(safe(L.sr_exp_equity_median_label), mL+18, 216, 7, true, SLATE);
      txt(fV2(median), mL+18, 240, 22, true, GOLD);
      if (shares > 0) txt(safe(L.sr_exp_ie_per_share).replace('{0}',(median/shares).toFixed(2)), mL+18, 252, 8, false, '#94a3b8');
      // Range Q1-Q3
      txt(safe(L.sr_exp_range_retained).replace('{0}',fV2(q1)).replace('{1}',fV2(q3)), W-mR-20, 240, 8, false, '#94a3b8', 'r');
      // Infos
      const infoY = 280;
      const infoW = (contentW-20)/3;
      [[`${rows.length} ${rows.length>1?safe(L.sr_methods):safe(L.sr_method)}`, safe(L.sr_exp_methods_valued)],
       [dateShort, safe(L.sr_exp_report_date)],
       [safe(cabinetName), safe(L.sr_exp_cabinet)]
      ].forEach(([v,l],i) => {
        const ix = mL+8+i*(infoW+10);
        fillR(ix, infoY, infoW, 44, '#0d1825');
        strokeR(ix, infoY, infoW, 44, '#1e2d4a');
        txt(v, ix+10, infoY+18, 10, true, WHITE);
        txt(l, ix+10, infoY+32, 7, false, SLATE);
      });
      if (analystName) {
        txt(safe(L.sr_exp_prepared_by).replace('{0}',safe(analystName)), mL+8, infoY+58, 8, false, SLATE);
      }

      // ── Logo cabinet — embed via canvas drawImage → PDF inline image ─────
      if (pdfLogoB64) {
        await new Promise(resolve => {
          const img = new Image();
          img.onload = () => {
            try {
              const maxLogoW = 90, maxLogoH = 28;
              const scale = Math.min(maxLogoW / img.width, maxLogoH / img.height, 1);
              const lw = img.width * scale, lh = img.height * scale;
              const logoX = W - mR - lw, logoY = infoY;
              // Draw on off-screen canvas then embed PNG
              const lc = document.createElement('canvas');
              lc.width = Math.round(lw * 2); lc.height = Math.round(lh * 2);
              const lctx = lc.getContext('2d');
              lctx.scale(2, 2);
              lctx.drawImage(img, 0, 0, lw, lh);
              // Encode as PDF inline image via raster ops
              // We render the logo on the PDF football-field canvas so it appears in the PNG
              // For the raw PDF, draw a labeled placeholder rect + text
              strokeR(logoX, logoY, lw, lh, GOLD + '44', 0.5);
              txt(safe(cabinetName).substring(0,18), logoX + lw/2, logoY + lh/2 + 4, 7, true, GOLD);
            } catch {}
            resolve();
          };
          img.onerror = resolve;
          img.src = pdfLogoB64;
        });
      }

      // Catégories utilisées
      const cats = [...new Set(rows.map(r=>r.category))];
      let catY = infoY + 80;
      txt(safe(L.sr_exp_methodologies_used), mL+8, catY, 8, true, SLATE);
      catY += 14;
      cats.forEach(cat => {
        const catRows = rows.filter(r=>r.category===cat);
        txt(`• ${safe(cat)}: ${catRows.map(r=>safe(r.name)).join(', ')}`, mL+14, catY, 7.5, false, '#94a3b8');
        catY += 11;
      });
      // Footer cover
      fillR(0,H-30,W,30,'#040810');
      hline(0,H-30,W,GOLD+'44',0.5);
      txt('Hard to Value - Financial & Tax Valuation Platform', mL+8, H-10, 7, false, GOLD+'88');
      txt(safe(L.sr_exp_confidential), W-mR, H-10, 7, true, SLATE, 'r');
      newPage();

      // ──────────────────────────────────────────────────────────────────────
      // PAGE 2 — RÉSUMÉ EXÉCUTIF
      // ──────────────────────────────────────────────────────────────────────
      fillR(0,0,W,H,'#060b14');
      drawHeader(2, totalPages, safe(L.sr_exp_exec_summary));
      let y2 = 46;
      // Titre section
      fillR(mL,y2,contentW,32,'#0d1825');
      strokeR(mL,y2,contentW,32,BLUE+'44');
      fillR(mL,y2,3,32,BLUE);
      txt(safe(L.sr_exp_exec_title), mL+12, y2+13, 9, true, WHITE);
      txt(safe(L.sr_exp_valuation_of).replace('{0}',safe(companyName)).replace('{1}',dateShort), mL+12, y2+24, 7.5, false, SLATE);
      y2 += 42;
      // KPIs
      const kpis2 = [
        {l:safe(L.sr_exp_kpi_med_eq),   v:fV2(median),             c:BLUE},
        {l:safe(L.sr_exp_kpi_range_q), v:`${fShort(q1)}-${fShort(q3)} EUR`, c:'#10b981'},
        {l:safe(L.sr_exp_kpi_nfd_label),v:netDebt>0?fV2(netDebt):safe(L.sr_exp_not_provided), c:'#f59e0b'},
        {l:safe(L.sr_exp_kpi_vpa_med), v:shares>0?`${(median/shares).toFixed(2)} EUR`:safe(L.sr_exp_not_calc), c:'#8b5cf6'},
        {l:safe(L.sr_exp_kpi_nb_methods),      v:`${rows.length}`, c:GOLD},
        {l:safe(L.sr_exp_kpi_dispersion),       v:dispersion==='low'?safe(L.sr_exp_low)+' <30%':dispersion==='moderate'?safe(L.sr_exp_moderate)+' 30-60%':safe(L.sr_exp_high)+' >60%', c:dispersion==='low'?'#10b981':dispersion==='moderate'?'#f59e0b':'#ef4444'},
      ];
      const kW2=(contentW-10)/3, kH2=40;
      kpis2.forEach((k,i) => {
        const kx=mL+(i%3)*(kW2+5), ky=y2+(Math.floor(i/3))*(kH2+6);
        fillR(kx,ky,kW2,kH2,'#0d1825');
        strokeR(kx,ky,kW2,kH2,k.c+'55');
        fillR(kx,ky,3,kH2,k.c);
        txt(k.l, kx+9, ky+14, 6.5, true, SLATE);
        txt(k.v, kx+9, ky+30, k.v.length>14?9:11, true, k.c);
      });
      y2 += 2*(kH2+6)+12;
      // Analyse narrative
      fillR(mL,y2,contentW,14,'#0d1825');
      strokeR(mL,y2,contentW,14,'#1e2d4a');
      fillR(mL,y2,3,14,GOLD);
      txt(safe(L.sr_exp_narrative_title), mL+12, y2+9.5, 7.5, true, WHITE);
      y2 += 22;
      const paraStyle = {sz:8.5, col:'#94a3b8', lh:13};
      [narrative1, narrative2, narrative3].forEach(para => {
        const ls = wrapText(para, contentW-10, paraStyle.sz);
        ls.forEach(l => { txt(l, mL+5, y2, paraStyle.sz, false, paraStyle.col); y2+=paraStyle.lh; });
        y2 += 5;
      });
      // Recommandation
      y2 += 4;
      fillR(mL,y2,contentW,54,'#0d1c2e');
      strokeR(mL,y2,contentW,54,'#3b82f655');
      fillR(mL,y2,3,54,'#3b82f6');
      txt(safe(L.sr_exp_recommended_range), mL+12, y2+13, 7.5, true, '#60a5fa');
      txt(`${fV2(q1)}  -  ${fV2(q3)}`, mL+12, y2+32, 16, true, WHITE);
      txt(safe(L.sr_exp_median_label).replace('{0}',fV2(median)), mL+12, y2+46, 8, false, '#94a3b8');
      if (shares>0) {
        txt(safe(L.sr_exp_vpa_label).replace('{0}',`${(q1/shares).toFixed(2)} - ${(q3/shares).toFixed(2)} EUR  (${safe(L.sr_ff_median).toLowerCase()} ${(median/shares).toFixed(2)} EUR)`), W-mR-10, y2+32, 8, false, '#94a3b8', 'r');
      }
      drawFooter();
      newPage();

      // ──────────────────────────────────────────────────────────────────────
      // PAGE 3 — FOOTBALL FIELD (canvas → PNG data URL → PDF XObject image)
      // ──────────────────────────────────────────────────────────────────────
      // On génère le canvas du football field et l'encode en base64
      const ffCanvas = await (async () => {
        const BAR_H=28, LABEL_W=140, PAD=16;
        const cW=contentW+LABEL_W+PAD*2;
        const intlR2=rows.filter(r=>!r.isJur), dgiR2=rows.filter(r=>r.isJur);
        const secs2=[];
        if(intlR2.length) secs2.push({title:safe(L.sr_exp_ff_methods_intl),color:BLUE,rows:intlR2});
        if(dgiR2.length)  secs2.push({title:safe(L.sr_exp_ff_methods_jur),color:GOLD,rows:dgiR2});
        const cH=PAD*2+50+rows.length*(BAR_H+14)+secs2.length*24+56+20;
        const cv=document.createElement('canvas');
        cv.width=cW*2; cv.height=cH*2;
        const c2=cv.getContext('2d'); c2.scale(2,2);
        c2.fillStyle='#0d1117'; c2.fillRect(0,0,cW,cH);
        const trackW=cW-LABEL_W-PAD*2;
        const scX2=v=>LABEL_W+PAD+Math.max(0,Math.min(trackW,((v-chartMin)/(chartMax-chartMin))*trackW));
        const h2r=h=>[parseInt(h.slice(1,3),16),parseInt(h.slice(3,5),16),parseInt(h.slice(5,7),16)];
        c2.fillStyle='#f1f5f9'; c2.font='bold 12px Arial'; c2.textAlign='left';
        c2.fillText(`${safe(companyName)} — Football Field Valorisation`, PAD, PAD+12);
        c2.fillStyle=SLATE; c2.font='8px Arial';
        c2.fillText(`${rows.length} ${safe(L.sr_methods)} | Equity Value | ${dateShort}`, PAD, PAD+24);
        c2.fillStyle='#818cf8'; c2.font='bold 10px monospace'; c2.textAlign='right';
        c2.fillText(`${safe(L.sr_png_median)} ${fShort(median)} EUR`, cW-PAD, PAD+12);
        let cy=PAD+38;
        const drawBar2=r=>{
          const pL=scX2(r.eq_low),pM=scX2(r.eq_mid),pH=scX2(r.eq_high);
          const [rr,rg,rb]=h2r(r.color);
          c2.fillStyle='rgba(255,255,255,0.02)'; c2.fillRect(LABEL_W+PAD,cy,trackW,BAR_H);
          c2.fillStyle='rgba(99,102,241,0.07)'; c2.fillRect(scX2(q1),cy,Math.max(1,scX2(q3)-scX2(q1)),BAR_H);
          const bY=cy+BAR_H*0.18, bH=BAR_H*0.64;
          c2.fillStyle=`rgba(${rr},${rg},${rb},0.18)`; c2.strokeStyle=`rgba(${rr},${rg},${rb},0.5)`; c2.lineWidth=0.8;
          c2.fillRect(pL,bY,Math.max(1,pM-pL),bH); c2.strokeRect(pL,bY,Math.max(1,pM-pL),bH);
          c2.fillStyle=`rgba(${rr},${rg},${rb},0.40)`;
          c2.fillRect(pM,bY,Math.max(1,pH-pM),bH); c2.strokeRect(pM,bY,Math.max(1,pH-pM),bH);
          c2.shadowColor=r.color; c2.shadowBlur=5;
          c2.fillStyle=r.color; c2.fillRect(pM-1.5,cy+3,3,BAR_H-6);
          c2.shadowBlur=0;
          c2.fillStyle=r.color; c2.font='bold 9px Arial'; c2.textAlign='right';
          c2.fillText(safe(r.name),LABEL_W+PAD-6,cy+BAR_H/2+3);
          c2.fillStyle=r.color; c2.font='bold 8px monospace'; c2.textAlign='center';
          c2.fillText(fShort(r.eq_mid),pM,cy-2);
          c2.fillStyle=`rgba(${rr},${rg},${rb},0.65)`; c2.font='6.5px monospace';
          c2.fillText(fShort(r.eq_low),pL,cy+BAR_H+9);
          c2.fillText(fShort(r.eq_high),pH,cy+BAR_H+9);
          cy+=BAR_H+14;
        };
        secs2.forEach((sec,si)=>{
          c2.strokeStyle=sec.color+'66'; c2.lineWidth=0.6; c2.setLineDash([3,3]);
          c2.beginPath(); c2.moveTo(LABEL_W+PAD,cy+8); c2.lineTo(LABEL_W+PAD+trackW,cy+8); c2.stroke();
          c2.setLineDash([]);
          c2.fillStyle=sec.color; c2.font='bold 6.5px Arial'; c2.textAlign='center';
          c2.fillText(sec.title,LABEL_W+PAD+trackW/2,cy+7);
          cy+=20; sec.rows.forEach(drawBar2); if(si<secs2.length-1) cy+=4;
        });
        // Median bar
        const medX=scX2(median),q1X=scX2(q1),q3X=scX2(q3);
        c2.fillStyle='rgba(99,102,241,0.06)'; c2.strokeStyle='rgba(99,102,241,0.3)'; c2.lineWidth=0.8;
        c2.fillRect(LABEL_W+PAD,cy+2,trackW,20); c2.strokeRect(LABEL_W+PAD,cy+2,trackW,20);
        c2.fillStyle='rgba(99,102,241,0.2)'; c2.fillRect(q1X,cy+2,Math.max(1,q3X-q1X),20);
        c2.fillStyle='rgba(99,102,241,0.7)'; c2.fillRect(medX-1,cy+2,2,20);
        c2.fillStyle='#818cf8'; c2.font='bold 8px monospace'; c2.textAlign='center';
        c2.fillText(`${safe(L.sr_png_median)} ${fShort(median)}  |  Q1-Q3: ${fShort(q1)} - ${fShort(q3)}`,LABEL_W+PAD+trackW/2,cy+15);
        cy+=28;
        // Axis
        c2.strokeStyle='rgba(255,255,255,0.07)'; c2.lineWidth=0.8;
        c2.beginPath(); c2.moveTo(LABEL_W+PAD,cy); c2.lineTo(LABEL_W+PAD+trackW,cy); c2.stroke();
        [0,25,50,75,100].forEach(p=>{
          const v2=chartMin+(chartMax-chartMin)*p/100, x2=LABEL_W+PAD+trackW*p/100;
          c2.fillStyle='#475569'; c2.font='6px monospace'; c2.textAlign='center';
          c2.fillText(fShort(v2)+' EUR',x2,cy+11);
        });
        c2.fillStyle='rgba(201,168,76,0.3)'; c2.font='bold 8px Arial'; c2.textAlign='right';
        c2.fillText('Hard to Value',cW-PAD,cH-4);
        return {canvas:cv, w:cW, h:cH};
      })();

      // Embed canvas as PNG in PDF
      const pngDataUrl = ffCanvas.canvas.toDataURL('image/png');
      const pngB64 = pngDataUrl.split(',')[1];
      // Decode base64 to binary
      const binStr = atob(pngB64);
      const binArr = new Uint8Array(binStr.length);
      for (let i=0;i<binStr.length;i++) binArr[i]=binStr.charCodeAt(i);

      // Football field page
      fillR(0,0,W,H,'#060b14');
      drawHeader(3, totalPages, safe(L.sr_exp_ff_subtitle));
      const imgY = 36;
      const imgW = contentW + 2*mL - 2;  // full width
      const imgH = Math.min(H - imgY - 60, imgW * ffCanvas.h / ffCanvas.w);
      // Image will be an XObject — placeholder text since raw PDF image embedding is complex
      // We draw a framed box and describe what would be there
      // Actually embed via PDF inline image
      fillR(1,imgY,W-2,imgH+4,'#0d1117');
      strokeR(1,imgY,W-2,imgH+4,'#1e2d4a');
      // Write image as inline image stream (simplified: just draw the bars with PDF ops)
      // -- Rebuild football field in PDF ops directly --
      const ffLabelW=100, ffPad=10, ffBarH=18, ffTrackW=contentW-ffLabelW-ffPad;
      const scXpdf = v => mL+ffLabelW+ffPad+Math.max(0,Math.min(ffTrackW,((v-chartMin)/(chartMax-chartMin))*ffTrackW));
      let ffy = imgY+30;
      txt(safe(companyName)+' -- Football Field', mL, imgY+14, 10, true, WHITE);
      txt(`${rows.length} ${safe(L.sr_methods)} | Equity Value | ${dateShort}`, mL, imgY+24, 7, false, SLATE);
      txt(`${safe(L.sr_png_median)} ${fV2(median)}`, W-mR, imgY+14, 9, true, '#818cf8', 'r');
      // Header axis labels
      txt(fShort(chartMin)+' EUR', mL+ffLabelW+ffPad, ffy, 6, false, '#334155');
      txt(fShort(chartMax)+' EUR', mL+ffLabelW+ffPad+ffTrackW, ffy, 6, false, '#334155', 'r');
      ffy+=8;
      const intlR3=rows.filter(r=>!r.isJur), dgiR3=rows.filter(r=>r.isJur);
      const secs3=[];
      if(intlR3.length) secs3.push({title:safe(L.sr_exp_ff_methods_intl),color:BLUE,rows:intlR3});
      if(dgiR3.length)  secs3.push({title:safe(L.sr_exp_ff_methods_jur),color:GOLD,rows:dgiR3});
      secs3.forEach((sec,si)=>{
        hline(mL+ffLabelW+ffPad, ffy+5, mL+ffLabelW+ffPad+ffTrackW, sec.color+'55', 0.4);
        txt(sec.title, mL+ffLabelW+ffPad+ffTrackW/2-20, ffy+5, 6, true, sec.color);
        ffy+=14;
        sec.rows.forEach(r=>{
          const pL=scXpdf(r.eq_low), pM=scXpdf(r.eq_mid), pH=scXpdf(r.eq_high);
          const bY=ffy+ffBarH*0.15, bH=ffBarH*0.70;
          // Track bg
          fillR(mL+ffLabelW+ffPad, ffy, ffTrackW, ffBarH, '#0d1117');
          // Q1-Q3 zone
          fillR(scXpdf(q1), ffy, Math.max(0.5,scXpdf(q3)-scXpdf(q1)), ffBarH, '#6366f10a');
          // Bar low→mid
          ops.push(`${rgb(r.color)} rg 0.15 ca`);
          fillR(pL, bY, Math.max(0.5,pM-pL), bH, r.color+'28');
          strokeR(pL, bY, Math.max(0.5,pM-pL), bH, r.color+'60', 0.6);
          // Bar mid→high
          fillR(pM, bY, Math.max(0.5,pH-pM), bH, r.color+'45');
          strokeR(pM, bY, Math.max(0.5,pH-pM), bH, r.color+'60', 0.6);
          // Mid tick
          fillR(pM-1, ffy+2, 2, ffBarH-4, r.color);
          // Labels
          txt(safe(r.name), mL+ffLabelW+ffPad-4, ffy+ffBarH/2+3, 7, true, r.color, 'r');
          txt(fShort(r.eq_mid), pM, ffy-2, 6.5, true, r.color);
          txt(fShort(r.eq_low), pL, ffy+ffBarH+8, 5.5, false, r.color+'aa');
          txt(fShort(r.eq_high), pH, ffy+ffBarH+8, 5.5, false, r.color+'aa');
          if(shares>0) txt(`${r.vpa.toFixed(2)} EUR${safe(L.sr_png_per_share)}`, W-mR, ffy+ffBarH/2+3, 6, false, SLATE, 'r');
          ffy+=ffBarH+12;
        });
        if(si<secs3.length-1) ffy+=6;
      });
      // Median summary bar
      const medXpdf=scXpdf(median), q1Xpdf=scXpdf(q1), q3Xpdf=scXpdf(q3);
      ffy+=4;
      fillR(mL+ffLabelW+ffPad, ffy, ffTrackW, 16, '#1e235011');
      strokeR(mL+ffLabelW+ffPad, ffy, ffTrackW, 16, '#6366f133');
      fillR(q1Xpdf, ffy, Math.max(1,q3Xpdf-q1Xpdf), 16, '#6366f118');
      fillR(medXpdf-1, ffy, 2, 16, '#818cf8');
      txt(`${safe(L.sr_png_median)} ${fV2(median)}  |  Q1-Q3 : ${fV2(q1)} - ${fV2(q3)}`, mL+ffLabelW+ffPad+ffTrackW/2-30, ffy+10.5, 7, true, '#818cf8');
      ffy+=22;
      // X axis ticks
      hline(mL+ffLabelW+ffPad, ffy, mL+ffLabelW+ffPad+ffTrackW, '#1e2d4a55');
      [0,25,50,75,100].forEach(p=>{
        const v3=chartMin+(chartMax-chartMin)*p/100;
        const x3=mL+ffLabelW+ffPad+ffTrackW*p/100;
        txt(fShort(v3), x3, ffy+10, 6, false, '#334155');
      });
      drawFooter();
      newPage();

      // ──────────────────────────────────────────────────────────────────────
      // PAGE 4 — TABLEAU SYNTHÈSE
      // ──────────────────────────────────────────────────────────────────────
      fillR(0,0,W,H,'#060b14');
      drawHeader(4, totalPages, safe(L.sr_exp_tbl_subtitle));
      let y4=46;
      fillR(mL,y4,contentW,28,'#0d1825');
      strokeR(mL,y4,contentW,28,BLUE+'44');
      fillR(mL,y4,3,28,BLUE);
      txt(safe(L.sr_exp_tbl_title), mL+12, y4+12, 9, true, WHITE);
      txt(safe(L.sr_exp_tbl_info).replace('{0}',fV2(netDebt)).replace('{1}',shares>0?shares.toLocaleString():'—'), mL+12, y4+22, 7, false, SLATE);
      y4+=34;
      // Table header
      const cols4=[
        {h:safe(L.sr_exp_tbl_method),      w:90, a:'l'},
        {h:safe(L.sr_exp_tbl_category),    w:66, a:'l'},
        {h:safe(L.sr_exp_tbl_ev_low),     w:48, a:'r'},
        {h:safe(L.sr_exp_tbl_ev_mid),  w:52, a:'r'},
        {h:safe(L.sr_exp_tbl_ev_high),     w:48, a:'r'},
        {h:safe(L.sr_exp_tbl_eq_low),    w:48, a:'r'},
        {h:safe(L.sr_exp_tbl_eq_mid), w:52, a:'r'},
        {h:safe(L.sr_exp_tbl_eq_high),    w:48, a:'r'},
        {h:safe(L.sr_exp_tbl_range),   w:38, a:'c'},
        {h:safe(L.sr_exp_tbl_vpa),     w:44, a:'r'},
      ];
      const tW4=cols4.reduce((s,c)=>s+c.w,0);
      fillR(mL,y4,tW4,13,'#1e2a40');
      hline(mL,y4+13,mL+tW4,'#3b82f655',0.5);
      let cx4=mL+2;
      cols4.forEach(col=>{
        txt(col.h, col.a==='r'?cx4+col.w-3:cx4+2, y4+9, 6, true, '#64748b', col.a==='r'?'r':'l');
        cx4+=col.w;
      });
      y4+=13;
      const ROW4=14;
      [...rows].reverse().forEach((r,ri)=>{
        fillR(mL,y4,tW4,ROW4,ri%2===0?'#0d1420':'#0f1825');
        const spread2=((r.eq_high-r.eq_low)/r.eq_mid*100).toFixed(0);
        const sCol=+spread2<25?'#10b981':+spread2<50?'#f59e0b':'#ef4444';
        const cells4=[
          {v:r.name,c:r.color,b:true},
          {v:r.category,c:SLATE},
          {v:fShort(r.ev_low),c:DIM,a:'r'},
          {v:fShort(r.ev_mid),c:'#94a3b8',b:true,a:'r'},
          {v:fShort(r.ev_high),c:DIM,a:'r'},
          {v:fShort(r.eq_low),c:DIM,a:'r'},
          {v:fShort(r.eq_mid),c:WHITE,b:true,a:'r'},
          {v:fShort(r.eq_high),c:DIM,a:'r'},
          {v:`+/-${spread2}%`,c:sCol,a:'c'},
          {v:shares>0?r.vpa.toFixed(2):'--',c:'#94a3b8',a:'r'},
        ];
        cx4=mL+2;
        cells4.forEach((cell,ci)=>{
          const col=cols4[ci];
          const xp=cell.a==='r'?cx4+col.w-3:cx4+2;
          txt(safe(cell.v), xp, y4+ROW4-4, ci===0||ci===1?7:6.5, cell.b||false, cell.c||'#94a3b8', cell.a==='r'?'r':'l');
          cx4+=col.w;
        });
        hline(mL,y4+ROW4,mL+tW4,'#ffffff08');
        y4+=ROW4;
      });
      // Median row
      y4+=2;
      fillR(mL,y4,tW4,16,'#1a2050');
      strokeR(mL,y4,tW4,16,'#6366f155');
      const med4=[
        {v:safe(L.sr_exp_tbl_median_q),c:'#818cf8',b:true},
        {v:'',c:''},
        {v:fShort(Math.min(...rows.map(r=>r.ev_low))),c:SLATE,a:'r'},
        {v:fShort(median+netDebt),c:'#818cf8',b:true,a:'r'},
        {v:fShort(Math.max(...rows.map(r=>r.ev_high))),c:SLATE,a:'r'},
        {v:fShort(q1),c:SLATE,a:'r'},
        {v:fShort(median),c:'#818cf8',b:true,a:'r'},
        {v:fShort(q3),c:SLATE,a:'r'},
        {v:'',c:''},
        {v:shares>0?`${(median/shares).toFixed(2)}`:'--',c:'#818cf8',a:'r'},
      ];
      cx4=mL+2;
      med4.forEach((cell,ci)=>{
        if(cell.v){
          const col=cols4[ci];
          const xp=cell.a==='r'?cx4+col.w-3:cx4+2;
          txt(safe(cell.v),xp,y4+11,ci===0?7:6.5,cell.b||false,cell.c,'r'===cell.a?'r':'l');
        }
        cx4+=cols4[ci].w;
      });
      y4+=24;
      // Bridge EV→Equity mini
      if(y4 < H-120){
        fillR(mL,y4,contentW,14,'#080e1a');
        strokeR(mL,y4,contentW,14,'#1e2d4a');
        fillR(mL,y4,3,14,GOLD);
        txt('BRIDGE EV -> EQUITY VALUE', mL+12, y4+9.5, 7.5, true, WHITE);
        y4+=20;
        txt(safe(L.sr_exp_bridge_eq).replace('{0}',fV2(netDebt)), mL+6, y4, 7.5, false, '#94a3b8');
        y4+=12;
        txt(safe(L.sr_exp_bridge_formula).replace('{0}',fV2(median+netDebt)).replace('{1}',fV2(netDebt)).replace('{2}',fV2(median)), mL+6, y4, 8, true, WHITE);
        y4+=12;
        if(shares>0) { txt(safe(L.sr_exp_bridge_vpa).replace('{0}',(median/shares).toFixed(2)).replace('{1}',shares.toLocaleString()), mL+6, y4, 7.5, false, '#94a3b8'); y4+=12; }
      }
      drawFooter();
      newPage();

      // ──────────────────────────────────────────────────────────────────────
      // PAGES 5 à N — UNE PAGE PAR MÉTHODE
      // ──────────────────────────────────────────────────────────────────────
      rows.slice().reverse().forEach((r, ri) => {
        fillR(0,0,W,H,'#060b14');
        drawHeader(5+ri, totalPages, safe(L.sr_exp_detail_method).replace('{0}',safe(r.name)));
        let ym=46;
        // Header méthode
        fillR(mL,ym,contentW,36,r.color+'0d');
        strokeR(mL,ym,contentW,36,r.color+'55');
        fillR(mL,ym,4,36,r.color);
        txt(safe(r.name), mL+14, ym+15, 13, true, r.color);
        txt(safe(r.category), mL+14, ym+28, 8, false, SLATE);
        // Badge DGI
        if(r.isJur){
          fillR(W-mR-44,ym+10,42,16,'#C9A84C18');
          strokeR(W-mR-44,ym+10,42,16,GOLD+'66');
          txt('DGI 2006', W-mR-4, ym+21, 7, true, GOLD, 'r');
        }
        ym+=46;
        // KPIs méthode
        const mk=[
          {l:safe(L.sr_exp_eq_low),   v:fV2(r.eq_low),  c:'#475569'},
          {l:safe(L.sr_exp_eq_mid),v:fV2(r.eq_mid),  c:r.color},
          {l:safe(L.sr_exp_eq_high),   v:fV2(r.eq_high), c:'#475569'},
          {l:safe(L.sr_exp_range_label),     v:`+/-${((r.eq_high-r.eq_low)/r.eq_mid*100).toFixed(0)}%`, c:((r.eq_high-r.eq_low)/r.eq_mid)<0.3?'#10b981':'#f59e0b'},
        ];
        const mkW=(contentW-12)/4;
        mk.forEach((k,i)=>{
          const kx=mL+i*(mkW+4), ky=ym;
          fillR(kx,ky,mkW,44,'#0d1825');
          strokeR(kx,ky,mkW,44,k.c+'44');
          fillR(kx,ky,3,44,k.c);
          txt(k.l,kx+9,ky+14,6.5,true,SLATE);
          txt(k.v,kx+9,ky+32,k.v.length>14?9:11,true,k.c);
        });
        ym+=54;
        if(shares>0){
          txt(safe(L.sr_exp_vpa_central).replace('{0}',fVpa(r.vpa)), mL, ym, 8, false, '#94a3b8');
          ym+=14;
        }
        // Mini football field row
        fillR(mL,ym,contentW,14,'#080e1a');
        strokeR(mL,ym,contentW,14,'#1e2d4a');
        fillR(mL,ym,3,14,r.color);
        txt(safe(L.sr_exp_relative_pos), mL+12, ym+9.5, 7.5, true, WHITE);
        ym+=18;
        // Draw mini bar
        const miniTrack=contentW-60;
        const miniOff=mL+58;
        fillR(miniOff-2,ym,miniTrack+4,22,'#0d1117');
        // Q1-Q3 shading
        const msq1=miniOff+Math.max(0,(q1-chartMin)/(chartMax-chartMin)*miniTrack);
        const msq3=miniOff+Math.max(0,(q3-chartMin)/(chartMax-chartMin)*miniTrack);
        fillR(msq1,ym,Math.max(1,msq3-msq1),22,'#6366f112');
        // This bar
        const msL=miniOff+Math.max(0,(r.eq_low-chartMin)/(chartMax-chartMin)*miniTrack);
        const msM=miniOff+Math.max(0,(r.eq_mid-chartMin)/(chartMax-chartMin)*miniTrack);
        const msH=miniOff+Math.max(0,(r.eq_high-chartMin)/(chartMax-chartMin)*miniTrack);
        fillR(msL,ym+4,Math.max(1,msM-msL),14,r.color+'30');
        strokeR(msL,ym+4,Math.max(1,msM-msL),14,r.color+'70',0.5);
        fillR(msM,ym+4,Math.max(1,msH-msM),14,r.color+'50');
        strokeR(msM,ym+4,Math.max(1,msH-msM),14,r.color+'70',0.5);
        fillR(msM-1,ym+2,2,18,r.color);
        // Median line
        const msmed=miniOff+Math.max(0,(median-chartMin)/(chartMax-chartMin)*miniTrack);
        ops.push(`${rgb('#818cf8')} RG 0.6 w [2 2] d ${msmed.toFixed(1)} ${(H-ym).toFixed(1)} m ${msmed.toFixed(1)} ${(H-ym-22).toFixed(1)} l S [] d`);
        txt(safe(r.name),mL+54,ym+14,7,true,r.color,'r');
        txt(safe(L.sr_exp_median_all),msmed,ym+26,5.5,false,'#6366f1');
        ym+=36;
        // Paramètres clés
        const rawParams = results[r.key]?._params;
        if(rawParams){
          fillR(mL,ym,contentW,14,'#080e1a');
          strokeR(mL,ym,contentW,14,'#1e2d4a');
          fillR(mL,ym,3,14,BLUE);
          txt(safe(L.sr_exp_calc_params), mL+12, ym+9.5, 7.5, true, WHITE);
          ym+=18;
          const paramEntries=Object.entries(rawParams)
            .filter(([k,v])=>typeof v==='number'&&k!=='_params')
            .slice(0,16);
          const pcols=3, pcolW=(contentW-10)/pcols;
          paramEntries.forEach(([k,v],pi)=>{
            const px=mL+(pi%pcols)*(pcolW+5), py=ym+Math.floor(pi/pcols)*16;
            const label=k.replace(/_/g,' ');
            const val=Math.abs(v)<0.1&&v!==0?pct(v):`${Math.round(v*100)/100}`;
            txt(`${safe(label)} :`, px, py, 6.5, false, SLATE);
            txt(val, px+pcolW-4, py, 6.5, true, '#94a3b8', 'r');
          });
          ym += Math.ceil(paramEntries.length/pcols)*16 + 8;
        }
        // Description méthodologique
        const desc = r.isJur ? (dgiDesc[r.key]||'') : (intlDesc[r.key]||'');
        if(desc){
          fillR(mL,ym,contentW,14,'#080e1a');
          strokeR(mL,ym,contentW,14,'#1e2d4a');
          fillR(mL,ym,3,14,GOLD);
          txt(safe(L.sr_exp_methodology), mL+12, ym+9.5, 7.5, true, WHITE);
          ym+=18;
          const descLines=wrapText(desc, contentW-10, 8);
          descLines.forEach(l=>{ txt(l,mL+5,ym,8,false,'#94a3b8'); ym+=12; });
        }
        drawFooter();
        newPage();
      });

      // ──────────────────────────────────────────────────────────────────────
      // PAGE FINALE — ANNEXE MÉTHODOLOGIQUE
      // ──────────────────────────────────────────────────────────────────────
      fillR(0,0,W,H,'#060b14');
      drawHeader(totalPages, totalPages, safe(L.sr_exp_annex_subtitle));
      let ya=46;
      fillR(mL,ya,contentW,28,'#0d1825');
      strokeR(mL,ya,contentW,28,GOLD+'44');
      fillR(mL,ya,3,28,GOLD);
      txt(safe(L.sr_exp_annex_title), mL+12, ya+13, 9, true, WHITE);
      txt(safe(L.sr_exp_annex_desc), mL+12, ya+23, 7, false, SLATE);
      ya+=34;
      const allDescs=[
        {k:'dcf',n:'Discounted Cash Flow (DCF)',d:'Actualisation des flux de tresorerie libres (FCFF) sur une periode de projection determinee, plus actualisation de la valeur terminale (Gordon-Shapiro). Methode de reference pour les entreprises a flux previsibles.',c:BLUE},
        {k:'exc',n:'Excess Return / EVA',d:'Part de la valeur issue des rendements excedentaires au-dela du cout du capital (WACC). Met explicitement en evidence la creation de valeur: EV = Capital Investi + Somme actualisee des survaleurs.',c:'#8b5cf6'},
        {k:'mul',n:'Comparables boursiers (Multiples)',d:'Application des ratios de valorisation (VE/CA, VE/EBITDA, VE/EBIT, PER, PBR) de societes comparables cotees a la cible. Valorisation relative dependant de la qualite des pairs selectionnes.',c:'#10b981'},
        {k:'rfr',n:'Relief from Royalty',d:'Valorise un actif incorporel par la methode des redevances hypothetiques: estimation des royalties qu\'un licencie paierait pour utiliser l\'actif (marque, brevet, technologie). Methode OCDE recommandee.',c:'#f59e0b'},
        {k:'mpe',n:'MPEEM (Multi-Period Excess Earnings)',d:'Methode d\'isolement des flux attribuables a un actif incorporel specifique, apres deduction des charges des actifs contributifs (actifs circulants, immobilisations, personnel). Standard PPA.',c:'#ec4899'},
        {k:'ast',n:'Actif Net Comptable Corrige (ANCC)',d:'Somme des valeurs de marche des actifs de l\'entreprise, diminuee des dettes reelles et de l\'impot latent sur les plus-values de revalorisation. Approche patrimoniale pure.',c:'#06b6d4'},
        {k:'vp',n:'Valeur de Productivite (DGI)',d:'Capitalisation du benefice retraite moyen (N-2, N-1, N) par un taux de capitalisation calcule selon la formule DGI: (Rf - inflation) + beta x 5%. Methode principale du guide DGI 2006.',c:'#f97316'},
        {k:'vm',n:'Valeur Mathematique (DGI)',d:'Actif net comptable revalue integrant les plus-values latentes sur les actifs (immobilisations, stocks, creances), apres deduction de l\'impot latent. Calcul de la valeur intrinsèque patrimoniale.',c:'#a78bfa'},
        {k:'vr',n:'Valeur de Rendement (DGI)',d:'Capitalisation du dividende moyen (N-2, N-1, N) au taux de rendement des actions de meme secteur cote, apres application d\'une decote de non-liquidite (30% par defaut pour PME).',c:'#34d399'},
        {k:'mba',n:'Methode du Benefice Ajuste (MBA)',d:'Capitalisation de la MBA = Benefice net + Dotations aux amortissements + Dotations aux provisions - Reprises, par un coefficient multiplicateur sectoriel (4 a 10 selon le secteur).',c:'#fb7185'},
        {k:'ebe_re',n:'Multiples EBE / Resultat d\'Exploitation (DGI)',d:'Application d\'un multiple sectoriel a l\'Excedent Brut d\'Exploitation (EBE) ou au Resultat d\'Exploitation (RE), deduit de la dette financiere nette pour obtenir la valeur des capitaux propres.',c:'#38bdf8'},
        {k:'comb',n:'Combinaison DGI (Methode globale)',d:'Moyenne ponderee de la Valeur Patrimoniale (VP) et des methodes de rendement (VR, MBA, EBE, RE) selon les coefficients du guide DGI 2006 (VP x 1 + Vrendement x 2 / 3 par defaut).',c:GOLD},
      ].filter(d=>Object.keys(results).includes(d.k));
      allDescs.forEach(d=>{
        if(ya > H-80){ drawFooter(); newPage(); fillR(0,0,W,H,'#060b14'); drawHeader(totalPages,totalPages,safe(L.sr_exp_annex_contd)); ya=46; }
        fillR(mL,ya,contentW,12,d.c+'0d');
        strokeR(mL,ya,contentW,12,d.c+'33');
        fillR(mL,ya,3,12,d.c);
        txt(d.n, mL+10, ya+8.5, 7.5, true, d.c);
        ya+=14;
        const dls=wrapText(d.d, contentW-14, 7.5);
        dls.forEach(l=>{ txt(l,mL+8,ya,7.5,false,'#94a3b8'); ya+=11; });
        ya+=6;
      });
      // Disclaimer final
      ya+=8;
      fillR(mL,ya,contentW,50,'#080e1a');
      strokeR(mL,ya,contentW,50,'#1e2d4a');
      txt(safe(L.sr_exp_warning), mL+10, ya+11, 7.5, true, '#ef4444');
      const disclaimer=safe(L.sr_exp_disclaimer);
      const dls2=wrapText(disclaimer, contentW-20, 7);
      let dly=ya+20;
      dls2.forEach(l=>{ txt(l,mL+10,dly,7,false,'#475569'); dly+=10; });
      drawFooter();
      newPage();

      // ──────────────────────────────────────────────────────────────────────
      // ASSEMBLAGE PDF MULTI-PAGES
      // ──────────────────────────────────────────────────────────────────────
      // Retirer la dernière page vide (ajoutée par newPage() après la dernière page)
      if(pages.length>0 && pages[pages.length-1].join('').trim()==='') pages.pop();
      // La dernière page courante (ops non pushée dans pages)
      if(ops.join('').trim()) pages.push(ops);

      const encode = s => {
        let out='';
        for(let i=0;i<s.length;i++){
          const code=s.charCodeAt(i);
          out+=code>127?'?':s[i];
        }
        return out;
      };

      // Construire PDF
      let pdf='%PDF-1.4\n';
      const objOffsets=[];
      let objIdx=0;

      const addObj = body => {
        objIdx++;
        objOffsets.push(pdf.length);
        pdf+=`${objIdx} 0 obj\n${body}\nendobj\n`;
        return objIdx;
      };

      // Obj 1: Catalog (ref to Pages)
      addObj('<< /Type /Catalog /Pages 2 0 R >>');

      // Obj 2: Pages dict (placeholder, will be replaced)
      const pagesObjIdx=2;
      addObj(`<< /Type /Pages /Kids [${pages.map((_,i)=>`${3+i*2} 0 R`).join(' ')}] /Count ${pages.length} >>`);

      // Font objects (shared)
      const fontRIdx=3+pages.length*2;
      const fontBIdx=fontRIdx+1;

      // Add page objects
      pages.forEach((pageOps,pi)=>{
        const stream=encode(pageOps.join('\n'));
        const contentObjIdx=3+pi*2+1;
        const pageObj=`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W.toFixed(2)} ${H.toFixed(2)}] /Resources << /Font << /Hr ${fontRIdx} 0 R /Hb ${fontBIdx} 0 R >> >> /Contents ${contentObjIdx} 0 R >>`;
        addObj(pageObj);  // 3+pi*2
        addObj(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);  // 3+pi*2+1
      });

      // Font objects
      addObj('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
      addObj('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');

      // Cross-reference table
      const xrefStart=pdf.length;
      pdf+=`xref\n0 ${objIdx+1}\n0000000000 65535 f \n`;
      objOffsets.forEach(o=>{ pdf+=`${String(o).padStart(10,'0')} 00000 n \n`; });
      pdf+=`trailer\n<< /Size ${objIdx+1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

      const blob=new Blob([pdf],{type:'application/pdf'});
      triggerDownload(blob, `SoV_Rapport_${safe(companyName).replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch(err){
      console.error('PDF export failed', err);
      alert('Erreur lors de la generation du PDF: '+err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  if (rows.length === 0) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",gap:16,color:"#64748b"}}>
      <div style={{fontSize:48}}>📊</div>
      <div style={{fontSize:16,fontWeight:600,color:"#94a3b8"}}>{L.sr_no_model}</div>
      <div style={{fontSize:13}}>{L.dashboard_hint}</div>
      <button onClick={onBack} style={{marginTop:8,padding:"9px 22px",background:"rgba(59,130,246,0.15)",border:"1px solid rgba(59,130,246,0.3)",borderRadius:8,color:"#60a5fa",cursor:"pointer",fontSize:13,fontWeight:600}}>{L.sr_back_dash}</button>
    </div>
  );

  const tabStyle = (t) => ({
    padding:"8px 20px", fontSize:11, fontWeight:600, cursor:"pointer",
    borderRadius:"7px 7px 0 0", border:"none", transition:"all 0.15s",
    background: activeTab===t ? "rgba(255,255,255,0.07)" : "transparent",
    color: activeTab===t ? "#f1f5f9" : "#64748b",
    borderBottom: activeTab===t ? "2px solid #3b82f6" : "2px solid transparent",
  });

  // ── FOOTBALL FIELD STACKED BAR RENDERER ────────────────────────────────
  // Each row = [████░░░░●████████████░░░░░░░░░░░░]
  //             ^min   ^low  ^mid     ^high         ^max
  // Segments:
  //   1. transparent gap: 0 → low
  //   2. "low zone" light: low → mid   (color 15%)
  //   3. "high zone" medium: mid → high (color 30%)
  //   The central point (mid) is marked with a bright tick
  const renderFFRow = (r) => {
    const pLow  = scaleX(r.eq_low);
    const pMid  = scaleX(r.eq_mid);
    const pHigh = scaleX(r.eq_high);
    const segA  = pMid  - pLow;   // low → mid width
    const segB  = pHigh - pMid;   // mid → high width

    return (
      <div key={r.key} style={{display:"flex",alignItems:"center",marginBottom:10,gap:0}}>
        {/* Label left */}
        <div style={{width:136,flexShrink:0,paddingRight:12,textAlign:"right"}}>
          <div style={{fontSize:10,fontWeight:700,color:r.color,lineHeight:1.2}}>{r.name}</div>
          <div style={{fontSize:10,color:"#475569",fontWeight:400,lineHeight:1.2}}>{r.category}</div>
        </div>

        {/* Bar track */}
        <div style={{flex:1,height:28,position:"relative",background:"rgba(255,255,255,0.025)",borderRadius:5,overflow:"visible"}}>
          {/* Q1-Q3 convergence zone background */}
          <div style={{
            position:"absolute",
            left:`${scaleX(q1)}%`, width:`${Math.max(0.5,scaleX(q3)-scaleX(q1))}%`,
            top:0, bottom:0,
            background:"rgba(99,102,241,0.07)",
            borderLeft:"1px dashed rgba(99,102,241,0.25)",
            borderRight:"1px dashed rgba(99,102,241,0.25)",
            pointerEvents:"none"
          }}/>

          {/* Segment A: low → mid (lighter fill) */}
          <div style={{
            position:"absolute",
            left:`${pLow}%`, width:`${Math.max(0.2,segA)}%`,
            top:"15%", bottom:"15%",
            background:`${r.color}28`,
            borderLeft:`2px solid ${r.color}60`,
            borderTop:`1px solid ${r.color}40`,
            borderBottom:`1px solid ${r.color}40`,
          }}/>

          {/* Segment B: mid → high (darker fill) */}
          <div style={{
            position:"absolute",
            left:`${pMid}%`, width:`${Math.max(0.2,segB)}%`,
            top:"15%", bottom:"15%",
            background:`${r.color}45`,
            borderRight:`2px solid ${r.color}60`,
            borderTop:`1px solid ${r.color}40`,
            borderBottom:`1px solid ${r.color}40`,
          }}/>

          {/* Central tick — bright vertical bar */}
          <div style={{
            position:"absolute",
            left:`${pMid}%`, top:2, bottom:2, width:3,
            background:r.color,
            borderRadius:2,
            transform:"translateX(-50%)",
            boxShadow:`0 0 8px ${r.color}90`,
          }}/>

          {/* Labels: low / mid / high */}
          <div style={{position:"absolute",left:`${pLow}%`,top:"50%",transform:"translate(-50%,-50%)",
            fontSize:10,color:r.color,fontFamily:"monospace",fontWeight:500,
            background:"rgba(8,12,20,0.85)",padding:"0 2px",borderRadius:2,
            whiteSpace:"nowrap",pointerEvents:"none",
            marginTop:-14}}>
            {fV(r.eq_low)}
          </div>
          <div style={{position:"absolute",left:`${pMid}%`,bottom:-14,transform:"translateX(-50%)",
            fontSize:10,color:r.color,fontFamily:"monospace",fontWeight:800,
            whiteSpace:"nowrap",pointerEvents:"none"}}>
            {fV(r.eq_mid)}
          </div>
          <div style={{position:"absolute",left:`${pHigh}%`,top:"50%",transform:"translate(-50%,-50%)",
            fontSize:10,color:r.color,fontFamily:"monospace",fontWeight:500,
            background:"rgba(8,12,20,0.85)",padding:"0 2px",borderRadius:2,
            whiteSpace:"nowrap",pointerEvents:"none",
            marginTop:-14}}>
            {fV(r.eq_high)}
          </div>
        </div>

        {/* Valeur/action right */}
        {shares > 0 && (
          <div style={{width:72,flexShrink:0,paddingLeft:12,textAlign:"right",fontSize:10,fontFamily:"monospace",color:"#64748b"}}>
            {fVpa(r.vpa)}
          </div>
        )}
      </div>
    );
  };

  const intlRows = rows.filter(r=>!r.isJur);
  const dgiRows  = rows.filter(r=> r.isJur);

  // ── Modal de configuration PDF ───────────────────────────────────────────
  const PdfModal = () => showPdfModal ? (
    <div style={{
      position:"fixed",inset:0,zIndex:9999,
      background:"rgba(0,0,0,0.75)",backdropFilter:"blur(6px)",
      display:"flex",alignItems:"center",justifyContent:"center",
    }} onClick={e=>{if(e.target===e.currentTarget)setShowPdfModal(false)}}>
      <div style={{
        background:"#0d1825",border:"1px solid rgba(201,168,76,0.3)",
        borderRadius:14,padding:"32px 36px",width:460,
        boxShadow:"0 24px 80px rgba(0,0,0,0.6)",
      }}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
          <div>
            <h3 style={{fontSize:16,fontWeight:800,color:"#f1f5f9",margin:0,letterSpacing:"-0.3px"}}>
              {L.sr_pdf_title}
            </h3>
            <p style={{fontSize:11,color:"#64748b",margin:"4px 0 0"}}>
              {rows.length} {rows.length>1?L.sr_methods:L.sr_method} · {companyName}
            </p>
          </div>
          <button onClick={()=>setShowPdfModal(false)}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              borderRadius:6,width:28,height:28,cursor:"pointer",color:"#94a3b8",fontSize:14,
              display:"flex",alignItems:"center",justifyContent:"center"}}>
            ✕
          </button>
        </div>

        {/* Aperçu contenu */}
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
          borderRadius:8,padding:"12px 14px",marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,color:"#64748b",letterSpacing:"0.8px",
            textTransform:"uppercase",marginBottom:10}}>{L.sr_pdf_content} ({4+rows.length+1} {L.sr_pdf_pages})</div>
          {[
            L.sr_pdf_cover,
            L.sr_pdf_exec,
            L.sr_pdf_ff,
            L.sr_pdf_table,
            ...rows.slice().reverse().map(r=>`${L.sr_pdf_detail} ${r.name}`),
            L.sr_pdf_annex,
          ].map((item,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
              <div style={{width:16,height:16,borderRadius:3,
                background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.3)",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:10,color:"#C9A84C",fontWeight:700,flexShrink:0}}>
                {i+1}
              </div>
              <span style={{fontSize:12,color:"#94a3b8"}}>{item}</span>
            </div>
          ))}
        </div>

        {/* Champs configurables */}
        {[
          {label:L.sr_pdf_firm, placeholder:"Hard to Value", val:pdfCabinet, set:setPdfCabinet},
          {label:L.sr_pdf_analyst, placeholder:L.sr_pdf_analyst_ph, val:pdfAnalyst, set:setPdfAnalyst},
        ].map(({label,placeholder,val,set},i)=>(
          <div key={i} style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,fontWeight:600,color:"#64748b",
              letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6}}>{label}</label>
            <input
              value={val}
              onChange={e=>set(e.target.value)}
              placeholder={placeholder}
              style={{width:"100%",boxSizing:"border-box",
                background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",
                borderRadius:7,padding:"9px 12px",color:"#e2e8f0",fontSize:12,
                outline:"none",fontFamily:"inherit"}}
              onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.5)";e.target.style.background="rgba(201,168,76,0.04)"}}
              onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.1)";e.target.style.background="rgba(255,255,255,0.04)"}}
            />
          </div>
        ))}

        {/* Logo cabinet */}
        <div style={{marginBottom:14}}>
          <label style={{display:"block",fontSize:10,fontWeight:600,color:"#64748b",
            letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6}}>{L.sr_pdf_logo}</label>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <label style={{
              flex:1,display:"flex",alignItems:"center",gap:9,padding:"9px 12px",cursor:"pointer",
              background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",
              borderRadius:7,color:"#94a3b8",fontSize:11,
            }}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21,15 16,10 5,21"/>
              </svg>
              {pdfLogoB64 ? L.sr_pdf_logo_loaded : L.sr_pdf_logo_choose}
              <input type="file" accept="image/png,image/jpeg,image/svg+xml" style={{display:"none"}}
                onChange={e=>{
                  const file=e.target.files?.[0];
                  if(!file) return;
                  const reader=new FileReader();
                  reader.onload=ev=>{ setPdfLogoB64(ev.target.result); };
                  reader.readAsDataURL(file);
                }}
              />
            </label>
            {pdfLogoB64 && (
              <button onClick={()=>setPdfLogoB64(null)}
                style={{padding:"7px 11px",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                  borderRadius:7,color:"#fca5a5",fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>
                {L.sr_pdf_logo_remove}
              </button>
            )}
          </div>
          {pdfLogoB64 && (
            <div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}>
              <img src={pdfLogoB64} alt="Logo aperçu" style={{height:32,maxWidth:120,objectFit:"contain",
                background:"rgba(255,255,255,0.08)",borderRadius:4,padding:3}}/>
              <span style={{fontSize:10,color:"#475569"}}>{L.sr_pdf_logo_preview}</span>
            </div>
          )}
        </div>
        {/* Actions */}
        <div style={{display:"flex",gap:10,marginTop:6}}>
          <button onClick={()=>setShowPdfModal(false)}
            style={{flex:1,padding:"10px",background:"transparent",
              border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,
              color:"#64748b",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            {L.sr_pdf_cancel}
          </button>
          <button onClick={()=>{setShowPdfModal(false);exportPDF();}}
            style={{flex:2,padding:"10px",cursor:"pointer",
              background:"linear-gradient(135deg,rgba(239,68,68,0.2),rgba(239,68,68,0.12))",
              border:"1px solid rgba(239,68,68,0.4)",borderRadius:8,
              color:"#fca5a5",fontSize:12,fontWeight:700,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8}}
            onMouseEnter={e=>{e.currentTarget.style.background="linear-gradient(135deg,rgba(239,68,68,0.3),rgba(239,68,68,0.2))"}}
            onMouseLeave={e=>{e.currentTarget.style.background="linear-gradient(135deg,rgba(239,68,68,0.2),rgba(239,68,68,0.12))"}}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            {L.sr_pdf_generate}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <FadeIn>
      <div style={{maxWidth:1120,margin:"0 auto",padding:"20px 0 80px"}}>
      <PdfModal/>

        {/* ── HEADER ── */}
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:28}}>
          <div>
            <button onClick={onBack}
              style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"6px 14px",color:"#94a3b8",fontSize:11,cursor:"pointer",marginBottom:12,transition:"all 0.2s"}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
              onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}>
              {L.sr_back}
            </button>
            <h1 style={{fontSize:26,fontWeight:800,color:"#f1f5f9",margin:0,letterSpacing:"-0.6px"}}>{companyName}</h1>
            <p style={{color:"#64748b",fontSize:13,margin:"4px 0 0"}}>
              {L.sr_subtitle_report} · {rows.length} {rows.length>1?L.sr_methods:L.sr_method} {rows.length>1?L.sr_calculated_pl:L.sr_calculated} · {new Date().toLocaleDateString(undefined,{day:"2-digit",month:"long",year:"numeric"})}
            </p>
          </div>

          {/* Export buttons */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
            {[
              {label:"Excel", icon:<svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>, color:"#10b981", bg:"rgba(16,185,129,0.1)", border:"rgba(16,185,129,0.25)", fn:exportExcel},
              {label:pdfLoading?"PDF…":"PDF",   icon:<svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>, color:"#ef4444", bg:"rgba(239,68,68,0.1)", border:"rgba(239,68,68,0.25)", fn:()=>setShowPdfModal(true), disabled:pdfLoading},
              {label:pngLoading?"Export…":"PNG", icon:<svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>, color:"#f59e0b", bg:"rgba(245,158,11,0.1)", border:"rgba(245,158,11,0.25)", fn:exportPNG, disabled:pngLoading},
            ].map(btn=>(
              <button key={btn.label} onClick={btn.fn} disabled={btn.disabled}
                style={{display:"flex",alignItems:"center",gap:7,padding:"9px 16px",
                  background:btn.bg, border:`1px solid ${btn.border}`,borderRadius:8,
                  color:btn.color,fontSize:12,fontWeight:600,cursor:btn.disabled?"not-allowed":"pointer",
                  transition:"all 0.2s",opacity:btn.disabled?0.6:1}}
                onMouseEnter={e=>!btn.disabled&&(e.currentTarget.style.filter="brightness(1.25)")}
                onMouseLeave={e=>!btn.disabled&&(e.currentTarget.style.filter="none")}>
                {btn.icon}{btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── KPI CARDS ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
          {[
            {label:L.sr_kpi_median,    value:fV(median),             sub:`${rows.length} ${L.sr_methods}`,       color:"#3b82f6"},
            {label:L.sr_kpi_range,  value:`${fV(q1)} – ${fV(q3)}`,sub:L.sr_kpi_percentile,            color:"#10b981"},
            {label:L.sr_kpi_nfd,       value:fV(netDebt),            sub:L.sr_kpi_ev_bridge,            color:"#64748b"},
            {label:L.sr_kpi_vps, value:shares>0?`${(median/shares).toFixed(2)}€`:"—", sub:shares>0?`${shares.toLocaleString()} ${L.sr_kpi_shares}`:L.sr_kpi_shares_na, color:"#8b5cf6"},
          ].map((c,i)=>(
            <div key={i} style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${c.color}22`,borderRadius:10,padding:"16px 18px"}}>
              <div style={{fontSize:10,color:"#64748b",fontWeight:700,letterSpacing:"0.8px",textTransform:"uppercase",marginBottom:6}}>{c.label}</div>
              <div style={{fontSize:c.value.length>10?18:22,fontWeight:800,color:c.color,letterSpacing:"-0.5px",lineHeight:1.1}}>{c.value}</div>
              <div style={{fontSize:10,color:"#475569",marginTop:4}}>{c.sub}</div>
            </div>
          ))}
        </div>

        {/* ── TABS ── */}
        <div style={{borderBottom:"1px solid rgba(255,255,255,0.08)",marginBottom:0,display:"flex",gap:2}}>
          {[["football",L.sr_tab_football],["table",L.sr_tab_table],["bridge",L.sr_tab_bridge],["overview",L.sr_tab_overview]].map(([t,l])=>(
            <button key={t} style={tabStyle(t)} onClick={()=>setActiveTab(t)}>{l}</button>
          ))}
        </div>

        <div style={{background:"rgba(255,255,255,0.018)",border:"1px solid rgba(255,255,255,0.06)",borderTop:"none",borderRadius:"0 0 12px 12px",padding:"26px 24px"}}>

          {/* ════════════════════════════════════════════════════════════
              TAB 1 — FOOTBALL FIELD  (stacked bars)
          ════════════════════════════════════════════════════════════ */}
          {activeTab==="football" && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                <div style={{fontSize:11,color:"#64748b",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <span style={{display:"flex",alignItems:"center",gap:5}}>
                    <span style={{width:24,height:10,background:"rgba(99,102,241,0.15)",border:"1px dashed rgba(99,102,241,0.35)",borderRadius:2,display:"inline-block"}}/>
                    {L.sr_ff_legend_zone}
                  </span>
                  <span style={{display:"flex",alignItems:"center",gap:5}}>
                    <span style={{width:16,height:10,background:"rgba(255,255,255,0.12)",borderLeft:"2px solid rgba(255,255,255,0.4)",borderRight:"2px solid rgba(255,255,255,0.4)",display:"inline-block"}}/>
                    {L.sr_ff_legend_range}
                  </span>
                  <span style={{display:"flex",alignItems:"center",gap:5}}>
                    <span style={{width:3,height:12,background:"#94a3b8",borderRadius:1,display:"inline-block",boxShadow:"0 0 4px #94a3b890"}}/>
                    {L.sr_ff_legend_mid}
                  </span>
                </div>
                <button onClick={exportPNG} disabled={pngLoading}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"6px 13px",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:7,color:"#f59e0b",fontSize:11,fontWeight:600,cursor:"pointer",transition:"all 0.2s"}}>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  {pngLoading?"Export…":"PNG"}
                </button>
              </div>

              {/* The chart div — captured for PNG */}
              <div ref={ffRef} style={{background:"#0d1117",borderRadius:10,padding:"20px 16px 32px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,paddingLeft:136}}>
                  {shares>0&&<div style={{position:"absolute",right:24+72,fontSize:10,color:"#475569",letterSpacing:"0.5px",fontWeight:600}}>{L.sr_vpa_short}</div>}
                </div>

                {/* Headers */}
                <div style={{display:"flex",alignItems:"center",marginBottom:14,gap:0}}>
                  <div style={{width:136,flexShrink:0}}/>
                  <div style={{flex:1,paddingLeft:0,fontSize:10,color:"#334155",letterSpacing:"0.5px",display:"flex",justifyContent:"space-between"}}>
                    <span>{fV(chartMin)}</span>
                    <span style={{color:"#3b4a5c"}}>{L.sr_ff_eq_header}</span>
                    <span>{fV(chartMax)}</span>
                  </div>
                  {shares>0&&<div style={{width:72,flexShrink:0}}/>}
                </div>

                {/* Méthodes Financières */}
                {intlRows.length > 0 && (
                  <>
                    <div style={{display:"flex",alignItems:"center",gap:8,paddingLeft:136,marginBottom:10,marginTop:4}}>
                      <div style={{flex:1,height:"1px",background:"rgba(59,130,246,0.15)"}}/>
                      <span style={{fontSize:10,fontWeight:700,color:"#3b82f6",letterSpacing:"1px",textTransform:"uppercase",whiteSpace:"nowrap"}}>{L.sr_ff_section_intl}</span>
                      <div style={{flex:1,height:"1px",background:"rgba(59,130,246,0.15)"}}/>
                    </div>
                    {intlRows.map(renderFFRow)}
                  </>
                )}

                {/* Séparateur */}
                {intlRows.length>0 && dgiRows.length>0 && (
                  <div style={{height:1,background:"rgba(201,168,76,0.12)",margin:"14px 0 14px 136px",borderStyle:"dashed",borderWidth:"1px 0 0",borderColor:"rgba(201,168,76,0.2)"}}/>
                )}

                {/* Méthodes par Juridiction */}
                {dgiRows.length > 0 && (
                  <>
                    <div style={{display:"flex",alignItems:"center",gap:8,paddingLeft:136,marginBottom:10}}>
                      <div style={{flex:1,height:"1px",background:"rgba(201,168,76,0.15)"}}/>
                      <span style={{fontSize:10,fontWeight:700,color:"#C9A84C",letterSpacing:"1px",textTransform:"uppercase",whiteSpace:"nowrap"}}>{L.sr_ff_section_jur}</span>
                      <div style={{flex:1,height:"1px",background:"rgba(201,168,76,0.15)"}}/>
                    </div>
                    {dgiRows.map(renderFFRow)}
                  </>
                )}

                {/* Median line across full chart */}
                <div style={{position:"relative",paddingLeft:136,marginTop:18}}>
                  <div style={{position:"relative",height:24,background:"rgba(99,102,241,0.04)",borderRadius:5,border:"1px solid rgba(99,102,241,0.12)"}}>
                    {/* Median marker */}
                    <div style={{position:"absolute",left:`${scaleX(median)}%`,top:0,bottom:0,width:2,background:"rgba(99,102,241,0.7)",transform:"translateX(-50%)"}}/>
                    <div style={{position:"absolute",left:`${scaleX(q1)}%`,width:`${Math.max(0.5,scaleX(q3)-scaleX(q1))}%`,top:0,bottom:0,background:"rgba(99,102,241,0.08)"}}/>
                    <div style={{position:"absolute",left:"50%",transform:"translateX(-50%)",top:"50%",marginTop:-6,fontSize:10,color:"#818cf8",fontWeight:700,fontFamily:"monospace",whiteSpace:"nowrap"}}>
                      {L.sr_ff_median} : {fV(median)}  |  Q1–Q3 : {fV(q1)} – {fV(q3)}
                    </div>
                  </div>
                </div>

                {/* X axis */}
                <div style={{display:"flex",paddingLeft:136,marginTop:6}}>
                  <div style={{flex:1,borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:5,display:"flex",justifyContent:"space-between",fontSize:10,color:"#334155"}}>
                    {[0,25,50,75,100].map(pct=>{
                      const val = chartMin + (chartMax-chartMin)*pct/100;
                      return <span key={pct} style={{color:pct===50?"#475569":"#334155"}}>{fV(val)}</span>;
                    })}
                  </div>
                  {shares>0&&<div style={{width:72,flexShrink:0}}/>}
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
              TAB 2 — TABLEAU DÉTAILLÉ
          ════════════════════════════════════════════════════════════ */}
          {activeTab==="table" && (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr style={{borderBottom:"2px solid rgba(255,255,255,0.08)"}}>
                    {[
                      {h:L.sr_tbl_method,       a:"left"},
                      {h:L.sr_tbl_category,     a:"left"},
                      {h:L.sr_tbl_ev_low,      a:"right"},
                      {h:L.sr_tbl_ev_mid,   a:"right"},
                      {h:L.sr_tbl_ev_high,      a:"right"},
                      {h:L.sr_tbl_eq_low,  a:"right"},
                      {h:L.sr_tbl_eq_mid,a:"right"},
                      {h:L.sr_tbl_eq_high,  a:"right"},
                      {h:L.sr_tbl_range,    a:"center"},
                      {h:L.sr_tbl_vps,      a:"right"},
                      {h:L.sr_tbl_weight, a:"center"},
                    ].map(({h,a})=>(
                      <th key={h} style={{padding:"9px 11px",textAlign:a,fontSize:10,fontWeight:700,color:"#64748b",letterSpacing:"0.6px",textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...rows].reverse().map((r,i)=>{
                    const spreadPct = ((r.eq_high-r.eq_low)/r.eq_mid*100).toFixed(0);
                    const spreadColor = +spreadPct<25?"#10b981":+spreadPct<50?"#f59e0b":"#ef4444";
                    return (
                      <tr key={r.key}
                        style={{borderBottom:"1px solid rgba(255,255,255,0.04)",background:i%2===0?"transparent":"rgba(255,255,255,0.01)",transition:"background 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"transparent":"rgba(255,255,255,0.01)"}>
                        <td style={{padding:"10px 11px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:7}}>
                            <span style={{width:7,height:7,borderRadius:"50%",background:r.color,flexShrink:0,boxShadow:`0 0 5px ${r.color}80`}}/>
                            <span style={{fontWeight:700,color:r.color,whiteSpace:"nowrap"}}>{r.name}</span>
                          </div>
                        </td>
                        <td style={{padding:"10px 11px",color:"#64748b",fontSize:10,whiteSpace:"nowrap"}}>{r.category}</td>
                        <td style={{padding:"10px 11px",textAlign:"right",color:"#475569",fontFamily:"monospace",fontSize:10}}>{fV(r.ev_low)}</td>
                        <td style={{padding:"10px 11px",textAlign:"right",color:"#94a3b8",fontWeight:700,fontFamily:"monospace"}}>{fV(r.ev_mid)}</td>
                        <td style={{padding:"10px 11px",textAlign:"right",color:"#475569",fontFamily:"monospace",fontSize:10}}>{fV(r.ev_high)}</td>
                        <td style={{padding:"10px 11px",textAlign:"right",color:"#475569",fontFamily:"monospace",fontSize:10}}>{fV(r.eq_low)}</td>
                        <td style={{padding:"10px 11px",textAlign:"right",color:"#f1f5f9",fontWeight:800,fontFamily:"monospace"}}>{fV(r.eq_mid)}</td>
                        <td style={{padding:"10px 11px",textAlign:"right",color:"#475569",fontFamily:"monospace",fontSize:10}}>{fV(r.eq_high)}</td>
                        <td style={{padding:"10px 11px",textAlign:"center"}}>
                          <span style={{fontSize:10,padding:"2px 8px",borderRadius:5,background:`${spreadColor}18`,color:spreadColor,fontWeight:700}}>±{spreadPct}%</span>
                        </td>
                        <td style={{padding:"10px 11px",textAlign:"right",color:"#94a3b8",fontFamily:"monospace"}}>{shares>0?fVpa(r.vpa):"—"}</td>
                        <td style={{padding:"10px 11px",textAlign:"center"}}>
                          <span style={{fontSize:10,color:"#64748b",background:"rgba(255,255,255,0.05)",padding:"2px 8px",borderRadius:5,whiteSpace:"nowrap"}}>{r.weight}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Médiane row */}
                  <tr style={{borderTop:"2px solid rgba(99,102,241,0.35)",background:"rgba(99,102,241,0.06)"}}>
                    <td colSpan={2} style={{padding:"11px 11px",fontWeight:800,color:"#818cf8",fontSize:12}}>{L.sr_tbl_median}</td>
                    <td style={{padding:"11px 11px",textAlign:"right",color:"#818cf8",fontFamily:"monospace",fontWeight:600}}>{fV(q1+netDebt)}</td>
                    <td style={{padding:"11px 11px",textAlign:"right",color:"#818cf8",fontFamily:"monospace",fontWeight:800,fontSize:13}}>{fV(median+netDebt)}</td>
                    <td style={{padding:"11px 11px",textAlign:"right",color:"#818cf8",fontFamily:"monospace",fontWeight:600}}>{fV(q3+netDebt)}</td>
                    <td style={{padding:"11px 11px",textAlign:"right",color:"#818cf8",fontFamily:"monospace",fontWeight:600}}>{fV(q1)}</td>
                    <td style={{padding:"11px 11px",textAlign:"right",color:"#818cf8",fontFamily:"monospace",fontWeight:800,fontSize:13}}>{fV(median)}</td>
                    <td style={{padding:"11px 11px",textAlign:"right",color:"#818cf8",fontFamily:"monospace",fontWeight:600}}>{fV(q3)}</td>
                    <td/>
                    <td style={{padding:"11px 11px",textAlign:"right",color:"#818cf8",fontFamily:"monospace",fontWeight:700}}>{shares>0?`${(median/shares).toFixed(2)}€`:"—"}</td>
                    <td/>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
              TAB 3 — BRIDGE EV → EQUITY
          ════════════════════════════════════════════════════════════ */}
          {activeTab==="bridge" && (
            <div style={{maxWidth:700,margin:"0 auto"}}>
              {/* Bridge header */}
              <div style={{display:"flex",alignItems:"center",gap:20,marginBottom:22,padding:"14px 18px",background:"rgba(255,255,255,0.02)",borderRadius:8,border:"1px solid rgba(255,255,255,0.06)"}}>
                {[
                  {label:L.sr_br_nfd,value:fV(netDebt),color:"#ef4444"},
                  {label:L.sr_br_shares,value:shares>0?shares.toLocaleString():"—",color:"#94a3b8"},
                  {label:L.sr_br_eq_median,value:fV(median),color:"#818cf8"},
                  {label:L.sr_br_vps_median,value:shares>0?`${(median/shares).toFixed(2)}€`:"—",color:"#818cf8"},
                ].map((item,i)=>(
                  <div key={i} style={{flex:1}}>
                    <div style={{fontSize:10,color:"#475569",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:3}}>{item.label}</div>
                    <div style={{fontSize:14,fontWeight:800,color:item.color,fontFamily:"monospace"}}>{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Bridge rows */}
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {[...rows].reverse().map(r=>(
                  <div key={r.key} style={{display:"grid",gridTemplateColumns:"130px 1fr 90px 1fr 90px",alignItems:"center",gap:10,padding:"11px 16px",background:"rgba(255,255,255,0.02)",borderRadius:8,border:"1px solid rgba(255,255,255,0.05)",transition:"border-color 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=r.color+"40"}
                    onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.05)"}>
                    {/* Name */}
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <span style={{width:6,height:6,borderRadius:"50%",background:r.color,flexShrink:0}}/>
                      <span style={{fontSize:10,fontWeight:700,color:r.color}}>{r.name}</span>
                    </div>
                    {/* EV */}
                    <div>
                      <div style={{fontSize:10,color:"#475569",marginBottom:2,letterSpacing:"0.4px"}}>ENTERPRISE VALUE</div>
                      <div style={{fontSize:12,fontWeight:700,color:"#94a3b8",fontFamily:"monospace"}}>{fV(r.ev_mid)}</div>
                    </div>
                    {/* Arrow */}
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:10,color:"#ef4444",fontFamily:"monospace",fontWeight:600}}>−{fV(netDebt)}</div>
                      <div style={{fontSize:10,color:"#475569",marginBottom:2}}>{L.sr_nfd_short}</div>
                      <svg width={20} height={8} viewBox="0 0 20 8" style={{display:"block",margin:"0 auto"}}><path d="M0 4h16M12 1l4 3-4 3" stroke="#475569" strokeWidth={1.5} fill="none"/></svg>
                    </div>
                    {/* Equity */}
                    <div>
                      <div style={{fontSize:10,color:"#475569",marginBottom:2,letterSpacing:"0.4px"}}>EQUITY VALUE</div>
                      <div style={{fontSize:14,fontWeight:800,color:r.color,fontFamily:"monospace"}}>{fV(r.eq_mid)}</div>
                    </div>
                    {/* V/action */}
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:10,color:"#475569",marginBottom:2,letterSpacing:"0.4px"}}>{L.sr_vpa_short}</div>
                      <div style={{fontSize:12,fontWeight:700,color:"#f1f5f9",fontFamily:"monospace"}}>{shares>0?fVpa(r.vpa):"—"}</div>
                    </div>
                  </div>
                ))}

                {/* Médiane bridge */}
                <div style={{display:"grid",gridTemplateColumns:"130px 1fr 90px 1fr 90px",alignItems:"center",gap:10,padding:"14px 16px",background:"rgba(99,102,241,0.06)",borderRadius:8,border:"2px solid rgba(99,102,241,0.3)"}}>
                  <div style={{fontSize:11,fontWeight:800,color:"#818cf8"}}>{L.sr_ff_median.toUpperCase()}</div>
                  <div>
                    <div style={{fontSize:10,color:"#818cf8",opacity:0.7,marginBottom:2}}>{L.sr_br_ev_median}</div>
                    <div style={{fontSize:13,fontWeight:800,color:"#818cf8",fontFamily:"monospace"}}>{fV(median+netDebt)}</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:10,color:"#ef4444",fontFamily:"monospace",fontWeight:600}}>−{fV(netDebt)}</div>
                    <div style={{fontSize:10,color:"#475569"}}>{L.sr_nfd_short}</div>
                    <svg width={20} height={8} viewBox="0 0 20 8" style={{display:"block",margin:"0 auto"}}><path d="M0 4h16M12 1l4 3-4 3" stroke="#818cf8" strokeWidth={1.5} fill="none"/></svg>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:"#818cf8",opacity:0.7,marginBottom:2}}>{L.sr_br_eq_median_short}</div>
                    <div style={{fontSize:15,fontWeight:800,color:"#818cf8",fontFamily:"monospace"}}>{fV(median)}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,color:"#818cf8",opacity:0.7,marginBottom:2}}>{L.sr_vpa_short}</div>
                    <div style={{fontSize:13,fontWeight:800,color:"#818cf8",fontFamily:"monospace"}}>{shares>0?`${(median/shares).toFixed(2)}€`:"—"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
              TAB 4 — VUE D'ENSEMBLE
          ════════════════════════════════════════════════════════════ */}
          {activeTab==="overview" && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:22}}>
              {/* Barres par valeur */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#64748b",letterSpacing:"0.8px",textTransform:"uppercase",marginBottom:14}}>{L.sr_ov_eq_by_method}</div>
                <div style={{display:"flex",flexDirection:"column",gap:9}}>
                  {[...rows].reverse().map(r=>{
                    const barW = Math.max(4, (r.eq_mid/globalEqMax)*100);
                    return (
                      <div key={r.key} style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:110,fontSize:10,fontWeight:700,color:r.color,textAlign:"right",flexShrink:0,lineHeight:1.2}}>
                          {r.name}
                        </div>
                        <div style={{flex:1,height:24,background:"rgba(255,255,255,0.025)",borderRadius:5,overflow:"hidden",position:"relative"}}>
                          <div style={{width:`${barW}%`,height:"100%",background:`${r.color}28`,borderRadius:5,borderRight:`2px solid ${r.color}`,transition:"width 0.7s ease"}}/>
                          <span style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",fontSize:10,fontWeight:800,color:r.color,fontFamily:"monospace"}}>{fV(r.eq_mid)}</span>
                        </div>
                        {shares>0&&<div style={{width:68,fontSize:10,color:"#64748b",textAlign:"right",fontFamily:"monospace",flexShrink:0}}>{fVpa(r.vpa)}</div>}
                      </div>
                    );
                  })}
                  {/* Médiane line */}
                  <div style={{display:"flex",alignItems:"center",gap:10,marginTop:4,paddingTop:8,borderTop:"1px solid rgba(99,102,241,0.2)"}}>
                    <div style={{width:110,fontSize:10,fontWeight:800,color:"#818cf8",textAlign:"right",flexShrink:0}}>{L.sr_ff_median.toUpperCase()}</div>
                    <div style={{flex:1,height:24,background:"rgba(99,102,241,0.08)",borderRadius:5,position:"relative",border:"1px solid rgba(99,102,241,0.25)"}}>
                      <span style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",fontSize:11,fontWeight:800,color:"#818cf8",fontFamily:"monospace"}}>{fV(median)}</span>
                    </div>
                    {shares>0&&<div style={{width:68,fontSize:10,color:"#818cf8",textAlign:"right",fontFamily:"monospace",fontWeight:700,flexShrink:0}}>{`${(median/shares).toFixed(2)}€`}</div>}
                  </div>
                </div>
              </div>

              {/* Stats + convergence */}
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{fontSize:10,fontWeight:700,color:"#64748b",letterSpacing:"0.8px",textTransform:"uppercase"}}>{L.sr_ov_convergence}</div>

                {/* Dispersion */}
                <div style={{background:"rgba(255,255,255,0.02)",borderRadius:8,border:"1px solid rgba(255,255,255,0.06)",padding:"14px 16px"}}>
                  {(()=>{
                    const spread = (globalEqMax-globalEqMin)/median*100;
                    const conv = spread<30?L.sr_ov_strong:spread<60?L.sr_ov_moderate:L.sr_ov_weak;
                    const cC = spread<30?"#10b981":spread<60?"#f59e0b":"#ef4444";
                    return <>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                        <span style={{fontSize:10,color:"#94a3b8"}}>{L.sr_ov_conv_level}</span>
                        <span style={{fontSize:11,fontWeight:800,color:cC}}>{conv}</span>
                      </div>
                      <div style={{height:6,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden",marginBottom:6}}>
                        <div style={{width:`${Math.min(100,100-spread/1.8)}%`,height:"100%",background:cC,borderRadius:3,transition:"width 0.6s"}}/>
                      </div>
                      <div style={{fontSize:10,color:"#475569"}}>{L.sr_ov_dispersion} : {spread.toFixed(1)}% {L.sr_ov_of_median} · Min {fV(globalEqMin)} · Max {fV(globalEqMax)}</div>
                    </>;
                  })()}
                </div>

                {/* Par catégorie */}
                <div style={{background:"rgba(255,255,255,0.02)",borderRadius:8,border:"1px solid rgba(255,255,255,0.06)",padding:"14px 16px"}}>
                  <div style={{fontSize:10,color:"#64748b",fontWeight:700,letterSpacing:"0.6px",textTransform:"uppercase",marginBottom:10}}>{L.sr_ov_cat_title}</div>
                  {[L.sr_cat_financial,L.sr_cat_intangible,L.sr_cat_asset,L.sr_cat_jur_fr,L.sr_cat_jur_us,L.sr_cat_jur_de,L.sr_cat_jur_ch,L.sr_cat_jur_uk].map(cat=>{
                    const catRows = rows.filter(r=>r.category===cat);
                    if (!catRows.length) return null;
                    const catMedian = catRows.map(r=>r.eq_mid).sort((a,b)=>a-b);
                    const cm = catMedian.length%2===0?(catMedian[catMedian.length/2-1]+catMedian[catMedian.length/2])/2:catMedian[Math.floor(catMedian.length/2)];
                    return (
                      <div key={cat} style={{paddingBottom:8,marginBottom:8,borderBottom:"1px solid rgba(255,255,255,0.04)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <span style={{fontSize:10,color:"#94a3b8",fontWeight:600}}>{cat}</span>
                          <div style={{display:"flex",gap:4,marginTop:3,flexWrap:"wrap"}}>
                            {catRows.map(r=><span key={r.key} style={{fontSize:10,padding:"1px 6px",borderRadius:3,background:`${r.color}18`,color:r.color,fontWeight:600}}>{r.name}</span>)}
                          </div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:10,color:"#475569"}}>{L.sr_ov_cat_median}</div>
                          <div style={{fontSize:11,fontWeight:700,color:"#94a3b8",fontFamily:"monospace"}}>{fV(cm)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </FadeIn>
  );
};


// ==========================================================================
// PIN LOGIN
// ==========================================================================
const APP_PIN = "000000"; // ← Set your PIN here
const AUTH_KEY = "sov_auth";
const MAX_ATTEMPTS = 3;
const LOCKOUT_SECONDS = 30;

const LoginScreen = ({ onAuth, L }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shaking, setShaking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockout, setLockout] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (lockout <= 0) return;
    const t = setTimeout(() => setLockout(l => l - 1), 1000);
    return () => clearTimeout(t);
  }, [lockout]);

  const triggerShake = () => {
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
  };

  const handleSubmit = () => {
    if (lockout > 0 || pin.length !== 6) return;
    if (pin === APP_PIN) {
      sessionStorage.setItem(AUTH_KEY, btoa(Date.now() + ":sov"));
      onAuth();
    } else {
      const next = attempts + 1;
      setAttempts(next);
      setPin("");
      triggerShake();
      if (next >= MAX_ATTEMPTS) {
        setError(L.login_too_many);
        setLockout(LOCKOUT_SECONDS);
        setAttempts(0);
      } else {
        setError(`${L.login_incorrect} (${MAX_ATTEMPTS - next} ${MAX_ATTEMPTS - next > 1 ? L.login_attempts_remaining : L.login_attempt_remaining})`);
      }
    }
  };

  const handleChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(v);
    setError("");
    if (v.length === 6) setTimeout(() => { /* auto-submit after last digit */
      if (v === APP_PIN) {
        sessionStorage.setItem(AUTH_KEY, btoa(Date.now() + ":sov"));
        onAuth();
      } else {
        const next = attempts + 1;
        setAttempts(next);
        setPin("");
        triggerShake();
        if (next >= MAX_ATTEMPTS) {
          setError(L.login_too_many);
          setLockout(LOCKOUT_SECONDS);
          setAttempts(0);
        } else {
          setError(`${L.login_incorrect} (${MAX_ATTEMPTS - next} ${MAX_ATTEMPTS - next > 1 ? L.login_attempts_remaining : L.login_attempt_remaining})`);
        }
      }
    }, 80);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSubmit(); };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:"#080c14", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sov-shake{0%,100%{transform:translateX(0)}15%,45%,75%{transform:translateX(-8px)}30%,60%,90%{transform:translateX(8px)}}`}</style>
      <div style={{ position:"fixed", inset:0, background:"radial-gradient(ellipse at 15% 0%,rgba(201,168,76,0.06) 0%,transparent 55%),radial-gradient(ellipse at 85% 100%,rgba(37,99,235,0.04) 0%,transparent 55%),radial-gradient(ellipse at 50% 50%,rgba(13,27,62,0.8) 0%,transparent 100%)", pointerEvents:"none" }} />
      <div style={{ position:"relative", zIndex:1, textAlign:"center", width:340 }}>
        <div style={{ marginBottom:32 }}>
          <HtvLogo size={56} />
          <div style={{ marginTop:12, fontSize:20, fontWeight:800, color:"#f1f5f9", letterSpacing:"-0.5px" }}>{L.login_title}</div>
          <div style={{ fontSize:10, color:"#C9A84C", fontWeight:600, letterSpacing:"1.5px", textTransform:"uppercase", marginTop:4 }}>{L.login_subtitle}</div>
        </div>
        <div style={{
          background:"rgba(255,255,255,0.03)", border:"1px solid rgba(201,168,76,0.15)", borderRadius:16, padding:"32px 28px",
          backdropFilter:"blur(12px)",
        }}>
          <div style={{ fontSize:13, color:"#94a3b8", marginBottom:24, fontWeight:500 }}>{L.login_enter_pin}</div>
          <div style={{ animation: shaking ? "sov-shake 0.5s ease" : "none", marginBottom:24 }}>
            <input
              ref={inputRef}
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={lockout > 0}
              autoComplete="off"
              placeholder="······"
              style={{
                width:"100%", boxSizing:"border-box", textAlign:"center",
                fontSize:28, letterSpacing:12, fontFamily:"monospace",
                padding:"14px 16px",
                background:"rgba(255,255,255,0.04)", color:"#f1f5f9",
                border: error ? "1.5px solid rgba(239,68,68,0.5)" : "1.5px solid rgba(201,168,76,0.25)",
                borderRadius:12, outline:"none",
                transition:"border-color 0.25s ease, box-shadow 0.25s ease",
                caretColor:"#C9A84C",
              }}
              onFocus={e => { if (!error) e.currentTarget.style.borderColor="rgba(201,168,76,0.6)"; e.currentTarget.style.boxShadow="0 0 0 3px rgba(201,168,76,0.1)"; }}
              onBlur={e => { e.currentTarget.style.borderColor= error ? "rgba(239,68,68,0.5)" : "rgba(201,168,76,0.25)"; e.currentTarget.style.boxShadow="none"; }}
            />
          </div>
          {lockout > 0 ? (
            <div style={{ fontSize:13, color:"#ef4444", fontWeight:600, padding:"10px 0" }}>
              {L.login_locked} {lockout}s
            </div>
          ) : (
            <button onClick={handleSubmit} disabled={pin.length !== 6} style={{
              width:"100%", background: pin.length === 6 ? "linear-gradient(135deg,#C9A84C,#a88a2e)" : "rgba(255,255,255,0.04)",
              border: pin.length === 6 ? "none" : "1px solid rgba(255,255,255,0.08)",
              borderRadius:10, padding:"12px 0", color: pin.length === 6 ? "#0a0e1a" : "#475569",
              fontSize:14, fontWeight:700, cursor: pin.length === 6 ? "pointer" : "default",
              transition:"all 0.25s ease", letterSpacing:"0.5px",
            }}>
              {L.login_unlock}
            </button>
          )}
          {error && (
            <div style={{ marginTop:14, fontSize:12, color:"#ef4444", fontWeight:500, transition:"opacity 0.3s" }}>{error}</div>
          )}
        </div>
        <div style={{ marginTop:20, fontSize:10, color:"#334155" }}>{L.login_session_expires}</div>
      </div>
    </div>
  );
};

export default function App(){
  const[isAuthenticated,setIsAuthenticated]=useState(()=>!!sessionStorage.getItem(AUTH_KEY));
  const[lang,setLang]=useState("fr");
  const[view,setView]=useState("dash");
  const[dashTab,setDashTab]=useState("strat");
  const[modelResults,setModelResults]=useState({});
  const[xlData,setXlData]=useState(null);
  const[diagStratData,setDiagStratData]=useState(()=>{try{const s=localStorage.getItem("sov_diag_strat");return s?JSON.parse(s):null;}catch{return null;}});
  const[diagFinData,setDiagFinData]=useState(()=>{try{const s=localStorage.getItem("sov_diag_fin");return s?JSON.parse(s):null;}catch{return null;}});
  const[synthesisData,setSynthesisData]=useState(()=>{try{const s=localStorage.getItem("sov_synthesis");return s?JSON.parse(s):null;}catch{return null;}});
  const[hypothesisVersion,setHypothesisVersion]=useState(0);
  const[aiSuggestions,setAiSuggestions]=useState(()=>{try{const s=localStorage.getItem("sov_ai_suggestions");return s?JSON.parse(s):{};}catch{return{};}});
  const L=TR[lang];
  const Comp=comps[view];

  const storeResult=(model,result,params)=>{
    setModelResults(prev=>({...prev,[model]:{...result, _params: params}}));
  };

  const handleXlImport = (data) => {
    setXlData(data || null);
    if (data?.diag_strat) {
      setDiagStratData(data.diag_strat);
      try{localStorage.setItem("sov_diag_strat",JSON.stringify(data.diag_strat));}catch{}
    }
  };

  const handleDiagStratChange = useCallback((data) => {
    setDiagStratData(data);
    try{localStorage.setItem("sov_diag_strat",JSON.stringify(data));}catch{}
  }, []);

  const handleDiagFinChange = useCallback((data) => {
    setDiagFinData(data);
    try{localStorage.setItem("sov_diag_fin",JSON.stringify(data));}catch{}
  }, []);

  const handleSynthesisChange = useCallback((data) => {
    setSynthesisData(data);
    try{localStorage.setItem("sov_synthesis",JSON.stringify(data));}catch{}
  }, []);

  const handleAiSuggestionsChange = useCallback((data) => {
    setAiSuggestions(data);
    try{localStorage.setItem("sov_ai_suggestions",JSON.stringify(data));}catch{}
  }, []);

  const handleApplyHypotheses = useCallback((payload) => {
    setXlData(prev => ({...(prev||{}), _suggestedHypotheses: payload}));
    setHypothesisVersion(v => v + 1);
  }, []);

  const handleResetSession = useCallback(() => {
    // Clear all diagnostic & synthesis state
    setDiagStratData(null);
    setDiagFinData(null);
    setSynthesisData(null);
    setAiSuggestions({});
    // Clear Excel import data & model results
    setXlData(null);
    setModelResults({});
    setHypothesisVersion(0);
    // Reset navigation to first tab
    setView("dash");
    // Clear all persisted data
    try { localStorage.removeItem("sov_diag_strat"); } catch {}
    try { localStorage.removeItem("sov_diag_fin"); } catch {}
    try { localStorage.removeItem("sov_synthesis"); } catch {}
    try { localStorage.removeItem("sov_ai_suggestions"); } catch {}
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(AUTH_KEY);
    setIsAuthenticated(false);
  }, []);

  // When navigating to a model, merge xlData preset into that model's component
  // via a key on the Comp wrapper to force remount with fresh defaults
  const xlPreset = view !== "dash" ? {...(xlData?.[view]||{}), ...(xlData?._suggestedHypotheses||{})} : null;

  if (!isAuthenticated) return <LoginScreen onAuth={() => setIsAuthenticated(true)} L={L} />;

  return(
    <div style={{minHeight:"100vh",background:"#080c14",color:"#e2e8f0",fontFamily:"'DM Sans','Segoe UI',sans-serif"}}>
      <div style={{position:"fixed",inset:0,background:"radial-gradient(ellipse at 15% 0%,rgba(201,168,76,0.06) 0%,transparent 55%),radial-gradient(ellipse at 85% 100%,rgba(37,99,235,0.04) 0%,transparent 55%),radial-gradient(ellipse at 50% 50%,rgba(13,27,62,0.8) 0%,transparent 100%)",pointerEvents:"none",zIndex:0}}/>
      <div style={{position:"relative",zIndex:1}}>
        <nav style={{borderBottom:"1px solid rgba(201,168,76,0.12)",padding:"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",height:52}}>
          <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>setView("dash")}>
            <HtvLogo size={32}/>
            <div style={{display:"flex",flexDirection:"column",lineHeight:1}}>
              <span style={{fontSize:14,fontWeight:800,color:"#f1f5f9",letterSpacing:"-0.3px"}}>{L.brand}</span>
              <span style={{fontSize:10,color:"#C9A84C",fontWeight:600,letterSpacing:"0.5px",textTransform:"uppercase",marginTop:1}}>{L.tag}</span>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {/* Excel import indicator in nav when file loaded */}
            {xlData && view!=="dash" && (
              <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(96,165,250,0.08)",border:"1px solid rgba(96,165,250,0.2)",borderRadius:6,padding:"3px 10px"}}>
                <svg width={10} height={10} viewBox="0 0 10 10" fill="none"><rect x="1" y="1" width="8" height="5" rx="0.8" stroke="#60a5fa" strokeWidth="1.2"/><line x1="1" y1="3.5" x2="9" y2="3.5" stroke="#60a5fa" strokeWidth="0.8"/><line x1="3.5" y1="1" x2="3.5" y2="6" stroke="#60a5fa" strokeWidth="0.8"/></svg>
                <span style={{fontSize:10,color:"#60a5fa",fontWeight:600}}>{xlData._meta?.company_name||"Excel"}</span>
              </div>
            )}
            {(()=>{const intlC=INTL_METHODS.filter(k=>!!modelResults[k]).length;const jurC=JUR_METHODS.filter(k=>!!modelResults[k]).length;return(intlC+jurC)>0&&(<span style={{fontSize:10,color:"#64748b",background:"rgba(255,255,255,0.04)",padding:"3px 8px",borderRadius:6}}>{intlC}/{INTL_METHODS.length} {L.nav_models} · {jurC}/{JUR_METHODS.length} {L.nav_jurisdictions}</span>);})()}
            <button onClick={()=>setLang(lang==="en"?"fr":"en")} style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,padding:"4px 10px",color:"#94a3b8",fontSize:11,cursor:"pointer",fontWeight:600,transition:"all 0.2s"}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
              onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}>{lang==="en"?"FR":"EN"}</button>
              <button onClick={()=>setView("jurisdictions")} style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,padding:"4px 12px",color:"#94a3b8",fontSize:11,cursor:"pointer",fontWeight:600}}>🌍 {L.nav_jurisdiction}</button>
              <button onClick={handleLogout} title={L.nav_logout} style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:7,padding:"4px 10px",color:"#ef4444",fontSize:11,cursor:"pointer",fontWeight:600,transition:"all 0.2s",display:"flex",alignItems:"center",gap:4}}
                onMouseEnter={e=>{e.currentTarget.style.background="rgba(239,68,68,0.15)";e.currentTarget.style.borderColor="rgba(239,68,68,0.4)";}}
                onMouseLeave={e=>{e.currentTarget.style.background="rgba(239,68,68,0.08)";e.currentTarget.style.borderColor="rgba(239,68,68,0.2)";}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></button>
          </div>
        </nav>
        <div style={{padding:"0 24px",maxWidth:1150,margin:"0 auto"}}>
          {view==="dash"&&<Dash L={L} lang={lang} onSel={setView} modelResults={modelResults} xlData={xlData} onXlImport={handleXlImport} diagStratData={diagStratData} diagFinData={diagFinData} onDiagStratChange={handleDiagStratChange} onDiagFinChange={handleDiagFinChange} synthesisData={synthesisData} onSynthesisChange={handleSynthesisChange} onApplyHypotheses={handleApplyHypotheses} aiSuggestions={aiSuggestions} onAiSuggestionsChange={handleAiSuggestionsChange} onResetSession={handleResetSession} dashTab={dashTab} setDashTab={setDashTab}/>}
          {view==="rapport"&&<SynthesisReport results={modelResults} xlData={xlData} L={L} onBack={()=>{setDashTab("models");setView("dash");}}/>}
          {view==="jurisdictions"&&<JuridictionView modelResults={modelResults} L={L} lang={lang} onBack={()=>{setDashTab("models");setView("dash");}}/>}
          {Comp&&view!=="rapport"&&<FadeIn key={view}><div style={{padding:"24px 0"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
              <button onClick={()=>{setDashTab("models");setView("dash");}} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"6px 14px",color:"#94a3b8",fontSize:11,cursor:"pointer",transition:"all 0.2s"}}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}>{L.back}</button>
              <div style={{textAlign:"right"}}>
                <h2 style={{fontSize:18,fontWeight:700,color:"#f1f5f9",margin:0}}>{L.m[view].n}</h2>
                <p style={{fontSize:11,color:colors[view],fontWeight:500,margin:0}}>{L.m[view].x}</p>
              </div>
            </div>
            {/* Pass xlPreset to model as initialValues prop — each model checks for it */}
            <Comp key={view+(xlData?xlData._meta?.company_name||"xl":"")+hypothesisVersion} L={L} onResult={(r)=>storeResult(view,r)} initialValues={xlPreset}/>
          </div></FadeIn>}
        </div>
      </div>
    </div>
  );
}
